import React, {
  lazy,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AppShell } from '../../features/ui-system';

import { Link, useLocation, useNavigate } from 'react-router-dom';

import { ArrowLeft, CreditCard, History, Home, LogOut, Map, MapPin, Maximize2, MessageSquare, Minimize2, Network, Newspaper, Pause, Phone, Play, RefreshCw, Search, Settings, SkipBack, SkipForward, Ticket, User, Users, Wrench } from 'lucide-react';

import { Circle, MapContainer, Marker, Popup, Polyline, Tooltip, useMap, useMapEvent } from 'react-leaflet';

import L from 'leaflet';

import 'leaflet/dist/leaflet.css';
import LeafletTileLayer from '../../components/LeafletTileLayer';

import axios from 'axios';

import { Button } from '../../components/ui/button';

import { Input } from '../../components/ui/input';

import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import SearchableComboBox from '../../components/SearchableComboBox';

import { toast } from 'sonner';

import ktcLogo from '../../assets/ktc.png';
import StaffPageLoadingOverlay from '../../components/StaffPageLoadingOverlay';
import InfraServiceHealthPanel from '../../components/InfraServiceHealthPanel';
import MapStatusPill from '../../components/MapStatusPill';
import StaffPageHeader from '../../components/StaffPageHeader';
import AIInsightPanel from '../../components/dashboard/AIInsightPanel.jsx';
import LeafletFlowDirection from '../../components/LeafletFlowDirection';
import LeafletRealtimeNetworkLayer from '../../components/LeafletRealtimeNetworkLayer.jsx';
import LeafletRealtimeWebGLLayer from '../../components/LeafletRealtimeWebGLLayer.jsx';
import { getBandwidthStyle, resolveLinkLoad } from '../../components/leafletBandwidthStyle';

import './AdminConnectivityMap.css';

import { getInfraNodePreviewClassName } from '../../components/leafletNetworkIcons';
import { createOLTMarker, createODPMarker, drawConnection } from '../../components/leafletFuturisticNetwork';
import { clearStaffSession, isJwtExpired, resolveBackendBaseUrl } from '../../utils/runtimeConfig';
import { resolveGenieHealthStatus } from '../../utils/integrationHealth';
import {
  buildCapacityInsights,
  buildCapacityRecommendations,
  buildNasCapacityPredictions,
  buildOdpCapacityPredictions,
  getRecommendationTypeBadgeClass,
  getRecommendationTypeLabel,
  getRiskBadgeClass,
  getUrgencyBadgeClass,
} from '../../utils/capacityPrediction';
import useMinimumLoadingVisibility from '../../hooks/useMinimumLoadingVisibility';
import { normalizeFtthTopologyPayload, normalizeVoucherNasPayload } from './utils/coreEndpointContract';
import {
  mapMonitoringStatusToTone,
  semanticToneToBadgeClass,
  semanticToneToColor,
  semanticToneToTextClass,
} from '../../features/ui-system/status/semanticStatus';



const API = `${resolveBackendBaseUrl()}/api`;
const MONITORING_INCIDENT_API_ENABLED = String(process.env.REACT_APP_MONITORING_INCIDENTS_ENABLED || 'false').trim().toLowerCase() === 'true';
const MONITORING_UI_V2_ENABLED = String(process.env.REACT_APP_MONITORING_UI_V2 || 'true').trim().toLowerCase() !== 'false';
const MONITORING_BASELINE_FOCUS = true;
const MONITORING_PRIMARY_TABS = MONITORING_BASELINE_FOCUS
  ? ['infra', 'customers', 'capacity']
  : ['ai', 'infra', 'capacity', 'queue', 'tickets', 'customers'];
const LazyGpuNetworkMap = lazy(() => import('../../components/GpuNetworkMap.jsx'));

const DEFAULT_CENTER = { latitude: -3.4324, longitude: 119.3430 };
const FETCH_RETRY_DELAYS_MS = [2000, 5000, 10000];
const STATUS_POLL_INTERVAL_MS = 8000;
const MAP_SVG_RENDERER = L.svg();
const STATUS_CHIPS = [
  { value: 'all', label: 'Semua', tone: 'info' },
  { value: 'online', label: 'Online', tone: 'ok' },
  { value: 'offline', label: 'Offline/Loss', tone: 'danger' },
  { value: 'not_mapped', label: 'Belum Mapping', tone: 'warn' },
  { value: 'unknown', label: 'Tidak Diketahui', tone: 'neutral' },
];
const VIEW_MODE_OPTIONS = [
  { value: 'all', label: 'Semua Node' },
  { value: 'onu', label: 'ONU' },
  { value: 'odp', label: 'ODP' },
  { value: 'olt', label: 'OLT' },
];
const VISUAL_MODE_OPTIONS = [
  { value: 'normal', label: 'Normal' },
  { value: 'flow', label: 'Flow' },
  { value: 'ai', label: 'AI' },
];
const REPLAY_SPEED_OPTIONS = [1, 2, 4];
const ACTION_QUEUE_STORAGE_KEY = 'ktc_monitoring_action_queue_v1';
const ACTION_QUEUE_GUARD_STORAGE_KEY = 'ktc_monitoring_recommendation_guard_v1';
const ACTION_QUEUE_FEEDBACK_STORAGE_KEY = 'ktc_monitoring_recommendation_feedback_v1';
const ACTION_QUEUE_EVAL_STORAGE_KEY = 'ktc_monitoring_recommendation_eval_v1';
const ACTION_QUEUE_AUTO_HOLD_STORAGE_KEY = 'ktc_monitoring_auto_action_hold_v1';
const ACTION_QUEUE_AUTO_LOG_STORAGE_KEY = 'ktc_monitoring_auto_action_log_v1';
const ACTION_QUEUE_LIMIT = 10;
const ACTION_QUEUE_GROUP_ORDER = ['kritis', 'tinggi', 'sedang', 'rendah'];
const ACTION_QUEUE_URGENCY_RANK = { kritis: 4, tinggi: 3, sedang: 2, rendah: 1 };
const ACTION_QUEUE_STATUS_OPTIONS = ['pending', 'accepted', 'completed', 'ignored', 'pending_review', 'approved', 'executed', 'skipped', 'failed', 'cancelled'];
const ACTION_QUEUE_FEEDBACK_TYPES = ['positive', 'negative'];
const AUTO_ACTION_CANDIDATE_STATUSES = ['pending_review', 'approved', 'executed', 'skipped', 'failed', 'cancelled'];
const AUTO_ACTION_MODES = ['manual_review', 'semi_auto', 'auto_low_risk'];
const AUTO_ACTION_RULE_POLICIES = {
  overdue_reminder_ready: {
    rule_key: 'overdue_reminder_ready',
    enabled: true,
    action_type: 'monitor',
    action_mode: 'auto_low_risk',
    confidence_threshold: 0.5,
    cooldown_ms: 4 * 60 * 60 * 1000,
  },
  billing_suspend_ready: {
    rule_key: 'billing_suspend_ready',
    enabled: true,
    action_type: 'monitor',
    action_mode: 'manual_review',
    confidence_threshold: 0.7,
    cooldown_ms: 8 * 60 * 60 * 1000,
  },
  billing_restore_ready: {
    rule_key: 'billing_restore_ready',
    enabled: true,
    action_type: 'monitor',
    action_mode: 'semi_auto',
    confidence_threshold: 0.65,
    cooldown_ms: 3 * 60 * 60 * 1000,
  },
  anomaly_ticket_ready: {
    rule_key: 'anomaly_ticket_ready',
    enabled: true,
    action_type: 'monitor',
    action_mode: 'manual_review',
    confidence_threshold: 0.62,
    cooldown_ms: 3 * 60 * 60 * 1000,
  },
  capacity_planning_ready: {
    rule_key: 'capacity_planning_ready',
    enabled: true,
    action_type: 'planning',
    action_mode: 'semi_auto',
    confidence_threshold: 0.6,
    cooldown_ms: 6 * 60 * 60 * 1000,
  },
};
const RECOMMENDATION_RUBRIC_EMPTY = {
  accuracy: null,
  relevance: null,
  timing: null,
  actionability: null,
  total: null,
};
const AI_ACTION_STATES = {
  IDLE: 'idle',
  DETECTED: 'detected',
  RECOMMENDED: 'recommended',
  APPROVED: 'approved',
  EXECUTING: 'executing',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed',
  ESCALATED: 'escalated',
};

const AI_ACTION_DONE_STATES = new Set([AI_ACTION_STATES.SUCCEEDED, AI_ACTION_STATES.FAILED, AI_ACTION_STATES.ESCALATED]);

const toActionKey = (action = {}) => {
  const type = String(action?.type || '').toUpperCase();
  const payload = action?.payload && typeof action.payload === 'object' ? action.payload : {};
  const customerId = String(payload?.customer_id || payload?.target_customer_id || payload?.customer_number || '').trim();
  const odpId = String(payload?.odp_id || '').trim();
  const oltId = String(payload?.olt_id || '').trim();
  return [type, customerId || '-', odpId || '-', oltId || '-'].join('::');
};

const resolveActionTarget = (action = {}) => {
  const payload = action?.payload && typeof action.payload === 'object' ? action.payload : {};
  const customerId = String(payload?.customer_id || payload?.target_customer_id || payload?.customer_number || '').trim();
  if (customerId) return { type: 'customer', id: customerId, label: `Pelanggan ${customerId}` };
  const odpId = String(payload?.odp_id || '').trim();
  if (odpId) return { type: 'odp', id: odpId, label: `ODP ${odpId}` };
  const oltId = String(payload?.olt_id || '').trim();
  if (oltId) return { type: 'olt', id: oltId, label: `OLT ${oltId}` };
  return { type: 'global', id: '', label: 'Global' };
};

const isSafeAutoActionType = (typeRaw) => {
  const type = String(typeRaw || '').toUpperCase();
  return ['OPEN_MONITORING', 'SEND_BILL_REMINDER', 'SEND_REMINDER_BATCH_AUTO', 'ASSIGN_TECHNICIAN', 'ASSIGN_TECHNICIAN_AUTO'].includes(type);
};

const formatElapsedShort = (msValue) => {
  const ms = Math.max(0, Number(msValue || 0));
  const totalSec = Math.floor(ms / 1000);
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  if (min <= 0) return `${sec}s`;
  return `${min}m ${sec}s`;
};

const parseTimestampMs = (value) => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCompactDateTime = (ms) => {
  const value = Number(ms || 0);
  if (!Number.isFinite(value) || value <= 0) return '-';
  return new Date(value).toLocaleString('id-ID', {
    day: '2-digit',
    month: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const createReplayJumpPoints = (events = []) => {
  const jumpLabelByType = {
    incident_start: 'Mulai',
    anomaly_detected: 'Anomali',
    root_cause_identified: 'Akar Masalah',
    ticket_created: 'Tiket',
    in_progress: 'Diproses',
    escalated: 'Dieskalasi',
    recovery_start: 'Pemulihan',
    resolved: 'Selesai',
    incident_end: 'Akhir',
  };
  return events
    .filter((event) => jumpLabelByType[event?.type])
    .map((event) => ({
      key: String(event.id || event.type),
      label: jumpLabelByType[event.type],
      atMs: Number(event.atMs || 0),
    }));
};

const checkWebglAvailability = () => {
  try {
    const canvas = document.createElement('canvas');
    return Boolean(
      canvas.getContext('webgl2')
      || canvas.getContext('webgl')
      || canvas.getContext('experimental-webgl'),
    );
  } catch {
    return false;
  }
};



const hasValidCoordinates = (lat, lon) => {

  const latitude = Number(lat);

  const longitude = Number(lon);

  return Number.isFinite(latitude) && Number.isFinite(longitude) && latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180;

};


const normalizeNodeId = (value) => String(value ?? '').trim();
const normalizeActionQueueUrgency = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'kritis' || raw === 'critical') return 'kritis';
  if (raw === 'tinggi' || raw === 'high') return 'tinggi';
  if (raw === 'sedang' || raw === 'medium' || raw === 'warning') return 'sedang';
  return 'rendah';
};
const normalizeActionQueueActionType = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'capacity_expansion' || raw === 'expansion') return 'expansion';
  if (raw === 'rebalance') return 'rebalance';
  if (raw === 'planning') return 'planning';
  return 'monitor';
};
const getActionQueueUrgencyTone = (urgency) => {
  const normalized = normalizeActionQueueUrgency(urgency);
  if (normalized === 'kritis') return 'critical';
  if (normalized === 'tinggi') return 'high';
  if (normalized === 'sedang') return 'medium';
  return 'low';
};
const getActionQueueStatusClass = (status) => {
  const normalized = String(status || '').trim().toLowerCase();
  if (normalized === 'failed') return semanticToneToBadgeClass('danger');
  if (normalized === 'executed' || normalized === 'completed') return semanticToneToBadgeClass('ok');
  if (normalized === 'approved' || normalized === 'accepted') return semanticToneToBadgeClass('info');
  if (normalized === 'skipped' || normalized === 'ignored' || normalized === 'cancelled') return semanticToneToBadgeClass('neutral');
  return semanticToneToBadgeClass('warn');
};
const normalizeAutoActionStatus = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'pending' || raw === 'accepted' || raw === 'completed' || raw === 'ignored') return raw;
  if (AUTO_ACTION_CANDIDATE_STATUSES.includes(raw)) return raw;
  return 'pending_review';
};
const normalizeAutoActionMode = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  return AUTO_ACTION_MODES.includes(raw) ? raw : 'manual_review';
};
const getAutoActionModeLabel = (mode) => {
  const normalized = normalizeAutoActionMode(mode);
  if (normalized === 'auto_low_risk') return 'Auto';
  if (normalized === 'semi_auto') return 'Semi Auto';
  return 'Needs Review';
};
const getAutoActionModeClass = (mode) => {
  const normalized = normalizeAutoActionMode(mode);
  if (normalized === 'auto_low_risk') return 'border-emerald-500/45 bg-emerald-500/10 text-emerald-200';
  if (normalized === 'semi_auto') return 'border-sky-500/45 bg-sky-500/10 text-sky-200';
  return 'border-amber-500/45 bg-amber-500/10 text-amber-200';
};
const getActionQueueFeedbackClass = (feedbackType) => {
  const normalized = String(feedbackType || '').trim().toLowerCase();
  if (normalized === 'positive') return 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200';
  if (normalized === 'negative') return 'border-rose-500/40 bg-rose-500/10 text-rose-200';
  return 'border-white/15 bg-white/[0.03] text-slate-300';
};
const ACTION_CLASS_OPTIONS = ['investigative', 'operational', 'business', 'disruptive'];
const ACTION_CLASS_RISK_SCORE = {
  investigative: 12,
  operational: 28,
  business: 22,
  disruptive: 74,
};
const classifyActionCandidate = (item) => {
  const actionType = normalizeActionQueueActionType(item?.action_type);
  const ruleKey = String(item?.rule_key || '').toLowerCase();
  const text = `${String(item?.title || '')} ${String(item?.recommended_action || '')} ${String(item?.reason || '')}`.toLowerCase();
  if (
    text.includes('restart')
    || text.includes('suspend')
    || text.includes('reprovision')
    || text.includes('disconnect')
    || ruleKey.includes('suspend')
  ) return 'disruptive';
  if (
    text.includes('monitor')
    || text.includes('investigasi')
    || text.includes('diagnose')
    || text.includes('analisa')
    || text.includes('cek ')
    || text.includes('tinjau')
  ) return 'investigative';
  if (
    text.includes('reminder')
    || text.includes('billing')
    || text.includes('tagihan')
    || ruleKey.includes('reminder')
    || ruleKey.includes('billing')
  ) return 'business';
  if (actionType === 'monitor' && String(item?.source_type || '').toLowerCase() === 'customer') return 'business';
  if (actionType === 'monitor') return 'investigative';
  if (actionType === 'planning') return 'operational';
  if (actionType === 'expansion' || actionType === 'rebalance') return 'operational';
  return 'operational';
};
const clampScore100 = (value) => Math.max(0, Math.min(100, Number(value || 0)));
const getDecisionLabelFromScore = (score) => {
  if (score >= 82) return 'Primary Recommended';
  if (score >= 68) return 'Recommended';
  if (score >= 52) return 'Conditional';
  if (score >= 36) return 'Investigate First';
  return 'Low Priority';
};
const getDecisionLabelClass = (label) => {
  const raw = String(label || '').toLowerCase();
  if (raw.includes('primary')) return 'border-cyan-500/50 bg-cyan-500/15 text-cyan-200';
  if (raw === 'recommended') return 'border-emerald-500/50 bg-emerald-500/15 text-emerald-200';
  if (raw.includes('conditional')) return 'border-amber-500/50 bg-amber-500/15 text-amber-200';
  if (raw.includes('investigate')) return 'border-sky-500/50 bg-sky-500/15 text-sky-200';
  return 'border-slate-500/50 bg-slate-500/15 text-slate-300';
};
const normalizeDecisionEventType = (eventTypeRaw) => {
  const raw = String(eventTypeRaw || '').trim().toLowerCase();
  if (raw === 'approved') return 'accepted';
  if (raw === 'executed') return 'completed';
  if (raw === 'skipped') return 'ignored';
  if (raw === 'cancelled') return 'held';
  return raw;
};
const toScoreBand = (value, bands = []) => {
  const numeric = Number(value || 0);
  for (const band of bands) {
    if (numeric >= band.min && numeric < band.max) return band.key;
  }
  return bands[bands.length - 1]?.key || 'unknown';
};
const getRiskBandFromUrgency = (urgency) => {
  const normalized = normalizeActionQueueUrgency(urgency);
  if (normalized === 'kritis') return 'critical';
  if (normalized === 'tinggi') return 'high';
  if (normalized === 'sedang') return 'medium';
  return 'low';
};
const getConfidenceBand = (confidencePct) => toScoreBand(confidencePct, [
  { key: 'low_conf', min: 0, max: 45 },
  { key: 'mid_conf', min: 45, max: 70 },
  { key: 'high_conf', min: 70, max: 200 },
]);
const buildDecisionPatternKey = ({ actionType, sourceType, rootCauseType, riskBand, confidenceBand }) => {
  const parts = [
    normalizeActionQueueActionType(actionType || 'monitor'),
    normalizeIncidentSourceType(sourceType || 'topology_node'),
    normalizeIncidentSourceType(rootCauseType || 'topology_node'),
    String(riskBand || 'medium').trim().toLowerCase() || 'medium',
    String(confidenceBand || 'mid_conf').trim().toLowerCase() || 'mid_conf',
  ];
  return parts.join('|');
};
const computeAdaptiveModifierFromPatternStats = (stats) => {
  if (!stats || typeof stats !== 'object') {
    return { modifier: 0, reason: 'No adaptive adjustment due to missing pattern stats.', sampleCount: 0 };
  }
  const sampleCount = Number(stats.sample_count || 0);
  const weightedSample = Number(stats.weighted_sample_count || 0);
  if (sampleCount < 5 || weightedSample < 3.2) {
    return {
      modifier: 0,
      reason: 'No adaptive adjustment due to low sample count.',
      sampleCount,
    };
  }
  const safeRate = (value) => {
    const raw = Number(value || 0);
    if (!Number.isFinite(raw) || raw <= 0 || weightedSample <= 0) return 0;
    return Math.max(0, Math.min(1, raw / weightedSample));
  };
  const positiveSignal = (safeRate(stats.helpful_weighted) * 7.5) + (safeRate(stats.completed_weighted) * 5.8) + (safeRate(stats.accepted_weighted) * 2.5);
  const negativeSignal = (safeRate(stats.not_relevant_weighted) * 8.2) + (safeRate(stats.failed_weighted) * 8.8) + (safeRate(stats.skipped_weighted) * 4.1) + (safeRate(stats.held_weighted) * 3.2);
  const stabilityScale = Math.min(1, weightedSample / 16);
  const rawModifier = (positiveSignal - negativeSignal) * stabilityScale;
  const modifier = Math.max(-12, Math.min(12, Number(rawModifier.toFixed(1))));
  if (modifier > 0) {
    return {
      modifier,
      reason: `Adjusted +${modifier.toFixed(1)} from similar operator-approved outcomes.`,
      sampleCount,
    };
  }
  if (modifier < 0) {
    return {
      modifier,
      reason: `Adjusted ${modifier.toFixed(1)} because similar actions were often ignored/skipped/failed.`,
      sampleCount,
    };
  }
  return {
    modifier: 0,
    reason: 'No adaptive adjustment: positive and negative history are balanced.',
    sampleCount,
  };
};
const getActionClassBadgeClass = (actionClass) => {
  const raw = String(actionClass || '').toLowerCase();
  if (raw === 'investigative') return 'border-sky-500/50 bg-sky-500/15 text-sky-200';
  if (raw === 'operational') return 'border-cyan-500/50 bg-cyan-500/15 text-cyan-200';
  if (raw === 'business') return 'border-emerald-500/50 bg-emerald-500/15 text-emerald-200';
  if (raw === 'disruptive') return 'border-rose-500/50 bg-rose-500/15 text-rose-200';
  return 'border-slate-500/50 bg-slate-500/15 text-slate-300';
};
const buildDecisionScorePayload = (params = {}) => {
  const {
    item,
    monitorContext,
    history = null,
  } = params;
  const urgency = normalizeActionQueueUrgency(item?.urgency);
  const actionClass = ACTION_CLASS_OPTIONS.includes(String(item?.action_class || '').toLowerCase())
    ? String(item?.action_class || '').toLowerCase()
    : classifyActionCandidate(item);
  const riskScore = ACTION_CLASS_RISK_SCORE[actionClass] ?? 30;
  const severityFromUrgency = urgency === 'kritis' ? 95 : urgency === 'tinggi' ? 78 : urgency === 'sedang' ? 58 : 35;
  const severityFromMetrics = Number.isFinite(Number(item?.metrics?.occupancyPercent))
    ? Math.min(95, Math.max(20, Number(item.metrics.occupancyPercent)))
    : Number.isFinite(Number(item?.impact))
      ? Math.min(90, Math.max(18, Number(item.impact) * 8))
      : 40;
  const severityScore = clampScore100((severityFromUrgency * 0.68) + (severityFromMetrics * 0.32));
  const confidenceScore = clampScore100(Number(item?.confidenceScore || 0) * 100);
  const impactScore = clampScore100(
    Number.isFinite(Number(item?.impact))
      ? (Math.min(80, Math.max(8, Number(item.impact) * 10)))
      : Number.isFinite(Number(item?.metrics?.clusterPressure))
        ? Math.min(85, Math.max(10, Number(item.metrics.clusterPressure) * 100))
        : 28,
  );
  const urgencyScore = clampScore100(urgency === 'kritis' ? 94 : urgency === 'tinggi' ? 76 : urgency === 'sedang' ? 56 : 34);
  const ignoredCount = Number(history?.lifecycle?.ignoredCount || 0);
  const helpful = String(history?.feedbackType || '').toLowerCase() === 'positive';
  const negative = String(history?.feedbackType || '').toLowerCase() === 'negative';
  const failedCount = Array.isArray(history?.events)
    ? history.events.filter((event) => String(event?.type || '').toLowerCase() === 'failed').length
    : 0;
  const historyScore = clampScore100(50 + (helpful ? 9 : 0) - (negative ? 9 : 0) - Math.min(14, ignoredCount * 2) - Math.min(16, failedCount * 4));

  const monitorDomain = String(monitorContext?.domain || '').toLowerCase();
  const ruleKey = String(item?.rule_key || '').toLowerCase();
  const billingSnapshotState = String(item?.context_snapshot?.billing_state || '').toLowerCase();
  const billingOrigin = actionClass === 'business'
    || ruleKey.includes('billing')
    || ruleKey.includes('reminder')
    || ruleKey.includes('suspend')
    || ruleKey.includes('restore')
    || Boolean(billingSnapshotState);
  const candidateDomain = billingOrigin ? 'billing' : 'network';
  const contextMismatchPenalty = monitorDomain && candidateDomain !== monitorDomain
    ? (monitorDomain === 'network' && candidateDomain === 'billing' ? 16 : 12)
    : 0;
  const lowConfidence = confidenceScore < 45;
  const lowConfidenceSafetyBias = lowConfidence
    ? (actionClass === 'disruptive' ? -14 : actionClass === 'investigative' ? 7 : 0)
    : 0;
  const disruptiveLowConfidencePenalty = actionClass === 'disruptive' && confidenceScore < 55 ? 12 : 0;
  const duplicateSuppressionPenalty = Number(history?.lifecycle?.suppressedCount || 0) >= 2 ? 6 : 0;

  const weightedBase = (
    (severityScore * 0.22)
    + (confidenceScore * 0.24)
    + (impactScore * 0.16)
    + (urgencyScore * 0.14)
    + (historyScore * 0.12)
    + ((100 - riskScore) * 0.12)
  );
  let baseScore = clampScore100(
    weightedBase
    - contextMismatchPenalty
    - disruptiveLowConfidencePenalty
    - duplicateSuppressionPenalty
    + lowConfidenceSafetyBias,
  );
  if (actionClass === 'disruptive' && confidenceScore < 55) {
    baseScore = Math.min(baseScore, 49);
  }

  const factors = [
    severityScore >= 70 ? 'severity tinggi' : 'severity moderat',
    confidenceScore >= 70 ? 'confidence kuat' : confidenceScore >= 45 ? 'confidence moderat' : 'confidence rendah',
    riskScore >= 60 ? 'risiko eksekusi tinggi' : 'risiko eksekusi rendah',
  ];
  if (contextMismatchPenalty > 0) factors.push('konteks kurang selaras');
  if (actionClass === 'disruptive' && confidenceScore < 55) factors.push('aksi disruptif diturunkan');
  if (helpful) factors.push('riwayat feedback positif');
  if (negative || ignoredCount > 1) factors.push('riwayat kurang efektif');

  const decisionReason = `${factors[0]}, ${factors[1]}, ${factors[2]}${contextMismatchPenalty > 0 ? '; konteks mismatch.' : '.'}`;
  return {
    actionClass,
    actionRiskScore: riskScore,
    severityScore,
    confidenceScore,
    impactScore,
    urgencyScore,
    historyScore,
    contextMismatchPenalty,
    lowConfidenceSafetyBias,
    disruptiveLowConfidencePenalty,
    duplicateSuppressionPenalty,
    baseScore: Number(baseScore.toFixed(1)),
    decisionLabel: getDecisionLabelFromScore(baseScore),
    decisionReason,
  };
};
const toActionQueueTestIdToken = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9_-]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 80) || 'item';
const normalizeRecommendationStrategyMode = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'cost') return 'cost';
  if (raw === 'growth') return 'growth';
  if (raw === 'balanced') return 'balanced';
  return null;
};
const getActionQueuePrimaryCtaLabel = (item) => {
  const actionType = normalizeActionQueueActionType(item?.action_type);
  if (actionType === 'expansion' || actionType === 'planning') return 'Buka FTTH Planning';
  if (actionType === 'rebalance' || String(item?.source_type || '').toLowerCase() === 'nas') return 'Buka NAS';
  return 'Lihat Monitoring';
};
const canTransitionActionQueueStatus = (currentStatus, nextStatus) => {
  const current = normalizeAutoActionStatus(currentStatus || 'pending');
  const next = String(nextStatus || '').trim().toLowerCase();
  if (!ACTION_QUEUE_STATUS_OPTIONS.includes(next)) return false;
  if (current === next) return false;
  if (['executed', 'skipped', 'failed', 'cancelled'].includes(current)) return false;
  if (current === 'pending_review') return ['approved', 'executed', 'skipped', 'cancelled'].includes(next);
  if (current === 'approved') return ['executed', 'skipped', 'failed', 'cancelled'].includes(next);
  if (current === 'pending') return ['accepted', 'completed', 'ignored', 'approved', 'executed', 'skipped', 'cancelled'].includes(next);
  if (current === 'accepted') return ['completed', 'ignored', 'executed', 'skipped', 'cancelled'].includes(next);
  return false;
};
const resolveNextActionQueueStatus = (item, nextStatus) => {
  const requested = String(nextStatus || '').trim().toLowerCase();
  const isAutoAction = Boolean(item?.isAutoAction);
  if (!isAutoAction) return requested;
  if (requested === 'accepted') return 'approved';
  if (requested === 'completed') return 'executed';
  if (requested === 'ignored') return 'skipped';
  return normalizeAutoActionStatus(requested);
};
const normalizeRecommendationEvalQueueStatus = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (!raw) return 'pending';
  if (raw === 'pending' || raw === 'accepted' || raw === 'completed' || raw === 'ignored') return raw;
  if (raw === 'pending_review') return 'pending';
  if (raw === 'approved') return 'accepted';
  if (raw === 'executed') return 'completed';
  if (raw === 'skipped' || raw === 'cancelled' || raw === 'failed' || raw === 'held') return 'ignored';
  return 'pending';
};
const formatActionQueueAge = (createdAt) => {
  const ts = Date.parse(String(createdAt || ''));
  if (!Number.isFinite(ts)) return '-';
  const ageMs = Math.max(0, Date.now() - ts);
  if (ageMs < 10 * 60 * 1000) return 'Baru';
  const ageMinutes = Math.floor(ageMs / (60 * 1000));
  if (ageMinutes < 60) return `${ageMinutes}m`;
  const ageHours = Math.floor(ageMinutes / 60);
  if (ageHours < 24) return `${ageHours}j`;
  const ageDays = Math.floor(ageHours / 24);
  return `${ageDays}h`;
};
const getAgingWeight = (createdAt, status) => {
  const ts = Date.parse(String(createdAt || ''));
  if (!Number.isFinite(ts)) return 0;
  const normalizedStatus = String(status || 'pending').trim().toLowerCase();
  if (normalizedStatus === 'completed' || normalizedStatus === 'ignored') return 0;
  const ageHours = (Date.now() - ts) / (60 * 60 * 1000);
  if (ageHours >= 24) return 1.5;
  if (ageHours >= 12) return 1.1;
  if (ageHours >= 6) return 0.7;
  if (ageHours >= 2) return 0.35;
  return 0;
};
const getAgeHighlightClass = (ageWeight) => {
  if (ageWeight >= 1.5) return 'ring-1 ring-rose-400/35 border-rose-500/35';
  if (ageWeight >= 1.1) return 'ring-1 ring-amber-300/30 border-amber-500/30';
  return '';
};
const getDominantActionType = (items) => {
  const ordered = ['expansion', 'rebalance', 'planning', 'monitor'];
  for (const actionType of ordered) {
    if (items.some((item) => normalizeActionQueueActionType(item?.action_type) === actionType)) {
      return actionType;
    }
  }
  return 'monitor';
};
const shouldResurfaceRecommendation = (guardEntry, item) => {
  if (!guardEntry || typeof guardEntry !== 'object') return true;
  const currentUrgencyRank = ACTION_QUEUE_URGENCY_RANK[normalizeActionQueueUrgency(item?.urgency)] || 1;
  const previousUrgencyRank = Number(guardEntry?.lastUrgencyRank || 1);
  if (currentUrgencyRank > previousUrgencyRank) return true;

  const currentEta = Number(item?.metrics?.daysToFull);
  const previousEta = Number(guardEntry?.lastEta);
  if (Number.isFinite(currentEta) && Number.isFinite(previousEta) && (previousEta - currentEta) >= 10) return true;

  const currentOccupancy = Number(item?.metrics?.occupancyPercent);
  const previousOccupancy = Number(guardEntry?.lastOccupancy);
  if (Number.isFinite(currentOccupancy) && Number.isFinite(previousOccupancy) && previousOccupancy < 85 && currentOccupancy >= 85) return true;

  const currentGrowth = Number(item?.metrics?.growth30d);
  const previousGrowth = Number(guardEntry?.lastGrowth);
  if (Number.isFinite(currentGrowth) && Number.isFinite(previousGrowth) && (currentGrowth - previousGrowth) >= 2) return true;

  return false;
};

const buildOverlapPositionLookup = (items) => {
  const groups = {};
  (Array.isArray(items) ? items : []).forEach((item) => {
    const id = String(item?.id || '').trim();
    const lat = Number(item?.latitude);
    const lon = Number(item?.longitude);
    if (!id || !Number.isFinite(lat) || !Number.isFinite(lon)) return;
    const key = `${lat.toFixed(4)}:${lon.toFixed(4)}`;
    if (!Array.isArray(groups[key])) groups[key] = [];
    groups[key].push({ id, lat, lon });
  });

  const lookup = {};
  Object.values(groups).forEach((group) => {
    const sorted = group.slice().sort((a, b) => a.id.localeCompare(b.id));
    if (sorted.length === 1) {
      lookup[sorted[0].id] = [sorted[0].lat, sorted[0].lon];
      return;
    }
    sorted.forEach((node, idx) => {
      const angle = (Math.PI * 2 * idx) / sorted.length;
      const radiusMeters = 12 + Math.floor(idx / 8) * 8;
      const latOffset = (radiusMeters * Math.sin(angle)) / 111320;
      const lonOffset = (radiusMeters * Math.cos(angle)) / (111320 * Math.max(Math.cos((node.lat * Math.PI) / 180), 0.2));
      lookup[node.id] = [node.lat + latOffset, node.lon + lonOffset];
    });
  });
  return lookup;
};
const mapConnectionStatusToTone = (status) => {
  const clean = String(status || '').trim().toLowerCase();
  if (clean === 'online') return 'info';
  if (clean === 'offline') return 'danger';
  if (clean === 'not_mapped') return 'warn';
  return mapMonitoringStatusToTone(clean);
};

const getStatusColor = (status) => semanticToneToColor(mapConnectionStatusToTone(status));



const getStatusLabel = (status) => {

  if (status === 'online') return 'Online';

  if (status === 'offline') return 'Offline/Loss';

  if (status === 'not_mapped') return 'Belum mapping ONU';

  return 'Tidak diketahui';

};

const getPppoeStatusLabel = (status) => {
  if (status === 'online') return 'Online';
  if (status === 'offline') return 'Offline';
  return 'Tidak diketahui';
};

const getPppoeStatusColor = (status) => semanticToneToColor(mapMonitoringStatusToTone(status));

const normalizeTicketLifecycle = (status) => {
  const raw = String(status || '').trim().toLowerCase();
  if (raw === 'approved') return { key: 'in_progress', label: 'Diproses', tone: 'info' };
  if (raw === 'pending_approval' || raw === 'open') return { key: 'open', label: 'Terbuka', tone: 'warn' };
  if (raw === 'on_hold') return { key: 'on_hold', label: 'Ditunda', tone: 'neutral' };
  if (raw === 'completed' || raw === 'resolved') return { key: 'resolved', label: 'Selesai', tone: 'ok' };
  if (raw === 'needs_special_handling' || raw === 'escalated') return { key: 'escalated', label: 'Dieskalasi', tone: 'danger' };
  return { key: raw || 'open', label: raw ? raw.replace(/_/g, ' ') : 'Terbuka', tone: 'neutral' };
};

function normalizeIncidentSourceType(value) {
  const raw = String(value || '').trim().toLowerCase();
  if (['customer', 'odp', 'olt', 'nas', 'anomaly_cluster', 'topology_node'].includes(raw)) return raw;
  return raw || 'customer';
}

const getIncidentSeverityLabel = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'critical') return 'Kritis';
  if (raw === 'high') return 'Tinggi';
  if (raw === 'medium') return 'Sedang';
  return 'Rendah';
};

const getIncidentStatusLabel = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'open') return 'Terbuka';
  if (raw === 'monitoring') return 'Monitoring';
  if (raw === 'resolved') return 'Selesai';
  if (raw === 'false_positive') return 'Positif Palsu';
  return '-';
};

const mapIncidentStatusToTone = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'open') return 'warn';
  if (raw === 'monitoring') return 'info';
  if (raw === 'resolved') return 'ok';
  if (raw === 'false_positive') return 'neutral';
  return mapMonitoringStatusToTone(raw);
};

const mapIncidentSeverityToTone = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'critical') return 'danger';
  if (raw === 'high' || raw === 'medium' || raw === 'warning') return 'warn';
  if (raw === 'low') return 'ok';
  return mapMonitoringStatusToTone(raw);
};

const mapBillingStatusToTone = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'active') return 'ok';
  if (raw === 'suspended') return 'warn';
  if (raw === 'terminated') return 'danger';
  return 'neutral';
};

const mapAiRiskToTone = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'high') return 'danger';
  if (raw === 'medium') return 'warn';
  if (raw === 'low') return 'ok';
  return 'neutral';
};

const mapSelfHealingStatusToTone = (value) => {
  const raw = String(value || '').trim().toLowerCase();
  if (raw === 'verified' || raw === 'optimized') return 'ok';
  if (raw === 'partial') return 'warn';
  if (raw === 'no_action') return 'neutral';
  return 'info';
};

const mapRiskScoreToTone = (value) => {
  const score = Number(value || 0);
  if (score > 70) return 'danger';
  if (score > 50) return 'warn';
  return 'ok';
};

const toneToMarkerStateClass = (tone) => {
  if (tone === 'ok' || tone === 'info') return 'online';
  if (tone === 'danger') return 'offline';
  return 'unknown';
};

const buildIncidentSourceKey = (sourceType, sourceId) => {
  const type = normalizeIncidentSourceType(sourceType);
  const id = String(sourceId || '').trim();
  if (!id) return '';
  return `${type}:${id}`;
};

const isTicketActiveLifecycle = (ticket) => {
  const state = normalizeTicketLifecycle(ticket?.status);
  return ['open', 'in_progress', 'on_hold', 'escalated'].includes(state.key);
};

const isTicketLifecycleLocked = (ticket) => {
  const state = normalizeTicketLifecycle(ticket?.status);
  return state.key === 'resolved' || state.key === 'escalated';
};

const getTicketToneClass = (tone) => {
  if (tone === 'success') return semanticToneToBadgeClass('ok');
  if (tone === 'warning') return semanticToneToBadgeClass('warn');
  if (tone === 'muted') return semanticToneToBadgeClass('neutral');
  return semanticToneToBadgeClass(tone);
};

const buildDistributionPath = (fromPoint, toPoint, seed = '') => {
  if (!Array.isArray(fromPoint) || !Array.isArray(toPoint)) return [fromPoint, toPoint];
  const fromLat = Number(fromPoint[0]);
  const fromLon = Number(fromPoint[1]);
  const toLat = Number(toPoint[0]);
  const toLon = Number(toPoint[1]);
  if (!Number.isFinite(fromLat) || !Number.isFinite(fromLon) || !Number.isFinite(toLat) || !Number.isFinite(toLon)) {
    return [fromPoint, toPoint];
  }
  const hash = String(seed || '').split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const direction = hash % 2 === 0 ? 1 : -1;
  const curveStrength = 0.00012 + (hash % 7) * 0.00001;
  const midLat = (fromLat + toLat) / 2;
  const midLon = (fromLon + toLon) / 2;
  const ctrlLat = midLat + (toLon - fromLon) * curveStrength * direction;
  const ctrlLon = midLon - (toLat - fromLat) * curveStrength * direction;
  return [
    [fromLat, fromLon],
    [ctrlLat, ctrlLon],
    [toLat, toLon],
  ];
};



const normalizeInfraKriteria = (value) => (String(value || 'live').trim().toLowerCase() === 'plan' ? 'plan' : 'live');

const getInfraCategoryLabel = (value) => (normalizeInfraKriteria(value) === 'plan' ? 'Plan' : 'Live');

const getInfraKriteriaColor = (value) => (normalizeInfraKriteria(value) === 'plan' ? '#f59e0b' : '#3b82f6');



const formatSignal = (status) => {

  if (!status) return 'Tidak tersedia';

  if (Number.isFinite(status.attenuation_db)) return `${status.attenuation_db.toFixed(2)} dB`;

  if (Number.isFinite(status.rx_power_dbm)) return `${status.rx_power_dbm.toFixed(2)} dBm`;

  return 'Tidak tersedia';

};

const formatLastInformAge = (value) => {
  if (!value) return 'Tidak tersedia';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return 'Tidak tersedia';
  const diffMs = Date.now() - parsed.getTime();
  if (!Number.isFinite(diffMs)) return 'Tidak tersedia';
  const diffMinutes = Math.max(0, Math.floor(diffMs / 60000));
  if (diffMinutes < 1) return 'Baru saja';
  if (diffMinutes < 60) return `${diffMinutes} menit lalu`;
  const diffHours = Math.floor(diffMinutes / 60);
  if (diffHours < 24) return `${diffHours} jam lalu`;
  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} hari lalu`;
};

const parseLastInformLatencySeconds = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const diffSeconds = Math.floor((Date.now() - parsed.getTime()) / 1000);
  if (!Number.isFinite(diffSeconds) || diffSeconds < 0) return null;
  return diffSeconds;
};

const parseNodeRxPower = (node) => {
  const statusDetail = node?.status_detail || {};
  const candidates = [
    statusDetail?.rx_power_dbm,
    statusDetail?.rx_power,
    node?.rx_power_dbm,
    node?.rx_power,
  ];
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value)) return value;
  }
  return null;
};

const parsePacketLoss = (node) => {
  const statusDetail = node?.status_detail || {};
  const candidates = [
    statusDetail?.packet_loss,
    statusDetail?.packet_loss_percent,
    statusDetail?.loss_percent,
    statusDetail?.loss,
    node?.packet_loss,
    node?.packet_loss_percent,
  ];
  for (const candidate of candidates) {
    const value = Number(candidate);
    if (Number.isFinite(value) && value >= 0) return value;
  }
  return null;
};

const evaluateAiLevel = (score) => {
  if (score >= 75) return 'normal';
  if (score >= 45) return 'warning';
  return 'critical';
};

const NODE_HISTORY_LIMIT = 48;
const NETWORK_HISTORY_LIMIT = 72;
const ANOMALY_SCORE_THRESHOLD = 45;

const computeAiScore = (node) => {
  let score = 100;
  const penalties = [];

  const connectionStatus = String(node?.connection_status || '').trim().toLowerCase();
  const pppoeStatus = String(node?.pppoe_connection_status || '').trim().toLowerCase();
  const latencySeconds = parseLastInformLatencySeconds(node?.status_detail?.last_inform);
  const rxPower = parseNodeRxPower(node);
  const packetLoss = parsePacketLoss(node);
  const flapping = isNodeFlapping(node);

  if (connectionStatus === 'offline' || connectionStatus === 'unavailable') {
    score -= 40;
    penalties.push({ code: 'onu_offline', penalty: 40, label: 'ONU offline' });
  }

  if (pppoeStatus === 'offline') {
    score -= 22;
    penalties.push({ code: 'pppoe_offline', penalty: 22, label: 'PPPoE offline' });
  }

  if (Number.isFinite(latencySeconds)) {
    if (latencySeconds > 120) {
      score -= 22;
      penalties.push({ code: 'latency_critical', penalty: 22, label: `Latency kritikal (${latencySeconds}s)` });
    } else if (latencySeconds > 80) {
      score -= 12;
      penalties.push({ code: 'latency_warning', penalty: 12, label: `Latency tinggi (${latencySeconds}s)` });
    } else if (latencySeconds > 45) {
      score -= 6;
      penalties.push({ code: 'latency_minor', penalty: 6, label: `Latency moderat (${latencySeconds}s)` });
    }
  }

  if (Number.isFinite(rxPower)) {
    if (rxPower < -27) {
      score -= 24;
      penalties.push({ code: 'signal_critical', penalty: 24, label: `Signal sangat lemah (${rxPower.toFixed(2)} dBm)` });
    } else if (rxPower < -25) {
      score -= 14;
      penalties.push({ code: 'signal_warning', penalty: 14, label: `Signal lemah (${rxPower.toFixed(2)} dBm)` });
    } else if (rxPower < -23.5) {
      score -= 7;
      penalties.push({ code: 'signal_minor', penalty: 7, label: `Signal menurun (${rxPower.toFixed(2)} dBm)` });
    }
  }

  if (Number.isFinite(packetLoss)) {
    if (packetLoss >= 10) {
      score -= 18;
      penalties.push({ code: 'packet_loss_critical', penalty: 18, label: `Packet loss tinggi (${packetLoss}%)` });
    } else if (packetLoss >= 4) {
      score -= 10;
      penalties.push({ code: 'packet_loss_warning', penalty: 10, label: `Packet loss meningkat (${packetLoss}%)` });
    } else if (packetLoss >= 1) {
      score -= 5;
      penalties.push({ code: 'packet_loss_minor', penalty: 5, label: `Packet loss ringan (${packetLoss}%)` });
    }
  }

  if (flapping) {
    score -= 14;
    penalties.push({ code: 'status_flapping', penalty: 14, label: 'Status flapping' });
  }

  score = Math.max(0, Math.min(100, score));
  const level = evaluateAiLevel(score);
  return {
    score,
    level,
    penalties,
    metrics: {
      latencySeconds,
      rxPower,
      packetLoss,
      pppoeStatus,
      flapping,
      connectionStatus,
    },
  };
};

const resolveTrendDirection = (firstValue, latestValue, tolerance = 2) => {
  if (!Number.isFinite(firstValue) || !Number.isFinite(latestValue)) return 'stable';
  const diff = latestValue - firstValue;
  if (Math.abs(diff) <= tolerance) return 'stable';
  return diff > 0 ? 'up' : 'down';
};

const analyzeNodeTrend = (history = []) => {
  if (!Array.isArray(history) || history.length < 3) {
    return {
      scoreTrend: 'stable',
      latencyTrend: 'stable',
      trendLabel: 'Data trend belum cukup',
      trendDelta: 0,
    };
  }
  const windowed = history.slice(-8);
  const first = windowed[0] || {};
  const latest = windowed[windowed.length - 1] || {};
  const scoreTrend = resolveTrendDirection(Number(first.score), Number(latest.score), 3);
  const latencyTrend = resolveTrendDirection(Number(first.latency), Number(latest.latency), 4);
  const trendDelta = Number(latest.score || 0) - Number(first.score || 0);
  let trendLabel = 'Stabil';
  if (scoreTrend === 'down' || latencyTrend === 'up') trendLabel = 'Memburuk';
  if (scoreTrend === 'up' && latencyTrend !== 'up') trendLabel = 'Membaik';
  return { scoreTrend, latencyTrend, trendLabel, trendDelta };
};

const computeRiskLevel = ({ anomaly, trend, score }) => {
  let riskPoints = 0;
  if (anomaly?.hasAnomaly) riskPoints += 2;
  if (score < ANOMALY_SCORE_THRESHOLD) riskPoints += 2;
  if (trend?.scoreTrend === 'down') riskPoints += 1;
  if (trend?.latencyTrend === 'up') riskPoints += 1;
  if (trend?.trendLabel === 'Memburuk') riskPoints += 1;
  if (riskPoints >= 5) return 'high';
  if (riskPoints >= 3) return 'medium';
  return 'low';
};

const buildPrediction = ({ risk, anomaly, trend, score, customerName }) => {
  if (risk === 'high') {
    return `Risiko tinggi: potensi gangguan/offline dalam waktu dekat pada ${customerName || 'node ini'}.`;
  }
  if (risk === 'medium') {
    if (trend?.trendLabel === 'Memburuk') {
      return 'Risiko sedang: performa menurun, disarankan inspeksi proaktif.';
    }
    return 'Risiko sedang: ada indikasi anomali, pantau intensif.';
  }
  if (anomaly?.hasAnomaly && score >= 60) {
    return 'Anomali ringan terdeteksi, kemungkinan stabil jika kondisi tidak memburuk.';
  }
  return 'Kondisi relatif stabil.';
};

const buildSparklinePath = (values = [], width = 180, height = 48, padding = 4) => {
  if (!Array.isArray(values) || values.length === 0) return '';
  const numeric = values.map((v) => Number(v)).filter((v) => Number.isFinite(v));
  if (numeric.length === 0) return '';
  const max = Math.max(...numeric);
  const min = Math.min(...numeric);
  const range = Math.max(1, max - min);
  const innerW = Math.max(1, width - padding * 2);
  const innerH = Math.max(1, height - padding * 2);
  return numeric
    .map((value, index) => {
      const x = padding + (numeric.length === 1 ? innerW / 2 : (index / (numeric.length - 1)) * innerW);
      const y = padding + ((max - value) / range) * innerH;
      return `${index === 0 ? 'M' : 'L'} ${x.toFixed(2)} ${y.toFixed(2)}`;
    })
    .join(' ');
};

const estimateCustomerLoad = (customer = {}) => {
  const direct = resolveLinkLoad(customer);
  if (direct > 0) return direct;
  const stations = Number(customer?.status_detail?.total_stations);
  const isOnline = String(customer?.connection_status || '').toLowerCase() === 'online';
  if (Number.isFinite(stations) && stations > 0) {
    return Math.min(100, stations * 16 + (isOnline ? 18 : 0));
  }
  if (isOnline) return 42;
  if (String(customer?.connection_status || '').toLowerCase() === 'offline') return 0;
  return 14;
};

function isNodeFlapping(node) {
  const statusDetail = node?.status_detail || {};
  const candidates = [
    statusDetail?.is_flapping,
    statusDetail?.flapping,
    node?.is_flapping,
    node?.flapping,
    node?.status_flapping,
  ];
  return candidates.some((value) => {
    if (typeof value === 'boolean') return value;
    const normalized = String(value || '').trim().toLowerCase();
    return normalized === 'true' || normalized === 'flapping' || normalized === 'yes';
  });
}

const detectAnomaly = (node) => {
  const aiScore = computeAiScore(node);
  const issues = [];
  const {
    latencySeconds,
    rxPower,
    packetLoss,
    pppoeStatus,
    flapping,
    connectionStatus,
  } = aiScore.metrics;

  if (connectionStatus === 'offline' || connectionStatus === 'unavailable') {
    issues.push({ code: 'offline', label: 'Node offline', severity: 'high' });
  }
  if (pppoeStatus === 'offline') {
    issues.push({ code: 'pppoe_offline', label: 'PPPoE offline', severity: 'medium' });
  }
  if (Number.isFinite(latencySeconds) && latencySeconds > 80) {
    issues.push({ code: 'latency_high', label: `Latency tinggi (${latencySeconds}s)`, severity: latencySeconds > 120 ? 'high' : 'medium' });
  }
  if (Number.isFinite(rxPower) && rxPower < -25) {
    issues.push({ code: 'signal_weak', label: `RX power lemah (${rxPower.toFixed(2)} dBm)`, severity: rxPower < -27 ? 'high' : 'medium' });
  }
  if (Number.isFinite(packetLoss) && packetLoss >= 4) {
    issues.push({ code: 'packet_loss', label: `Packet loss tinggi (${packetLoss}%)`, severity: packetLoss >= 10 ? 'high' : 'medium' });
  }
  if (flapping) {
    issues.push({ code: 'status_flapping', label: 'Status flapping', severity: 'medium' });
  }
  if (Number.isFinite(aiScore?.score) && aiScore.score < ANOMALY_SCORE_THRESHOLD) {
    issues.push({ code: 'score_threshold', label: `Skor AI di bawah threshold (${aiScore.score}/100)`, severity: aiScore.score < 30 ? 'high' : 'medium' });
  }

  return {
    hasAnomaly: issues.length > 0 || aiScore.level !== 'normal',
    issues,
    latencySeconds,
    rxPower,
    packetLoss,
    aiScore,
  };
};

const predictIssue = (node) => {
  const predictions = [];
  const latencySeconds = parseLastInformLatencySeconds(node?.status_detail?.last_inform);
  const rxPower = parseNodeRxPower(node);
  const packetLoss = parsePacketLoss(node);
  if (Number.isFinite(rxPower) && rxPower < -27) {
    predictions.push('Signal berpotensi drop (RX < -27 dBm)');
  }
  if (Number.isFinite(latencySeconds) && latencySeconds > 120) {
    predictions.push('Potensi congestion (latency > 120s)');
  }
  if (Number.isFinite(latencySeconds) && latencySeconds > 80 && Number.isFinite(rxPower) && rxPower < -24.5) {
    predictions.push('Kombinasi latency + signal menunjukkan risiko gangguan');
  }
  if (Number.isFinite(packetLoss) && packetLoss >= 4 && Number.isFinite(rxPower) && rxPower < -24) {
    predictions.push('Kualitas link menurun, potensi putus singkat meningkat');
  }
  return predictions;
};



const createWifiIcon = (
  status,
  selected = false,
  searchHit = false,
  ai = { anomaly: false, predictive: false, level: 'normal', feedback: '', rootCause: false, suspected: false },
  ticket = { active: false, state: '' },
) => {
  const stateClass = toneToMarkerStateClass(mapConnectionStatusToTone(status));
  const selectedClass = selected ? ' wifi-selected' : '';
  const searchClass = searchHit ? ' wifi-search-hit' : '';
  const anomalyClass = ai?.anomaly ? ' wifi-anomaly node-alert' : '';
  const predictiveBadge = ai?.predictive ? '<span class="wifi-predict-badge">AI</span>' : '';



  return L.divIcon({

    className: 'wifi-marker-wrapper',

    html: `<div class=\"wifi-marker wifi-${stateClass}${selectedClass}${searchClass}${anomalyClass}\">${predictiveBadge}<span class=\"wifi-pulse\"></span><svg viewBox=\"0 0 24 24\" fill=\"none\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M12 20h.01\"></path><path d=\"M2 8.82a15 15 0 0 1 20 0\"></path><path d=\"M5 12.86a10 10 0 0 1 14 0\"></path><path d=\"M8.5 16.43a5 5 0 0 1 7 0\"></path></svg></div>`,

    iconSize: [34, 34],

    iconAnchor: [17, 17],

    popupAnchor: [0, -18],

  });

};

const createClusterIcon = (cluster = {}) => {
  const status = cluster.dominantStatus || 'unknown';
  const count = Number(cluster.count || 0);
  const statusClass = toneToMarkerStateClass(mapConnectionStatusToTone(status));
  const anomalyClass = Number(cluster.anomaly || 0) > 0 ? ' has-anomaly' : '';
  return L.divIcon({
    className: 'wifi-cluster-wrapper',
    html: `<div class="wifi-cluster-marker ${statusClass}${anomalyClass}"><span class="count">${count}</span></div>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
    popupAnchor: [0, -18],
  });
};



const FitBoundsController = ({ points, enabled, triggerKey, onComplete }) => {
  const map = useMap();
  const lastAppliedRef = useRef('');



  useEffect(() => {

    if (!enabled) return;
    if (!Array.isArray(points) || points.length === 0) return;

    const compactPoints = points
      .map((point) => {
        const lat = Number(point?.[0]);
        const lng = Number(point?.[1]);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return '';
        return `${lat.toFixed(5)},${lng.toFixed(5)}`;
      })
      .filter(Boolean);
    if (compactPoints.length === 0) return;

    const signature = `${String(triggerKey ?? 'auto')}::${compactPoints.join('|')}`;
    if (lastAppliedRef.current === signature) return;
    lastAppliedRef.current = signature;



    if (points.length === 1) {

      map.setView(points[0], 16, { animate: true });
      if (onComplete) onComplete();
      return;

    }



    const bounds = L.latLngBounds(points);

    map.fitBounds(bounds, { padding: [72, 72], maxZoom: 16, animate: true });
    if (onComplete) onComplete();

  }, [map, points, enabled, triggerKey, onComplete]);



  return null;

};

const AutoFitDisabler = ({ onDisable, onUserAdjust }) => {
  const handleUserMove = (event) => {
    if (!event?.originalEvent) return;
    if (typeof onUserAdjust === 'function') onUserAdjust();
    if (typeof onDisable === 'function') onDisable();
  };
  useMapEvent('zoomstart', handleUserMove);
  useMapEvent('dragstart', handleUserMove);
  useMapEvent('movestart', handleUserMove);
  return null;
};

const MapZoomWatcher = ({ onChange }) => {
  const map = useMap();
  useEffect(() => {
    onChange(map.getZoom());
  }, [map, onChange]);
  useMapEvent('zoomend', () => {
    onChange(map.getZoom());
  });
  return null;
};



const FocusCustomerController = ({ customer }) => {

  const map = useMap();
  const lastFocusRef = useRef({ id: null, lat: null, lon: null });



  useEffect(() => {

    if (!customer) return;

    if (!hasValidCoordinates(customer.latitude, customer.longitude)) return;

    const lat = Number(customer.latitude);
    const lon = Number(customer.longitude);
    const currentId = String(customer.id || '');
    const last = lastFocusRef.current;
    if (last.id === currentId && last.lat === lat && last.lon === lon) return;
    lastFocusRef.current = { id: currentId, lat, lon };
    map.setView([lat, lon], 17, { animate: true });

  }, [customer, map]);



  return null;

};



const AdminConnectivityMap = () => {

  const navigate = useNavigate();
  const location = useLocation();

  const [user, setUser] = useState(null);

  const [customers, setCustomers] = useState([]);
  const [nasRouters, setNasRouters] = useState([]);

  const [olts, setOlts] = useState([]);

  const [odps, setOdps] = useState([]);

  const [statusByCustomerId, setStatusByCustomerId] = useState({});

  const [serverAvailable, setServerAvailable] = useState(true);
  const [genieacsHealthAvailable, setGenieacsHealthAvailable] = useState(null);
  const [systemMetrics, setSystemMetrics] = useState(null);

  const [loading, setLoading] = useState(true);
  const showLoadingOverlay = useMinimumLoadingVisibility(loading, 1200);

  const [statusFilter, setStatusFilter] = useState('all');

  const [infraFilter, setInfraFilter] = useState('all');
  const [autoFitEnabled, setAutoFitEnabled] = useState(true);
  const [autoFitKey, setAutoFitKey] = useState(0);
  const [statusStale, setStatusStale] = useState(false);
  const [selectedNasId, setSelectedNasId] = useState('all');

  const [selectedOltId, setSelectedOltId] = useState('all');
  const [viewMode, setViewMode] = useState('all');

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [pendingCustomerFocus, setPendingCustomerFocus] = useState(null);
  const [pendingSourceFocus, setPendingSourceFocus] = useState(null);
  const [focusNode, setFocusNode] = useState(null);
  const [focusPulseCustomerId, setFocusPulseCustomerId] = useState('');
  const [rightPanelTab, setRightPanelTab] = useState('infra');
  const [actionQueueStateById, setActionQueueStateById] = useState({});
  const [actionQueueCreatedAtById, setActionQueueCreatedAtById] = useState({});
  const [actionQueueFeedbackById, setActionQueueFeedbackById] = useState({});
  const [actionQueueAutoHoldById, setActionQueueAutoHoldById] = useState({});
  const [autoActionLifecycleLogs, setAutoActionLifecycleLogs] = useState([]);
  const [recommendationEvalById, setRecommendationEvalById] = useState({});
  const [recommendationGuardById, setRecommendationGuardById] = useState({});
  const [actionQueueShowLowUrgency, setActionQueueShowLowUrgency] = useState(false);
  const [actionQueueShowCompleted, setActionQueueShowCompleted] = useState(false);
  const [actionQueueGroupOpen, setActionQueueGroupOpen] = useState({
    kritis: true,
    tinggi: true,
    sedang: false,
    rendah: false,
  });
  const [capacityRecommendationStrategy, setCapacityRecommendationStrategy] = useState('balanced');
  const [customerFilterMode, setCustomerFilterMode] = useState('all');
  const [visualMode, setVisualMode] = useState('normal');
  const [mapZoom, setMapZoom] = useState(13);
  const [hoveredNode, setHoveredNode] = useState(null);

  const [lastUpdated, setLastUpdated] = useState('');

  const [restartingCustomerId, setRestartingCustomerId] = useState('');
  const [syncingCustomerId, setSyncingCustomerId] = useState('');
  const [isolatingCustomerId, setIsolatingCustomerId] = useState('');
  const [creatingTicketCustomerId, setCreatingTicketCustomerId] = useState('');
  const [creatingIncidentTicket, setCreatingIncidentTicket] = useState(false);
  const [updatingTicketId, setUpdatingTicketId] = useState('');
  const [fieldTickets, setFieldTickets] = useState([]);
  const [loadingFieldTickets, setLoadingFieldTickets] = useState(false);
  const [monitoringIncidents, setMonitoringIncidents] = useState([]);
  const [loadingMonitoringIncidents, setLoadingMonitoringIncidents] = useState(false);
  const [resolvingMonitoringIncidentId, setResolvingMonitoringIncidentId] = useState('');
  const [incidentSourceOverride, setIncidentSourceOverride] = useState(null);
  const [selfHealingRunningCustomerId, setSelfHealingRunningCustomerId] = useState('');
  const [selfHealingLogs, setSelfHealingLogs] = useState([]);
  const [selfHealingLogsLoading, setSelfHealingLogsLoading] = useState(false);
  const [optimizingCustomerId, setOptimizingCustomerId] = useState('');
  const [actionFeedbackByCustomerId, setActionFeedbackByCustomerId] = useState({});
  const [optimizationLogs, setOptimizationLogs] = useState([]);
  const [optimizationLogsLoading, setOptimizationLogsLoading] = useState(false);
  const [actionFeedbackByInfraId, setActionFeedbackByInfraId] = useState({});
  const [aiActionQueue, setAiActionQueue] = useState([]);
  const [aiMonitoringResult, setAiMonitoringResult] = useState({
    risk: 0,
    anomaly: { offline_count: 0, total: 0, ratio: 0, is_anomaly: false },
    root_cause: null,
    actions: [],
    recommendation: [],
    auto_mode: true,
  });
  const [aiActionButtons, setAiActionButtons] = useState([]);
  const [aiActionLogs, setAiActionLogs] = useState([]);
  const [aiActionExecuting, setAiActionExecuting] = useState(false);
  const [aiActionToast, setAiActionToast] = useState('');
  const [aiAutoModeUpdating, setAiAutoModeUpdating] = useState(false);
  const [nodeHistoryById, setNodeHistoryById] = useState({});
  const [networkHistory, setNetworkHistory] = useState([]);
  const [isReplayMode, setIsReplayMode] = useState(false);
  const [replayIncidentId, setReplayIncidentId] = useState('');
  const [replayPlaying, setReplayPlaying] = useState(false);
  const [replayCursorMs, setReplayCursorMs] = useState(0);
  const [replaySpeed, setReplaySpeed] = useState(1);
  const [replaySession, setReplaySession] = useState(null);
  const [incidentSummaryExpanded, setIncidentSummaryExpanded] = useState(false);
  const [pendingPayments, setPendingPayments] = useState([]);

  const [streamConnected, setStreamConnected] = useState(false);
  const [streamReconnectCount, setStreamReconnectCount] = useState(0);
  const [isRetryScheduled, setIsRetryScheduled] = useState(false);

  const streamErrorNotifiedRef = useRef(false);
  const streamReadyRef = useRef(false);
  const authExpiredNotifiedRef = useRef(false);
  const fetchRetryAttemptRef = useRef(0);
  const fetchRetryTimerRef = useRef(null);
  const initialLoadRef = useRef(true);
  const mapFullscreenRef = useRef(null);
  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const lastAiFocusRef = useRef({ nodeId: '', at: 0 });
  const focusPulseTimeoutRef = useRef(null);
  const aiModeEnabled = visualMode === 'ai';
  const flowModeEnabled = visualMode === 'flow' || visualMode === 'ai';
  const isDevMode = String(process.env.NODE_ENV || '').toLowerCase() !== 'production';
  const [forceLegacyMap, setForceLegacyMap] = useState(() => {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search || '');
    const forceGpu = params.get('gpu') === '1';
    if (forceGpu) return false;
    return String(process.env.NODE_ENV || '').toLowerCase() === 'production';
  });
  const gpuSupported = useMemo(() => checkWebglAvailability(), []);
  const useGpuMapEngine = gpuSupported && !forceLegacyMap;
  const [gpuStats, setGpuStats] = useState({
    fps: 0,
    nodeCount: 0,
    lineCount: 0,
    particleCount: 0,
    selectedNodeId: '',
    lastUpdateAt: 0,
    webgl: gpuSupported,
    detailMode: true,
  });
  const [showDebugOverlay, setShowDebugOverlay] = useState(isDevMode);
  const [webglOverlayActive, setWebglOverlayActive] = useState(false);
  const [webglOverlayFailed, setWebglOverlayFailed] = useState(false);
  const [webglOverlayProbeDone, setWebglOverlayProbeDone] = useState(false);
  const actionFeedbackTimersRef = useRef({});
  const infraActionFeedbackTimersRef = useRef({});
  const aiAutoExecuteGuardRef = useRef({});
  const recommendationEvalPendingRef = useRef([]);
  const recommendationEvalFlushTimerRef = useRef(null);
  const lastStableOverlayRef = useRef({ nodes: [], links: [], at: 0 });
  const filterAutoFitInitializedRef = useRef(false);
  const hasUserAdjustedViewportRef = useRef(false);
  const pendingCustomerFocusResolvedRef = useRef(false);
  const pendingSourceFocusResolvedRef = useRef(false);

  useEffect(() => {
    if (!MONITORING_BASELINE_FOCUS) return;
    if (MONITORING_PRIMARY_TABS.includes(rightPanelTab)) return;
    setRightPanelTab('infra');
  }, [rightPanelTab]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      const raw = window.localStorage.getItem(ACTION_QUEUE_STORAGE_KEY);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        if (parsed?.stateById && typeof parsed.stateById === 'object') {
          setActionQueueStateById(parsed.stateById);
        }
        if (parsed?.createdAtById && typeof parsed.createdAtById === 'object') {
          setActionQueueCreatedAtById(parsed.createdAtById);
        }
      }
    } catch {
      // noop
    }
    try {
      const rawGuard = window.localStorage.getItem(ACTION_QUEUE_GUARD_STORAGE_KEY);
      if (rawGuard) {
        const parsedGuard = JSON.parse(rawGuard);
        if (parsedGuard && typeof parsedGuard === 'object') {
          setRecommendationGuardById(parsedGuard);
        }
      }
    } catch {
      // noop
    }
    try {
      const rawFeedback = window.localStorage.getItem(ACTION_QUEUE_FEEDBACK_STORAGE_KEY);
      if (rawFeedback) {
        const parsedFeedback = JSON.parse(rawFeedback);
        if (parsedFeedback && typeof parsedFeedback === 'object') {
          setActionQueueFeedbackById(parsedFeedback);
        }
      }
    } catch {
      // noop
    }
    try {
      const rawEval = window.localStorage.getItem(ACTION_QUEUE_EVAL_STORAGE_KEY);
      if (rawEval) {
        const parsedEval = JSON.parse(rawEval);
        if (parsedEval && typeof parsedEval === 'object') {
          setRecommendationEvalById(parsedEval);
        }
      }
    } catch {
      // noop
    }
    try {
      const rawAutoHold = window.localStorage.getItem(ACTION_QUEUE_AUTO_HOLD_STORAGE_KEY);
      if (rawAutoHold) {
        const parsedAutoHold = JSON.parse(rawAutoHold);
        if (parsedAutoHold && typeof parsedAutoHold === 'object') {
          setActionQueueAutoHoldById(parsedAutoHold);
        }
      }
    } catch {
      // noop
    }
    try {
      const rawAutoLog = window.localStorage.getItem(ACTION_QUEUE_AUTO_LOG_STORAGE_KEY);
      if (rawAutoLog) {
        const parsedAutoLog = JSON.parse(rawAutoLog);
        if (Array.isArray(parsedAutoLog)) {
          setAutoActionLifecycleLogs(parsedAutoLog.slice(-120));
        }
      }
    } catch {
      // noop
    }
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(
        ACTION_QUEUE_STORAGE_KEY,
        JSON.stringify({
          stateById: actionQueueStateById,
          createdAtById: actionQueueCreatedAtById,
        }),
      );
    } catch {
      // noop
    }
  }, [actionQueueCreatedAtById, actionQueueStateById]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ACTION_QUEUE_GUARD_STORAGE_KEY, JSON.stringify(recommendationGuardById || {}));
    } catch {
      // noop
    }
  }, [recommendationGuardById]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ACTION_QUEUE_FEEDBACK_STORAGE_KEY, JSON.stringify(actionQueueFeedbackById || {}));
    } catch {
      // noop
    }
  }, [actionQueueFeedbackById]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ACTION_QUEUE_EVAL_STORAGE_KEY, JSON.stringify(recommendationEvalById || {}));
    } catch {
      // noop
    }
  }, [recommendationEvalById]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ACTION_QUEUE_AUTO_HOLD_STORAGE_KEY, JSON.stringify(actionQueueAutoHoldById || {}));
    } catch {
      // noop
    }
  }, [actionQueueAutoHoldById]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(ACTION_QUEUE_AUTO_LOG_STORAGE_KEY, JSON.stringify((autoActionLifecycleLogs || []).slice(-120)));
    } catch {
      // noop
    }
  }, [autoActionLifecycleLogs]);

  const setCustomerActionFeedback = useCallback((customerId, state, durationMs = 2800) => {
    const id = String(customerId || '').trim();
    if (!id) return;
    const safeState = String(state || 'processing').toLowerCase();
    setActionFeedbackByCustomerId((prev) => ({
      ...prev,
      [id]: {
        state: safeState,
        updatedAt: Date.now(),
      },
    }));
    if (actionFeedbackTimersRef.current[id]) {
      window.clearTimeout(actionFeedbackTimersRef.current[id]);
    }
    actionFeedbackTimersRef.current[id] = window.setTimeout(() => {
      setActionFeedbackByCustomerId((prev) => {
        const next = { ...prev };
        delete next[id];
        return next;
      });
      delete actionFeedbackTimersRef.current[id];
    }, Math.max(1800, Number(durationMs || 2800)));
  }, []);

  const setInfraActionFeedback = useCallback((nodeType, nodeId, state, durationMs = 2800) => {
    const type = String(nodeType || '').toLowerCase();
    const id = String(nodeId || '').trim();
    if (!id || !['odp', 'olt'].includes(type)) return;
    const key = `${type}:${id}`;
    const safeState = String(state || AI_ACTION_STATES.EXECUTING).toLowerCase();
    setActionFeedbackByInfraId((prev) => ({
      ...prev,
      [key]: {
        state: safeState,
        updatedAt: Date.now(),
      },
    }));
    if (infraActionFeedbackTimersRef.current[key]) {
      window.clearTimeout(infraActionFeedbackTimersRef.current[key]);
    }
    infraActionFeedbackTimersRef.current[key] = window.setTimeout(() => {
      setActionFeedbackByInfraId((prev) => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      delete infraActionFeedbackTimersRef.current[key];
    }, Math.max(1800, Number(durationMs || 2800)));
  }, []);

  const updateQueueItemState = useCallback((action, nextState, extra = {}) => {
    const key = toActionKey(action);
    const now = Date.now();
    const target = resolveActionTarget(action);
    const risk = Number(extra?.risk ?? action?.risk ?? action?.payload?.risk ?? 0);
    const confidence = Number(extra?.confidence ?? action?.confidence ?? action?.payload?.confidence ?? 0);
    const baseItem = {
      id: key,
      key,
      type: String(action?.type || '').toUpperCase(),
      label: String(action?.label || action?.type || 'Action'),
      targetType: target.type,
      targetId: target.id,
      targetLabel: target.label,
      risk: Number.isFinite(risk) ? risk : 0,
      confidence: Number.isFinite(confidence) ? confidence : 0,
      auto: Boolean(extra?.auto ?? action?.auto),
      createdAt: now,
      updatedAt: now,
      elapsedMs: 0,
      status: nextState,
    };
    setAiActionQueue((prev) => {
      const idx = prev.findIndex((item) => item.key === key);
      if (idx < 0) return [baseItem, ...prev].slice(0, 30);
      const prevItem = prev[idx];
      const nextItem = {
        ...prevItem,
        ...baseItem,
        createdAt: prevItem.createdAt || now,
        status: nextState,
        updatedAt: now,
      };
      const next = [...prev];
      next[idx] = nextItem;
      return next;
    });
  }, []);

  useEffect(() => {
    if (visualMode === 'normal') {
      setWebglOverlayActive(false);
      setWebglOverlayFailed(false);
      setWebglOverlayProbeDone(false);
      return;
    }
    setWebglOverlayActive(false);
    setWebglOverlayFailed(false);
    setWebglOverlayProbeDone(false);
  }, [visualMode, selectedNasId, selectedOltId, statusFilter, infraFilter, customerFilterMode, viewMode]);

  const rendererBadge = useMemo(() => {
    if (useGpuMapEngine) {
      return { label: 'GPU Map Engine', tone: 'hybrid' };
    }
    if (visualMode === 'normal') {
      return { label: 'Leaflet-only', tone: 'leaflet' };
    }
    if (!webglOverlayProbeDone) {
      return { label: 'Hybrid probing...', tone: 'leaflet' };
    }
    if (webglOverlayActive) {
      return { label: 'Hybrid WebGL', tone: 'hybrid' };
    }
    if (webglOverlayFailed) {
      return { label: 'Fallback', tone: 'fallback' };
    }
    return { label: 'Leaflet-only', tone: 'leaflet' };
  }, [useGpuMapEngine, visualMode, webglOverlayActive, webglOverlayFailed, webglOverlayProbeDone]);



  useEffect(() => {

    const storedUser = localStorage.getItem('ktc_user');

    if (storedUser) {
      try {
        setUser(JSON.parse(storedUser));
      } catch {
        setUser(null);
      }

    }

  }, []);



  const handleAuthExpired = useCallback(() => {
    if (authExpiredNotifiedRef.current) return;
    authExpiredNotifiedRef.current = true;
    clearStaffSession();
    toast.error('Sesi login berakhir. Silakan login ulang.');
    navigate('/dadu/login');
  }, [navigate]);

  const getAuthToken = useCallback(() => {
    const token = localStorage.getItem('ktc_token');
    if (!token || isJwtExpired(token)) {
      handleAuthExpired();
      return '';
    }
    return token;
  }, [handleAuthExpired]);

  const getHeaders = useCallback(() => {
    const token = getAuthToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getAuthToken]);

  const flushRecommendationEvalEvents = useCallback(async () => {
    const pending = Array.isArray(recommendationEvalPendingRef.current)
      ? recommendationEvalPendingRef.current.slice(0, 50)
      : [];
    if (pending.length === 0) return;
    recommendationEvalPendingRef.current = recommendationEvalPendingRef.current.slice(pending.length);
    try {
      const headers = getHeaders();
      if (!headers.Authorization) return;
      await axios.post(
        `${API}/monitoring/recommendation-evaluation/logs`,
        { items: pending },
        { headers, timeout: 7000 },
      );
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      }
      recommendationEvalPendingRef.current = [...pending, ...(recommendationEvalPendingRef.current || [])].slice(0, 200);
    }
  }, [getHeaders, handleAuthExpired]);

  const queueRecommendationEvalEvent = useCallback((eventPayload) => {
    if (!eventPayload || typeof eventPayload !== 'object') return;
    recommendationEvalPendingRef.current = [...(recommendationEvalPendingRef.current || []), eventPayload].slice(-200);
    if (recommendationEvalFlushTimerRef.current) return;
    recommendationEvalFlushTimerRef.current = window.setTimeout(async () => {
      recommendationEvalFlushTimerRef.current = null;
      await flushRecommendationEvalEvents();
    }, 1500);
  }, [flushRecommendationEvalEvents]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const fromQueryId = String(params.get('customer_id') || '').trim();
    const fromQueryNumber = String(params.get('customer_number') || '').trim();
    const fromQueryPppoe = String(params.get('pppoe_username') || '').trim();
    const fromQuerySourceType = String(params.get('sourceType') || '').trim();
    const fromQuerySourceId = String(params.get('sourceId') || '').trim();
    const fromStateId = String(location.state?.focusCustomerId || '').trim();
    const fromStateNumber = String(location.state?.focusCustomerNumber || '').trim();
    const fromStatePppoe = String(location.state?.focusPppoeUsername || '').trim();
    const fromStateSourceType = String(location.state?.focusSourceType || '').trim();
    const fromStateSourceId = String(location.state?.focusSourceId || '').trim();

    const focusPayload = {
      customerId: fromQueryId || fromStateId || '',
      customerNumber: fromQueryNumber || fromStateNumber || '',
      pppoeUsername: (fromQueryPppoe || fromStatePppoe || '').toLowerCase(),
      source: String(params.get('source') || location.state?.source || '').trim(),
    };

    if (!focusPayload.customerId && !focusPayload.customerNumber && !focusPayload.pppoeUsername) return;
    pendingCustomerFocusResolvedRef.current = false;
    setPendingCustomerFocus(focusPayload);
    setRightPanelTab('customers');
  }, [location.search, location.state]);

  useEffect(() => {
    return () => {
      if (recommendationEvalFlushTimerRef.current) {
        window.clearTimeout(recommendationEvalFlushTimerRef.current);
        recommendationEvalFlushTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const sourceType = String(params.get('sourceType') || location.state?.focusSourceType || '').trim();
    const sourceId = String(params.get('sourceId') || location.state?.focusSourceId || '').trim();
    if (!sourceType || !sourceId) return;
    pendingSourceFocusResolvedRef.current = false;
    setPendingSourceFocus({
      sourceType: normalizeIncidentSourceType(sourceType),
      sourceId,
    });
    setRightPanelTab('tickets');
  }, [location.search, location.state]);

  const syncMapFullscreenState = useCallback(() => {
    const container = mapFullscreenRef.current;
    setIsMapFullscreen(Boolean(container) && document.fullscreenElement === container);
  }, []);

  useEffect(() => {
    const onFullscreenChange = () => syncMapFullscreenState();
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', onFullscreenChange);
    };
  }, [syncMapFullscreenState]);

  const handleToggleMapFullscreen = useCallback(async () => {
    try {
      if (!isMapFullscreen) {
        const container = mapFullscreenRef.current;
        if (container?.requestFullscreen) {
          await container.requestFullscreen();
        }
      } else if (document.fullscreenElement && document.exitFullscreen) {
        await document.exitFullscreen();
      }
    } catch (error) {
      toast.error('Gagal mengubah mode fullscreen peta.');
    }
  }, [isMapFullscreen]);

  const clearFetchRetry = useCallback(() => {
    fetchRetryAttemptRef.current = 0;
    if (fetchRetryTimerRef.current) {
      window.clearTimeout(fetchRetryTimerRef.current);
      fetchRetryTimerRef.current = null;
    }
    setIsRetryScheduled(false);
  }, []);



  const applyStatusPayload = useCallback((statusPayload, radiusPayload = null) => {

    const payload = statusPayload || {};
    const radius = radiusPayload || {};

    const statuses = Array.isArray(payload.statuses) ? payload.statuses : [];
    const radiusStatuses = Array.isArray(radius.statuses) ? radius.statuses : [];
    const hasStatuses = statuses.length > 0;
    const isStaleUnavailable = payload?.stale === true && payload?.available === false;

    const mappedStatus = {};



    statuses.forEach((item) => {

      if (item?.customer_id) {

        mappedStatus[item.customer_id] = {
          ...item,
          pppoe_connection_status: 'unknown',
          pppoe_status_detail: null,
        };

      }

    });

    radiusStatuses.forEach((item) => {
      if (!item?.customer_id) return;
      const existing = mappedStatus[item.customer_id] || { customer_id: item.customer_id, connection_status: 'unknown', status_detail: null };
      mappedStatus[item.customer_id] = {
        ...existing,
        pppoe_connection_status: item.connection_status || 'unknown',
        pppoe_status_detail: item.status_detail || null,
      };
    });



    if (isStaleUnavailable && !hasStatuses) {
      setServerAvailable(false);
      setLastUpdated(payload.updated_at || new Date().toISOString());
      setStatusStale(true);
      return;
    }

    if (hasStatuses) {
      setStatusByCustomerId(mappedStatus);
    }

    if (typeof payload.available === 'boolean' && (hasStatuses || payload.available)) {
      setServerAvailable(Boolean(payload.available));
    }

    setLastUpdated(payload.updated_at || new Date().toISOString());
    if (payload?.updated_at) {
      const ageMs = Date.now() - new Date(payload.updated_at).getTime();
      setStatusStale(Number.isFinite(ageMs) && ageMs > 20000);
    } else {
      setStatusStale(false);
    }

  }, []);

  const fetchGenieacsHealth = useCallback(async () => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.get(`${API}/genieacs/health`, { headers: headersValue });
      const genieStatus = resolveGenieHealthStatus(response?.data || {});
      const available = genieStatus === 'ok';
      setGenieacsHealthAvailable(available);
      if (available) {
        setServerAvailable(true);
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        setGenieacsHealthAvailable(false);
      }
    }
  }, [getHeaders, handleAuthExpired]);

  const fetchSystemMetrics = useCallback(async () => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.get(`${API}/system/metrics`, { headers: headersValue });
      setSystemMetrics(response?.data || null);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      }
    }
  }, [getHeaders, handleAuthExpired]);

  const fetchSelfHealingLogs = useCallback(async () => {
    try {
      setSelfHealingLogsLoading(true);
      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        setSelfHealingLogs([]);
        return;
      }
      const response = await axios.get(`${API}/self-healing/logs`, {
        headers: headersValue,
        params: { limit: 30 },
      });
      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      setSelfHealingLogs(items);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        setSelfHealingLogs([]);
      }
    } finally {
      setSelfHealingLogsLoading(false);
    }
  }, [getHeaders, handleAuthExpired]);

  const fetchOptimizationLogs = useCallback(async () => {
    try {
      setOptimizationLogsLoading(true);
      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        setOptimizationLogs([]);
        return;
      }
      const response = await axios.get(`${API}/auto-optimization/logs`, {
        headers: headersValue,
        params: { limit: 30 },
      });
      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      setOptimizationLogs(items);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        setOptimizationLogs([]);
      }
    } finally {
      setOptimizationLogsLoading(false);
    }
  }, [getHeaders, handleAuthExpired]);

  const fetchFieldTickets = useCallback(async () => {
    try {
      setLoadingFieldTickets(true);
      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        setFieldTickets([]);
        return;
      }
      const response = await axios.get(`${API}/field-tickets`, { headers: headersValue });
      const items = Array.isArray(response?.data) ? response.data : [];
      setFieldTickets(items);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        setFieldTickets([]);
      }
    } finally {
      setLoadingFieldTickets(false);
    }
  }, [getHeaders, handleAuthExpired]);

  const fetchMonitoringIncidents = useCallback(async () => {
    if (!MONITORING_INCIDENT_API_ENABLED) {
      setMonitoringIncidents([]);
      setLoadingMonitoringIncidents(false);
      return;
    }
    try {
      setLoadingMonitoringIncidents(true);
      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        setMonitoringIncidents([]);
        return;
      }
      const response = await axios.get(`${API}/monitoring-incidents`, {
        headers: headersValue,
        params: { limit: 25 },
      });
      const items = Array.isArray(response?.data?.items) ? response.data.items : [];
      setMonitoringIncidents(items);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        setMonitoringIncidents([]);
      }
    } finally {
      setLoadingMonitoringIncidents(false);
    }
  }, [getHeaders, handleAuthExpired]);

  const handleResolveMonitoringIncident = useCallback(async (incident, falsePositive = false) => {
    const incidentId = String(incident?.id || '').trim();
    if (!incidentId) return;
    const prompt = falsePositive
      ? 'Tandai incident ini sebagai false positive?'
      : 'Tandai incident ini sebagai resolved?';
    if (!window.confirm(prompt)) return;
    try {
      setResolvingMonitoringIncidentId(incidentId);
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      await axios.post(
        `${API}/monitoring-incidents/${encodeURIComponent(incidentId)}/resolve`,
        { false_positive: Boolean(falsePositive) },
        { headers: headersValue, timeout: 12000 },
      );
      toast.success(falsePositive ? 'Incident ditandai false positive.' : 'Incident ditandai resolved.');
      fetchMonitoringIncidents();
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(String(error?.response?.data?.detail || 'Gagal memperbarui status incident.'));
      }
    } finally {
      setResolvingMonitoringIncidentId('');
    }
  }, [fetchMonitoringIncidents, getHeaders, handleAuthExpired]);

  const getIncidentRecommendedActionLabel = useCallback((action) => {
    const type = String(action?.type || '').trim().toLowerCase();
    if (type === 'task') return 'Buka Tugas';
    if (type === 'ticket') return 'Buka Tiket';
    if (type === 'monitor') return 'Pantau Monitoring';
    if (type === 'broadcast') return 'Info Gangguan';
    return String(action?.label || 'Lihat Detail');
  }, []);

  const prioritizeIncidentActions = useCallback((actionsRaw) => {
    const actions = Array.isArray(actionsRaw) ? actionsRaw : [];
    const rank = (action) => {
      const type = String(action?.type || '').trim().toLowerCase();
      if (type === 'task') return 1;
      if (type === 'ticket') return 2;
      if (type === 'monitor') return 3;
      if (type === 'broadcast') return 4;
      return 9;
    };
    return actions
      .slice()
      .sort((a, b) => rank(a) - rank(b))
      .slice(0, 3);
  }, []);

  const handleIncidentRecommendedAction = useCallback((action, incident) => {
    const actionType = String(action?.type || '').trim().toLowerCase();
    const deepLink = String(action?.deep_link || '').trim().toLowerCase();
    const sourceType = normalizeIncidentSourceType(incident?.root_source_type);
    const sourceId = String(incident?.root_source_id || '').trim();
    const focusToSource = () => {
      if (!sourceType || !sourceId) return;
      setIncidentSourceOverride({
        sourceType,
        sourceId,
        sourceKey: buildIncidentSourceKey(sourceType, sourceId),
      });
      setRightPanelTab('tickets');
    };

    if (actionType === 'monitor' || deepLink === 'map') {
      focusToSource();
      toast.success(`Monitoring difokuskan ke ${sourceType.toUpperCase()} ${sourceId}.`);
      return;
    }
    if (actionType === 'ticket' || deepLink === 'tickets') {
      setRightPanelTab('tickets');
      toast.info('Buka panel ticket untuk tindak lanjut incident.');
      return;
    }
    if (actionType === 'task' || deepLink === 'tasks') {
      navigate('/dadu/penanganan');
      return;
    }
    if (actionType === 'broadcast' || deepLink === 'home') {
      toast.info('Broadcast gangguan dikelola melalui modul notifikasi.');
      return;
    }
    toast.info('Aksi rekomendasi belum memiliki navigasi khusus.');
  }, [navigate]);

  const fetchAiActionCenter = useCallback(async () => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const [monitorRes, actionsRes] = await Promise.all([
        axios.get(`${API}/ai/monitoring`, { headers: headersValue }),
        axios.get(`${API}/ai/actions`, { headers: headersValue }),
      ]);

      const monitorPayload = monitorRes?.data && typeof monitorRes.data === 'object' ? monitorRes.data : {};
      const anomaly = monitorPayload?.anomaly && typeof monitorPayload.anomaly === 'object' ? monitorPayload.anomaly : {};
      const resolvedAutoMode = Boolean(monitorPayload?.auto_mode ?? aiMonitoringResult?.auto_mode ?? true);
      setAiMonitoringResult({
        risk: Number(monitorPayload?.risk || 0),
        anomaly: {
          offline_count: Number(anomaly?.offline_count || 0),
          total: Number(anomaly?.total || 0),
          ratio: Number(anomaly?.ratio || 0),
          is_anomaly: Boolean(anomaly?.is_anomaly),
        },
        root_cause: monitorPayload?.root_cause && typeof monitorPayload.root_cause === 'object' ? monitorPayload.root_cause : null,
        actions: Array.isArray(monitorPayload?.actions) ? monitorPayload.actions : [],
        recommendation: Array.isArray(monitorPayload?.recommendation) ? monitorPayload.recommendation : [],
        auto_mode: resolvedAutoMode,
      });

      const actionsPayload = actionsRes?.data && typeof actionsRes.data === 'object' ? actionsRes.data : {};
      const recommendedActions = Array.isArray(actionsPayload.items)
        ? actionsPayload.items
        : (Array.isArray(monitorPayload?.actions)
          ? monitorPayload.actions.map((type) => ({ type, label: type }))
          : []);
      setAiActionButtons(recommendedActions);
      setAiActionLogs(Array.isArray(monitorPayload?.execution_logs) ? monitorPayload.execution_logs : []);

      const queueRisk = Number(monitorPayload?.risk || 0);
      const queueConfidence = Math.max(35, Math.min(98, 100 - Math.round(queueRisk * 0.55)));
      setAiActionQueue((prev) => {
        const prevByKey = prev.reduce((acc, item) => {
          acc[item.key] = item;
          return acc;
        }, {});
        const nextKeys = new Set();
        const nextSuggested = recommendedActions.map((action) => {
          const key = toActionKey(action);
          nextKeys.add(key);
          const existing = prevByKey[key];
          const target = resolveActionTarget(action);
          const keepStatus = existing && !AI_ACTION_DONE_STATES.has(existing.status) ? existing.status : null;
          return {
            id: key,
            key,
            type: String(action?.type || '').toUpperCase(),
            label: String(action?.label || action?.type || 'Action'),
            targetType: target.type,
            targetId: target.id,
            targetLabel: target.label,
            risk: Number(action?.risk ?? queueRisk ?? 0),
            confidence: Number(action?.confidence ?? queueConfidence ?? 0),
            auto: Boolean(action?.auto),
            createdAt: existing?.createdAt || Date.now(),
            updatedAt: Date.now(),
            elapsedMs: existing?.elapsedMs || 0,
            status: keepStatus || (queueRisk >= 55 ? AI_ACTION_STATES.RECOMMENDED : AI_ACTION_STATES.DETECTED),
          };
        });

        const preserveCompleted = prev
          .filter((item) => AI_ACTION_DONE_STATES.has(item.status) && !nextKeys.has(item.key))
          .slice(0, 10);

        return [...nextSuggested, ...preserveCompleted].slice(0, 30);
      });
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        setAiActionButtons([]);
        setAiActionLogs([]);
      }
    }
  }, [aiMonitoringResult?.auto_mode, getHeaders, handleAuthExpired]);

  const triggerFocusPulse = useCallback((customerId) => {
    const id = String(customerId || '').trim();
    if (!id) return;
    setFocusPulseCustomerId(id);
    if (focusPulseTimeoutRef.current) {
      clearTimeout(focusPulseTimeoutRef.current);
    }
    focusPulseTimeoutRef.current = setTimeout(() => {
      setFocusPulseCustomerId((prev) => (prev === id ? '' : prev));
      focusPulseTimeoutRef.current = null;
    }, 3200);
  }, []);

  const handleFocusNode = useCallback((type, id) => {
    const normalizedId = String(id || '').trim();
    if (!normalizedId) return;
    setFocusNode({ type, id: normalizedId });
    if (type === 'customer') {
      triggerFocusPulse(normalizedId);
    }
  }, [triggerFocusPulse]);

  const handleExecuteAiAction = useCallback(async (action) => {
    const actionType = String(action?.type || '').toUpperCase();
    if (actionType === 'RESTART_ONU_BATCH') {
      const ok = window.confirm('Yakin restart ONU batch untuk node terdampak?');
      if (!ok) return;
    }
    const payloadCustomerId = String(
      action?.payload?.customer_id
      || action?.payload?.target_customer_id
      || selectedCustomerId
      || '',
    ).trim();
    const actionTarget = resolveActionTarget(action);
    updateQueueItemState(action, AI_ACTION_STATES.APPROVED, {
      risk: Number(action?.risk ?? aiMonitoringResult?.risk ?? 0),
      confidence: Number(action?.confidence ?? Math.max(35, 100 - Math.round(Number(aiMonitoringResult?.risk || 0) * 0.55))),
      auto: Boolean(action?.auto),
    });
    if (payloadCustomerId && ['RESTART_ONU_BATCH', 'RESTART_ONU', 'ASSIGN_TECHNICIAN', 'ASSIGN_TECHNICIAN_AUTO'].includes(actionType)) {
      setCustomerActionFeedback(payloadCustomerId, 'processing', 3600);
    }
    if (actionTarget.type === 'odp' || actionTarget.type === 'olt') {
      setInfraActionFeedback(actionTarget.type, actionTarget.id, 'processing', 3800);
      handleFocusNode(actionTarget.type, actionTarget.id);
    }
    if (actionTarget.type === 'customer' && actionTarget.id) {
      handleFocusNode('customer', actionTarget.id);
    }
    updateQueueItemState(action, AI_ACTION_STATES.EXECUTING, { auto: Boolean(action?.auto) });
    setAiActionExecuting(true);
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.post(
        `${API}/ai/actions/execute`,
        { action },
        { headers: headersValue, timeout: 20000 },
      );
      const payload = response?.data && typeof response.data === 'object' ? response.data : {};
      if (payload?.redirect) {
        navigate(String(payload.redirect));
      }
      if (payloadCustomerId) setCustomerActionFeedback(payloadCustomerId, 'success', 2800);
      if (actionTarget.type === 'odp' || actionTarget.type === 'olt') {
        setInfraActionFeedback(actionTarget.type, actionTarget.id, 'success', 2600);
      }
      updateQueueItemState(action, AI_ACTION_STATES.SUCCEEDED, { auto: Boolean(action?.auto) });
      setAiActionToast(String(payload?.message || 'Aksi AI berhasil dijalankan.'));
      window.setTimeout(() => setAiActionToast(''), 2600);
      await fetchAiActionCenter();
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        if (payloadCustomerId) setCustomerActionFeedback(payloadCustomerId, 'failed', 3400);
        if (actionTarget.type === 'odp' || actionTarget.type === 'olt') {
          setInfraActionFeedback(actionTarget.type, actionTarget.id, 'failed', 3200);
        }
        updateQueueItemState(action, AI_ACTION_STATES.FAILED, { auto: Boolean(action?.auto) });
        setAiActionToast(String(error?.response?.data?.detail || 'Aksi AI gagal dijalankan.'));
        window.setTimeout(() => setAiActionToast(''), 2600);
      }
    } finally {
      setAiActionExecuting(false);
    }
  }, [aiMonitoringResult?.risk, fetchAiActionCenter, getHeaders, handleAuthExpired, navigate, selectedCustomerId, setCustomerActionFeedback, setInfraActionFeedback, updateQueueItemState, handleFocusNode]);

  const handleToggleAutoAiMode = useCallback(async (enabled) => {
    setAiAutoModeUpdating(true);
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.post(
        `${API}/monitoring/ai-monitoring/auto-mode`,
        { enabled: Boolean(enabled) },
        { headers: headersValue, timeout: 10000 },
      );
      setAiMonitoringResult((prev) => ({ ...prev, auto_mode: Boolean(response?.data?.enabled ?? enabled) }));
      setAiActionToast(`Auto AI Mode ${enabled ? 'aktif' : 'nonaktif'}.`);
      window.setTimeout(() => setAiActionToast(''), 2200);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        setAiActionToast('Gagal mengubah Auto AI Mode.');
        window.setTimeout(() => setAiActionToast(''), 2200);
      }
    } finally {
      setAiAutoModeUpdating(false);
    }
  }, [getHeaders, handleAuthExpired]);

  const logRecommendationEvent = useCallback((itemRaw, eventTypeRaw, eventMetaRaw = null) => {
    const item = itemRaw && typeof itemRaw === 'object' ? itemRaw : { id: String(itemRaw || '').trim() };
    const recommendationId = String(item?.id || '').trim();
    if (!recommendationId) return;
    const eventType = String(eventTypeRaw || '').trim().toLowerCase();
    if (!eventType) return;
    const canonicalEventType = normalizeDecisionEventType(eventType);
    const nowMs = Date.now();
    const nowIso = new Date(nowMs).toISOString();
    const compactMeta = eventMetaRaw && typeof eventMetaRaw === 'object'
      ? Object.fromEntries(Object.entries(eventMetaRaw).filter(([, value]) => value !== undefined))
      : {};
    const sourceType = normalizeIncidentSourceType(item?.source_type || item?.sourceType || 'topology_node');
    const sourceId = String(item?.source_id || item?.sourceId || '').trim();
    const actionType = normalizeActionQueueActionType(item?.action_type || item?.actionType || 'monitor');
    const urgency = normalizeActionQueueUrgency(item?.urgency || 'rendah');
    const strategyMode = normalizeRecommendationStrategyMode(
      item?.strategyMode
      || item?.strategy
      || capacityRecommendationStrategy,
    );
    const confidence = Number(item?.confidenceScore || item?.confidence || 0);
    const recommendationContext = {
      title: String(item?.title || '').trim(),
      reason: String(item?.reason || '').trim(),
      recommendedAction: String(item?.recommended_action || item?.recommendedAction || item?.suggestedAction || '').trim(),
      sourceLabel: String(item?.sourceLabel || '').trim() || null,
      links: item?.links && typeof item.links === 'object' ? { ...item.links } : {},
    };
    const metricsSnapshot = item?.metrics && typeof item.metrics === 'object'
      ? {
        occupancy: Number.isFinite(Number(item?.metrics?.occupancyPercent)) ? Number(item.metrics.occupancyPercent) : null,
        remainingCapacity: Number.isFinite(Number(item?.metrics?.remaining)) ? Number(item.metrics.remaining) : null,
        growth30d: Number.isFinite(Number(item?.metrics?.growth30d)) ? Number(item.metrics.growth30d) : null,
        growth60d: Number.isFinite(Number(item?.metrics?.growth60d)) ? Number(item.metrics.growth60d) : null,
        etaDays: Number.isFinite(Number(item?.metrics?.daysToFull)) ? Number(item.metrics.daysToFull) : null,
        clusterPressure: Number.isFinite(Number(item?.metrics?.clusterPressure)) ? Number(item.metrics.clusterPressure) : null,
        riskLevel: String(item?.metrics?.riskLevel || '').trim() || null,
      }
      : {};
    const eventPayload = {
      recommendationId,
      eventType,
      eventAt: nowIso,
      sourceType: sourceType || null,
      sourceId: sourceId || null,
      actionType: actionType || null,
      urgency: urgency || null,
      confidence: Number.isFinite(confidence) ? Number(confidence.toFixed(2)) : null,
      queueStatus: normalizeRecommendationEvalQueueStatus(
        compactMeta?.queueStatus
        || compactMeta?.status
        || item?.status
        || 'pending',
      ),
      feedbackType: compactMeta?.feedbackType || item?.feedbackType || null,
      feedbackReason: compactMeta?.feedbackReason || null,
      strategyMode: strategyMode || null,
      recommendationContext,
      metricsSnapshot,
      rubric: { ...RECOMMENDATION_RUBRIC_EMPTY },
      meta: compactMeta,
    };
    queueRecommendationEvalEvent(eventPayload);

    setRecommendationEvalById((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[recommendationId] && typeof next[recommendationId] === 'object' ? next[recommendationId] : null;
      const existingEvents = Array.isArray(existing?.events) ? existing.events : [];
      const lastEvent = existingEvents.length > 0 ? existingEvents[existingEvents.length - 1] : null;
      const duplicateBurst = lastEvent
        && String(lastEvent?.type || '') === eventType
        && (nowMs - Number(lastEvent?.atMs || 0)) < 2500;
      if (duplicateBurst && eventType !== 'feedback_positive' && eventType !== 'feedback_negative') {
        return prev;
      }

      const queueStatus = String(
        compactMeta?.queueStatus
        || compactMeta?.status
        || item?.status
        || existing?.queueStatus
        || 'pending',
      ).trim().toLowerCase();
      const currentEvents = [...existingEvents, {
        type: eventType,
        at: nowIso,
        atMs: nowMs,
        meta: compactMeta,
      }].slice(-30);

      const nextRecord = {
        recommendationId,
        sourceType: sourceType || (existing?.sourceType || 'topology_node'),
        sourceId: sourceId || existing?.sourceId || '',
        actionType: actionType || existing?.actionType || 'monitor',
        urgency: urgency || existing?.urgency || 'rendah',
        confidence: Number.isFinite(confidence) ? Number(confidence.toFixed(2)) : Number(existing?.confidence || 0),
        createdAt: existing?.createdAt || null,
        shownAt: existing?.shownAt || null,
        lastShownAt: existing?.lastShownAt || null,
        acceptedAt: existing?.acceptedAt || null,
        completedAt: existing?.completedAt || null,
        ignoredAt: existing?.ignoredAt || null,
        feedbackType: existing?.feedbackType || null,
        feedbackAt: existing?.feedbackAt || null,
        feedbackReason: existing?.feedbackReason || null,
        queueStatus,
        strategyMode: strategyMode || existing?.strategyMode || null,
        recommendationContext: {
          ...(existing?.recommendationContext || {}),
          ...recommendationContext,
        },
        metricsSnapshot: {
          ...(existing?.metricsSnapshot || {}),
          ...metricsSnapshot,
        },
        rubric: {
          ...(existing?.rubric || RECOMMENDATION_RUBRIC_EMPTY),
        },
        inferredSignals: {
          ...(existing?.inferredSignals || {}),
        },
        lifecycle: {
          ...(existing?.lifecycle || {}),
        },
        events: currentEvents,
      };

      if (canonicalEventType === 'generated') {
        if (!nextRecord.createdAt) nextRecord.createdAt = nowIso;
        nextRecord.lifecycle.generatedAt = nowIso;
      }
      if (canonicalEventType === 'shown') {
        if (!nextRecord.shownAt) nextRecord.shownAt = nowIso;
        nextRecord.lastShownAt = nowIso;
        nextRecord.lifecycle.shownCount = Number(nextRecord.lifecycle.shownCount || 0) + 1;
      }
      if (canonicalEventType === 're_shown') {
        nextRecord.lastShownAt = nowIso;
        nextRecord.lifecycle.reshownCount = Number(nextRecord.lifecycle.reshownCount || 0) + 1;
      }
      if (canonicalEventType === 'suppressed') {
        nextRecord.lifecycle.lastSuppressedAt = nowIso;
        nextRecord.lifecycle.suppressedCount = Number(nextRecord.lifecycle.suppressedCount || 0) + 1;
        nextRecord.lifecycle.lastSuppressedReason = String(compactMeta?.reason || existing?.lifecycle?.lastSuppressedReason || '').trim() || null;
      }
      if (canonicalEventType === 'accepted') {
        nextRecord.acceptedAt = nowIso;
        nextRecord.queueStatus = 'accepted';
        nextRecord.lifecycle.acceptedCount = Number(nextRecord.lifecycle.acceptedCount || 0) + 1;
        if (nextRecord.shownAt) {
          const deltaMs = durationMsBetween(nextRecord.shownAt, nowIso);
          if (deltaMs !== null) {
            nextRecord.inferredSignals.acceptDelayMs = deltaMs;
            nextRecord.inferredSignals.acceptedQuickly = deltaMs <= (5 * 60 * 1000);
          }
        }
      }
      if (canonicalEventType === 'completed') {
        nextRecord.completedAt = nowIso;
        nextRecord.queueStatus = 'completed';
        nextRecord.lifecycle.completedCount = Number(nextRecord.lifecycle.completedCount || 0) + 1;
        if (nextRecord.acceptedAt) {
          const deltaMs = durationMsBetween(nextRecord.acceptedAt, nowIso);
          if (deltaMs !== null) {
            nextRecord.inferredSignals.completeAfterAcceptMs = deltaMs;
            nextRecord.inferredSignals.completedAfterAccept = true;
          }
        }
      }
      if (canonicalEventType === 'ignored') {
        nextRecord.ignoredAt = nowIso;
        nextRecord.queueStatus = 'ignored';
        nextRecord.lifecycle.ignoredCount = Number(nextRecord.lifecycle.ignoredCount || 0) + 1;
        if (nextRecord.shownAt) {
          const deltaMs = durationMsBetween(nextRecord.shownAt, nowIso);
          if (deltaMs !== null) {
            nextRecord.inferredSignals.ignoreDelayMs = deltaMs;
            nextRecord.inferredSignals.ignoredQuickly = deltaMs <= (5 * 60 * 1000);
          }
        }
      }
      if (canonicalEventType === 'held') {
        nextRecord.queueStatus = 'held';
        nextRecord.lifecycle.heldCount = Number(nextRecord.lifecycle.heldCount || 0) + 1;
      }
      if (canonicalEventType === 'failed') {
        nextRecord.queueStatus = 'failed';
        nextRecord.lifecycle.failedCount = Number(nextRecord.lifecycle.failedCount || 0) + 1;
      }
      if (canonicalEventType === 'feedback_positive') {
        nextRecord.feedbackType = 'positive';
        nextRecord.feedbackAt = nowIso;
      }
      if (canonicalEventType === 'feedback_negative') {
        nextRecord.feedbackType = 'negative';
        nextRecord.feedbackAt = nowIso;
      }
      if (compactMeta?.feedbackReason) {
        nextRecord.feedbackReason = String(compactMeta.feedbackReason || '').trim() || null;
      }

      const rubricValues = ['accuracy', 'relevance', 'timing', 'actionability']
        .map((key) => Number(nextRecord?.rubric?.[key]));
      if (rubricValues.every((value) => Number.isFinite(value))) {
        nextRecord.rubric.total = rubricValues.reduce((acc, value) => acc + value, 0);
      } else {
        nextRecord.rubric.total = null;
      }

      const before = JSON.stringify(existing || {});
      const after = JSON.stringify(nextRecord);
      if (before === after) return prev;
      next[recommendationId] = nextRecord;
      return next;
    });
  }, [capacityRecommendationStrategy, queueRecommendationEvalEvent]);

  const appendAutoActionLifecycleLog = useCallback((entryRaw) => {
    if (!entryRaw || typeof entryRaw !== 'object') return;
    const nowIso = new Date().toISOString();
    const safeEntry = {
      id: String(entryRaw?.id || `${entryRaw?.event_type || 'auto_action'}:${nowIso}`).trim(),
      event_type: String(entryRaw?.event_type || 'auto_action_generated').trim().toLowerCase(),
      rule_key: String(entryRaw?.rule_key || '').trim() || null,
      source_type: normalizeIncidentSourceType(entryRaw?.source_type || 'topology_node'),
      source_id: String(entryRaw?.source_id || '').trim() || null,
      action_type: normalizeActionQueueActionType(entryRaw?.action_type || 'monitor'),
      status: normalizeAutoActionStatus(entryRaw?.status || 'pending_review'),
      action_mode: normalizeAutoActionMode(entryRaw?.action_mode || 'manual_review'),
      reason: String(entryRaw?.reason || '').trim() || null,
      created_at: nowIso,
      meta: entryRaw?.meta && typeof entryRaw.meta === 'object' ? { ...entryRaw.meta } : {},
    };
    setAutoActionLifecycleLogs((prev) => [...(Array.isArray(prev) ? prev : []), safeEntry].slice(-120));
  }, []);

  const handleActionQueueHoldToggle = useCallback((item) => {
    const guardKey = String(item?.guardKey || item?.id || '').trim();
    const itemId = String(item?.id || '').trim();
    if (!guardKey) return;
    const now = Date.now();
    setActionQueueAutoHoldById((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[guardKey] && typeof next[guardKey] === 'object' ? next[guardKey] : null;
      if (existing?.hold_flag) {
        delete next[guardKey];
        appendAutoActionLifecycleLog({
          id: `${guardKey}:unhold:${now}`,
          event_type: 'auto_action_cancelled',
          rule_key: item?.rule_key,
          source_type: item?.source_type,
          source_id: item?.source_id,
          action_type: item?.action_type,
          status: item?.status,
          action_mode: item?.action_mode,
          reason: 'Manual unhold by operator.',
        });
      } else {
        next[guardKey] = {
          hold_flag: true,
          hold_at: now,
          hold_by: 'operator_session',
          hold_reason: 'manual_hold',
        };
        appendAutoActionLifecycleLog({
          id: `${guardKey}:hold:${now}`,
          event_type: 'auto_action_suppressed',
          rule_key: item?.rule_key,
          source_type: item?.source_type,
          source_id: item?.source_id,
          action_type: item?.action_type,
          status: item?.status,
          action_mode: item?.action_mode,
          reason: 'Manual hold by operator.',
        });
        logRecommendationEvent({ ...item, id: itemId || guardKey }, 'held', { queueStatus: 'held' });
      }
      return next;
    });
    if (itemId && !Boolean(actionQueueAutoHoldById[guardKey]?.hold_flag)) {
      setActionQueueStateById((prev) => ({
        ...(prev || {}),
        [itemId]: 'cancelled',
      }));
    }
  }, [actionQueueAutoHoldById, appendAutoActionLifecycleLog, logRecommendationEvent]);

  const handleActionQueueStatusChange = useCallback((itemOrId, nextStatus) => {
    const sourceItem = itemOrId && typeof itemOrId === 'object' ? itemOrId : { id: itemOrId };
    const id = String(sourceItem?.id || '').trim();
    const status = resolveNextActionQueueStatus(sourceItem, nextStatus || 'pending');
    if (!id || !ACTION_QUEUE_STATUS_OPTIONS.includes(status)) return;
    const guardKey = String(sourceItem?.guardKey || sourceItem?.duplicate_key || id).trim();
    setActionQueueStateById((prev) => ({
      ...(prev || {}),
      [id]: status,
    }));
    const now = Date.now();
    const cooldownMs = status === 'ignored' || status === 'skipped'
      ? 60 * 60 * 1000
      : status === 'accepted' || status === 'approved'
        ? 45 * 60 * 1000
        : status === 'completed' || status === 'executed'
          ? 30 * 60 * 1000
          : 0;
    setRecommendationGuardById((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[guardKey] && typeof next[guardKey] === 'object' ? next[guardKey] : {};
      next[guardKey] = {
        ...existing,
        lastActionAt: now,
        lastOutcome: status,
        cooldownUntil: cooldownMs > 0 ? now + cooldownMs : Number(existing?.cooldownUntil || 0),
      };
      return next;
    });
    if (status === 'accepted' || status === 'completed' || status === 'ignored' || status === 'approved' || status === 'executed' || status === 'skipped' || status === 'failed' || status === 'cancelled') {
      logRecommendationEvent({ ...sourceItem, id, status }, status, { queueStatus: status });
      appendAutoActionLifecycleLog({
        id: `${id}:${status}:${now}`,
        event_type: status === 'approved'
          ? 'auto_action_approved'
          : status === 'executed'
            ? 'auto_action_executed'
            : status === 'skipped'
              ? 'auto_action_skipped'
              : status === 'failed'
                ? 'auto_action_failed'
                : status === 'cancelled'
                  ? 'auto_action_cancelled'
                  : 'auto_action_updated',
        rule_key: sourceItem?.rule_key,
        source_type: sourceItem?.source_type,
        source_id: sourceItem?.source_id,
        action_type: sourceItem?.action_type,
        status,
        action_mode: sourceItem?.action_mode,
        reason: String(sourceItem?.reason || '').trim() || null,
      });
    }
  }, [appendAutoActionLifecycleLog, logRecommendationEvent]);

  const handleActionQueueFeedback = useCallback((item, feedbackTypeRaw) => {
    const itemId = String(item?.id || '').trim();
    const feedbackType = String(feedbackTypeRaw || '').trim().toLowerCase();
    if (!itemId || !ACTION_QUEUE_FEEDBACK_TYPES.includes(feedbackType)) return;
    const now = Date.now();

    let shouldApply = false;
    setActionQueueFeedbackById((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[itemId] && typeof next[itemId] === 'object' ? next[itemId] : null;
      const existingType = String(existing?.feedbackType || '').trim().toLowerCase();
      const existingAt = Number(existing?.feedbackAt || 0);
      const recentVote = Number.isFinite(existingAt) && (now - existingAt) < 60000;

      if (existingType === feedbackType) return prev;
      if (existingType !== feedbackType && recentVote) return prev;

      next[itemId] = {
        feedbackType,
        feedbackAt: now,
        feedbackBy: 'operator_session',
        feedbackReason: String(existing?.feedbackReason || ''),
      };
      shouldApply = true;
      return next;
    });

    if (!shouldApply) {
      toast.info('Feedback sudah direkam. Tunggu sebentar untuk mengubah kembali.');
      return;
    }

    const guardKey = String(item?.guardKey || itemId).trim();
    setRecommendationGuardById((prev) => {
      const next = { ...(prev || {}) };
      const existing = next[guardKey] && typeof next[guardKey] === 'object' ? next[guardKey] : {};
      const sourceType = String(item?.source_type || '').trim().toLowerCase();
      const sourceId = String(item?.source_id || '').trim();
      const actionType = normalizeActionQueueActionType(item?.action_type);
      const scopeKey = `${sourceType}:${sourceId}:${actionType}:${String(capacityRecommendationStrategy || 'balanced').trim().toLowerCase()}`;
      const previousBias = Number(existing?.feedbackBias || 0);
      const delta = feedbackType === 'positive' ? 0.05 : -0.06;
      const nextBias = Math.max(-0.5, Math.min(0.5, previousBias + delta));
      const urgencyRank = ACTION_QUEUE_URGENCY_RANK[normalizeActionQueueUrgency(item?.urgency)] || 1;
      const negativeCooldownMs = urgencyRank >= 3 ? 12 * 60 * 1000 : 35 * 60 * 1000;
      const cooldownUntil = feedbackType === 'negative'
        ? now + negativeCooldownMs
        : Number(existing?.cooldownUntil || 0);
      next[guardKey] = {
        ...existing,
        feedbackScopeKey: scopeKey,
        feedbackType,
        feedbackUpdatedAt: now,
        feedbackBias: Number(nextBias.toFixed(2)),
        cooldownUntil,
      };
      return next;
    });

    logRecommendationEvent(
      { ...item, id: itemId, feedbackType, queueStatus: String(item?.status || 'pending').trim().toLowerCase() || 'pending' },
      feedbackType === 'positive' ? 'feedback_positive' : 'feedback_negative',
      { queueStatus: String(item?.status || 'pending').trim().toLowerCase() || 'pending' },
    );

    toast.success(feedbackType === 'positive' ? 'Feedback membantu disimpan.' : 'Feedback tidak relevan disimpan.');
  }, [capacityRecommendationStrategy, logRecommendationEvent]);

  const handleOpenActionQueueWorkflow = useCallback((item) => {
    if (!item || typeof item !== 'object') return;
    const sourceType = normalizeIncidentSourceType(item?.source_type);
    const sourceId = String(item?.source_id || '').trim();
    const actionType = normalizeActionQueueActionType(item?.action_type);
    const links = item?.links && typeof item.links === 'object' ? item.links : {};

    if (actionType === 'expansion' || actionType === 'planning') {
            navigate(String(links?.planning || '/dadu/ftth'));
      return;
    }
    if (actionType === 'rebalance' || sourceType === 'nas') {
      navigate(String(links?.nas || '/dadu/voucher?tab=nas'));
      return;
    }

    if (sourceType === 'customer' && sourceId) {
      setSelectedCustomerId(sourceId);
      setFocusPulseCustomerId(sourceId);
      setRightPanelTab('customers');
      toast.success('Fokus monitoring ke pelanggan dipilih.');
      return;
    }
    if (sourceType === 'odp' && sourceId) {
      const linkedOdp = odps.find((odp) => String(odp?.id || '').trim() === sourceId);
      if (linkedOdp?.router_id) setSelectedNasId(String(linkedOdp.router_id));
      if (linkedOdp?.olt_id) setSelectedOltId(String(linkedOdp.olt_id));
      setFocusNode({ type: 'odp', id: sourceId });
      setRightPanelTab('infra');
      toast.success(`Fokus monitoring ke ODP ${sourceId}.`);
      return;
    }
    if (sourceType === 'olt' && sourceId) {
      setSelectedOltId(sourceId);
      setFocusNode({ type: 'olt', id: sourceId });
      setRightPanelTab('infra');
      toast.success(`Fokus monitoring ke OLT ${sourceId}.`);
      return;
    }
    if (sourceType === 'nas' && sourceId) {
      setSelectedNasId(sourceId);
      setRightPanelTab('infra');
      toast.success(`Fokus monitoring ke NAS ${sourceId}.`);
      return;
    }
    if (sourceType === 'anomaly_cluster') {
      setRightPanelTab('ai');
      toast.success('Membuka konteks AI untuk cluster anomaly.');
      return;
    }

        navigate(String(links?.monitoring || '/dadu/monitoring'));
  }, [navigate, odps]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setAiActionQueue((prev) => prev.map((item) => {
        if (AI_ACTION_DONE_STATES.has(item.status)) return item;
        const base = Number(item?.updatedAt || item?.createdAt || Date.now());
        return {
          ...item,
          elapsedMs: Math.max(0, Date.now() - base),
        };
      }));
    }, 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (isReplayMode) return;
    if (!aiMonitoringResult?.auto_mode) return;
    if (aiActionExecuting) return;
    const safeCandidate = aiActionQueue.find((item) => item.status === AI_ACTION_STATES.RECOMMENDED && isSafeAutoActionType(item.type));
    if (!safeCandidate) return;
    const guardKey = `${safeCandidate.key}:${Math.floor(Date.now() / 120000)}`;
    if (aiAutoExecuteGuardRef.current[guardKey]) return;
    aiAutoExecuteGuardRef.current[guardKey] = true;
    const action = aiActionButtons.find((btn) => toActionKey(btn) === safeCandidate.key);
    if (!action) return;
    handleExecuteAiAction({ ...action, auto: true });
  }, [aiActionButtons, aiActionExecuting, aiActionQueue, aiMonitoringResult?.auto_mode, handleExecuteAiAction, isReplayMode]);

  const handleViewportUserAdjust = useCallback(() => {
    hasUserAdjustedViewportRef.current = true;
  }, []);

  const triggerAutoFit = useCallback(() => {
    if (hasUserAdjustedViewportRef.current) return;
    setAutoFitEnabled(true);
    setAutoFitKey((value) => value + 1);
  }, []);



  const fetchData = useCallback(async (showLoader = true) => {
    let keepLoader = false;

    const normalizedSearch = String(debouncedSearchQuery || '').trim();
    const useServerSearch = normalizedSearch.length >= 3;
    const skipCustomerLoad = normalizedSearch.length > 0 && normalizedSearch.length < 3;
    const shouldShowGlobalLoader = showLoader && normalizedSearch.length === 0;
    if (shouldShowGlobalLoader) setLoading(true);



    try {

      const headersValue = getHeaders();
      if (!headersValue.Authorization) {
        if (shouldShowGlobalLoader) setLoading(false);
        return;
      }
      const headers = { headers: headersValue };
      
      const customerRequest = skipCustomerLoad
        ? Promise.resolve({ data: [] })
        : axios.get(`${API}/customers`, {
          ...headers,
          params: useServerSearch ? { search: normalizedSearch } : undefined,
        });

      const [customersRes, oltsRes, odpsRes, nasRes, topologyRes, statusRes, radiusRes, pendingPaymentsRes] = await Promise.all([

        customerRequest,

        axios.get(`${API}/ftth/olts`, headers),

        axios.get(`${API}/ftth/odps`, headers),

        axios.get(`${API}/voucher/nas`, headers).catch(() => ({ data: { items: [] } })),

        axios.get(`${API}/ftth/topology`, headers).catch(() => ({ data: {} })),

        axios.get(`${API}/genieacs/customer-status`, headers).catch(() => ({
          data: {
            available: false,
            statuses: [],
            stale: true,
            updated_at: new Date().toISOString(),
          },
        })),
        axios.get(`${API}/freeradius/customer-status`, headers).catch(() => ({ data: {} })),
        axios.get(`${API}/payments`, { ...headers, params: { approval_status: 'pending', limit: 300 } }).catch(() => ({ data: { items: [] } })),

      ]);



      const topologyPayload = normalizeFtthTopologyPayload(topologyRes?.data && typeof topologyRes.data === 'object' ? topologyRes.data : {});
      const nasPayload = normalizeVoucherNasPayload(nasRes?.data && typeof nasRes.data === 'object' ? nasRes.data : {});

      const topologyCustomers = Array.isArray(topologyPayload.customers) ? topologyPayload.customers : [];

      const topologyOlts = Array.isArray(topologyPayload.olts) ? topologyPayload.olts : [];

      const topologyOdps = Array.isArray(topologyPayload.odps) ? topologyPayload.odps : [];

      const nextCustomers = skipCustomerLoad
        ? customers
        : (useServerSearch
          ? (Array.isArray(customersRes.data) ? customersRes.data : [])
          : (topologyCustomers.length > 0 ? topologyCustomers : (Array.isArray(customersRes.data) ? customersRes.data : [])));

      const nextOlts = topologyOlts.length > 0 ? topologyOlts : (Array.isArray(oltsRes.data) ? oltsRes.data : []);

      const nextOdps = topologyOdps.length > 0 ? topologyOdps : (Array.isArray(odpsRes.data) ? odpsRes.data : []);



      if (!skipCustomerLoad) {
        setCustomers(nextCustomers);
      }

      setOlts(nextOlts);

      setOdps(nextOdps);
      setNasRouters(Array.isArray(nasPayload.items) ? nasPayload.items : []);
      const pendingPaymentItems = Array.isArray(pendingPaymentsRes?.data?.items) ? pendingPaymentsRes.data.items : [];
      setPendingPayments(pendingPaymentItems);

      applyStatusPayload(statusRes.data || {}, radiusRes?.data || {});
      if (showLoader) {
        hasUserAdjustedViewportRef.current = false;
        triggerAutoFit();
      }
      clearFetchRetry();

    } catch (error) {
      if (error?.response?.status === 401) {
        clearFetchRetry();
        handleAuthExpired();
      } else {
        const attempt = fetchRetryAttemptRef.current;
        const retryDelay = FETCH_RETRY_DELAYS_MS[attempt] ?? null;
        if (retryDelay !== null) {
          fetchRetryAttemptRef.current = attempt + 1;
          keepLoader = false;
          if (shouldShowGlobalLoader) setLoading(false);
          if (!fetchRetryTimerRef.current) {
            setIsRetryScheduled(true);
            fetchRetryTimerRef.current = window.setTimeout(() => {
              fetchRetryTimerRef.current = null;
              setIsRetryScheduled(false);
              fetchData(false);
            }, retryDelay);
            if (attempt === 0) {
              toast.warning('Gagal memuat monitoring, mencoba ulang otomatis...');
            }
          }
        } else if (shouldShowGlobalLoader) {
          setIsRetryScheduled(false);
          toast.error(error.response?.data?.detail || 'Gagal memuat monitoring konektivitas');
          setServerAvailable(false);
          setStreamConnected(false);
        }
      }

    } finally {
      if (shouldShowGlobalLoader && !keepLoader) setLoading(false);

    }

  }, [applyStatusPayload, clearFetchRetry, customers, debouncedSearchQuery, getHeaders, handleAuthExpired, triggerAutoFit]);

  const fetchStatusOnly = useCallback(async () => {
    try {
      const headersValue = getHeaders();
      if (!headersValue.Authorization) return;
      const response = await axios.get(`${API}/genieacs/customer-status`, { headers: headersValue });
      applyStatusPayload(response.data || {});
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      }
    }
  }, [applyStatusPayload, getHeaders, handleAuthExpired]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      const normalized = String(searchQuery || '').trim();
      if (normalized.length === 0 || normalized.length >= 3) {
        setDebouncedSearchQuery(normalized);
      }
    }, 350);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);



  useEffect(() => {
    if (initialLoadRef.current) {
      initialLoadRef.current = false;
      fetchData(true);
      return;
    }
    fetchData(false);
  }, [fetchData]);

  useEffect(() => () => clearFetchRetry(), [clearFetchRetry]);

  useEffect(() => {
    return () => {
      Object.values(actionFeedbackTimersRef.current || {}).forEach((timer) => {
        window.clearTimeout(timer);
      });
      actionFeedbackTimersRef.current = {};
      Object.values(infraActionFeedbackTimersRef.current || {}).forEach((timer) => {
        window.clearTimeout(timer);
      });
      infraActionFeedbackTimersRef.current = {};
    };
  }, []);

  useEffect(() => {
    if (streamConnected) return undefined;
    const timer = window.setInterval(() => {
      fetchStatusOnly();
    }, STATUS_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [fetchStatusOnly, streamConnected]);

  useEffect(() => {
    fetchGenieacsHealth();
    fetchSystemMetrics();
    fetchSelfHealingLogs();
    fetchOptimizationLogs();
    fetchFieldTickets();
    fetchMonitoringIncidents();
    fetchAiActionCenter();
    const timer = window.setInterval(() => {
      fetchGenieacsHealth();
      fetchSystemMetrics();
      fetchSelfHealingLogs();
      fetchOptimizationLogs();
      fetchFieldTickets();
      fetchMonitoringIncidents();
      fetchAiActionCenter();
    }, 30000);
    return () => window.clearInterval(timer);
  }, [fetchAiActionCenter, fetchFieldTickets, fetchGenieacsHealth, fetchMonitoringIncidents, fetchSystemMetrics, fetchSelfHealingLogs, fetchOptimizationLogs]);



  useEffect(() => {

    const token = getAuthToken();
    if (!token) return undefined;



    const streamUrl = `${API}/genieacs/customer-status/stream?token=${encodeURIComponent(token)}`;

    const source = new EventSource(streamUrl);



    const handleStatusEvent = (event) => {

      try {

        const payload = JSON.parse(event.data);

        applyStatusPayload(payload);

        setStreamConnected(true);
        streamReadyRef.current = true;
        streamErrorNotifiedRef.current = false;

      } catch {

        // Ignore malformed payload from stream

      }

    };



    const handleStreamError = () => {

      setStreamConnected(false);
      setStreamReconnectCount((value) => value + 1);

      if (!streamReadyRef.current) return;

      if (!streamErrorNotifiedRef.current) {

        toast.error('Koneksi realtime terputus, mencoba menyambung ulang...');

        streamErrorNotifiedRef.current = true;

      }

    };



    source.addEventListener('status', handleStatusEvent);

    source.addEventListener('error', handleStreamError);



    return () => {

      source.removeEventListener('status', handleStatusEvent);

      source.removeEventListener('error', handleStreamError);

      source.close();

    };

  }, [applyStatusPayload, getAuthToken]);

  const handleLogout = () => {
    clearStaffSession();
    navigate('/dadu/login');
  };



  const handleRestartOnu = async (customer) => {

    if (!customer?.id) return;

    if (!window.confirm(`Restart ONU untuk ${customer.name}? Koneksi akan terputus sementara.`)) return;



    setRestartingCustomerId(customer.id);
    setCustomerActionFeedback(customer.id, 'processing', 3600);
    const action = {
      type: 'RESTART_ONU',
      label: 'Restart ONU',
      payload: { customer_id: customer.id },
    };
    updateQueueItemState(action, AI_ACTION_STATES.EXECUTING, { risk: Number(aiMonitoringResult?.risk || 0), confidence: 82 });

    try {

      await axios.post(`${API}/customers/${customer.id}/restart-onu`, {}, { headers: getHeaders() });

      toast.success(`Perintah restart ONU terkirim untuk ${customer.name}`);
      setCustomerActionFeedback(customer.id, 'success', 3000);
      updateQueueItemState(action, AI_ACTION_STATES.SUCCEEDED);

      fetchData(false);

    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error.response?.data?.detail || 'Gagal mengirim restart ONU');
        setCustomerActionFeedback(customer.id, 'failed', 3400);
        updateQueueItemState(action, AI_ACTION_STATES.FAILED);
      }

    } finally {

      setRestartingCustomerId('');

    }

  };

  const handleSyncOnu = async (customer) => {
    if (!customer?.id) return;
    setSyncingCustomerId(customer.id);
    try {
      await axios.post(`${API}/customers/${customer.id}/sync-onu`, {}, { headers: getHeaders() });
      toast.success(`Sinkronisasi ONU dikirim untuk ${customer.name}`);
      fetchData(false);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error.response?.data?.detail || 'Gagal sinkronisasi ONU');
      }
    } finally {
      setSyncingCustomerId('');
    }
  };

  const handleIsolateCustomer = async (customer) => {
    if (!customer?.id) return;
    if (!window.confirm(`Isolir pelanggan ${customer.name}?`)) return;
    setIsolatingCustomerId(customer.id);
    setCustomerActionFeedback(customer.id, 'processing', 3600);
    const action = {
      type: 'ISOLATE_CUSTOMER',
      label: 'Isolir Pelanggan',
      payload: { customer_id: customer.id },
    };
    updateQueueItemState(action, AI_ACTION_STATES.EXECUTING, { risk: Number(aiMonitoringResult?.risk || 0), confidence: 74 });
    try {
      await axios.put(
        `${API}/customers/${customer.id}`,
        { status: 'suspended' },
        { headers: getHeaders() },
      );
      toast.success(`Pelanggan ${customer.name} berhasil diisolir`);
      setCustomerActionFeedback(customer.id, 'success', 2800);
      updateQueueItemState(action, AI_ACTION_STATES.SUCCEEDED);
      fetchData(false);
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error?.response?.data?.detail || 'Gagal mengisolir pelanggan');
        setCustomerActionFeedback(customer.id, 'failed', 3400);
        updateQueueItemState(action, AI_ACTION_STATES.FAILED);
      }
    } finally {
      setIsolatingCustomerId('');
    }
  };

  const normalizedFieldTickets = useMemo(() => {
    return (Array.isArray(fieldTickets) ? fieldTickets : []).map((ticket) => {
      const lifecycle = normalizeTicketLifecycle(ticket?.status);
      const customerId = String(ticket?.customer_id || '').trim();
      const sourceType = normalizeIncidentSourceType(ticket?.source_type || (customerId ? 'customer' : 'topology_node'));
      const sourceId = String(ticket?.source_id || customerId || '').trim();
      const sourceKey = buildIncidentSourceKey(sourceType, sourceId);
      return {
        ...ticket,
        lifecycle,
        customerId,
        sourceType,
        sourceId,
        sourceKey,
        nasId: String(ticket?.nas_id || '').trim(),
        oltId: String(ticket?.olt_id || '').trim(),
        odpId: String(ticket?.odp_id || '').trim(),
        topologyNodeId: String(ticket?.topology_node_id || '').trim(),
        anomalyClusterId: String(ticket?.anomaly_cluster_id || '').trim(),
        id: String(ticket?.id || '').trim(),
        isActive: isTicketActiveLifecycle(ticket),
      };
    });
  }, [fieldTickets]);

  const activeTicketByCustomerId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      if (!ticket?.isActive || !ticket?.customerId) return;
      if (!mapped[ticket.customerId]) {
        mapped[ticket.customerId] = ticket;
      }
    });
    return mapped;
  }, [normalizedFieldTickets]);

  const activeTicketBySourceKey = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      if (!ticket?.isActive) return;
      const sourceKey = String(ticket?.sourceKey || '').trim();
      if (!sourceKey || mapped[sourceKey]) return;
      mapped[sourceKey] = ticket;
    });
    return mapped;
  }, [normalizedFieldTickets]);

  const handleCreateTicket = async (customer) => {
    if (!customer?.id) return;
    const customerId = String(customer.id || '').trim();
    const existingTicket = activeTicketByCustomerId[customerId];
    if (existingTicket?.id) {
      const proceed = window.confirm(
        `Issue ini sudah memiliki tiket aktif (${existingTicket.lifecycle?.label || existingTicket.status || 'Terbuka'}). Klik OK untuk buat tiket baru, atau Batal untuk buka tab tiket.`,
      );
      if (!proceed) {
        setRightPanelTab('tickets');
        return;
      }
    }
    setCreatingTicketCustomerId(customer.id);
    setCustomerActionFeedback(customer.id, 'processing', 3200);
    const action = {
      type: 'CREATE_TICKET',
      label: 'Buat Ticket',
      payload: { customer_id: customer.id },
    };
    updateQueueItemState(action, AI_ACTION_STATES.EXECUTING, { risk: Number(aiMonitoringResult?.risk || 0), confidence: 76 });
    try {
      await axios.post(
        `${API}/field-tickets`,
        {
          customer_id: customer.id,
          issue_notes: `Investigasi kualitas koneksi - ${customer.name}. Dibuat dari Monitoring Koneksi oleh admin.`,
        },
        { headers: getHeaders() },
      );
      toast.success(`Tiket penanganan dibuat untuk ${customer.name}`);
      setCustomerActionFeedback(customer.id, 'success', 2600);
      updateQueueItemState(action, AI_ACTION_STATES.SUCCEEDED);
      fetchFieldTickets();
      setRightPanelTab('tickets');
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error?.response?.data?.detail || 'Gagal membuat tiket penanganan');
        setCustomerActionFeedback(customer.id, 'failed', 3400);
        updateQueueItemState(action, AI_ACTION_STATES.ESCALATED);
      }
    } finally {
      setCreatingTicketCustomerId('');
    }
  };

  const handleCreateIncidentTicket = async (context, options = {}) => {
    const forceDuplicate = Boolean(options?.forceDuplicate);
    const linkedCustomer = context?.customer;
    const customerId = String(linkedCustomer?.id || '').trim();
    const sourceType = normalizeIncidentSourceType(context?.sourceType || (customerId ? 'customer' : 'topology_node'));
    const sourceId = String(context?.sourceId || customerId || '').trim();
    const sourceKey = buildIncidentSourceKey(sourceType, sourceId);
    if (!sourceKey) {
      toast.warning('Source incident belum lengkap. Pilih node/customer/infra yang ingin diticketkan.');
      return;
    }
    const existingTicket = context?.activeTicket
      || activeTicketBySourceKey[sourceKey]
      || (customerId ? activeTicketByCustomerId[customerId] : null);
    if (existingTicket?.id && !forceDuplicate) {
      const proceed = window.confirm(
        `Sudah ada tiket aktif untuk issue ini (${existingTicket.lifecycle?.label || existingTicket.status || 'Terbuka'}). Klik OK jika tetap ingin membuat tiket baru.`,
      );
      if (!proceed) {
        setRightPanelTab('tickets');
        return;
      }
    }
    setCreatingIncidentTicket(true);
    setCreatingTicketCustomerId(customerId);
    if (customerId) {
      setCustomerActionFeedback(customerId, 'processing', 3000);
    }
    try {
      const issueNotesParts = [
        context?.suggestedSummary || `Investigasi issue monitoring untuk ${context?.sourceLabel || linkedCustomer?.name || linkedCustomer?.customer_id || sourceId}.`,
        `Source: ${sourceType} (${context?.sourceLabel || sourceId}).`,
        `Impact: ${Number(context?.impactCount || 1)} pelanggan.`,
      ];
      if (context?.linkage?.nasName) issueNotesParts.push(`NAS: ${context.linkage.nasName}.`);
      if (context?.linkage?.odpName) issueNotesParts.push(`ODP: ${context.linkage.odpName}.`);
      if (context?.linkage?.oltName) issueNotesParts.push(`OLT: ${context.linkage.oltName}.`);
      const payload = {
        issue_notes: issueNotesParts.join(' '),
        source_type: sourceType,
        source_id: sourceId,
        topology_node_id: String(context?.topologyNodeId || sourceId || '').trim() || undefined,
        customer_id: customerId || undefined,
        nas_id: String(context?.nasId || context?.linkage?.routerId || '').trim() || undefined,
        olt_id: String(context?.oltId || context?.linkage?.oltId || '').trim() || undefined,
        odp_id: String(context?.odpId || context?.linkage?.odpId || '').trim() || undefined,
        anomaly_cluster_id: String(context?.anomalyClusterId || '').trim() || undefined,
        monitoring_status_snapshot: context?.monitoringStatusSnapshot || undefined,
        severity: String(context?.suggestedSeverity || '').trim() || undefined,
        created_from: 'monitoring',
      };
      await axios.post(
        `${API}/field-tickets`,
        payload,
        { headers: getHeaders() },
      );
      toast.success(`Ticket monitoring dibuat untuk ${context?.sourceLabel || linkedCustomer?.name || sourceId}`);
      if (customerId) {
        setCustomerActionFeedback(customerId, 'success', 2500);
      }
      setRightPanelTab('tickets');
      fetchFieldTickets();
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error?.response?.data?.detail || 'Gagal membuat ticket monitoring');
        if (customerId) {
          setCustomerActionFeedback(customerId, 'failed', 3200);
        }
      }
    } finally {
      setCreatingIncidentTicket(false);
      setCreatingTicketCustomerId('');
    }
  };

  const handleJoinIncidentTicket = async (ticket) => {
    const ticketId = String(ticket?.id || '').trim();
    if (!ticketId) return;
    if (isTicketLifecycleLocked(ticket)) {
      toast.info('Ticket sudah resolved/escalated, tidak bisa dipindah ke progress.');
      return;
    }
    setUpdatingTicketId(ticketId);
    try {
      await axios.post(`${API}/field-tickets/${ticketId}/join`, {}, { headers: getHeaders() });
      toast.success(`Ticket ${ticketId.slice(0, 8)} diambil (In Progress).`);
      fetchFieldTickets();
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error?.response?.data?.detail || 'Gagal update status ticket');
      }
    } finally {
      setUpdatingTicketId('');
    }
  };

  const handleCompleteIncidentTicket = async (ticket, escalated = false) => {
    const ticketId = String(ticket?.id || '').trim();
    if (!ticketId) return;
    const lifecycle = normalizeTicketLifecycle(ticket?.status);
    if (lifecycle.key === 'resolved') {
      toast.info('Ticket sudah resolved.');
      return;
    }
    if (lifecycle.key === 'escalated' && escalated) {
      toast.info('Ticket sudah berstatus escalated.');
      return;
    }
    if (lifecycle.key === 'escalated' && !escalated) {
      toast.info('Ticket escalated perlu ditangani dari halaman Penanganan.');
      return;
    }
    const confirmation = window.confirm(escalated
      ? 'Eskalasi ticket ini ke penanganan khusus?'
      : 'Tandai ticket ini sebagai resolved?');
    if (!confirmation) return;
    setUpdatingTicketId(ticketId);
    try {
      await axios.post(
        `${API}/field-tickets/${ticketId}/complete`,
        {
          completion_status: escalated ? 'needs_special_handling' : 'completed',
          completion_notes: escalated
            ? 'Eskalasi dari monitoring: butuh penanganan khusus.'
            : 'Resolved dari monitoring context.',
        },
        { headers: getHeaders() },
      );
      toast.success(escalated ? 'Ticket berhasil dieskalasi.' : 'Ticket ditandai resolved.');
      fetchFieldTickets();
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error?.response?.data?.detail || 'Gagal menyimpan status ticket');
      }
    } finally {
      setUpdatingTicketId('');
    }
  };

  const handleRunSelfHealing = async (customer) => {
    if (!customer?.id) return;
    setSelfHealingRunningCustomerId(customer.id);
    setCustomerActionFeedback(customer.id, 'processing', 4200);
    const action = {
      type: 'SELF_HEALING',
      label: 'Self-Heal',
      payload: { customer_id: customer.id },
    };
    updateQueueItemState(action, AI_ACTION_STATES.EXECUTING, { risk: Number(aiMonitoringResult?.risk || 0), confidence: 80, auto: true });
    try {
      const response = await axios.post(
        `${API}/self-healing/run`,
        { customer_id: customer.id, force: true },
        { headers: getHeaders() },
      );
      const item = Array.isArray(response?.data?.items) ? response.data.items[0] : null;
      const status = String(item?.status || '').toLowerCase();
      if (status === 'verified') {
        toast.success(`Self-healing berhasil diverifikasi untuk ${customer.name}`);
      } else if (status === 'partial') {
        toast.warning(`Self-healing selesai parsial untuk ${customer.name}`);
      } else if (status === 'no_action') {
        toast.message(`Self-healing: tidak ada aksi untuk ${customer.name}`);
      } else {
        toast.success(`Self-healing dijalankan untuk ${customer.name}`);
      }
      fetchData(false);
      fetchSelfHealingLogs();
      setCustomerActionFeedback(customer.id, 'success', 3200);
      updateQueueItemState(action, AI_ACTION_STATES.SUCCEEDED, { auto: true });
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error?.response?.data?.detail || 'Gagal menjalankan self-healing');
        setCustomerActionFeedback(customer.id, 'failed', 3600);
        updateQueueItemState(action, AI_ACTION_STATES.FAILED, { auto: true });
      }
    } finally {
      setSelfHealingRunningCustomerId('');
    }
  };

  const handleRunAutoOptimization = async (customer) => {
    if (!customer?.id) return;
    setOptimizingCustomerId(customer.id);
    setCustomerActionFeedback(customer.id, 'processing', 4200);
    const action = {
      type: 'AUTO_OPTIMIZATION',
      label: 'Auto Optimization',
      payload: { customer_id: customer.id },
    };
    updateQueueItemState(action, AI_ACTION_STATES.EXECUTING, { risk: Number(aiMonitoringResult?.risk || 0), confidence: 78, auto: true });
    try {
      const response = await axios.post(
        `${API}/auto-optimization/run`,
        { customer_id: customer.id, force: true },
        { headers: getHeaders() },
      );
      const item = Array.isArray(response?.data?.items) ? response.data.items[0] : null;
      const status = String(item?.status || '').toLowerCase();
      if (status === 'optimized') {
        toast.success(`Auto optimization berhasil untuk ${customer.name}`);
      } else if (status === 'partial') {
        toast.warning(`Auto optimization parsial untuk ${customer.name}`);
      } else if (status === 'no_action') {
        toast.message(`Auto optimization: tidak ada aksi untuk ${customer.name}`);
      } else {
        toast.success(`Auto optimization dijalankan untuk ${customer.name}`);
      }
      fetchData(false);
      fetchOptimizationLogs();
      setCustomerActionFeedback(customer.id, 'success', 3000);
      updateQueueItemState(action, AI_ACTION_STATES.SUCCEEDED, { auto: true });
    } catch (error) {
      if (error?.response?.status === 401) {
        handleAuthExpired();
      } else {
        toast.error(error?.response?.data?.detail || 'Gagal menjalankan auto optimization');
        setCustomerActionFeedback(customer.id, 'failed', 3600);
        updateQueueItemState(action, AI_ACTION_STATES.FAILED, { auto: true });
      }
    } finally {
      setOptimizingCustomerId('');
    }
  };

  const handleRetryNow = useCallback(() => {
    clearFetchRetry();
    fetchData(true);
  }, [clearFetchRetry, fetchData]);

  useEffect(() => {
    if (!filterAutoFitInitializedRef.current) {
      filterAutoFitInitializedRef.current = true;
      return;
    }
    hasUserAdjustedViewportRef.current = false;
    triggerAutoFit();
  }, [customerFilterMode, infraFilter, selectedNasId, selectedOltId, statusFilter, triggerAutoFit, viewMode]);



  const customersWithStatus = useMemo(() => {

    return customers.map((customer) => {

      const status = statusByCustomerId[customer.id] || null;

      return {

        ...customer,

        connection_status: status?.connection_status || 'unknown',

        status_detail: status,
        pppoe_connection_status: status?.pppoe_connection_status || 'unknown',
        pppoe_status_detail: status?.pppoe_status_detail || null,

      };

    });

  }, [customers, statusByCustomerId]);

  const optimizingCustomerIdSet = useMemo(() => {
    const set = new Set();
    const nowTs = Date.now();
    optimizationLogs.forEach((log) => {
      const customerId = String(log?.customer_id || '').trim();
      if (!customerId) return;
      const createdAt = Date.parse(log?.created_at || '');
      const ageMinutes = Number.isFinite(createdAt) ? ((nowTs - createdAt) / 60000) : 9999;
      if (ageMinutes <= 30 && ['optimized', 'partial', 'processing'].includes(String(log?.status || '').toLowerCase())) {
        set.add(customerId);
      }
    });
    if (optimizingCustomerId) {
      set.add(String(optimizingCustomerId));
    }
    return set;
  }, [optimizationLogs, optimizingCustomerId]);

  useEffect(() => {
    if (!Array.isArray(customersWithStatus) || customersWithStatus.length === 0) return;
    const nowIso = new Date().toISOString();
    setNodeHistoryById((previous) => {
      const next = { ...previous };
      customersWithStatus.forEach((customer) => {
        const id = String(customer?.id || '').trim();
        if (!id) return;
        const aiScore = computeAiScore(customer);
        const entry = {
          timestamp: nowIso,
          latency: Number(aiScore?.metrics?.latencySeconds ?? null),
          rxPower: Number(aiScore?.metrics?.rxPower ?? null),
          packetLoss: Number(aiScore?.metrics?.packetLoss ?? null),
          status: String(customer?.connection_status || 'unknown'),
          pppoe: String(customer?.pppoe_connection_status || 'unknown'),
          score: Number(aiScore?.score ?? 100),
          level: String(aiScore?.level || 'normal'),
        };
        const current = Array.isArray(next[id]) ? next[id] : [];
        const last = current[current.length - 1];
        const isDuplicate = last
          && last.status === entry.status
          && last.pppoe === entry.pppoe
          && last.score === entry.score
          && last.latency === entry.latency
          && last.rxPower === entry.rxPower
          && last.packetLoss === entry.packetLoss;
        if (isDuplicate) return;
        next[id] = [...current, entry].slice(-NODE_HISTORY_LIMIT);
      });
      return next;
    });
  }, [customersWithStatus]);

  useEffect(() => {
    if (!Array.isArray(customersWithStatus) || customersWithStatus.length === 0) return;
    const rows = customersWithStatus.map((customer) => computeAiScore(customer));
    const avgScore = Math.round(
      rows.reduce((acc, row) => acc + Number(row?.score || 0), 0) / Math.max(1, rows.length),
    );
    const validLatency = rows
      .map((row) => Number(row?.metrics?.latencySeconds))
      .filter((value) => Number.isFinite(value));
    const avgLatency = validLatency.length
      ? Math.round(validLatency.reduce((acc, value) => acc + value, 0) / validLatency.length)
      : 0;
    const anomalies = rows.filter((row) => Number(row?.score ?? 100) < ANOMALY_SCORE_THRESHOLD).length;
    const snapshot = {
      timestamp: Date.now(),
      avgScore,
      avgLatency,
      anomalies,
    };
    setNetworkHistory((previous) => {
      const list = Array.isArray(previous) ? previous : [];
      const last = list[list.length - 1];
      if (last && last.avgScore === snapshot.avgScore && last.avgLatency === snapshot.avgLatency && last.anomalies === snapshot.anomalies) {
        return list;
      }
      return [...list, snapshot].slice(-NETWORK_HISTORY_LIMIT);
    });
  }, [customersWithStatus]);

  const nasByRouterId = useMemo(() => {
    const mapped = {};
    nasRouters.forEach((item) => {
      const key = normalizeNodeId(item?.router_id || item?.id);
      if (!key) return;
      mapped[key] = item;
    });
    return mapped;
  }, [nasRouters]);

  const nasOptions = useMemo(() => {
    return Object.entries(nasByRouterId)
      .map(([routerId, item]) => ({
        router_id: routerId,
        name: String(item?.name || item?.nas_identifier || routerId),
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [nasByRouterId]);

  const nasFilterOptions = useMemo(() => {
    return nasOptions.map((item) => ({
      value: String(item.router_id),
      label: String(item.name),
      keywords: String(item.name),
    }));
  }, [nasOptions]);

  const oltsById = useMemo(() => {
    const mapped = {};
    olts.forEach((item) => {
      const id = normalizeNodeId(item?.id);
      if (id) mapped[id] = item;
    });
    return mapped;
  }, [olts]);



  const filteredOlts = useMemo(() => {
    return olts.filter((olt) => {
      if (infraFilter !== 'all' && normalizeInfraKriteria(olt.kriteria) !== infraFilter) return false;
      if (selectedNasId !== 'all' && normalizeNodeId(olt.router_id) !== normalizeNodeId(selectedNasId)) return false;
      if (selectedOltId !== 'all' && normalizeNodeId(olt.id) !== normalizeNodeId(selectedOltId)) return false;
      return true;
    });
  }, [infraFilter, olts, selectedNasId, selectedOltId]);

  const oltFilterOptions = useMemo(() => {
    return filteredOlts.map((item) => ({
      value: String(item.id),
      label: item.code ? `${item.code} - ${item.name}` : String(item.name || item.id || ''),
      description: String(item.location || ''),
      keywords: `${item.code || ''} ${item.name || ''} ${item.location || ''}`.trim(),
    }));
  }, [filteredOlts]);



  const filteredOdps = useMemo(() => {
    return odps.filter((odp) => {
      if (infraFilter !== 'all' && normalizeInfraKriteria(odp.kriteria) !== infraFilter) return false;
      const linkedOlt = oltsById[normalizeNodeId(odp.olt_id)];
      const linkedRouterId = normalizeNodeId(odp.router_id || linkedOlt?.router_id);
      if (selectedNasId !== 'all' && linkedRouterId !== normalizeNodeId(selectedNasId)) return false;
      if (selectedOltId !== 'all' && normalizeNodeId(odp.olt_id) !== normalizeNodeId(selectedOltId)) return false;
      return true;
    });
  }, [infraFilter, odps, oltsById, selectedNasId, selectedOltId]);

  const scopedNasRouters = useMemo(() => {
    return nasRouters.filter((router) => {
      const routerId = normalizeNodeId(router?.router_id || router?.id);
      if (!routerId) return false;
      if (selectedNasId !== 'all' && routerId !== normalizeNodeId(selectedNasId)) return false;
      if (selectedOltId !== 'all') {
        const hasLinkedOlt = filteredOlts.some((olt) => normalizeNodeId(olt?.router_id) === routerId);
        if (!hasLinkedOlt) return false;
      }
      return true;
    });
  }, [filteredOlts, nasRouters, selectedNasId, selectedOltId]);



  const filteredOltsById = useMemo(() => {

    const mapped = {};

    filteredOlts.forEach((olt) => {

      const id = normalizeNodeId(olt?.id);

      if (id) mapped[id] = olt;

    });

    return mapped;

  }, [filteredOlts]);



  const filteredOdpsById = useMemo(() => {

    const mapped = {};

    filteredOdps.forEach((odp) => {

      const id = normalizeNodeId(odp?.id);

      if (id) mapped[id] = odp;

    });

    return mapped;

  }, [filteredOdps]);



  const allOdpsById = useMemo(() => {

    const mapped = {};

    odps.forEach((odp) => {

      const id = normalizeNodeId(odp?.id);

      if (id) mapped[id] = odp;

    });

    return mapped;

  }, [odps]);



  const allOdpLookup = useMemo(() => {

    const mapped = {};

    odps.forEach((odp) => {

      [odp?.id, odp?.code, odp?.name].forEach((value) => {

        const key = String(value || '').trim().toLowerCase();

        const odpId = normalizeNodeId(odp?.id);

        if (key && odpId) mapped[key] = odpId;

      });

    });

    return mapped;

  }, [odps]);


  const filteredOdpLookup = useMemo(() => {

    const mapped = {};

    filteredOdps.forEach((odp) => {

      [odp?.id, odp?.code, odp?.name].forEach((value) => {

        const key = String(value || '').trim().toLowerCase();

        const odpId = normalizeNodeId(odp?.id);

        if (key && odpId) mapped[key] = odpId;

      });

    });

    return mapped;

  }, [filteredOdps]);



  const resolveFilteredOdp = useCallback((customer) => {

    const directOdpId = normalizeNodeId(customer?.linked_odp_id || customer?.odp_id);

    if (directOdpId && filteredOdpsById[directOdpId]) {

      return filteredOdpsById[directOdpId];

    }



    const odpKey = String(customer?.odp || '').trim().toLowerCase();

    const lookupOdpId = odpKey ? filteredOdpLookup[odpKey] : null;

    if (!lookupOdpId) return null;



    return filteredOdpsById[lookupOdpId] || null;

  }, [filteredOdpLookup, filteredOdpsById]);



  const resolveCustomerOltId = useCallback((customer) => {

    const directOltId = normalizeNodeId(customer?.linked_olt_id || customer?.olt_id);

    if (directOltId) return directOltId;



    const directOdpId = normalizeNodeId(customer?.linked_odp_id || customer?.odp_id);

    if (directOdpId && allOdpsById[directOdpId]?.olt_id) {

      return normalizeNodeId(allOdpsById[directOdpId].olt_id);

    }



    const odpKey = String(customer?.odp || '').trim().toLowerCase();

    const lookupOdpId = odpKey ? allOdpLookup[odpKey] : null;

    if (lookupOdpId && allOdpsById[lookupOdpId]?.olt_id) {

      return normalizeNodeId(allOdpsById[lookupOdpId].olt_id);

    }



    return '';

  }, [allOdpLookup, allOdpsById]);

  const resolveCustomerRouterId = useCallback((customer) => {
    const directRouterId = normalizeNodeId(customer?.linked_router_id || customer?.router_id);
    if (directRouterId) return directRouterId;

    const directOdpId = normalizeNodeId(customer?.linked_odp_id || customer?.odp_id);
    if (directOdpId && allOdpsById[directOdpId]) {
      const odp = allOdpsById[directOdpId];
      const linkedOlt = oltsById[normalizeNodeId(odp?.olt_id)];
      const inferred = normalizeNodeId(odp?.router_id || linkedOlt?.router_id);
      if (inferred) return inferred;
    }

    const directOltId = resolveCustomerOltId(customer);
    if (directOltId && oltsById[directOltId]) {
      return normalizeNodeId(oltsById[directOltId].router_id);
    }

    return '';
  }, [allOdpsById, oltsById, resolveCustomerOltId]);

  const resolveCustomerLinkageMeta = useCallback((customer) => {
    const routerId = resolveCustomerRouterId(customer);
    const oltId = resolveCustomerOltId(customer);
    const odp = resolveFilteredOdp(customer);
    const odpId = normalizeNodeId(odp?.id || customer?.linked_odp_id || customer?.odp_id);
    const topologyNodeId = String(customer?.topology_node_id || '').trim();
    const hasMonitoringPoint = Boolean(topologyNodeId) || hasValidCoordinates(customer?.latitude, customer?.longitude);

    const hasNas = Boolean(routerId);
    const hasOlt = Boolean(oltId);
    const hasOdp = Boolean(odpId || String(customer?.odp || '').trim());
    const infraSignals = [hasNas, hasOlt, hasOdp].filter(Boolean).length;

    let mappingStatus = 'unmapped';
    let mappingLabel = 'Belum Mapping';
    let mappingTone = semanticToneToBadgeClass('neutral');
    let helper = 'Belum ada relasi NAS/OLT/ODP ke topology.';

    if (hasNas && hasOlt && hasOdp && hasMonitoringPoint) {
      mappingStatus = 'mapped';
      mappingLabel = 'Mapped Lengkap';
      mappingTone = semanticToneToBadgeClass('ok');
      helper = 'Relasi pelanggan sudah lengkap hingga node monitoring.';
    } else if (infraSignals > 0 || hasMonitoringPoint) {
      mappingStatus = 'partial';
      mappingLabel = 'Mapped Parsial';
      mappingTone = semanticToneToBadgeClass('warn');
      helper = hasOdp ? 'Sebagian relasi sudah ada, cek kelengkapan NAS/OLT/topology.' : 'Relasi infrastruktur belum lengkap.';
    }

    return {
      mappingStatus,
      mappingLabel,
      mappingTone,
      helper,
      routerId,
      oltId,
      odp,
      odpId,
      topologyNodeId,
      hasMonitoringPoint,
      nasName: nasByRouterId[routerId]?.name || '-',
      siteName: nasByRouterId[routerId]?.site_name || '-',
      oltName: oltsById[oltId]?.name || '-',
      odpName: String(odp?.code || odp?.name || customer?.odp || '-').trim() || '-',
    };
  }, [nasByRouterId, oltsById, resolveCustomerOltId, resolveCustomerRouterId, resolveFilteredOdp]);

  const openCustomerRecord = useCallback((customer, mode = 'view') => {
    const customerId = String(customer?.id || customer?.customer_id || '').trim();
    if (!customerId) {
      toast.error('ID pelanggan tidak ditemukan.');
      return;
    }
    const statePayload = mode === 'edit'
      ? { editCustomerId: customerId, source: 'monitoring' }
      : { viewCustomerId: customerId, source: 'monitoring' };
    navigate('/dadu/pelanggan', { state: statePayload });
  }, [navigate]);



  const filteredCustomers = useMemo(() => {

    return customersWithStatus.filter((customer) => {

      if (statusFilter !== 'all' && customer.connection_status !== statusFilter) return false;

      if (infraFilter !== 'all' && !resolveFilteredOdp(customer)) return false;
      if (selectedNasId !== 'all' && resolveCustomerRouterId(customer) !== normalizeNodeId(selectedNasId)) return false;
      if (selectedOltId !== 'all' && resolveCustomerOltId(customer) !== normalizeNodeId(selectedOltId)) return false;



      const searchValue = searchQuery.trim();
      if (!searchValue) return true;
      if (searchValue.length < 3) return false;

      const key = searchValue.toLowerCase();

      return (

        String(customer.name || '').toLowerCase().includes(key) ||

        String(customer.customer_id || '').toLowerCase().includes(key) ||

        String(customer.phone || '').toLowerCase().includes(key) ||

        String(customer.odp || '').toLowerCase().includes(key)

      );

    });

  }, [customersWithStatus, statusFilter, infraFilter, selectedNasId, selectedOltId, searchQuery, resolveCustomerRouterId, resolveFilteredOdp, resolveCustomerOltId]);

  const filteredCustomersByMode = useMemo(() => {
    if (customerFilterMode === 'all') return filteredCustomers;
    if (customerFilterMode === 'online') {
      return filteredCustomers.filter((customer) => customer.connection_status === 'online');
    }
    if (customerFilterMode === 'offline') {
      return filteredCustomers.filter((customer) => customer.connection_status === 'offline');
    }
    if (customerFilterMode === 'anomaly') {
      return filteredCustomers.filter((customer) => detectAnomaly(customer).hasAnomaly);
    }
    return filteredCustomers;
  }, [customerFilterMode, filteredCustomers]);

  const customerOdpIdByCustomerId = useMemo(() => {
    const mapped = {};
    customersWithStatus.forEach((customer) => {
      const customerId = String(customer?.id || '').trim();
      if (!customerId) return;
      const odpId = normalizeNodeId(resolveFilteredOdp(customer)?.id || customer?.linked_odp_id || customer?.odp_id);
      if (odpId) mapped[customerId] = odpId;
    });
    return mapped;
  }, [customersWithStatus, resolveFilteredOdp]);

  const customerOltIdByCustomerId = useMemo(() => {
    const mapped = {};
    customersWithStatus.forEach((customer) => {
      const customerId = String(customer?.id || '').trim();
      if (!customerId) return;
      const oltId = normalizeNodeId(resolveCustomerOltId(customer));
      if (oltId) mapped[customerId] = oltId;
    });
    return mapped;
  }, [customersWithStatus, resolveCustomerOltId]);

  const customerRouterIdByCustomerId = useMemo(() => {
    const mapped = {};
    customersWithStatus.forEach((customer) => {
      const customerId = String(customer?.id || '').trim();
      if (!customerId) return;
      const routerId = normalizeNodeId(resolveCustomerRouterId(customer));
      if (routerId) mapped[customerId] = routerId;
    });
    return mapped;
  }, [customersWithStatus, resolveCustomerRouterId]);

  const activeTicketByOdpId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      if (!ticket?.isActive) return;
      const directOdpId = normalizeNodeId(ticket?.odpId);
      const customerId = String(ticket?.customerId || '').trim();
      const odpId = directOdpId || customerOdpIdByCustomerId[customerId] || '';
      if (!odpId || mapped[odpId]) return;
      mapped[odpId] = ticket;
    });
    return mapped;
  }, [customerOdpIdByCustomerId, normalizedFieldTickets]);

  const activeTicketByOltId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      if (!ticket?.isActive) return;
      const directOltId = normalizeNodeId(ticket?.oltId);
      const customerId = String(ticket?.customerId || '').trim();
      const oltId = directOltId || customerOltIdByCustomerId[customerId] || '';
      if (!oltId || mapped[oltId]) return;
      mapped[oltId] = ticket;
    });
    return mapped;
  }, [customerOltIdByCustomerId, normalizedFieldTickets]);

  const activeTicketByNasId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      if (!ticket?.isActive) return;
      const directNasId = normalizeNodeId(ticket?.nasId);
      const customerId = String(ticket?.customerId || '').trim();
      const inferredNasId = customerRouterIdByCustomerId[customerId] || '';
      const nasId = directNasId || inferredNasId;
      if (!nasId || mapped[nasId]) return;
      mapped[nasId] = ticket;
    });
    return mapped;
  }, [customerRouterIdByCustomerId, normalizedFieldTickets]);

  const ticketsBySourceKey = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      const key = String(ticket?.sourceKey || '').trim();
      if (!key) return;
      if (!Array.isArray(mapped[key])) mapped[key] = [];
      mapped[key].push(ticket);
    });
    return mapped;
  }, [normalizedFieldTickets]);

  const ticketsByCustomerId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      const customerId = String(ticket?.customerId || '').trim();
      if (!customerId) return;
      if (!Array.isArray(mapped[customerId])) mapped[customerId] = [];
      mapped[customerId].push(ticket);
    });
    return mapped;
  }, [normalizedFieldTickets]);

  const ticketsByTopologyNodeId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      const topologyNodeId = String(ticket?.topologyNodeId || '').trim();
      if (!topologyNodeId) return;
      if (!Array.isArray(mapped[topologyNodeId])) mapped[topologyNodeId] = [];
      mapped[topologyNodeId].push(ticket);
    });
    return mapped;
  }, [normalizedFieldTickets]);

  const ticketsByOdpId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      const customerId = String(ticket?.customerId || '').trim();
      const directOdpId = normalizeNodeId(ticket?.odpId);
      const inferredOdpId = customerOdpIdByCustomerId[customerId] || '';
      const odpId = directOdpId || inferredOdpId;
      if (!odpId) return;
      if (!Array.isArray(mapped[odpId])) mapped[odpId] = [];
      mapped[odpId].push(ticket);
    });
    return mapped;
  }, [customerOdpIdByCustomerId, normalizedFieldTickets]);

  const ticketsByOltId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      const customerId = String(ticket?.customerId || '').trim();
      const directOltId = normalizeNodeId(ticket?.oltId);
      const inferredOltId = customerOltIdByCustomerId[customerId] || '';
      const oltId = directOltId || inferredOltId;
      if (!oltId) return;
      if (!Array.isArray(mapped[oltId])) mapped[oltId] = [];
      mapped[oltId].push(ticket);
    });
    return mapped;
  }, [customerOltIdByCustomerId, normalizedFieldTickets]);

  const ticketsByNasId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      const customerId = String(ticket?.customerId || '').trim();
      const directNasId = normalizeNodeId(ticket?.nasId);
      const inferredNasId = customerRouterIdByCustomerId[customerId] || '';
      const nasId = directNasId || inferredNasId;
      if (!nasId) return;
      if (!Array.isArray(mapped[nasId])) mapped[nasId] = [];
      mapped[nasId].push(ticket);
    });
    return mapped;
  }, [customerRouterIdByCustomerId, normalizedFieldTickets]);

  const activeTicketByTopologyNodeId = useMemo(() => {
    const mapped = {};
    normalizedFieldTickets.forEach((ticket) => {
      if (!ticket?.isActive) return;
      const topologyNodeId = String(ticket?.topologyNodeId || '').trim();
      if (!topologyNodeId || mapped[topologyNodeId]) return;
      mapped[topologyNodeId] = ticket;
    });
    return mapped;
  }, [normalizedFieldTickets]);



  useEffect(() => {
    if (selectedOltId === 'all') return;
    const exists = filteredOlts.some((olt) => normalizeNodeId(olt.id) === normalizeNodeId(selectedOltId));
    if (!exists) setSelectedOltId('all');
  }, [filteredOlts, selectedOltId]);

  useEffect(() => {
    if (selectedNasId === 'all') return;
    if (!nasByRouterId[normalizeNodeId(selectedNasId)]) {
      setSelectedNasId('all');
    }
  }, [nasByRouterId, selectedNasId]);

  const overlapNodePositionLookup = useMemo(() => {
    const infraNodes = [
      ...filteredOlts
        .filter((olt) => hasValidCoordinates(olt.latitude, olt.longitude))
        .map((olt) => ({ id: `olt:${olt.id}`, latitude: Number(olt.latitude), longitude: Number(olt.longitude) })),
      ...filteredOdps
        .filter((odp) => hasValidCoordinates(odp.latitude, odp.longitude))
        .map((odp) => ({ id: `odp:${odp.id}`, latitude: Number(odp.latitude), longitude: Number(odp.longitude) })),
    ];
    return buildOverlapPositionLookup(infraNodes);
  }, [filteredOlts, filteredOdps]);

  const getInfraDisplayPosition = useCallback((type, node) => {
    const id = String(node?.id || '').trim();
    if (!id || !hasValidCoordinates(node?.latitude, node?.longitude)) return null;
    const key = `${type}:${id}`;
    return overlapNodePositionLookup[key] || [Number(node.latitude), Number(node.longitude)];
  }, [overlapNodePositionLookup]);

  const selectedCustomer = useMemo(() => {

    if (!selectedCustomerId) return null;

    return filteredCustomersByMode.find((customer) => customer.id === selectedCustomerId) || null;

  }, [filteredCustomersByMode, selectedCustomerId]);

  useEffect(() => {
    if (!pendingCustomerFocus || pendingCustomerFocusResolvedRef.current) return;
    if (!Array.isArray(customersWithStatus) || customersWithStatus.length === 0) return;

    const focusId = String(pendingCustomerFocus.customerId || '').trim();
    const focusNumber = String(pendingCustomerFocus.customerNumber || '').trim().toLowerCase();
    const focusPppoe = String(pendingCustomerFocus.pppoeUsername || '').trim().toLowerCase();

    const targetCustomer = customersWithStatus.find((customer) => {
      const idMatch = focusId && String(customer?.id || '').trim() === focusId;
      const numberMatch = focusNumber && String(customer?.customer_id || '').trim().toLowerCase() === focusNumber;
      const pppoeMatch = focusPppoe && String(customer?.pppoe_username || '').trim().toLowerCase() === focusPppoe;
      return idMatch || numberMatch || pppoeMatch;
    });

    pendingCustomerFocusResolvedRef.current = true;
    if (!targetCustomer) {
      toast.warning('Pelanggan dari halaman Data Pelanggan tidak ditemukan di scope monitoring saat ini.');
      setPendingCustomerFocus(null);
      return;
    }

    const customerId = String(targetCustomer?.id || '').trim();
    if (!customerId) {
      setPendingCustomerFocus(null);
      return;
    }

    const linkage = resolveCustomerLinkageMeta(targetCustomer);
    if (linkage.routerId) setSelectedNasId(linkage.routerId);
    if (linkage.oltId) setSelectedOltId(linkage.oltId);
    setStatusFilter('all');
    setInfraFilter('all');
    setCustomerFilterMode('all');
    setSearchQuery('');
    setSelectedCustomerId(customerId);
    handleFocusNode('customer', customerId);
    triggerFocusPulse(customerId);
    setAutoFitEnabled(false);
    setRightPanelTab('customers');

    if (linkage.mappingStatus === 'unmapped') {
      toast.warning('Pelanggan belum termapping ke topology. Menampilkan detail pelanggan tanpa fokus node penuh.');
    } else if (linkage.mappingStatus === 'partial') {
      toast.info('Pelanggan termapping parsial. Fokus network mengikuti data yang tersedia.');
    } else {
      toast.success('Monitoring difokuskan ke pelanggan terpilih.');
    }

    setPendingCustomerFocus(null);
  }, [
    customersWithStatus,
    handleFocusNode,
    pendingCustomerFocus,
    resolveCustomerLinkageMeta,
    triggerFocusPulse,
  ]);

  useEffect(() => {
    if (!pendingSourceFocus || pendingSourceFocusResolvedRef.current) return;
    const sourceType = normalizeIncidentSourceType(pendingSourceFocus?.sourceType);
    const sourceId = String(pendingSourceFocus?.sourceId || '').trim();
    if (!sourceType || !sourceId) return;

    const sourceKey = buildIncidentSourceKey(sourceType, sourceId);
    setIncidentSourceOverride({ sourceType, sourceId, sourceKey });
    setRightPanelTab('tickets');

    if (sourceType === 'nas') {
      setSelectedNasId(sourceId);
      setAutoFitEnabled(false);
      pendingSourceFocusResolvedRef.current = true;
      setPendingSourceFocus(null);
      return;
    }

    if (sourceType === 'odp') {
      const linkedOdp = filteredOdps.find((item) => normalizeNodeId(item?.id) === normalizeNodeId(sourceId)) || null;
      if (linkedOdp?.router_id) setSelectedNasId(String(linkedOdp.router_id));
      if (linkedOdp?.olt_id) setSelectedOltId(String(linkedOdp.olt_id));
      handleFocusNode('odp', sourceId);
      triggerAutoFit();
      pendingSourceFocusResolvedRef.current = true;
      setPendingSourceFocus(null);
      return;
    }

    if (sourceType === 'olt') {
      setSelectedOltId(sourceId);
      handleFocusNode('olt', sourceId);
      triggerAutoFit();
      pendingSourceFocusResolvedRef.current = true;
      setPendingSourceFocus(null);
      return;
    }

    if (sourceType === 'customer') {
      setSelectedCustomerId(sourceId);
      handleFocusNode('customer', sourceId);
      triggerFocusPulse(sourceId);
      setAutoFitEnabled(false);
      pendingSourceFocusResolvedRef.current = true;
      setPendingSourceFocus(null);
      return;
    }

    pendingSourceFocusResolvedRef.current = true;
    setPendingSourceFocus(null);
  }, [
    filteredOdps,
    handleFocusNode,
    pendingSourceFocus,
    triggerAutoFit,
    triggerFocusPulse,
  ]);

  const visibleCustomerPoints = useMemo(() => {

    return filteredCustomersByMode.filter((customer) => hasValidCoordinates(customer.latitude, customer.longitude));

  }, [filteredCustomersByMode]);

  const customerCountByOdpId = useMemo(() => {
    const mapped = {};
    filteredCustomersByMode.forEach((customer) => {
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      mapped[odpId] = (mapped[odpId] || 0) + 1;
    });
    return mapped;
  }, [filteredCustomersByMode, resolveFilteredOdp]);

  const offlineCountByOdpId = useMemo(() => {
    const mapped = {};
    filteredCustomersByMode.forEach((customer) => {
      if (String(customer?.connection_status || '').toLowerCase() !== 'offline') return;
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      mapped[odpId] = (mapped[odpId] || 0) + 1;
    });
    return mapped;
  }, [filteredCustomersByMode, resolveFilteredOdp]);

  const odpRuntimeStatusById = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((odp) => {
      const odpId = normalizeNodeId(odp?.id);
      if (odpId) mapped[odpId] = 'unknown';
    });
    filteredCustomersByMode.forEach((customer) => {
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      const state = String(customer.connection_status || '').toLowerCase();
      if (state === 'online') {
        mapped[odpId] = 'online';
      } else if (mapped[odpId] !== 'online') {
        mapped[odpId] = state === 'offline' ? 'offline' : 'unknown';
      }
    });
    return mapped;
  }, [filteredCustomersByMode, filteredOdps, resolveFilteredOdp]);

  const oltCustomerCountById = useMemo(() => {
    const mapped = {};
    filteredOdps.forEach((odp) => {
      const oltId = normalizeNodeId(odp?.olt_id);
      const odpId = normalizeNodeId(odp?.id);
      if (!oltId || !odpId) return;
      mapped[oltId] = (mapped[oltId] || 0) + Number(customerCountByOdpId[odpId] || 0);
    });
    return mapped;
  }, [customerCountByOdpId, filteredOdps]);

  const oltRuntimeStatusById = useMemo(() => {
    const mapped = {};
    filteredOlts.forEach((olt) => {
      const oltId = normalizeNodeId(olt?.id);
      if (oltId) mapped[oltId] = 'unknown';
    });
    filteredOdps.forEach((odp) => {
      const oltId = normalizeNodeId(odp?.olt_id);
      const odpId = normalizeNodeId(odp?.id);
      if (!oltId || !odpId) return;
      const odpStatus = String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase();
      if (odpStatus === 'online') {
        mapped[oltId] = 'online';
      } else if (mapped[oltId] !== 'online' && odpStatus === 'offline') {
        mapped[oltId] = 'offline';
      }
    });
    return mapped;
  }, [filteredOdps, filteredOlts, odpRuntimeStatusById]);

  const focusedInfraEntity = useMemo(() => {
    if (!focusNode?.id) return null;
    if (focusNode.type === 'odp') {
      const odp = filteredOdps.find((item) => normalizeNodeId(item?.id) === normalizeNodeId(focusNode.id));
      if (!odp) return null;
      const odpId = normalizeNodeId(odp.id);
      return {
        type: 'odp',
        data: odp,
        status: String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase(),
        pelangganCount: Number(customerCountByOdpId[odpId] || 0),
      };
    }
    if (focusNode.type === 'olt') {
      const olt = filteredOlts.find((item) => normalizeNodeId(item?.id) === normalizeNodeId(focusNode.id));
      if (!olt) return null;
      const oltId = normalizeNodeId(olt.id);
      return {
        type: 'olt',
        data: olt,
        status: String(oltRuntimeStatusById[oltId] || 'unknown').toLowerCase(),
        pelangganCount: Number(oltCustomerCountById[oltId] || 0),
      };
    }
    return null;
  }, [
    customerCountByOdpId,
    filteredOdps,
    filteredOlts,
    focusNode,
    oltCustomerCountById,
    oltRuntimeStatusById,
    odpRuntimeStatusById,
  ]);

  const showCustomerClusters = useMemo(() => {
    return (viewMode === 'all' || viewMode === 'onu') && mapZoom <= 12 && !focusNode?.id;
  }, [focusNode?.id, mapZoom, viewMode]);

  const customerClusters = useMemo(() => {
    if (!showCustomerClusters) return [];
    const gridFactor = mapZoom <= 10 ? 0.028 : 0.018;
    const clusters = new Map();

    visibleCustomerPoints.forEach((customer) => {
      const lat = Number(customer.latitude);
      const lon = Number(customer.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      const key = `${Math.round(lat / gridFactor)}:${Math.round(lon / gridFactor)}`;
      if (!clusters.has(key)) {
        clusters.set(key, {
          id: key,
          latitude: 0,
          longitude: 0,
          count: 0,
          online: 0,
          offline: 0,
          anomaly: 0,
          items: [],
        });
      }
      const item = clusters.get(key);
      item.latitude += lat;
      item.longitude += lon;
      item.count += 1;
      if (customer.connection_status === 'online') item.online += 1;
      if (customer.connection_status === 'offline') item.offline += 1;
      if (detectAnomaly(customer).hasAnomaly) item.anomaly += 1;
      if (item.items.length < 6) item.items.push(customer);
    });

    return Array.from(clusters.values()).map((cluster) => ({
      ...cluster,
      latitude: cluster.latitude / cluster.count,
      longitude: cluster.longitude / cluster.count,
      dominantStatus: cluster.offline > 0 ? 'offline' : (cluster.online > 0 ? 'online' : 'unknown'),
    }));
  }, [showCustomerClusters, visibleCustomerPoints, mapZoom]);

  const recentIncidentSources = useMemo(() => {
    const seen = new Set();
    const items = [];
    (Array.isArray(normalizedFieldTickets) ? normalizedFieldTickets : []).forEach((ticket) => {
      const sourceType = normalizeIncidentSourceType(ticket?.sourceType || ticket?.source_type);
      const sourceId = String(ticket?.sourceId || ticket?.source_id || '').trim();
      const sourceKey = buildIncidentSourceKey(sourceType, sourceId);
      if (!sourceKey || seen.has(sourceKey)) return;
      seen.add(sourceKey);
      items.push({
        sourceType,
        sourceId,
        sourceKey,
        ticketId: String(ticket?.id || '').trim(),
        label: String(ticket?.customer_name || ticket?.customer_code || ticket?.customerId || ticket?.sourceLabel || sourceId || '-').trim(),
        updatedAt: parseTimestampMs(ticket?.updated_at) || parseTimestampMs(ticket?.created_at) || 0,
      });
    });
    return items
      .sort((a, b) => b.updatedAt - a.updatedAt)
      .slice(0, 12);
  }, [normalizedFieldTickets]);

  const buildIncidentContextFromSource = useCallback((sourceTypeRaw, sourceIdRaw) => {
    const sourceType = normalizeIncidentSourceType(sourceTypeRaw);
    const sourceId = String(sourceIdRaw || '').trim();
    if (!sourceId) return null;
    const sourceKey = buildIncidentSourceKey(sourceType, sourceId);
    const activeTicket = activeTicketBySourceKey[sourceKey] || null;

    if (sourceType === 'customer') {
      const customer = filteredCustomers.find((item) => String(item?.id || '').trim() === sourceId) || null;
      if (!customer) {
        return {
          sourceType,
          sourceId,
          sourceKey,
          sourceLabel: `CUSTOMER ${sourceId}`,
          customer: null,
          linkage: null,
          activeTicket,
          suggestedSeverity: 'normal',
          suggestedSummary: `Context customer ${sourceId} dipilih dari ticket, namun belum terlihat pada scope filter saat ini.`,
          impactCount: 0,
          topologyNodeId: sourceId,
        };
      }
      const linkage = resolveCustomerLinkageMeta(customer);
      return {
        sourceType,
        sourceId,
        sourceKey,
        sourceLabel: customer.name || customer.customer_id || `CUSTOMER ${sourceId}`,
        customer,
        linkage,
        activeTicket,
        suggestedSeverity: String(customer?.connection_status || '').toLowerCase() === 'offline' ? 'high' : 'normal',
        suggestedSummary: `Customer node ${customer.name || customer.customer_id || sourceId} ${getStatusLabel(customer.connection_status).toLowerCase()} di ${linkage.odpName || 'ODP tidak diketahui'}.`,
        impactCount: 1,
        topologyNodeId: sourceId,
        nasId: linkage?.routerId || '',
        oltId: linkage?.oltId || '',
        odpId: linkage?.odpId || '',
      };
    }

    if (sourceType === 'odp') {
      const impactedCustomers = filteredCustomers.filter((item) => normalizeNodeId(resolveFilteredOdp(item)?.id) === normalizeNodeId(sourceId));
      const primaryCustomer = impactedCustomers[0] || null;
      const primaryLinkage = primaryCustomer ? resolveCustomerLinkageMeta(primaryCustomer) : null;
      const odp = filteredOdps.find((item) => normalizeNodeId(item?.id) === normalizeNodeId(sourceId)) || null;
      return {
        sourceType,
        sourceId,
        sourceKey,
        sourceLabel: odp?.name || odp?.code || `ODP ${sourceId}`,
        customer: primaryCustomer,
        linkage: primaryLinkage,
        activeTicket: activeTicket || activeTicketByOdpId[normalizeNodeId(sourceId)] || null,
        suggestedSeverity: 'high',
        suggestedSummary: `ODP ${odp?.name || odp?.code || sourceId} dipilih dari context ticket dengan impact ${impactedCustomers.length} pelanggan.`,
        impactCount: impactedCustomers.length,
        topologyNodeId: normalizeNodeId(sourceId),
        nasId: primaryLinkage?.routerId || '',
        oltId: normalizeNodeId(odp?.olt_id) || primaryLinkage?.oltId || '',
        odpId: normalizeNodeId(sourceId),
      };
    }

    if (sourceType === 'olt') {
      const impactedCustomers = filteredCustomers.filter((item) => normalizeNodeId(resolveCustomerOltId(item)) === normalizeNodeId(sourceId));
      const primaryCustomer = impactedCustomers[0] || null;
      const primaryLinkage = primaryCustomer ? resolveCustomerLinkageMeta(primaryCustomer) : null;
      const olt = filteredOlts.find((item) => normalizeNodeId(item?.id) === normalizeNodeId(sourceId)) || null;
      return {
        sourceType,
        sourceId,
        sourceKey,
        sourceLabel: olt?.name || olt?.code || `OLT ${sourceId}`,
        customer: primaryCustomer,
        linkage: primaryLinkage,
        activeTicket: activeTicket || activeTicketByOltId[normalizeNodeId(sourceId)] || null,
        suggestedSeverity: 'high',
        suggestedSummary: `OLT ${olt?.name || olt?.code || sourceId} dipilih dari context ticket dengan impact ${impactedCustomers.length} pelanggan.`,
        impactCount: impactedCustomers.length,
        topologyNodeId: normalizeNodeId(sourceId),
        nasId: primaryLinkage?.routerId || '',
        oltId: normalizeNodeId(sourceId),
        odpId: primaryLinkage?.odpId || '',
      };
    }

    if (sourceType === 'nas') {
      const impactedCustomers = filteredCustomers.filter((item) => normalizeNodeId(resolveCustomerRouterId(item)) === normalizeNodeId(sourceId));
      const primaryCustomer = impactedCustomers[0] || null;
      const primaryLinkage = primaryCustomer ? resolveCustomerLinkageMeta(primaryCustomer) : null;
      const nasLabel = String(nasByRouterId[normalizeNodeId(sourceId)]?.name || `NAS ${sourceId}`).trim();
      return {
        sourceType,
        sourceId,
        sourceKey,
        sourceLabel: nasLabel,
        customer: primaryCustomer,
        linkage: primaryLinkage,
        activeTicket: activeTicket || activeTicketByNasId[normalizeNodeId(sourceId)] || null,
        suggestedSeverity: impactedCustomers.some((item) => String(item?.connection_status || '').toLowerCase() === 'offline') ? 'high' : 'normal',
        suggestedSummary: `${nasLabel} dipilih dari context ticket. Dampak terpantau ${impactedCustomers.length} pelanggan.`,
        impactCount: impactedCustomers.length,
        topologyNodeId: normalizeNodeId(sourceId),
        nasId: normalizeNodeId(sourceId),
        oltId: primaryLinkage?.oltId || '',
        odpId: primaryLinkage?.odpId || '',
      };
    }

    if (sourceType === 'anomaly_cluster' || sourceType === 'cluster') {
      const cluster = (Array.isArray(customerClusters) ? customerClusters : []).find((item) => String(item?.id || '').trim() === sourceId) || null;
      const impactedItems = Array.isArray(cluster?.items) ? cluster.items : [];
      const primaryCustomerId = String(impactedItems[0]?.id || '').trim();
      const primaryCustomer = primaryCustomerId
        ? (filteredCustomers.find((item) => String(item?.id || '').trim() === primaryCustomerId) || null)
        : null;
      const primaryLinkage = primaryCustomer ? resolveCustomerLinkageMeta(primaryCustomer) : null;
      return {
        sourceType: 'anomaly_cluster',
        sourceId,
        sourceKey,
        sourceLabel: cluster?.label || `Cluster ${sourceId}`,
        customer: primaryCustomer,
        linkage: primaryLinkage,
        activeTicket,
        suggestedSeverity: 'high',
        suggestedSummary: `Cluster anomaly ${cluster?.label || sourceId} dipilih dari context ticket.`,
        impactCount: impactedItems.length,
        topologyNodeId: String(cluster?.rootCustomerId || primaryCustomerId || sourceId),
        nasId: primaryLinkage?.routerId || '',
        oltId: primaryLinkage?.oltId || '',
        odpId: primaryLinkage?.odpId || '',
      };
    }

    return {
      sourceType,
      sourceId,
      sourceKey,
      sourceLabel: `${String(sourceType || 'source').toUpperCase()} ${sourceId}`,
      customer: null,
      linkage: null,
      activeTicket,
      suggestedSeverity: 'normal',
      suggestedSummary: `Source ${sourceId} dipilih dari daftar ticket terbaru.`,
      impactCount: 0,
      topologyNodeId: sourceId,
    };
  }, [
    activeTicketByNasId,
    activeTicketByOdpId,
    activeTicketByOltId,
    activeTicketBySourceKey,
    customerClusters,
    filteredCustomers,
    filteredOdps,
    filteredOlts,
    nasByRouterId,
    resolveCustomerLinkageMeta,
    resolveCustomerOltId,
    resolveCustomerRouterId,
    resolveFilteredOdp,
  ]);

  const selectedIncidentContext = useMemo(() => {
    if (incidentSourceOverride?.sourceType && incidentSourceOverride?.sourceId) {
      const overrideContext = buildIncidentContextFromSource(incidentSourceOverride.sourceType, incidentSourceOverride.sourceId);
      if (overrideContext) return overrideContext;
    }
    if (selectedCustomer) {
      const linkage = resolveCustomerLinkageMeta(selectedCustomer);
      const sourceId = String(selectedCustomer.id || '').trim();
      const sourceKey = buildIncidentSourceKey('customer', sourceId);
      const activeTicket = activeTicketBySourceKey[sourceKey] || activeTicketByCustomerId[sourceId] || null;
      const summary = `Customer node ${selectedCustomer.name || selectedCustomer.customer_id || '-'} ${getStatusLabel(selectedCustomer.connection_status).toLowerCase()} di ${linkage.odpName || 'ODP tidak diketahui'}.`;
      return {
        sourceType: 'customer',
        sourceId,
        sourceKey,
        sourceLabel: selectedCustomer.name || selectedCustomer.customer_id || 'Pelanggan',
        customer: selectedCustomer,
        linkage,
        activeTicket,
        suggestedSeverity: selectedCustomer.connection_status === 'offline' ? 'high' : 'normal',
        suggestedSummary: summary,
        impactCount: 1,
        topologyNodeId: sourceId,
        nasId: linkage?.routerId || '',
        oltId: linkage?.oltId || '',
        odpId: linkage?.odpId || '',
        monitoringStatusSnapshot: {
          connection_status: String(selectedCustomer?.connection_status || '').toLowerCase(),
          pppoe_status: String(selectedCustomer?.pppoe_status || '').toLowerCase(),
          rx_power_db: Number(selectedCustomer?.rx_power_db ?? selectedCustomer?.rx_power ?? 0) || undefined,
        },
      };
    }
    if (focusedInfraEntity?.type === 'odp' && focusedInfraEntity?.data) {
      const odpId = normalizeNodeId(focusedInfraEntity.data.id);
      const impactedCustomers = filteredCustomers.filter((item) => normalizeNodeId(resolveFilteredOdp(item)?.id) === odpId);
      const primaryCustomer = impactedCustomers[0] || null;
      const primaryLinkage = primaryCustomer ? resolveCustomerLinkageMeta(primaryCustomer) : null;
      const sourceKey = buildIncidentSourceKey('odp', odpId);
      const activeTicket = activeTicketBySourceKey[sourceKey] || activeTicketByOdpId[odpId] || null;
      return {
        sourceType: 'odp',
        sourceId: odpId,
        sourceKey,
        sourceLabel: focusedInfraEntity.data.name || focusedInfraEntity.data.code || `ODP ${odpId}`,
        customer: primaryCustomer,
        linkage: primaryLinkage,
        activeTicket,
        suggestedSeverity: focusedInfraEntity.status === 'offline' ? 'high' : 'normal',
        suggestedSummary: `ODP ${focusedInfraEntity.data.name || focusedInfraEntity.data.code || odpId} mengalami gangguan dengan impact ${impactedCustomers.length} pelanggan.`,
        impactCount: impactedCustomers.length,
        topologyNodeId: odpId,
        nasId: primaryLinkage?.routerId || '',
        oltId: normalizeNodeId(focusedInfraEntity?.data?.olt_id) || '',
        odpId,
      };
    }
    if (focusedInfraEntity?.type === 'olt' && focusedInfraEntity?.data) {
      const oltId = normalizeNodeId(focusedInfraEntity.data.id);
      const impactedCustomers = filteredCustomers.filter((item) => normalizeNodeId(resolveCustomerOltId(item)) === oltId);
      const primaryCustomer = impactedCustomers[0] || null;
      const primaryLinkage = primaryCustomer ? resolveCustomerLinkageMeta(primaryCustomer) : null;
      const sourceKey = buildIncidentSourceKey('olt', oltId);
      const activeTicket = activeTicketBySourceKey[sourceKey] || activeTicketByOltId[oltId] || null;
      return {
        sourceType: 'olt',
        sourceId: oltId,
        sourceKey,
        sourceLabel: focusedInfraEntity.data.name || focusedInfraEntity.data.code || `OLT ${oltId}`,
        customer: primaryCustomer,
        linkage: primaryLinkage,
        activeTicket,
        suggestedSeverity: focusedInfraEntity.status === 'offline' ? 'high' : 'normal',
        suggestedSummary: `OLT ${focusedInfraEntity.data.name || focusedInfraEntity.data.code || oltId} membutuhkan investigasi, dampak ${impactedCustomers.length} pelanggan.`,
        impactCount: impactedCustomers.length,
        topologyNodeId: oltId,
        nasId: primaryLinkage?.routerId || '',
        oltId,
        odpId: primaryLinkage?.odpId || '',
      };
    }
    if (selectedNasId !== 'all') {
      const nasId = normalizeNodeId(selectedNasId);
      const impactedCustomers = filteredCustomers.filter((item) => normalizeNodeId(resolveCustomerRouterId(item)) === nasId);
      const primaryCustomer = impactedCustomers[0] || null;
      const primaryLinkage = primaryCustomer ? resolveCustomerLinkageMeta(primaryCustomer) : null;
      const sourceKey = buildIncidentSourceKey('nas', nasId);
      const activeTicket = activeTicketBySourceKey[sourceKey] || activeTicketByNasId[nasId] || null;
      const nasLabel = String(nasByRouterId[nasId]?.name || `NAS ${nasId}`).trim();
      return {
        sourceType: 'nas',
        sourceId: nasId,
        sourceKey,
        sourceLabel: nasLabel,
        customer: primaryCustomer,
        linkage: primaryLinkage,
        activeTicket,
        suggestedSeverity: impactedCustomers.some((item) => String(item?.connection_status || '').toLowerCase() === 'offline') ? 'high' : 'normal',
        suggestedSummary: `${nasLabel} perlu investigasi operasional. Dampak terpantau ${impactedCustomers.length} pelanggan.`,
        impactCount: impactedCustomers.length,
        topologyNodeId: nasId,
        nasId,
        oltId: primaryLinkage?.oltId || '',
        odpId: primaryLinkage?.odpId || '',
      };
    }
    if (focusNode?.id) {
      const sourceId = String(focusNode.id || '').trim();
      const sourceType = focusNode.type === 'customer' ? 'customer' : 'topology_node';
      const sourceKey = buildIncidentSourceKey(sourceType, sourceId);
      const activeTicket = activeTicketBySourceKey[sourceKey] || activeTicketByTopologyNodeId[sourceId] || null;
      return {
        sourceType,
        sourceId,
        sourceKey,
        sourceLabel: `${String(focusNode.type || 'Node').toUpperCase()} ${sourceId}`,
        customer: null,
        linkage: null,
        activeTicket,
        suggestedSeverity: 'normal',
        suggestedSummary: `Node ${sourceId} terdeteksi membutuhkan investigasi monitoring.`,
        impactCount: 0,
        topologyNodeId: sourceId,
      };
    }
    return null;
  }, [
    buildIncidentContextFromSource,
    incidentSourceOverride,
    activeTicketByCustomerId,
    activeTicketBySourceKey,
    activeTicketByNasId,
    activeTicketByOdpId,
    activeTicketByOltId,
    activeTicketByTopologyNodeId,
    filteredCustomers,
    focusedInfraEntity,
    focusNode,
    nasByRouterId,
    resolveCustomerLinkageMeta,
    resolveCustomerOltId,
    resolveCustomerRouterId,
    resolveFilteredOdp,
    selectedNasId,
    selectedCustomer,
  ]);

  const incidentTicketSummary = useMemo(() => {
    let open = 0;
    let inProgress = 0;
    let onHold = 0;
    let resolved = 0;
    let escalated = 0;
    normalizedFieldTickets.forEach((ticket) => {
      const stateKey = String(ticket?.lifecycle?.key || '').toLowerCase();
      if (stateKey === 'open') open += 1;
      else if (stateKey === 'in_progress') inProgress += 1;
      else if (stateKey === 'on_hold') onHold += 1;
      else if (stateKey === 'resolved') resolved += 1;
      else if (stateKey === 'escalated') escalated += 1;
    });
    return {
      open,
      inProgress,
      onHold,
      resolved,
      escalated,
      total: normalizedFieldTickets.length,
      active: open + inProgress + onHold + escalated,
    };
  }, [normalizedFieldTickets]);

  const monitoringIncidentSummary = useMemo(() => {
    const summary = {
      total: 0,
      open: 0,
      monitoring: 0,
      resolved: 0,
      falsePositive: 0,
      critical: 0,
      high: 0,
    };
    (Array.isArray(monitoringIncidents) ? monitoringIncidents : []).forEach((incident) => {
      summary.total += 1;
      const status = String(incident?.status || '').toLowerCase();
      if (status === 'open') summary.open += 1;
      else if (status === 'monitoring') summary.monitoring += 1;
      else if (status === 'resolved') summary.resolved += 1;
      else if (status === 'false_positive') summary.falsePositive += 1;
      const severity = String(incident?.severity || '').toLowerCase();
      if (severity === 'critical') summary.critical += 1;
      if (severity === 'high') summary.high += 1;
    });
    return summary;
  }, [monitoringIncidents]);

  const monitoringIncidentsByContext = useMemo(() => {
    if (!selectedIncidentContext) return [];
    const sourceType = normalizeIncidentSourceType(selectedIncidentContext.sourceType);
    const sourceId = String(selectedIncidentContext.sourceId || '').trim();
    if (!sourceType || !sourceId) return [];
    return (Array.isArray(monitoringIncidents) ? monitoringIncidents : []).filter((incident) => {
      const incidentSourceType = normalizeIncidentSourceType(incident?.root_source_type);
      const incidentSourceId = String(incident?.root_source_id || '').trim();
      return incidentSourceType === sourceType && incidentSourceId === sourceId;
    });
  }, [monitoringIncidents, selectedIncidentContext]);

  const incidentTicketsByContext = useMemo(() => {
    if (!selectedIncidentContext) return [];
    const sourceType = String(selectedIncidentContext.sourceType || '').toLowerCase();
    const sourceId = String(selectedIncidentContext.sourceId || '').trim();
    const sourceKey = String(selectedIncidentContext.sourceKey || '').trim();
    const customerId = String(selectedIncidentContext.customer?.id || '').trim();
    if (sourceKey && Array.isArray(ticketsBySourceKey[sourceKey])) {
      return ticketsBySourceKey[sourceKey];
    }
    if (sourceType === 'customer' && customerId && Array.isArray(ticketsByCustomerId[customerId])) {
      return ticketsByCustomerId[customerId];
    }
    if (sourceType === 'odp' && sourceId) {
      return ticketsByOdpId[sourceId] || [];
    }
    if (sourceType === 'olt' && sourceId) {
      return ticketsByOltId[sourceId] || [];
    }
    if (sourceType === 'nas' && sourceId) {
      return ticketsByNasId[sourceId] || [];
    }
    if (sourceType === 'topology_node' && sourceId && Array.isArray(ticketsByTopologyNodeId[sourceId])) {
      return ticketsByTopologyNodeId[sourceId];
    }
    return [];
  }, [
    selectedIncidentContext,
    ticketsByCustomerId,
    ticketsByNasId,
    ticketsByOdpId,
    ticketsByOltId,
    ticketsBySourceKey,
    ticketsByTopologyNodeId,
  ]);




  const odpCoverageZones = useMemo(() => {

    const mapped = {};

    if (viewMode === 'all' || viewMode === 'onu') visibleCustomerPoints.forEach((customer) => {

      const odp = resolveFilteredOdp(customer);

      if (!odp?.id) return;

      if (!hasValidCoordinates(odp.latitude, odp.longitude)) return;

      if (!mapped[odp.id]) {

        mapped[odp.id] = {

          id: odp.id,

          latitude: Number(odp.latitude),

          longitude: Number(odp.longitude),

          count: 0,

        };

      }

      mapped[odp.id].count += 1;

    });



    return Object.values(mapped).map((zone) => ({

      ...zone,

      radius: 130 + Math.min(zone.count * 18, 190),

    }));

  }, [resolveFilteredOdp, visibleCustomerPoints, viewMode]);

  const mapPoints = useMemo(() => {

    const points = [];



    if (viewMode === 'all' || viewMode === 'onu') visibleCustomerPoints.forEach((customer) => {

      points.push([Number(customer.latitude), Number(customer.longitude)]);

    });



    if (viewMode === 'all' || viewMode === 'odp') filteredOdps.forEach((odp) => {

      if (hasValidCoordinates(odp.latitude, odp.longitude)) {

        points.push([Number(odp.latitude), Number(odp.longitude)]);

      }

    });



    if (viewMode === 'all' || viewMode === 'olt') filteredOlts.forEach((olt) => {

      if (hasValidCoordinates(olt.latitude, olt.longitude)) {

        points.push([Number(olt.latitude), Number(olt.longitude)]);

      }

    });



    return points;

  }, [visibleCustomerPoints, filteredOdps, filteredOlts, viewMode]);

  const primaryOdpFocusId = useMemo(() => {
    if (focusNode?.id) return '';
    if (selectedNasId !== 'all' || selectedOltId !== 'all') return '';
    if (statusFilter !== 'all' || searchQuery.trim()) return '';
    let bestId = '';
    let bestScore = -1;
    filteredOdps.forEach((odp) => {
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      const offlineScore = Number(offlineCountByOdpId[odpId] || 0);
      const customerScore = Number(customerCountByOdpId[odpId] || 0);
      const score = (offlineScore * 1000) + customerScore;
      if (score > bestScore) {
        bestScore = score;
        bestId = odpId;
      }
    });
    return bestId;
  }, [
    customerCountByOdpId,
    filteredOdps,
    focusNode?.id,
    offlineCountByOdpId,
    searchQuery,
    selectedNasId,
    selectedOltId,
    statusFilter,
  ]);

  const activeTopologyPoints = useMemo(() => {
    const points = [];
    const addPoint = (lat, lon) => {
      if (!hasValidCoordinates(lat, lon)) return;
      points.push([Number(lat), Number(lon)]);
    };

    const collectByOdp = (odpId) => {
      const odp = filteredOdpsById[odpId];
      if (!odp) return;
      addPoint(odp.latitude, odp.longitude);
      const olt = filteredOltsById[normalizeNodeId(odp.olt_id)];
      if (olt) addPoint(olt.latitude, olt.longitude);
      filteredCustomersByMode.forEach((customer) => {
        const linkedOdp = resolveFilteredOdp(customer);
        if (normalizeNodeId(linkedOdp?.id) !== normalizeNodeId(odpId)) return;
        addPoint(customer.latitude, customer.longitude);
      });
    };

    if (focusNode?.id) {
      if (focusNode.type === 'customer') {
        const customer = filteredCustomersByMode.find((item) => String(item?.id || '') === String(focusNode.id));
        if (customer) {
          addPoint(customer.latitude, customer.longitude);
          const linkedOdp = resolveFilteredOdp(customer);
          if (linkedOdp) {
            addPoint(linkedOdp.latitude, linkedOdp.longitude);
            const linkedOlt = filteredOltsById[normalizeNodeId(linkedOdp.olt_id)];
            if (linkedOlt) addPoint(linkedOlt.latitude, linkedOlt.longitude);
          }
        }
      } else if (focusNode.type === 'odp') {
        collectByOdp(normalizeNodeId(focusNode.id));
      } else if (focusNode.type === 'olt') {
        const olt = filteredOlts.find((item) => normalizeNodeId(item?.id) === normalizeNodeId(focusNode.id));
        if (olt) addPoint(olt.latitude, olt.longitude);
        filteredOdps.forEach((odp) => {
          if (normalizeNodeId(odp?.olt_id) !== normalizeNodeId(focusNode.id)) return;
          collectByOdp(normalizeNodeId(odp.id));
        });
      }
    } else if (primaryOdpFocusId) {
      collectByOdp(primaryOdpFocusId);
    } else {
      mapPoints.forEach((point) => addPoint(point?.[0], point?.[1]));
    }

    if (points.length === 0) {
      mapPoints.forEach((point) => addPoint(point?.[0], point?.[1]));
    }
    return points;
  }, [
    filteredCustomersByMode,
    filteredOdps,
    filteredOdpsById,
    filteredOlts,
    filteredOltsById,
    focusNode,
    mapPoints,
    primaryOdpFocusId,
    resolveFilteredOdp,
  ]);

  const onlineCount = filteredCustomersByMode.filter((customer) => customer.connection_status === 'online').length;

  const offlineCount = filteredCustomersByMode.filter((customer) => customer.connection_status === 'offline').length;

  const unknownCount = filteredCustomersByMode.filter((customer) => customer.connection_status !== 'online' && customer.connection_status !== 'offline').length;
  const notMappedCount = filteredCustomersByMode.filter((customer) => customer.connection_status === 'not_mapped').length;
  const rightPanelTabLabel = ({
    ai: 'Wawasan AI',
    infra: 'Infrastruktur',
    capacity: 'Kapasitas',
    queue: 'Antrean Aksi',
    tickets: 'Tiket',
    customers: 'Pelanggan',
  })[rightPanelTab] || 'Wawasan AI';
  const capacityPrediction = useMemo(() => {
    const odpPredictions = buildOdpCapacityPredictions({
      odps: filteredOdps,
      customers: filteredCustomersByMode,
      resolveCustomerOdpId: (customer) => normalizeNodeId(resolveFilteredOdp(customer)?.id || customer?.linked_odp_id || customer?.odp_id),
    });
    const nasPredictions = buildNasCapacityPredictions({
      nasRouters: scopedNasRouters,
      customers: filteredCustomersByMode,
      resolveCustomerRouterId,
    });
    return {
      odps: odpPredictions,
      nas: nasPredictions,
      insights: buildCapacityInsights({ odpPredictions, nasPredictions }),
    };
  }, [filteredCustomersByMode, filteredOdps, resolveCustomerRouterId, resolveFilteredOdp, scopedNasRouters]);

  const topRiskOdp = capacityPrediction.odps[0] || null;
  const topRiskNas = capacityPrediction.nas[0] || null;
  const capacityRecommendations = useMemo(() => {
    return buildCapacityRecommendations({
      odpPredictions: capacityPrediction.odps,
      nasPredictions: capacityPrediction.nas,
      strategy: capacityRecommendationStrategy,
      limit: 6,
    });
  }, [capacityPrediction.nas, capacityPrediction.odps, capacityRecommendationStrategy]);
  const actionQueueItemsRaw = useMemo(() => {
    const now = Date.now();
    const baseItems = [];
    const pendingPaymentKeys = new Set(
      (Array.isArray(pendingPayments) ? pendingPayments : []).map((payment) => String(
        payment?.customer_id
        || payment?.billing_id
        || payment?.invoice_id
        || payment?.invoice_number
        || '',
      ).trim()).filter(Boolean),
    );
    const queueAutoCandidate = (candidateRaw) => {
      if (!candidateRaw || typeof candidateRaw !== 'object') return;
      const ruleKey = String(candidateRaw?.rule_key || '').trim();
      if (!ruleKey || !AUTO_ACTION_RULE_POLICIES[ruleKey]?.enabled) return;
      const policy = AUTO_ACTION_RULE_POLICIES[ruleKey];
      const sourceType = normalizeIncidentSourceType(candidateRaw?.source_type || 'topology_node');
      const sourceId = String(candidateRaw?.source_id || '').trim();
      const actionType = normalizeActionQueueActionType(candidateRaw?.action_type || policy.action_type || 'monitor');
      const duplicateKey = String(candidateRaw?.duplicate_key || `${ruleKey}:${sourceType}:${sourceId || 'scope'}:${actionType}`).trim();
      const confidenceScore = Number(candidateRaw?.confidenceScore ?? candidateRaw?.confidence ?? 0);
      if (!Number.isFinite(confidenceScore) || confidenceScore < Number(policy.confidence_threshold || 0)) {
        appendAutoActionLifecycleLog({
          id: `${duplicateKey}:suppressed:confidence:${now}`,
          event_type: 'auto_action_suppressed',
          rule_key: ruleKey,
          source_type: sourceType,
          source_id: sourceId,
          action_type: actionType,
          status: 'pending_review',
          action_mode: policy.action_mode,
          reason: 'Suppressed by confidence threshold.',
          meta: { duplicate_key: duplicateKey, confidence: confidenceScore },
        });
        return;
      }
      const guardEntry = recommendationGuardById[duplicateKey] && typeof recommendationGuardById[duplicateKey] === 'object'
        ? recommendationGuardById[duplicateKey]
        : null;
      const holdEntry = actionQueueAutoHoldById[duplicateKey] && typeof actionQueueAutoHoldById[duplicateKey] === 'object'
        ? actionQueueAutoHoldById[duplicateKey]
        : null;
      const cooldownUntil = Number(guardEntry?.cooldownUntil || 0);
      if (holdEntry?.hold_flag) {
        appendAutoActionLifecycleLog({
          id: `${duplicateKey}:suppressed:hold:${now}`,
          event_type: 'auto_action_suppressed',
          rule_key: ruleKey,
          source_type: sourceType,
          source_id: sourceId,
          action_type: actionType,
          status: 'pending_review',
          action_mode: policy.action_mode,
          reason: 'Suppressed by hold flag.',
          meta: { duplicate_key: duplicateKey },
        });
        return;
      }
      if (Number.isFinite(cooldownUntil) && cooldownUntil > now) {
        appendAutoActionLifecycleLog({
          id: `${duplicateKey}:suppressed:cooldown:${now}`,
          event_type: 'auto_action_suppressed',
          rule_key: ruleKey,
          source_type: sourceType,
          source_id: sourceId,
          action_type: actionType,
          status: 'pending_review',
          action_mode: policy.action_mode,
          reason: 'Suppressed by cooldown.',
          meta: { duplicate_key: duplicateKey, cooldown_until: cooldownUntil },
        });
        return;
      }
      baseItems.push({
        id: duplicateKey,
        rule_key: ruleKey,
        source_type: sourceType,
        source_id: sourceId,
        action_type: actionType,
        action_mode: normalizeAutoActionMode(candidateRaw?.action_mode || policy.action_mode || 'manual_review'),
        status: normalizeAutoActionStatus(candidateRaw?.status || 'pending_review'),
        urgency: normalizeActionQueueUrgency(candidateRaw?.urgency || 'sedang'),
        title: String(candidateRaw?.title || 'Auto Action Candidate'),
        reason: String(candidateRaw?.reason || 'Signal memenuhi rule auto-action.'),
        reason_all: Array.isArray(candidateRaw?.reason_all) ? candidateRaw.reason_all.filter(Boolean).map((line) => String(line)) : [String(candidateRaw?.reason || '')].filter(Boolean),
        recommended_action: String(candidateRaw?.recommended_action || 'Review candidate sebelum eksekusi.'),
        created_at: String(candidateRaw?.created_at || new Date(now).toISOString()),
        updated_at: String(candidateRaw?.updated_at || new Date(now).toISOString()),
        confidenceScore: Number(confidenceScore.toFixed(2)),
        confidenceLabel: confidenceScore >= 0.72 ? 'Tinggi' : confidenceScore >= 0.48 ? 'Sedang' : 'Rendah',
        impact: Number(candidateRaw?.impact || 0),
        metrics: candidateRaw?.metrics && typeof candidateRaw.metrics === 'object' ? { ...candidateRaw.metrics } : {},
        context_snapshot: candidateRaw?.context_snapshot && typeof candidateRaw.context_snapshot === 'object' ? { ...candidateRaw.context_snapshot } : {},
        duplicate_key: duplicateKey,
        cooldown_until: Number(policy.cooldown_ms || 0) > 0 ? now + Number(policy.cooldown_ms || 0) : null,
        hold_flag: false,
        hold_reason: null,
        links: candidateRaw?.links && typeof candidateRaw.links === 'object' ? candidateRaw.links : {},
        isAutoAction: true,
      });
      appendAutoActionLifecycleLog({
        id: `${duplicateKey}:generated:${now}`,
        event_type: 'auto_action_generated',
        rule_key: ruleKey,
        source_type: sourceType,
        source_id: sourceId,
        action_type: actionType,
        status: 'pending_review',
        action_mode: normalizeAutoActionMode(candidateRaw?.action_mode || policy.action_mode || 'manual_review'),
        reason: String(candidateRaw?.reason || '').trim() || null,
        meta: { duplicate_key: duplicateKey },
      });
    };

    (Array.isArray(capacityRecommendations?.recommendations) ? capacityRecommendations.recommendations : []).forEach((item, idx) => {
      const sourceType = normalizeIncidentSourceType(item?.sourceType || 'topology_node');
      const sourceId = String(item?.sourceId || '').trim();
      const actionType = normalizeActionQueueActionType(item?.actionType);
      const itemId = `${sourceType}:${sourceId || `scope-${idx}`}:${actionType}`;
      const reasonPrimary = Array.isArray(item?.reasonLines) ? item.reasonLines[0] : '';
      baseItems.push({
        id: itemId,
        source_type: sourceType,
        source_id: sourceId,
        action_type: actionType,
        urgency: normalizeActionQueueUrgency(item?.urgency?.level),
        title: String(item?.title || `Aksi ${sourceType.toUpperCase()}`),
        reason: String(reasonPrimary || 'Perlu tinjauan operasional.'),
        recommended_action: String(item?.suggestedAction || item?.summary || 'Tinjau rekomendasi.'),
        created_at: String(item?.created_at || ''),
        status: 'pending',
        impact: Number(item?.metrics?.occupancyPercent || item?.metrics?.activeCount || 0),
        metrics: item?.metrics && typeof item.metrics === 'object' ? { ...item.metrics } : {},
        confidenceScore: Number(item?.confidenceScore || 0),
        confidenceLabel: String(item?.confidenceLabel || 'Rendah'),
        reason_all: Array.isArray(item?.reasonLines) ? item.reasonLines.filter(Boolean).map((line) => String(line)) : [String(reasonPrimary || '')].filter(Boolean),
        links: item?.links && typeof item.links === 'object' ? item.links : {},
      });
    });

    const anomalyOfflineCount = Number(aiMonitoringResult?.anomaly?.offline_count || 0);
    const anomalyRatio = Number(aiMonitoringResult?.anomaly?.ratio || 0);
    const monitorRisk = Math.max(0, Math.min(100, Number(aiMonitoringResult?.risk || 0)));
    const rootCauseId = String(aiMonitoringResult?.root_cause?.odp || '').trim();
    if (monitorRisk > 0 || anomalyOfflineCount > 0) {
      const urgency = monitorRisk >= 75 ? 'kritis' : monitorRisk >= 55 ? 'tinggi' : monitorRisk >= 35 ? 'sedang' : 'rendah';
      const sourceType = rootCauseId ? 'odp' : 'anomaly_cluster';
      const sourceId = rootCauseId || 'monitoring-cluster';
      const reason = `Offline ${anomalyOfflineCount} pelanggan (${Math.round(anomalyRatio * 100)}%) dengan risk ${monitorRisk}%.`;
      const recommendationSource = Array.isArray(aiMonitoringResult?.recommendation) ? aiMonitoringResult.recommendation : [];
      baseItems.push({
        id: `${sourceType}:${sourceId}:monitor`,
        source_type: sourceType,
        source_id: sourceId,
        action_type: 'monitor',
        urgency,
        title: rootCauseId ? `Stabilisasi ${rootCauseId}` : 'Anomaly cluster terdeteksi',
        reason,
        recommended_action: String(recommendationSource[0] || (rootCauseId ? `Investigasi ${rootCauseId} dan pantau cluster terkait.` : 'Investigasi cluster anomali dan pantau dampak pelanggan.')),
        created_at: '',
        status: 'pending',
        impact: anomalyOfflineCount,
        metrics: {
          occupancyPercent: Math.round(anomalyRatio * 100),
          growth30d: 0,
          daysToFull: null,
        },
        confidenceScore: Number((Math.max(0.3, Math.min(0.92, (monitorRisk / 100) * 0.65 + Math.min(0.27, anomalyRatio * 0.35)))).toFixed(2)),
        confidenceLabel: monitorRisk >= 75 ? 'Tinggi' : monitorRisk >= 45 ? 'Sedang' : 'Rendah',
        reason_all: [reason],
        links: {
            monitoring: `/dadu/monitoring?sourceType=${encodeURIComponent(sourceType)}&sourceId=${encodeURIComponent(sourceId)}`,
            planning: rootCauseId ? `/dadu/ftth?context=planning&planningAction=ai-odp&sourceType=odp&sourceId=${encodeURIComponent(rootCauseId)}` : '',
        },
      });
    }

    if (Number(incidentTicketSummary?.escalated || 0) > 0) {
      const escalatedCount = Number(incidentTicketSummary.escalated || 0);
      baseItems.push({
        id: 'anomaly_cluster:incident-escalated:monitor',
        source_type: 'anomaly_cluster',
        source_id: 'incident-escalated',
        action_type: 'monitor',
        urgency: escalatedCount >= 3 ? 'kritis' : 'tinggi',
        title: 'Insiden eskalasi aktif',
        reason: `${escalatedCount} ticket sedang status escalated dan butuh tindak lanjut.`,
        recommended_action: 'Buka panel ticket monitoring dan sinkronkan owner penanganan.',
        created_at: '',
        status: 'pending',
        impact: escalatedCount,
        metrics: {
          occupancyPercent: 0,
          growth30d: 0,
          daysToFull: null,
        },
        confidenceScore: Number((escalatedCount >= 3 ? 0.82 : 0.66).toFixed(2)),
        confidenceLabel: escalatedCount >= 3 ? 'Tinggi' : 'Sedang',
        reason_all: [`${escalatedCount} ticket escalated aktif.`],
        links: {
            monitoring: '/dadu/monitoring',
        },
      });
    }

    const billingSnapshotCandidates = (Array.isArray(filteredCustomers) ? filteredCustomers : []).slice(0, 300);
    billingSnapshotCandidates.forEach((customer) => {
      const customerId = String(customer?.id || '').trim();
      if (!customerId) return;
      const billingState = String(
        customer?.billing_state
        || customer?.billingState
        || customer?.billing_snapshot?.billing_state
        || customer?.status
        || '',
      ).trim().toLowerCase();
      const dueDateValue = new Date(customer?.billing_due_date || customer?.dueDate || customer?.due_date || 0).getTime();
      const overdueDays = Number(customer?.billing_overdue_days ?? customer?.overdueDays ?? customer?.overdue_days ?? 0);
      const suspendEligible = Boolean(customer?.billing_suspend_eligible ?? customer?.suspendEligible ?? customer?.suspend_eligible ?? false);
      const hasPendingPayment = pendingPaymentKeys.has(customerId) || pendingPaymentKeys.has(String(customer?.billing_last_invoice_id || '').trim());

      if ((billingState === 'unpaid' || billingState === 'overdue') && overdueDays >= 0 && overdueDays <= 2 && !hasPendingPayment) {
        queueAutoCandidate({
          rule_key: 'overdue_reminder_ready',
          source_type: 'customer',
          source_id: customerId,
          action_type: 'monitor',
          action_mode: 'auto_low_risk',
          urgency: overdueDays >= 2 ? 'sedang' : 'rendah',
          confidenceScore: Math.max(0.5, Math.min(0.86, 0.5 + (overdueDays * 0.08))),
          title: `Reminder billing ${customer?.name || customerId}`,
          reason: overdueDays > 0
            ? `Invoice overdue ${overdueDays} hari dan belum ada approval pembayaran.`
            : 'Mendekati due date, kirim pengingat pembayaran.',
          recommended_action: 'Kirim reminder billing dan pantau response pembayaran.',
          context_snapshot: {
            billing_state: billingState,
            overdue_days: overdueDays,
            due_date: Number.isFinite(dueDateValue) ? new Date(dueDateValue).toISOString() : null,
            pending_payment: hasPendingPayment,
          },
          duplicate_key: `overdue_reminder_ready:customer:${customerId}:monitor`,
                links: { monitoring: `/dadu/monitoring?sourceType=customer&sourceId=${encodeURIComponent(customerId)}` },
        });
      }

      if ((billingState === 'suspend_candidate' || suspendEligible) && !hasPendingPayment) {
        queueAutoCandidate({
          rule_key: 'billing_suspend_ready',
          source_type: 'customer',
          source_id: customerId,
          action_type: 'monitor',
          action_mode: 'manual_review',
          urgency: overdueDays >= 5 ? 'kritis' : 'tinggi',
          confidenceScore: Math.max(0.7, Math.min(0.95, 0.72 + (Math.max(0, overdueDays - 2) * 0.03))),
          title: `Suspend review ${customer?.name || customerId}`,
          reason: `Grace period terlampaui (${overdueDays} hari) dan tidak ada payment approval aktif.`,
          recommended_action: 'Review suspend candidate dan jalankan suspend jika valid.',
          context_snapshot: {
            billing_state: billingState,
            overdue_days: overdueDays,
            billing_suspend_eligible: suspendEligible,
            pending_payment: hasPendingPayment,
          },
          duplicate_key: `billing_suspend_ready:customer:${customerId}:monitor`,
          links: {
            monitoring: `/dadu/monitoring?sourceType=customer&sourceId=${encodeURIComponent(customerId)}`,
            billing: '/dadu/billing',
          },
        });
      }

      if (billingState === 'restore_candidate') {
        queueAutoCandidate({
          rule_key: 'billing_restore_ready',
          source_type: 'customer',
          source_id: customerId,
          action_type: 'monitor',
          action_mode: 'semi_auto',
          urgency: 'tinggi',
          confidenceScore: hasPendingPayment ? 0.58 : 0.74,
          title: `Restore candidate ${customer?.name || customerId}`,
          reason: hasPendingPayment
            ? 'Customer restore_candidate, tetapi approval pembayaran masih pending.'
            : 'Customer berada pada state restore_candidate dengan sinyal pembayaran siap.',
          recommended_action: hasPendingPayment
            ? 'Finalisasi approval pembayaran sebelum restore.'
            : 'Review restore service dan jalankan aktivasi ulang.',
          context_snapshot: {
            billing_state: billingState,
            billing_last_payment_at: customer?.billing_last_payment_at || customer?.last_payment_at || null,
            pending_payment: hasPendingPayment,
          },
          duplicate_key: `billing_restore_ready:customer:${customerId}:monitor`,
          links: {
            monitoring: `/dadu/monitoring?sourceType=customer&sourceId=${encodeURIComponent(customerId)}`,
            billing: '/dadu/billing',
          },
        });
      }
    });

    if (Number(incidentTicketSummary?.active || 0) === 0 && (anomalyOfflineCount >= 3 || monitorRisk >= 68)) {
      queueAutoCandidate({
        rule_key: 'anomaly_ticket_ready',
        source_type: rootCauseId ? 'odp' : 'anomaly_cluster',
        source_id: rootCauseId || 'monitoring-cluster',
        action_type: 'monitor',
        action_mode: 'manual_review',
        urgency: monitorRisk >= 80 ? 'kritis' : 'tinggi',
        confidenceScore: Math.max(0.62, Math.min(0.92, (monitorRisk / 100) * 0.78)),
        title: 'Ticket suggestion dari anomaly',
        reason: `Anomali berulang (offline ${anomalyOfflineCount}) tanpa ticket aktif.`,
        recommended_action: 'Buat ticket incident dari context monitoring untuk ownership penanganan.',
        context_snapshot: {
          monitor_risk: monitorRisk,
          anomaly_offline_count: anomalyOfflineCount,
          ticket_active: Number(incidentTicketSummary?.active || 0),
        },
        duplicate_key: `anomaly_ticket_ready:${rootCauseId || 'cluster'}:monitor`,
            links: { monitoring: '/dadu/monitoring' },
      });
    }

    const highestCapacityRec = (Array.isArray(capacityRecommendations?.recommendations) ? capacityRecommendations.recommendations : [])[0] || null;
    if (highestCapacityRec && Number(highestCapacityRec?.confidenceScore || 0) >= 0.6) {
      queueAutoCandidate({
        rule_key: 'capacity_planning_ready',
        source_type: normalizeIncidentSourceType(highestCapacityRec?.sourceType || 'topology_node'),
        source_id: String(highestCapacityRec?.sourceId || '').trim(),
        action_type: normalizeActionQueueActionType(highestCapacityRec?.actionType || 'planning'),
        action_mode: 'semi_auto',
        urgency: normalizeActionQueueUrgency(highestCapacityRec?.urgency?.level || 'sedang'),
        confidenceScore: Number(highestCapacityRec?.confidenceScore || 0.6),
        title: `Planning suggestion: ${String(highestCapacityRec?.title || 'Capacity pressure')}`,
        reason: Array.isArray(highestCapacityRec?.reasonLines) ? String(highestCapacityRec.reasonLines[0] || 'Kapasitas butuh tindak lanjut planning.') : 'Kapasitas butuh tindak lanjut planning.',
        recommended_action: String(highestCapacityRec?.suggestedAction || 'Buka FTTH planning untuk verifikasi ekspansi.'),
        context_snapshot: highestCapacityRec?.metrics && typeof highestCapacityRec.metrics === 'object' ? { ...highestCapacityRec.metrics } : {},
        duplicate_key: `capacity_planning_ready:${String(highestCapacityRec?.sourceType || 'scope').toLowerCase()}:${String(highestCapacityRec?.sourceId || 'scope')}`,
        links: highestCapacityRec?.links && typeof highestCapacityRec.links === 'object' ? highestCapacityRec.links : {},
      });
    }

    const dedupedMap = {};
    baseItems.forEach((item) => {
      const key = String(
        item?.isAutoAction
          ? (item?.duplicate_key || item?.id || `${String(item?.rule_key || 'auto')}:${String(item?.source_type || 'unknown')}:${String(item?.source_id || '')}:${String(item?.action_type || 'monitor')}`)
          : `${String(item?.source_type || 'unknown')}:${String(item?.source_id || '')}:${String(item?.action_type || 'monitor')}`,
      ).trim();
      const existing = dedupedMap[key];
      if (!existing) {
        dedupedMap[key] = {
          ...item,
          id: String(item?.id || key),
          guardKey: String(item?.guardKey || item?.duplicate_key || key),
          reason_all: Array.isArray(item?.reason_all) ? item.reason_all.slice(0, 4) : [String(item?.reason || '')].filter(Boolean),
        };
        return;
      }
      const existingRank = ACTION_QUEUE_URGENCY_RANK[normalizeActionQueueUrgency(existing?.urgency)] || 1;
      const nextRank = ACTION_QUEUE_URGENCY_RANK[normalizeActionQueueUrgency(item?.urgency)] || 1;
      const mergedReasons = [
        ...(Array.isArray(existing.reason_all) ? existing.reason_all : []),
        ...(Array.isArray(item.reason_all) ? item.reason_all : []),
      ].filter(Boolean);
      dedupedMap[key] = {
        ...existing,
        urgency: nextRank > existingRank ? normalizeActionQueueUrgency(item?.urgency) : existing.urgency,
        title: nextRank > existingRank ? item.title : existing.title,
        recommended_action: nextRank > existingRank ? item.recommended_action : existing.recommended_action,
        impact: Math.max(Number(existing?.impact || 0), Number(item?.impact || 0)),
        confidenceScore: Math.max(Number(existing?.confidenceScore || 0), Number(item?.confidenceScore || 0)),
        confidenceLabel: (Math.max(Number(existing?.confidenceScore || 0), Number(item?.confidenceScore || 0)) >= 0.72)
          ? 'Tinggi'
          : (Math.max(Number(existing?.confidenceScore || 0), Number(item?.confidenceScore || 0)) >= 0.48 ? 'Sedang' : 'Rendah'),
        metrics: {
          ...(existing?.metrics || {}),
          ...(item?.metrics || {}),
        },
        links: { ...(existing.links || {}), ...(item.links || {}) },
        reason_all: Array.from(new Set(mergedReasons)).slice(0, 4),
        guardKey: String(existing?.guardKey || existing?.duplicate_key || existing?.id || key),
        isAutoAction: Boolean(existing?.isAutoAction || item?.isAutoAction),
        action_mode: existing?.action_mode || item?.action_mode || null,
        rule_key: existing?.rule_key || item?.rule_key || null,
        duplicate_key: existing?.duplicate_key || item?.duplicate_key || null,
      };
    });

    const guarded = [];
    Object.values(dedupedMap).forEach((item) => {
      const guardKey = String(item?.id || '');
      if (!guardKey) return;
      const rawConfidenceScore = Number(item?.confidenceScore || 0);
      const urgency = normalizeActionQueueUrgency(item?.urgency);
      const actionType = normalizeActionQueueActionType(item?.action_type);
      const guardEntry = recommendationGuardById[guardKey] && typeof recommendationGuardById[guardKey] === 'object'
        ? recommendationGuardById[guardKey]
        : null;
      const holdEntry = actionQueueAutoHoldById[guardKey] && typeof actionQueueAutoHoldById[guardKey] === 'object'
        ? actionQueueAutoHoldById[guardKey]
        : null;
      const feedbackEntry = actionQueueFeedbackById[guardKey] && typeof actionQueueFeedbackById[guardKey] === 'object'
        ? actionQueueFeedbackById[guardKey]
        : null;
      const feedbackType = String(feedbackEntry?.feedbackType || '').trim().toLowerCase();
      const feedbackBias = Number(guardEntry?.feedbackBias || 0);
      const feedbackAdjustment = feedbackType === 'positive'
        ? 0.03
        : feedbackType === 'negative'
          ? -0.04
          : 0;
      const effectiveConfidenceScore = Math.max(0, Math.min(1, rawConfidenceScore + feedbackAdjustment + feedbackBias));
      const minConfidenceThreshold = Math.max(0.18, Math.min(0.34, 0.24 + (feedbackType === 'negative' ? 0.03 : 0) - (feedbackType === 'positive' ? 0.02 : 0)));

      if (holdEntry?.hold_flag) {
        return;
      }
      if (effectiveConfidenceScore < minConfidenceThreshold) {
        return;
      }

      let normalizedItem = { ...item };
      if (effectiveConfidenceScore < 0.48 && actionType !== 'monitor') {
        normalizedItem = {
          ...normalizedItem,
          action_type: 'monitor',
          recommended_action: `Pantau ${normalizedItem?.source_type?.toUpperCase() || 'infrastruktur'} terlebih dulu, confidence masih ${(effectiveConfidenceScore >= 0.72 ? 'Tinggi' : effectiveConfidenceScore >= 0.48 ? 'Sedang' : 'Rendah')}.`,
          reason_all: [
            ...(Array.isArray(normalizedItem.reason_all) ? normalizedItem.reason_all : []),
            'Confidence belum cukup kuat untuk aksi agresif.',
          ].slice(0, 4),
        };
      }

      const cooldownUntil = Number(guardEntry?.cooldownUntil || 0);
      const hasCooldown = Number.isFinite(cooldownUntil) && cooldownUntil > now;
      const resurfaced = shouldResurfaceRecommendation(guardEntry, normalizedItem);
      const throttleWindowMs = feedbackType === 'negative' ? 10 * 60 * 1000 : 8 * 60 * 1000;
      const justShownRecently = Number.isFinite(Number(guardEntry?.lastShownAt || 0)) && (now - Number(guardEntry?.lastShownAt || 0) < throttleWindowMs);
      const currentUrgencyRank = ACTION_QUEUE_URGENCY_RANK[urgency] || 1;
      const lowUrgencyThrottle = currentUrgencyRank <= 2 && justShownRecently && !resurfaced;

      if ((hasCooldown || lowUrgencyThrottle) && !resurfaced) {
        return;
      }

      guarded.push({
        ...normalizedItem,
        confidenceScore: Number(effectiveConfidenceScore.toFixed(2)),
        confidenceLabel: effectiveConfidenceScore >= 0.72 ? 'Tinggi' : effectiveConfidenceScore >= 0.48 ? 'Sedang' : 'Rendah',
        guardKey,
        isNew: !Number(guardEntry?.lastShownAt || 0),
        isUpdated: Boolean(guardEntry?.lastShownAt) && resurfaced,
        feedbackType: feedbackType || null,
        feedbackAt: Number(feedbackEntry?.feedbackAt || 0) || null,
        feedbackBy: feedbackEntry?.feedbackBy || null,
        hold_flag: Boolean(holdEntry?.hold_flag),
        hold_reason: holdEntry?.hold_reason || null,
      });
    });

    return guarded;
  }, [actionQueueAutoHoldById, actionQueueFeedbackById, aiMonitoringResult, appendAutoActionLifecycleLog, capacityRecommendations?.recommendations, filteredCustomers, incidentTicketSummary, pendingPayments, recommendationGuardById]);

  useEffect(() => {
    if (!Array.isArray(actionQueueItemsRaw) || actionQueueItemsRaw.length === 0) return;
    const nowIso = new Date().toISOString();
    const nowMs = Date.now();
    setActionQueueCreatedAtById((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      actionQueueItemsRaw.forEach((item) => {
        const id = String(item?.id || '').trim();
        if (!id || next[id]) return;
        next[id] = String(item?.created_at || nowIso);
        changed = true;
      });
      return changed ? next : prev;
    });
    setActionQueueStateById((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      actionQueueItemsRaw.forEach((item) => {
        const id = String(item?.id || '').trim();
        if (!id || next[id]) return;
        const initialStatusRaw = String(item?.status || (item?.isAutoAction ? 'pending_review' : 'pending')).trim().toLowerCase();
        next[id] = ACTION_QUEUE_STATUS_OPTIONS.includes(initialStatusRaw) ? initialStatusRaw : (item?.isAutoAction ? 'pending_review' : 'pending');
        changed = true;
      });
      return changed ? next : prev;
    });
    setRecommendationGuardById((prev) => {
      const next = { ...(prev || {}) };
      let changed = false;
      actionQueueItemsRaw.forEach((item) => {
        const key = String(item?.guardKey || item?.id || '').trim();
        if (!key) return;
        if (!item?.isNew && !item?.isUpdated) return;
        const existing = next[key] && typeof next[key] === 'object' ? next[key] : {};
        const nextSnapshot = {
          ...existing,
          lastShownAt: nowMs,
          lastUrgencyRank: ACTION_QUEUE_URGENCY_RANK[normalizeActionQueueUrgency(item?.urgency)] || 1,
          lastEta: Number.isFinite(Number(item?.metrics?.daysToFull)) ? Number(item.metrics.daysToFull) : null,
          lastOccupancy: Number.isFinite(Number(item?.metrics?.occupancyPercent)) ? Number(item.metrics.occupancyPercent) : null,
          lastGrowth: Number.isFinite(Number(item?.metrics?.growth30d)) ? Number(item.metrics.growth30d) : null,
          lastConfidence: Number(item?.confidenceScore || 0),
          lastOutcome: String(actionQueueStateById[String(item?.id || '').trim()] || 'pending'),
        };
        const before = JSON.stringify(existing);
        const after = JSON.stringify(nextSnapshot);
        if (before !== after) {
          next[key] = nextSnapshot;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
    actionQueueItemsRaw.forEach((item) => {
      logRecommendationEvent(item, 'generated', { queueStatus: String(actionQueueStateById[String(item?.id || '').trim()] || 'pending') });
      logRecommendationEvent(item, item?.isUpdated ? 're_shown' : 'shown', { queueStatus: String(actionQueueStateById[String(item?.id || '').trim()] || 'pending') });
    });
  }, [actionQueueItemsRaw, actionQueueStateById, logRecommendationEvent]);

  useEffect(() => {
    const suppressed = Array.isArray(capacityRecommendations?.suppressed) ? capacityRecommendations.suppressed : [];
    if (suppressed.length === 0) return;
    suppressed.forEach((entry) => {
      const sourceType = normalizeIncidentSourceType(entry?.sourceType || 'topology_node');
      const sourceId = String(entry?.sourceId || '').trim();
      const actionType = normalizeActionQueueActionType(entry?.actionType || 'monitor');
      const fallbackId = String(entry?.id || `${sourceType}:${sourceId || 'scope'}:${actionType}`).trim();
      logRecommendationEvent({
        id: fallbackId,
        source_type: sourceType,
        source_id: sourceId,
        action_type: actionType,
        urgency: 'rendah',
        confidenceScore: Number(entry?.confidenceScore || 0),
        metrics: {},
      }, 'suppressed', { reason: String(entry?.reason || 'guard_suppressed').trim() || 'guard_suppressed' });
    });
  }, [capacityRecommendations?.suppressed, logRecommendationEvent]);

  const adaptivePatternStatsByKey = useMemo(() => {
    const nowMs = Date.now();
    const statsByKey = {};
    const records = Object.values(recommendationEvalById || {});
    records.forEach((record) => {
      if (!record || typeof record !== 'object') return;
      const actionType = normalizeActionQueueActionType(record?.actionType || 'monitor');
      const sourceType = normalizeIncidentSourceType(record?.sourceType || 'topology_node');
      const rootCauseType = normalizeIncidentSourceType(record?.sourceType || 'topology_node');
      const urgency = normalizeActionQueueUrgency(record?.urgency || 'rendah');
      const riskBand = getRiskBandFromUrgency(urgency);
      const confidencePct = Number(record?.confidence || 0) * 100;
      const confidenceBand = getConfidenceBand(confidencePct);
      const patternKey = buildDecisionPatternKey({
        actionType,
        sourceType,
        rootCauseType,
        riskBand,
        confidenceBand,
      });
      if (!statsByKey[patternKey]) {
        statsByKey[patternKey] = {
          pattern_key: patternKey,
          action_type: actionType,
          source_type: sourceType,
          root_cause_type: rootCauseType,
          risk_band: riskBand,
          confidence_band: confidenceBand,
          sample_count: 0,
          helpful_count: 0,
          not_relevant_count: 0,
          accepted_count: 0,
          completed_count: 0,
          skipped_count: 0,
          held_count: 0,
          failed_count: 0,
          avg_accept_ms: null,
          adaptive_modifier: 0,
          updated_at: null,
          weighted_sample_count: 0,
          helpful_weighted: 0,
          not_relevant_weighted: 0,
          accepted_weighted: 0,
          completed_weighted: 0,
          skipped_weighted: 0,
          held_weighted: 0,
          failed_weighted: 0,
          _accept_delay_weighted_sum: 0,
          _accept_delay_weighted_count: 0,
        };
      }
      const stats = statsByKey[patternKey];
      const events = Array.isArray(record?.events) ? record.events : [];
      const seenTypes = new Set();
      events.forEach((event) => {
        const rawType = String(event?.type || '').trim().toLowerCase();
        const type = normalizeDecisionEventType(rawType);
        if (!type) return;
        const atMs = Number(event?.atMs || Date.parse(String(event?.at || '')) || 0);
        const ageDays = atMs > 0 ? Math.max(0, (nowMs - atMs) / (24 * 60 * 60 * 1000)) : 45;
        const decayWeight = Math.max(0.15, Math.exp(-ageDays / 30));
        if (['accepted', 'completed', 'ignored', 'held', 'failed', 'feedback_positive', 'feedback_negative'].includes(type)) {
          stats.sample_count += 1;
          stats.weighted_sample_count += decayWeight;
        }
        if (type === 'feedback_positive') {
          stats.helpful_count += 1;
          stats.helpful_weighted += decayWeight;
        }
        if (type === 'feedback_negative') {
          stats.not_relevant_count += 1;
          stats.not_relevant_weighted += decayWeight;
        }
        if (type === 'accepted') {
          stats.accepted_count += 1;
          stats.accepted_weighted += decayWeight;
        }
        if (type === 'completed') {
          stats.completed_count += 1;
          stats.completed_weighted += decayWeight;
        }
        if (type === 'ignored') {
          stats.skipped_count += 1;
          stats.skipped_weighted += decayWeight;
        }
        if (type === 'held') {
          stats.held_count += 1;
          stats.held_weighted += decayWeight;
        }
        if (type === 'failed') {
          stats.failed_count += 1;
          stats.failed_weighted += decayWeight;
        }
        seenTypes.add(type);
      });

      if (!events.length) {
        const queueType = normalizeDecisionEventType(record?.queueStatus || '');
        if (queueType && !seenTypes.has(queueType)) {
          stats.sample_count += 1;
          stats.weighted_sample_count += 0.35;
          if (queueType === 'accepted') {
            stats.accepted_count += 1;
            stats.accepted_weighted += 0.35;
          }
          if (queueType === 'completed') {
            stats.completed_count += 1;
            stats.completed_weighted += 0.35;
          }
          if (queueType === 'ignored') {
            stats.skipped_count += 1;
            stats.skipped_weighted += 0.35;
          }
          if (queueType === 'held') {
            stats.held_count += 1;
            stats.held_weighted += 0.35;
          }
          if (queueType === 'failed') {
            stats.failed_count += 1;
            stats.failed_weighted += 0.35;
          }
        }
      }
      const acceptMs = Number(record?.inferredSignals?.acceptDelayMs || 0);
      if (Number.isFinite(acceptMs) && acceptMs > 0) {
        stats._accept_delay_weighted_sum += acceptMs;
        stats._accept_delay_weighted_count += 1;
      }
      const updatedAt = Date.parse(String(record?.lastShownAt || record?.feedbackAt || record?.completedAt || record?.acceptedAt || record?.createdAt || ''));
      if (Number.isFinite(updatedAt) && updatedAt > 0) {
        const currentUpdated = Date.parse(String(stats.updated_at || ''));
        if (!Number.isFinite(currentUpdated) || updatedAt > currentUpdated) {
          stats.updated_at = new Date(updatedAt).toISOString();
        }
      }
    });
    Object.values(statsByKey).forEach((stats) => {
      const adaptive = computeAdaptiveModifierFromPatternStats(stats);
      stats.adaptive_modifier = Number(adaptive.modifier || 0);
      stats.adaptive_reason = adaptive.reason;
      stats.avg_accept_ms = stats._accept_delay_weighted_count > 0
        ? Math.round(stats._accept_delay_weighted_sum / stats._accept_delay_weighted_count)
        : null;
      delete stats._accept_delay_weighted_sum;
      delete stats._accept_delay_weighted_count;
    });
    return statsByKey;
  }, [recommendationEvalById]);
  const adaptivePatternStatsSummary = useMemo(() => {
    const list = Object.values(adaptivePatternStatsByKey || {});
    const sorted = list.slice().sort((a, b) => Number(b?.adaptive_modifier || 0) - Number(a?.adaptive_modifier || 0));
    const positive = sorted.find((item) => Number(item?.adaptive_modifier || 0) > 0) || null;
    const negative = sorted.slice().reverse().find((item) => Number(item?.adaptive_modifier || 0) < 0) || null;
    const lowSample = list.find((item) => Number(item?.sample_count || 0) < 5) || null;
    return {
      count: list.length,
      positive,
      negative,
      lowSample,
    };
  }, [adaptivePatternStatsByKey]);

  const actionQueueItems = useMemo(() => {
    const monitorRisk = Number(aiMonitoringResult?.risk || 0);
    const anomalyCount = Number(aiMonitoringResult?.anomaly?.offline_count || 0);
    const aiRootCause = aiMonitoringResult?.root_cause && typeof aiMonitoringResult.root_cause === 'object' ? aiMonitoringResult.root_cause : {};
    const rootCauseType = aiRootCause?.odp
      ? 'odp'
      : aiRootCause?.olt
        ? 'olt'
        : aiRootCause?.nas
          ? 'nas'
          : aiRootCause?.cluster
            ? 'anomaly_cluster'
            : 'topology_node';
    const hasNetworkPressure = monitorRisk >= 45 || anomalyCount > 0 || Number(incidentTicketSummary?.active || 0) > 0;
    const hasBillingPressure = Number((Array.isArray(pendingPayments) ? pendingPayments : []).length || 0) > 0;
    const monitorContext = {
      domain: hasNetworkPressure ? 'network' : (hasBillingPressure ? 'billing' : 'mixed'),
      risk: monitorRisk,
      anomalyCount,
      rootCauseType,
    };
    const decorated = (Array.isArray(actionQueueItemsRaw) ? actionQueueItemsRaw : []).map((item) => {
      const id = String(item?.id || '').trim();
      const urgency = normalizeActionQueueUrgency(item?.urgency);
      const createdAt = String(actionQueueCreatedAtById[id] || item?.created_at || new Date().toISOString());
      const statusRaw = String(actionQueueStateById[id] || item?.status || (item?.isAutoAction ? 'pending_review' : 'pending')).trim().toLowerCase();
      const status = ACTION_QUEUE_STATUS_OPTIONS.includes(statusRaw) ? statusRaw : (item?.isAutoAction ? 'pending_review' : 'pending');
      const ageWeight = getAgingWeight(createdAt, status);
      const feedbackEntry = actionQueueFeedbackById[id] && typeof actionQueueFeedbackById[id] === 'object'
        ? actionQueueFeedbackById[id]
        : null;
      return {
        ...item,
        id,
        urgency,
        action_type: normalizeActionQueueActionType(item?.action_type),
        action_class: classifyActionCandidate(item),
        status,
        created_at: createdAt,
        ageLabel: formatActionQueueAge(createdAt),
        ageWeight,
        feedbackType: String(feedbackEntry?.feedbackType || item?.feedbackType || '').trim().toLowerCase() || null,
        feedbackAt: Number(feedbackEntry?.feedbackAt || item?.feedbackAt || 0) || null,
        feedbackBy: feedbackEntry?.feedbackBy || item?.feedbackBy || null,
        guardKey: String(item?.guardKey || item?.duplicate_key || id),
        action_mode: normalizeAutoActionMode(item?.action_mode || 'manual_review'),
      };
    });

    const mergedBySource = {};
    decorated.forEach((item) => {
      const sourceKey = item?.isAutoAction
        ? `auto:${String(item?.id || '')}`
        : `${String(item?.source_type || 'unknown')}:${String(item?.source_id || '')}`;
      if (!sourceKey) return;
      if (!Array.isArray(mergedBySource[sourceKey])) mergedBySource[sourceKey] = [];
      mergedBySource[sourceKey].push(item);
    });
    const merged = Object.values(mergedBySource).map((bucket) => {
      if (!Array.isArray(bucket) || bucket.length === 0) return null;
      if (bucket.length === 1) return bucket[0];
      const sorted = bucket.slice().sort((a, b) => {
        const urgencyDiff = (ACTION_QUEUE_URGENCY_RANK[normalizeActionQueueUrgency(b?.urgency)] || 0) - (ACTION_QUEUE_URGENCY_RANK[normalizeActionQueueUrgency(a?.urgency)] || 0);
        if (urgencyDiff !== 0) return urgencyDiff;
        return Number(b?.impact || 0) - Number(a?.impact || 0);
      });
      const primary = sorted[0];
      const mergedReasons = Array.from(new Set(
        bucket.flatMap((item) => (Array.isArray(item?.reason_all) ? item.reason_all : [item?.reason]).filter(Boolean)),
      )).slice(0, 4);
      const statusPriority = ['pending_review', 'pending', 'approved', 'accepted', 'executed', 'completed', 'failed', 'skipped', 'ignored', 'cancelled'];
      const dominantStatus = statusPriority.find((statusKey) => bucket.some((item) => item.status === statusKey)) || 'pending';
      return {
        ...primary,
        action_type: getDominantActionType(bucket),
        status: dominantStatus,
        mergedCount: bucket.length,
        reason_all: mergedReasons,
        reason: mergedReasons[0] || primary.reason,
        impact: Math.max(...bucket.map((item) => Number(item?.impact || 0))),
        ageWeight: Math.max(...bucket.map((item) => Number(item?.ageWeight || 0))),
        isAutoAction: bucket.some((item) => item.isAutoAction),
        rule_key: primary?.rule_key || null,
        action_mode: normalizeAutoActionMode(primary?.action_mode || 'manual_review'),
      };
    }).filter(Boolean);

    const filtered = actionQueueShowLowUrgency
      ? merged
      : merged.filter((item) => item.urgency !== 'rendah' || item.status !== 'pending');

    const statusFiltered = actionQueueShowCompleted
      ? filtered
      : filtered.filter((item) => !['completed', 'executed', 'failed', 'cancelled'].includes(String(item?.status || '').toLowerCase()));

    const scored = statusFiltered.map((item) => {
      const history = recommendationEvalById[String(item?.id || '').trim()]
        || recommendationEvalById[String(item?.guardKey || '').trim()]
        || null;
      const decision = buildDecisionScorePayload({ item, monitorContext, history });
      const riskBand = getRiskBandFromUrgency(item?.urgency);
      const confidenceBand = getConfidenceBand(decision.confidenceScore);
      const patternKey = buildDecisionPatternKey({
        actionType: item?.action_type,
        sourceType: item?.source_type,
        rootCauseType: item?.source_type || monitorContext?.rootCauseType,
        riskBand,
        confidenceBand,
      });
      const patternStats = adaptivePatternStatsByKey[patternKey] || null;
      const adaptive = computeAdaptiveModifierFromPatternStats(patternStats);
      const adaptiveModifier = Number(adaptive.modifier || 0);
      let finalScore = clampScore100(Number(decision.baseScore || 0) + adaptiveModifier);
      if (decision.actionClass === 'disruptive' && decision.confidenceScore < 55) {
        finalScore = Math.min(finalScore, 49);
      }
      const finalDecisionLabel = getDecisionLabelFromScore(finalScore);
      const adaptiveReason = String(adaptive.reason || 'No adaptive adjustment due to low sample count.').trim();
      const combinedDecisionReason = `${decision.decisionReason} ${adaptiveReason}`.trim();
      return {
        ...item,
        action_class: decision.actionClass,
        decision_score: Number(finalScore.toFixed(1)),
        decision_label: finalDecisionLabel,
        decision_reason: combinedDecisionReason,
        base_score: Number(decision.baseScore || 0),
        adaptive_modifier: Number(adaptiveModifier.toFixed(1)),
        final_score: Number(finalScore.toFixed(1)),
        adaptive_reason: adaptiveReason,
        adaptive_sample_count: Number(patternStats?.sample_count || 0),
        decision_pattern_key: patternKey,
        decision_factors: {
          severity_score: decision.severityScore,
          confidence_score: decision.confidenceScore,
          impact_score: decision.impactScore,
          urgency_score: decision.urgencyScore,
          history_score: decision.historyScore,
          action_risk_score: decision.actionRiskScore,
          context_mismatch_penalty: decision.contextMismatchPenalty,
          safety_bias: decision.lowConfidenceSafetyBias,
          disruptive_penalty: decision.disruptiveLowConfidencePenalty,
          duplicate_penalty: decision.duplicateSuppressionPenalty,
          adaptive_modifier: Number(adaptiveModifier.toFixed(1)),
          base_score: Number(decision.baseScore || 0),
          final_score: Number(finalScore.toFixed(1)),
        },
      };
    });

    return scored
      .sort((a, b) => {
        const decisionDiff = Number(b?.decision_score || 0) - Number(a?.decision_score || 0);
        if (decisionDiff !== 0) return decisionDiff;
        const urgencyDiff = (ACTION_QUEUE_URGENCY_RANK[b.urgency] || 0) - (ACTION_QUEUE_URGENCY_RANK[a.urgency] || 0);
        if (urgencyDiff !== 0) return urgencyDiff;
        const impactDiff = Number(b?.impact || 0) - Number(a?.impact || 0);
        if (impactDiff !== 0) return impactDiff;
        const ageWeightDiff = Number(b?.ageWeight || 0) - Number(a?.ageWeight || 0);
        if (ageWeightDiff !== 0) return ageWeightDiff;
        const olderFirstDiff = Date.parse(String(a.created_at || '')) - Date.parse(String(b.created_at || ''));
        if (Number.isFinite(olderFirstDiff) && olderFirstDiff !== 0) return olderFirstDiff;
        return 0;
      })
      .slice(0, ACTION_QUEUE_LIMIT)
      .map((item, index) => ({
        ...item,
        decision_rank: index + 1,
      }));
  }, [actionQueueCreatedAtById, actionQueueFeedbackById, actionQueueItemsRaw, actionQueueShowCompleted, actionQueueShowLowUrgency, actionQueueStateById, aiMonitoringResult?.anomaly?.offline_count, aiMonitoringResult?.risk, aiMonitoringResult?.root_cause, incidentTicketSummary?.active, pendingPayments, recommendationEvalById, adaptivePatternStatsByKey]);

  const actionQueueSummary = useMemo(() => {
    return actionQueueItems.reduce((acc, item) => {
      acc.total += 1;
      if (item.status === 'pending' || item.status === 'pending_review') acc.pending += 1;
      else if (item.status === 'accepted' || item.status === 'approved') acc.accepted += 1;
      else if (item.status === 'completed' || item.status === 'executed') acc.completed += 1;
      else if (item.status === 'ignored' || item.status === 'skipped' || item.status === 'cancelled') acc.ignored += 1;
      else if (item.status === 'failed') acc.failed += 1;
      if (item.urgency === 'kritis') acc.critical += 1;
      if (item.urgency === 'tinggi') acc.high += 1;
      if (item.urgency === 'sedang') acc.medium += 1;
      if (item.urgency === 'rendah') acc.low += 1;
      return acc;
    }, {
      total: 0, pending: 0, accepted: 0, completed: 0, ignored: 0, failed: 0, critical: 0, high: 0, medium: 0, low: 0,
    });
  }, [actionQueueItems]);
  const actionQueueGrouped = useMemo(() => {
    const grouped = { kritis: [], tinggi: [], sedang: [], rendah: [] };
    actionQueueItems.forEach((item) => {
      const urgency = normalizeActionQueueUrgency(item?.urgency);
      if (!Array.isArray(grouped[urgency])) grouped[urgency] = [];
      grouped[urgency].push(item);
    });
    return grouped;
  }, [actionQueueItems]);
  const recommendationGuardSummary = useMemo(() => {
    const now = Date.now();
    let coolingDown = 0;
    Object.values(recommendationGuardById || {}).forEach((entry) => {
      const until = Number(entry?.cooldownUntil || 0);
      if (Number.isFinite(until) && until > now) coolingDown += 1;
    });
    return {
      suppressedLowConfidence: Array.isArray(capacityRecommendations?.suppressed) ? capacityRecommendations.suppressed.length : 0,
      coolingDown,
    };
  }, [capacityRecommendations?.suppressed, recommendationGuardById]);
  const aiNodeInsights = useMemo(() => {
    const byId = {};
    let totalAnomaly = 0;
    let latencyHigh = 0;
    let signalWeak = 0;
    let offline = 0;
    let flapping = 0;
    let predictiveCount = 0;
    let packetLossHigh = 0;
    let normalLevel = 0;
    let warningLevel = 0;
    let criticalLevel = 0;
    let totalScore = 0;
    let mostCriticalNode = null;
    let mostCriticalScore = 999;

    filteredCustomersByMode.forEach((customer) => {
      const anomaly = detectAnomaly(customer);
      const predictions = predictIssue(customer);
      const hasPredictive = predictions.length > 0;
      const score = Number(anomaly?.aiScore?.score ?? 100);
      const level = String(anomaly?.aiScore?.level || 'normal');
      const history = Array.isArray(nodeHistoryById[String(customer.id || '')]) ? nodeHistoryById[String(customer.id || '')] : [];
      const trend = analyzeNodeTrend(history);
      const risk = computeRiskLevel({ anomaly, trend, score });
      const prediction = buildPrediction({
        risk,
        anomaly,
        trend,
        score,
        customerName: customer?.name,
      });

      if (score < mostCriticalScore && hasValidCoordinates(customer.latitude, customer.longitude)) {
        mostCriticalScore = score;
        mostCriticalNode = customer;
      }

      if (anomaly.hasAnomaly) totalAnomaly += 1;
      if (anomaly.issues.some((item) => item.code === 'latency_high')) latencyHigh += 1;
      if (anomaly.issues.some((item) => item.code === 'signal_weak')) signalWeak += 1;
      if (anomaly.issues.some((item) => item.code === 'offline')) offline += 1;
      if (anomaly.issues.some((item) => item.code === 'status_flapping')) flapping += 1;
      if (anomaly.issues.some((item) => item.code === 'packet_loss')) packetLossHigh += 1;
      if (hasPredictive) predictiveCount += 1;
      totalScore += score;
      if (level === 'critical') criticalLevel += 1;
      else if (level === 'warning') warningLevel += 1;
      else normalLevel += 1;

      byId[String(customer.id || '')] = {
        anomaly,
        predictions,
        hasPredictive,
        score,
        level,
        trend,
        risk,
        prediction,
        history,
        aiSummary: `Skor ${score}/100 (${level}) â€¢ Trend: ${trend.trendLabel} â€¢ Risk: ${risk}`,
      };
    });

    const avgScore = filteredCustomersByMode.length > 0 ? Math.round(totalScore / filteredCustomersByMode.length) : 100;

    return {
      byId,
      summary: {
        totalAnomaly,
        latencyHigh,
        signalWeak,
        offline,
        flapping,
        predictiveCount,
        packetLossHigh,
        normalLevel,
        warningLevel,
        criticalLevel,
        avgScore,
      },
      mostCriticalNode,
    };
  }, [filteredCustomersByMode, nodeHistoryById]);

  const anomalyByOdpId = useMemo(() => {
    const mapped = {};
    filteredCustomersByMode.forEach((customer) => {
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      const insight = aiNodeInsights.byId[String(customer.id || '')];
      if (!insight?.anomaly?.hasAnomaly) return;
      mapped[odpId] = true;
    });
    return mapped;
  }, [filteredCustomersByMode, resolveFilteredOdp, aiNodeInsights.byId]);

  const aiLevelByOdpId = useMemo(() => {
    const mapped = {};
    filteredCustomersByMode.forEach((customer) => {
      const odp = resolveFilteredOdp(customer);
      const odpId = normalizeNodeId(odp?.id);
      if (!odpId) return;
      const level = String(aiNodeInsights.byId[String(customer.id || '')]?.level || 'normal');
      const current = mapped[odpId] || 'normal';
      if (level === 'critical' || current === 'critical') {
        mapped[odpId] = 'critical';
      } else if (level === 'warning' || current === 'warning') {
        mapped[odpId] = 'warning';
      } else {
        mapped[odpId] = 'normal';
      }
    });
    return mapped;
  }, [filteredCustomersByMode, resolveFilteredOdp, aiNodeInsights.byId]);

  const anomalyByOltId = useMemo(() => {
    const mapped = {};
    filteredCustomersByMode.forEach((customer) => {
      const oltId = resolveCustomerOltId(customer);
      if (!oltId) return;
      const insight = aiNodeInsights.byId[String(customer.id || '')];
      if (!insight?.anomaly?.hasAnomaly) return;
      mapped[normalizeNodeId(oltId)] = true;
    });
    return mapped;
  }, [filteredCustomersByMode, resolveCustomerOltId, aiNodeInsights.byId]);

  const aiLevelByOltId = useMemo(() => {
    const mapped = {};
    filteredCustomersByMode.forEach((customer) => {
      const oltId = normalizeNodeId(resolveCustomerOltId(customer));
      if (!oltId) return;
      const level = String(aiNodeInsights.byId[String(customer.id || '')]?.level || 'normal');
      const current = mapped[oltId] || 'normal';
      if (level === 'critical' || current === 'critical') {
        mapped[oltId] = 'critical';
      } else if (level === 'warning' || current === 'warning') {
        mapped[oltId] = 'warning';
      } else {
        mapped[oltId] = 'normal';
      }
    });
    return mapped;
  }, [filteredCustomersByMode, resolveCustomerOltId, aiNodeInsights.byId]);

  const rootCauseOdpId = useMemo(() => {
    const raw = String(aiMonitoringResult?.root_cause?.odp || '').trim();
    if (!raw) return '';
    const normalizedRaw = normalizeNodeId(raw);
    let matchId = '';
    filteredOdps.some((odp) => {
      const id = normalizeNodeId(odp?.id);
      const name = String(odp?.name || '').trim().toLowerCase();
      const code = String(odp?.code || '').trim().toLowerCase();
      if (
        (normalizedRaw && normalizedRaw === id)
        || raw.toLowerCase() === name
        || raw.toLowerCase() === code
      ) {
        matchId = id;
        return true;
      }
      return false;
    });
    return matchId;
  }, [aiMonitoringResult?.root_cause?.odp, filteredOdps]);

  const visibleCoverageOdpIds = useMemo(() => {
    const ids = new Set();
    if (aiModeEnabled) {
      Object.entries(anomalyByOdpId).forEach(([id, value]) => {
        if (value) ids.add(String(id));
      });
    }
    if (hoveredNode?.type === 'odp' && hoveredNode?.id) ids.add(String(hoveredNode.id));
    if (hoveredNode?.type === 'customer' && hoveredNode?.id) {
      const customer = filteredCustomersByMode.find((item) => String(item?.id || '') === String(hoveredNode.id));
      const odpId = normalizeNodeId(resolveFilteredOdp(customer)?.id);
      if (odpId) ids.add(odpId);
    }
    return ids;
  }, [aiModeEnabled, anomalyByOdpId, hoveredNode, filteredCustomersByMode, resolveFilteredOdp]);

  const aiIssueNodes = useMemo(() => {
    return filteredCustomersByMode
      .map((customer) => ({
        customer,
        insight: aiNodeInsights.byId[String(customer.id || '')],
      }))
      .filter((item) => item.insight?.anomaly?.hasAnomaly)
      .sort((left, right) => {
        const leftScore = Number(left?.insight?.score ?? 100);
        const rightScore = Number(right?.insight?.score ?? 100);
        return leftScore - rightScore;
      })
      .slice(0, 5);
  }, [filteredCustomersByMode, aiNodeInsights.byId]);

  const networkScoreSparklinePath = useMemo(() => {
    const values = (Array.isArray(networkHistory) ? networkHistory : []).map((row) => Number(row?.avgScore));
    return buildSparklinePath(values, 220, 56, 5);
  }, [networkHistory]);

  const networkLatencySparklinePath = useMemo(() => {
    const values = (Array.isArray(networkHistory) ? networkHistory : []).map((row) => Number(row?.avgLatency));
    return buildSparklinePath(values, 220, 56, 5);
  }, [networkHistory]);

  const networkTrendState = useMemo(() => {
    const records = Array.isArray(networkHistory) ? networkHistory : [];
    if (records.length < 3) {
      return { scoreTrend: 'stable', latencyTrend: 'stable', label: 'Data trend belum cukup' };
    }
    const first = records[Math.max(0, records.length - 12)];
    const latest = records[records.length - 1];
    const scoreTrend = resolveTrendDirection(Number(first?.avgScore), Number(latest?.avgScore), 2);
    const latencyTrend = resolveTrendDirection(Number(first?.avgLatency), Number(latest?.avgLatency), 2);
    let label = 'Stabil';
    if (scoreTrend === 'down' || latencyTrend === 'up') label = 'Memburuk';
    if (scoreTrend === 'up' && latencyTrend !== 'up') label = 'Membaik';
    return { scoreTrend, latencyTrend, label };
  }, [networkHistory]);

  const clearFocusNode = useCallback(() => setFocusNode(null), []);

  useEffect(() => () => {
    if (focusPulseTimeoutRef.current) {
      clearTimeout(focusPulseTimeoutRef.current);
      focusPulseTimeoutRef.current = null;
    }
  }, []);

  const isCustomerRelatedToFocus = useCallback((customer) => {
    if (!focusNode?.id) return true;
    const customerId = String(customer?.id || '').trim();
    const customerOdpId = normalizeNodeId(resolveFilteredOdp(customer)?.id || customer?.odp_id);
    const customerOltId = normalizeNodeId(resolveCustomerOltId(customer));
    if (focusNode.type === 'customer') return customerId === focusNode.id;
    if (focusNode.type === 'odp') return customerOdpId === focusNode.id;
    if (focusNode.type === 'olt') return customerOltId === focusNode.id;
    return true;
  }, [focusNode, resolveCustomerOltId, resolveFilteredOdp]);

  const isOdpRelatedToFocus = useCallback((odp) => {
    if (!focusNode?.id) return true;
    const odpId = normalizeNodeId(odp?.id);
    const oltId = normalizeNodeId(odp?.olt_id);
    if (focusNode.type === 'odp') return odpId === focusNode.id;
    if (focusNode.type === 'olt') return oltId === focusNode.id;
    if (focusNode.type === 'customer') {
      const customer = filteredCustomersByMode.find((item) => String(item?.id || '').trim() === focusNode.id);
      if (!customer) return false;
      const linkedOdpId = normalizeNodeId(resolveFilteredOdp(customer)?.id || customer?.odp_id);
      return linkedOdpId === odpId;
    }
    return true;
  }, [filteredCustomersByMode, focusNode, resolveFilteredOdp]);

  const isOltRelatedToFocus = useCallback((olt) => {
    if (!focusNode?.id) return true;
    const oltId = normalizeNodeId(olt?.id);
    if (focusNode.type === 'olt') return oltId === focusNode.id;
    if (focusNode.type === 'odp') {
      const odp = filteredOdps.find((item) => normalizeNodeId(item?.id) === focusNode.id);
      return normalizeNodeId(odp?.olt_id) === oltId;
    }
    if (focusNode.type === 'customer') {
      const customer = filteredCustomersByMode.find((item) => String(item?.id || '').trim() === focusNode.id);
      return normalizeNodeId(resolveCustomerOltId(customer)) === oltId;
    }
    return true;
  }, [filteredCustomersByMode, filteredOdps, focusNode, resolveCustomerOltId]);

  const actionProcessingCustomerSet = useMemo(() => {
    const set = new Set();
    [
      restartingCustomerId,
      isolatingCustomerId,
      creatingTicketCustomerId,
      selfHealingRunningCustomerId,
      optimizingCustomerId,
    ].forEach((value) => {
      const id = String(value || '').trim();
      if (id) set.add(id);
    });
    optimizingCustomerIdSet.forEach((value) => {
      const id = String(value || '').trim();
      if (id) set.add(id);
    });
    return set;
  }, [
    creatingTicketCustomerId,
    isolatingCustomerId,
    optimizingCustomerId,
    optimizingCustomerIdSet,
    restartingCustomerId,
    selfHealingRunningCustomerId,
  ]);

  const liveRecommendedTargets = useMemo(() => {
    const source = Array.isArray(aiActionQueue) ? aiActionQueue : [];
    const customer = new Set();
    const odp = new Set();
    const olt = new Set();
    source.forEach((item) => {
      const status = String(item?.status || '').toLowerCase();
      if (!['detected', 'recommended', 'approved'].includes(status)) return;
      const targetType = String(item?.targetType || '').toLowerCase();
      const targetId = String(item?.targetId || '').trim();
      if (targetType === 'customer' && targetId) customer.add(targetId);
      if (targetType === 'odp' && targetId) odp.add(normalizeNodeId(targetId));
      if (targetType === 'olt' && targetId) olt.add(normalizeNodeId(targetId));
      if (!targetId) {
        const fallbackTarget = resolveActionTarget(item);
        if (fallbackTarget.type === 'customer' && fallbackTarget.id) customer.add(String(fallbackTarget.id));
        if (fallbackTarget.type === 'odp' && fallbackTarget.id) odp.add(normalizeNodeId(fallbackTarget.id));
        if (fallbackTarget.type === 'olt' && fallbackTarget.id) olt.add(normalizeNodeId(fallbackTarget.id));
      }
    });
    return { customer, odp, olt };
  }, [aiActionQueue]);

  const realtimeOverlayNodes = useMemo(() => {
    const nodes = [];
    if (viewMode === 'all' || viewMode === 'olt') {
      filteredOlts.forEach((olt) => {
        if (!hasValidCoordinates(olt.latitude, olt.longitude)) return;
        const oltId = normalizeNodeId(olt.id);
        const aiLevel = String(aiLevelByOltId[oltId] || 'normal');
        const oltFeedbackState = String(actionFeedbackByInfraId[`olt:${oltId}`]?.state || '').toLowerCase();
        const oltRecommended = liveRecommendedTargets.olt.has(oltId) ? AI_ACTION_STATES.RECOMMENDED : '';
        const oltActionState = oltFeedbackState || oltRecommended;
        const statusRaw = String(oltRuntimeStatusById[oltId] || 'unknown').toLowerCase();
        const oltActiveTicket = activeTicketByOltId[oltId] || null;
        const isRootLinkedOlt = aiModeEnabled && !!rootCauseOdpId && filteredOdps.some((item) => normalizeNodeId(item?.olt_id) === oltId && normalizeNodeId(item?.id) === rootCauseOdpId);
        const status = aiModeEnabled && aiLevel === 'critical'
          ? 'critical'
          : (aiModeEnabled && aiLevel === 'warning' ? 'warning' : statusRaw);
        nodes.push({
          id: `olt:${oltId}`,
          rawId: oltId,
          type: 'olt',
          latitude: Number(olt.latitude),
          longitude: Number(olt.longitude),
          status,
          aiHighlight: aiModeEnabled && (aiLevel === 'critical' || aiLevel === 'warning'),
          rootCause: isRootLinkedOlt,
          processing: oltActionState === 'processing' || oltActionState === 'executing' || oltActionState === 'approved',
          actionState: oltActionState,
          ticketActive: Boolean(oltActiveTicket),
          ticketState: oltActiveTicket?.lifecycle?.key || '',
          relatedToFocus: isOltRelatedToFocus(olt),
        });
      });
    }
    if (viewMode === 'all' || viewMode === 'odp') {
      filteredOdps.forEach((odp) => {
        if (!hasValidCoordinates(odp.latitude, odp.longitude)) return;
        const odpId = normalizeNodeId(odp.id);
        const aiLevel = String(aiLevelByOdpId[odpId] || 'normal');
        const odpFeedbackState = String(actionFeedbackByInfraId[`odp:${odpId}`]?.state || '').toLowerCase();
        const odpRecommended = liveRecommendedTargets.odp.has(odpId) ? AI_ACTION_STATES.RECOMMENDED : '';
        const odpActionState = odpFeedbackState || odpRecommended;
        const statusRaw = String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase();
        const odpActiveTicket = activeTicketByOdpId[odpId] || null;
        const isRootCauseOdp = aiModeEnabled && !!rootCauseOdpId && rootCauseOdpId === odpId;
        const status = aiModeEnabled && aiLevel === 'critical'
          ? 'critical'
          : (aiModeEnabled && aiLevel === 'warning' ? 'warning' : statusRaw);
        const position = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
        nodes.push({
          id: `odp:${odpId}`,
          rawId: odpId,
          type: 'odp',
          latitude: Number(position[0]),
          longitude: Number(position[1]),
          status,
          aiHighlight: aiModeEnabled && (aiLevel === 'critical' || aiLevel === 'warning'),
          rootCause: isRootCauseOdp,
          processing: odpActionState === 'processing' || odpActionState === 'executing' || odpActionState === 'approved',
          actionState: odpActionState,
          ticketActive: Boolean(odpActiveTicket),
          ticketState: odpActiveTicket?.lifecycle?.key || '',
          relatedToFocus: isOdpRelatedToFocus(odp),
        });
      });
    }
    if ((viewMode === 'all' || viewMode === 'onu') && !showCustomerClusters) {
      visibleCustomerPoints.forEach((customer) => {
        const customerId = String(customer.id || '').trim();
        const insight = aiNodeInsights.byId[customerId];
        const level = String(insight?.level || 'normal');
        const statusRaw = String(customer.connection_status || 'unknown').toLowerCase();
        const status = aiModeEnabled && level === 'critical'
          ? 'critical'
          : (aiModeEnabled && level === 'warning' ? 'warning' : statusRaw);
        const linkedOdpId = normalizeNodeId(resolveFilteredOdp(customer)?.id);
        const customerActiveTicket = activeTicketByCustomerId[customerId] || null;
        const isRootCauseOnu = aiModeEnabled && !!rootCauseOdpId && linkedOdpId === rootCauseOdpId;
        const recommendedState = liveRecommendedTargets.customer.has(customerId) ? AI_ACTION_STATES.RECOMMENDED : '';
        const runtimeActionState = String(actionFeedbackByCustomerId[customerId]?.state || '').toLowerCase() || recommendedState;
        nodes.push({
          id: `onu:${customerId}`,
          rawId: customerId,
          type: 'onu',
          latitude: Number(customer.latitude),
          longitude: Number(customer.longitude),
          status,
          aiHighlight: aiModeEnabled && (!!insight?.anomaly?.hasAnomaly || !!insight?.hasPredictive),
          rootCause: isRootCauseOnu,
          processing: actionProcessingCustomerSet.has(customerId) || runtimeActionState === 'processing' || runtimeActionState === 'executing' || runtimeActionState === 'approved',
          actionState: runtimeActionState,
          ticketActive: Boolean(customerActiveTicket),
          ticketState: customerActiveTicket?.lifecycle?.key || '',
          relatedToFocus: isCustomerRelatedToFocus(customer),
        });
      });
    }
    return nodes;
  }, [
    actionProcessingCustomerSet,
    actionFeedbackByCustomerId,
    actionFeedbackByInfraId,
    aiLevelByOdpId,
    aiLevelByOltId,
    aiModeEnabled,
    aiNodeInsights.byId,
    filteredOdps,
    filteredOlts,
    getInfraDisplayPosition,
    isCustomerRelatedToFocus,
    isOdpRelatedToFocus,
    isOltRelatedToFocus,
    odpRuntimeStatusById,
    oltRuntimeStatusById,
    liveRecommendedTargets.customer,
    liveRecommendedTargets.odp,
    liveRecommendedTargets.olt,
    showCustomerClusters,
    viewMode,
    visibleCustomerPoints,
    rootCauseOdpId,
    resolveFilteredOdp,
    activeTicketByCustomerId,
    activeTicketByOdpId,
    activeTicketByOltId,
  ]);

  const realtimeOverlayLinks = useMemo(() => {
    const links = [];
    if (viewMode === 'all' || viewMode === 'odp') {
      filteredOdps.forEach((odp) => {
        const olt = odp?.olt_id ? filteredOltsById[odp.olt_id] : null;
        if (!olt) return;
        const oltPosition = getInfraDisplayPosition('olt', olt) || [Number(olt.latitude), Number(olt.longitude)];
        const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
        if (!hasValidCoordinates(oltPosition[0], oltPosition[1]) || !hasValidCoordinates(odpPosition[0], odpPosition[1])) return;
        const odpId = normalizeNodeId(odp.id);
        const oltId = normalizeNodeId(olt.id);
        const oltStatus = String(oltRuntimeStatusById[oltId] || 'unknown').toLowerCase();
        const odpStatus = String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase();
        const infraActionFeedback = String(actionFeedbackByInfraId[`odp:${odpId}`]?.state || actionFeedbackByInfraId[`olt:${oltId}`]?.state || '').toLowerCase();
        const infraRecommended = (liveRecommendedTargets.odp.has(odpId) || liveRecommendedTargets.olt.has(oltId)) ? AI_ACTION_STATES.RECOMMENDED : '';
        const infraActionState = infraActionFeedback || infraRecommended;
        const hasAiAnomaly = aiModeEnabled && (!!anomalyByOdpId[odpId] || !!anomalyByOltId[oltId]);
        const pelangganCount = Number(customerCountByOdpId[odpId] || 0);
        const inferredCoreLoad = Math.min(100, pelangganCount * 12 + (hasAiAnomaly ? 12 : 0));
        const status = infraActionState === 'failed'
          ? 'critical'
          : ((infraActionState === 'processing' || infraActionState === 'executing' || infraActionState === 'recommended' || infraActionState === 'detected')
            ? 'warning'
            : ((aiModeEnabled && hasAiAnomaly) || oltStatus !== 'online' || odpStatus !== 'online'
          ? 'critical'
          : (inferredCoreLoad >= 66 ? 'warning' : 'normal')));
        const relatedToFocus = isOltRelatedToFocus(olt) || isOdpRelatedToFocus(odp);
        links.push({
          id: `core-down-${oltId}-${odpId}`,
          from: `olt:${oltId}`,
          to: `odp:${odpId}`,
          direction: 'downstream',
          status,
          load: inferredCoreLoad,
          aiHighlight: aiModeEnabled && hasAiAnomaly,
          aiRootPath: aiModeEnabled && !!rootCauseOdpId && rootCauseOdpId === odpId,
          aiAffectedPath: aiModeEnabled && (!!rootCauseOdpId && rootCauseOdpId === odpId),
          relatedToFocus,
          processing: infraActionState === 'processing' || infraActionState === 'executing' || infraActionState === 'approved',
          points: buildDistributionPath(oltPosition, odpPosition, `core-${oltId}-${odpId}`),
        });
        links.push({
          id: `core-up-${oltId}-${odpId}`,
          from: `odp:${odpId}`,
          to: `olt:${oltId}`,
          direction: 'upstream',
          status,
          load: Math.max(6, Math.round(inferredCoreLoad * 0.75)),
          aiHighlight: aiModeEnabled && hasAiAnomaly,
          aiRootPath: aiModeEnabled && !!rootCauseOdpId && rootCauseOdpId === odpId,
          aiAffectedPath: aiModeEnabled && (!!rootCauseOdpId && rootCauseOdpId === odpId),
          relatedToFocus,
          processing: infraActionState === 'processing' || infraActionState === 'executing' || infraActionState === 'approved',
          points: buildDistributionPath(odpPosition, oltPosition, `core-up-${oltId}-${odpId}`),
        });
      });
    }
    if ((viewMode === 'all' || viewMode === 'onu') && !showCustomerClusters) {
      visibleCustomerPoints.forEach((customer) => {
        const odp = resolveFilteredOdp(customer);
        if (!odp) return;
        if (!hasValidCoordinates(customer.latitude, customer.longitude) || !hasValidCoordinates(odp.latitude, odp.longitude)) return;
        const customerId = String(customer.id || '').trim();
        const odpId = normalizeNodeId(odp.id);
        const insight = aiNodeInsights.byId[customerId];
        const hasAiAnomaly = aiModeEnabled && String(insight?.level || 'normal') === 'critical';
        const isRootCausePath = aiModeEnabled && !!rootCauseOdpId && rootCauseOdpId === odpId;
        const status = (aiModeEnabled && hasAiAnomaly) || String(customer.connection_status || '').toLowerCase() === 'offline'
          ? 'critical'
          : (String(customer.connection_status || '').toLowerCase() === 'online' ? 'normal' : 'warning');
        const load = estimateCustomerLoad(customer);
        const relatedToFocus = isCustomerRelatedToFocus(customer);
        const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
        const customerPoint = [Number(customer.latitude), Number(customer.longitude)];
        const latency = parseLastInformLatencySeconds(customer?.status_detail?.last_inform);
        const processing = actionProcessingCustomerSet.has(customerId);
        const recommendedState = liveRecommendedTargets.customer.has(customerId) ? AI_ACTION_STATES.RECOMMENDED : '';
        const actionState = String(actionFeedbackByCustomerId[customerId]?.state || '').toLowerCase() || recommendedState;
        const derivedStatus = actionState === 'failed'
          ? 'critical'
          : ((actionState === 'processing' || actionState === 'executing' || actionState === 'recommended' || actionState === 'detected')
            ? 'warning'
            : status);
        links.push({
          id: `dist-down-${odpId}-${customerId}`,
          from: `odp:${odpId}`,
          to: `onu:${customerId}`,
          direction: 'downstream',
          status: derivedStatus,
          load,
          latencySeconds: latency,
          aiHighlight: aiModeEnabled && (hasAiAnomaly || !!insight?.hasPredictive),
          aiRootPath: isRootCausePath,
          aiAffectedPath: isRootCausePath || hasAiAnomaly,
          relatedToFocus,
          processing: processing || actionState === 'executing' || actionState === 'approved',
          points: buildDistributionPath(odpPosition, customerPoint, `dist-${odpId}-${customerId}`),
        });
        links.push({
          id: `dist-up-${odpId}-${customerId}`,
          from: `onu:${customerId}`,
          to: `odp:${odpId}`,
          direction: 'upstream',
          status: derivedStatus,
          load: Math.max(4, Math.round(load * 0.7)),
          latencySeconds: latency,
          aiHighlight: aiModeEnabled && (hasAiAnomaly || !!insight?.hasPredictive),
          aiRootPath: isRootCausePath,
          aiAffectedPath: isRootCausePath || hasAiAnomaly,
          relatedToFocus,
          processing: processing || actionState === 'executing' || actionState === 'approved',
          points: buildDistributionPath(customerPoint, odpPosition, `dist-up-${odpId}-${customerId}`),
        });
      });
    }
    return links;
  }, [
    actionProcessingCustomerSet,
    actionFeedbackByCustomerId,
    aiModeEnabled,
    aiNodeInsights.byId,
    anomalyByOdpId,
    anomalyByOltId,
    actionFeedbackByInfraId,
    liveRecommendedTargets.customer,
    liveRecommendedTargets.odp,
    liveRecommendedTargets.olt,
    rootCauseOdpId,
    customerCountByOdpId,
    filteredOdps,
    filteredOltsById,
    getInfraDisplayPosition,
    isCustomerRelatedToFocus,
    isOdpRelatedToFocus,
    isOltRelatedToFocus,
    odpRuntimeStatusById,
    oltRuntimeStatusById,
    resolveFilteredOdp,
    showCustomerClusters,
    viewMode,
    visibleCustomerPoints,
  ]);

  const realtimeOverlayClusters = useMemo(() => {
    if (!(viewMode === 'all' || viewMode === 'odp' || viewMode === 'onu')) return [];
    const rows = [];
    filteredOdps.forEach((odp) => {
      const odpId = normalizeNodeId(odp?.id);
      const offline = Number(offlineCountByOdpId[odpId] || 0);
      if (offline <= 0) return;
      const position = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
      if (!hasValidCoordinates(position[0], position[1])) return;
      rows.push({
        id: `cluster-${odpId}`,
        latitude: Number(position[0]),
        longitude: Number(position[1]),
        severity: offline,
        count: offline,
        label: `${odp?.name || odpId} - ${offline} offline`,
      });
    });
    return rows;
  }, [filteredOdps, getInfraDisplayPosition, offlineCountByOdpId, viewMode]);

  const realtimeFocusedNodeId = useMemo(() => {
    if (!focusNode?.id) return '';
    const baseId = String(focusNode.id || '').trim();
    if (!baseId) return '';
    if (focusNode.type === 'olt') return `olt:${baseId}`;
    if (focusNode.type === 'odp') return `odp:${baseId}`;
    if (focusNode.type === 'customer') return `onu:${baseId}`;
    return '';
  }, [focusNode]);

  const handleGpuNodeClick = useCallback((node) => {
    const type = String(node?.type || '').toLowerCase();
    const rawId = String(node?.rawId || '').trim();
    if (!rawId) return;
    if (type === 'onu') {
      setSelectedCustomerId(rawId);
      handleFocusNode('customer', rawId);
      return;
    }
    if (type === 'odp') {
      handleFocusNode('odp', rawId);
      return;
    }
    if (type === 'olt') {
      handleFocusNode('olt', rawId);
    }
  }, [handleFocusNode]);

  const handleGpuClusterClick = useCallback(() => {
    setAutoFitEnabled(false);
    setAutoFitKey((value) => value + 1);
  }, []);

  const handleGpuMapError = useCallback(() => {
    if (!forceLegacyMap) {
      setForceLegacyMap(true);
      setGpuStats((prev) => ({ ...prev, webgl: false }));
      toast.error('GPU map gagal dimuat, fallback ke mode kompatibel.');
    }
  }, [forceLegacyMap]);

  const handleGpuStatsChange = useCallback((nextStats) => {
    setGpuStats((previous) => ({
      ...previous,
      ...(nextStats || {}),
    }));
  }, []);

  useEffect(() => {
    if (!aiModeEnabled) return;
    const critical = aiNodeInsights.mostCriticalNode;
    if (!critical?.id) return;
    const nowTs = Date.now();
    const last = lastAiFocusRef.current;
    const sameNode = String(last.nodeId || '') === String(critical.id || '');
    if (sameNode && nowTs - Number(last.at || 0) < 45000) return;
    lastAiFocusRef.current = { nodeId: String(critical.id || ''), at: nowTs };
    setSelectedCustomerId(String(critical.id || ''));
    setAutoFitEnabled(false);
  }, [aiModeEnabled, aiNodeInsights.mostCriticalNode]);

  useEffect(() => {
    if (!selectedCustomerId) return;
    setFocusNode((previous) => {
      const nextId = String(selectedCustomerId || '').trim();
      if (previous?.type === 'customer' && previous?.id === nextId) return previous;
      return { type: 'customer', id: nextId };
    });
  }, [selectedCustomerId]);

  const metricsHealth = systemMetrics?.genieacs_health || null;
  const metricsTransfer = systemMetrics?.transfer_approval || null;
  const metricsLastSource = metricsHealth?.last_source?.source || '-';
  const metricsAvgLatency = Number.isFinite(Number(metricsHealth?.avg_latency_ms)) ? `${Number(metricsHealth?.avg_latency_ms)} ms` : '-';
  const metricsP95Latency = Number.isFinite(Number(metricsHealth?.p95_latency_ms)) ? `${Number(metricsHealth?.p95_latency_ms)} ms` : '-';
  const mapSearchResults = useMemo(() => {
    const key = searchQuery.trim();
    if (key.length < 3) return [];
    return filteredCustomersByMode.slice(0, 12);
  }, [filteredCustomersByMode, searchQuery]);
  const searchHighlightCustomers = useMemo(() => {
    const key = searchQuery.trim();
    if (key.length < 3) return [];
    return filteredCustomersByMode.slice(0, 40).filter((customer) => hasValidCoordinates(customer.latitude, customer.longitude));
  }, [filteredCustomersByMode, searchQuery]);
  const searchHighlightSet = useMemo(() => {
    return new Set(searchHighlightCustomers.map((customer) => String(customer.id || '')));
  }, [searchHighlightCustomers]);

  const warningCount = useMemo(() => {
    return filteredCustomersByMode.filter((customer) => {
      const insight = aiNodeInsights.byId[String(customer?.id || '')];
      return String(insight?.level || '') === 'warning';
    }).length;
  }, [aiNodeInsights.byId, filteredCustomersByMode]);

  const lastAnomalyTimestamp = useMemo(() => {
    const anomalyTimes = filteredCustomersByMode
      .map((customer) => {
        const insight = aiNodeInsights.byId[String(customer?.id || '')];
        if (!insight?.anomaly?.hasAnomaly) return null;
        return Date.parse(customer?.status_detail?.last_inform || customer?.updated_at || '');
      })
      .filter((value) => Number.isFinite(value))
      .sort((a, b) => b - a);
    if (anomalyTimes.length === 0) return null;
    return new Date(anomalyTimes[0]);
  }, [aiNodeInsights.byId, filteredCustomersByMode]);

  const topologyScopeLabel = useMemo(() => {
    const scope = [];
    if (infraFilter !== 'all') scope.push(`Infra: ${infraFilter.toUpperCase()}`);
    if (selectedNasId !== 'all') {
      const nas = nasOptions.find((item) => String(item.router_id) === String(selectedNasId));
      scope.push(`NAS: ${nas?.name || selectedNasId}`);
    }
    if (selectedOltId !== 'all') {
      const olt = filteredOlts.find((item) => normalizeNodeId(item?.id) === normalizeNodeId(selectedOltId));
      scope.push(`OLT: ${olt?.name || selectedOltId}`);
    }
    if (scope.length === 0) return 'Semua topologi aktif';
    return scope.join(' â€¢ ');
  }, [filteredOlts, infraFilter, nasOptions, selectedNasId, selectedOltId]);

  const aiQueueSections = useMemo(() => {
    const list = Array.isArray(aiActionQueue) ? aiActionQueue : [];
    return {
      suggested: list.filter((item) => [AI_ACTION_STATES.DETECTED, AI_ACTION_STATES.RECOMMENDED].includes(item.status)).slice(0, 8),
      executing: list.filter((item) => [AI_ACTION_STATES.APPROVED, AI_ACTION_STATES.EXECUTING].includes(item.status)).slice(0, 8),
      completed: list.filter((item) => AI_ACTION_DONE_STATES.has(item.status)).slice(0, 8),
    };
  }, [aiActionQueue]);

  const aiActionSourceByKey = useMemo(() => {
    const map = {};
    (Array.isArray(aiActionButtons) ? aiActionButtons : []).forEach((action) => {
      map[toActionKey(action)] = action;
    });
    return map;
  }, [aiActionButtons]);

  const replayIncidents = useMemo(() => {
    if (!Array.isArray(realtimeOverlayNodes) || realtimeOverlayNodes.length === 0) return [];

    const normalizedLinks = Array.isArray(realtimeOverlayLinks) ? realtimeOverlayLinks : [];
    const overlayNodeById = {};
    realtimeOverlayNodes.forEach((node) => {
      const id = String(node?.id || '').trim();
      if (!id) return;
      overlayNodeById[id] = node;
    });

    const buildTicketReplay = (ticketRaw, rank = 0) => {
      const ticket = ticketRaw || {};
      const ticketId = String(ticket.id || '').trim();
      if (!ticketId) return null;
      const sourceType = normalizeIncidentSourceType(ticket.sourceType || ticket.source_type || 'topology_node');
      const sourceId = String(ticket.sourceId || ticket.source_id || ticket.topologyNodeId || ticket.topology_node_id || ticket.customerId || ticket.customer_id || '').trim();
      const fallbackNodeId = String(ticket.topologyNodeId || ticket.topology_node_id || '').trim();
      const rootNodeId = sourceId || fallbackNodeId;
      const rootNode = overlayNodeById[rootNodeId] || null;
      const rootLabel = rootNode?.label || rootNode?.id || ticket.source_name || ticket.customer_name || ticket.customer_code || sourceId || ticketId.slice(0, 8);
      const sourceLinkIds = rootNodeId
        ? normalizedLinks
          .filter((link) => String(link?.from || '').includes(rootNodeId) || String(link?.to || '').includes(rootNodeId))
          .map((link) => String(link?.id || '').trim())
          .filter(Boolean)
          .slice(0, 20)
        : [];

      const createdMs = parseTimestampMs(ticket.created_at);
      const approvedMs = parseTimestampMs(ticket.approved_at);
      const completedMs = parseTimestampMs(ticket.completed_at);
      const updatedMs = parseTimestampMs(ticket.updated_at);
      const nowMs = Date.now();
      const startedAt = createdMs || updatedMs || (nowMs - 180000) + (rank * 15000);
      const statusLifecycle = normalizeTicketLifecycle(ticket.status);
      const endedAt = completedMs || (statusLifecycle.key === 'resolved' ? updatedMs : null);
      const durationBase = endedAt && endedAt > startedAt ? (endedAt - startedAt) : Math.max(120000, (updatedMs || nowMs) - startedAt + 45000);
      const durationMs = Math.min(900000, Math.max(90000, durationBase));
      const offset = (value) => Math.max(0, Math.min(durationMs, Number(value || startedAt) - startedAt));

      const events = [];
      events.push({
        id: `incident_start_${ticketId}`,
        type: 'incident_start',
        label: 'Incident dimulai',
        atMs: 0,
        severity: 'warning',
        summary: `Gangguan terdeteksi dari source ${String(sourceType).toUpperCase()} (${rootLabel})`,
      });

      events.push({
        id: `ticket_created_${ticketId}`,
        type: 'ticket_created',
        label: 'Ticket dibuat',
        atMs: Math.min(5000, Math.floor(durationMs * 0.06)),
        severity: 'warning',
        summary: `Ticket #${ticketId.slice(0, 8)} dibuat dari monitoring.`,
        nodePatches: rootNodeId ? [{ id: rootNodeId, status: 'critical', aiHighlight: true, replayRootCause: true }] : [],
        linkPatches: sourceLinkIds.map((id) => ({ id, status: 'warning', aiAffectedPath: true })),
      });

      const markerAnomalyMs = Math.max(1000, Math.floor(durationMs * 0.02));
      events.push({
        id: `anomaly_detected_${ticketId}`,
        type: 'anomaly_detected',
        label: 'Anomaly terdeteksi',
        atMs: markerAnomalyMs,
        severity: 'critical',
        summary: `Anomaly awal pada ${rootLabel}.`,
        nodePatches: rootNodeId ? [{ id: rootNodeId, status: 'critical', aiHighlight: true, replayRootCause: true }] : [],
        linkPatches: sourceLinkIds.map((id) => ({ id, status: 'critical', aiAffectedPath: true, aiRootPath: true })),
      });

      if (approvedMs && approvedMs >= startedAt) {
        events.push({
          id: `in_progress_${ticketId}`,
          type: 'in_progress',
          label: 'Ticket in progress',
          atMs: offset(approvedMs),
          severity: 'warning',
          summary: 'Ticket diambil teknisi dan masuk proses penanganan.',
          nodePatches: rootNodeId ? [{ id: rootNodeId, actionState: 'processing' }] : [],
          linkPatches: sourceLinkIds.slice(0, 8).map((id) => ({ id, status: 'warning', processing: true, aiAffectedPath: true })),
        });
      } else if (statusLifecycle.key === 'in_progress') {
        events.push({
          id: `in_progress_snapshot_${ticketId}`,
          type: 'in_progress',
          label: 'Ticket in progress',
          atMs: Math.floor(durationMs * 0.45),
          severity: 'warning',
          summary: 'Status terakhir menunjukkan ticket sedang diproses.',
          nodePatches: rootNodeId ? [{ id: rootNodeId, actionState: 'processing' }] : [],
        });
      }

      if (statusLifecycle.key === 'escalated') {
        events.push({
          id: `escalated_${ticketId}`,
          type: 'escalated',
          label: 'Ticket escalated',
          atMs: offset(updatedMs || completedMs || (startedAt + Math.floor(durationMs * 0.8))),
          severity: 'critical',
          summary: 'Ticket diekskalasi ke penanganan khusus.',
          nodePatches: rootNodeId ? [{ id: rootNodeId, status: 'critical', actionState: 'failed', aiHighlight: true }] : [],
          linkPatches: sourceLinkIds.map((id) => ({ id, status: 'critical', aiAffectedPath: true })),
        });
      }

      if (statusLifecycle.key === 'resolved') {
        const recoveryAt = completedMs ? Math.max(0, offset(completedMs) - Math.floor(durationMs * 0.08)) : Math.floor(durationMs * 0.78);
        events.push({
          id: `recovery_start_${ticketId}`,
          type: 'recovery_start',
          label: 'Recovery dimulai',
          atMs: recoveryAt,
          severity: 'normal',
          summary: 'Mitigasi mulai berdampak, node menunjukkan pemulihan.',
          nodePatches: rootNodeId ? [{ id: rootNodeId, status: 'warning', actionState: 'succeeded' }] : [],
          linkPatches: sourceLinkIds.map((id) => ({ id, status: 'warning', processing: false })),
        });
        events.push({
          id: `resolved_${ticketId}`,
          type: 'resolved',
          label: 'Ticket resolved',
          atMs: offset(completedMs || updatedMs || (startedAt + durationMs)),
          severity: 'normal',
          summary: 'Ticket ditandai selesai dan layanan kembali stabil.',
          nodePatches: rootNodeId ? [{ id: rootNodeId, status: 'online', aiHighlight: false, actionState: '' }] : [],
          linkPatches: sourceLinkIds.map((id) => ({ id, status: 'normal', aiRootPath: false, aiAffectedPath: false, processing: false })),
        });
      }

      if (statusLifecycle.key === 'on_hold') {
        events.push({
          id: `on_hold_${ticketId}`,
          type: 'on_hold',
          label: 'Ticket on hold',
          atMs: offset(updatedMs || (startedAt + Math.floor(durationMs * 0.6))),
          severity: 'warning',
          summary: 'Ticket dalam status on hold, menunggu tindak lanjut.',
          nodePatches: rootNodeId ? [{ id: rootNodeId, status: 'warning', actionState: '' }] : [],
        });
      }

      events.push({
        id: `incident_end_${ticketId}`,
        type: 'incident_end',
        label: 'Incident selesai',
        atMs: durationMs,
        severity: statusLifecycle.key === 'resolved' ? 'normal' : 'warning',
        summary: statusLifecycle.key === 'resolved'
          ? 'Rekonstruksi insiden selesai (status resolved).'
          : `Rekonstruksi insiden selesai (status terakhir: ${statusLifecycle.label}).`,
      });

      const sortedEvents = events
        .filter((event) => Number.isFinite(Number(event?.atMs)))
        .sort((a, b) => Number(a?.atMs || 0) - Number(b?.atMs || 0));

      return {
        id: `ticket-${ticketId}`,
        ticketId,
        sourceType,
        sourceId,
        title: `Ticket ${ticketId.slice(0, 8)} â€¢ ${rootLabel}`,
        startedAt,
        durationMs,
        events: sortedEvents,
        jumpPoints: createReplayJumpPoints(sortedEvents),
        metadata: {
          rootNodeId,
          sourceLabel: rootLabel,
          lifecycle: statusLifecycle.key,
          lifecycleLabel: statusLifecycle.label,
        },
      };
    };

    const contextTickets = Array.isArray(incidentTicketsByContext) ? incidentTicketsByContext : [];
    const fallbackTickets = Array.isArray(normalizedFieldTickets) ? normalizedFieldTickets.slice(0, 8) : [];
    const prioritizedTickets = (contextTickets.length > 0 ? contextTickets : fallbackTickets)
      .slice()
      .sort((a, b) => {
        const aTime = parseTimestampMs(a?.updated_at) || parseTimestampMs(a?.created_at) || 0;
        const bTime = parseTimestampMs(b?.updated_at) || parseTimestampMs(b?.created_at) || 0;
        return bTime - aTime;
      })
      .slice(0, 4);

    const ticketIncidents = prioritizedTickets
      .map((ticket, index) => buildTicketReplay(ticket, index))
      .filter(Boolean);

    const odpNodes = realtimeOverlayNodes.filter((node) => node.type === 'odp');
    const onuNodes = realtimeOverlayNodes.filter((node) => node.type === 'onu');
    const criticalOnuNodes = onuNodes.filter((node) => ['critical', 'offline'].includes(String(node.status || '').toLowerCase()));
    const warningOnuNodes = onuNodes.filter((node) => String(node.status || '').toLowerCase() === 'warning');
    const rootCandidate = odpNodes.find((node) => String(node.status || '').toLowerCase() === 'critical') || odpNodes[0] || null;
    if (!rootCandidate) return ticketIncidents;

    const rootNodeId = String(rootCandidate.id || '').trim();
    const affectedOnu = criticalOnuNodes.slice(0, 8);
    const affectedOnuIds = affectedOnu.map((node) => String(node.id || '').trim()).filter(Boolean);
    const rootLinkIds = normalizedLinks
      .filter((link) => String(link?.from || '').includes(rootNodeId) || String(link?.to || '').includes(rootNodeId))
      .map((link) => String(link.id || '').trim())
      .filter(Boolean)
      .slice(0, 20);
    const fallbackAction = (Array.isArray(aiActionQueue) ? aiActionQueue : []).find((item) => item?.type) || null;
    const suggestedAction = fallbackAction
      ? {
        type: String(fallbackAction.type || 'OPEN_MONITORING').toUpperCase(),
        label: String(fallbackAction.label || fallbackAction.type || 'Action'),
        targetId: String(fallbackAction.targetId || rootNodeId),
        targetLabel: String(fallbackAction.targetLabel || rootCandidate.id || '-'),
        risk: Number(fallbackAction.risk || aiMonitoringResult?.risk || 0),
        confidence: Number(fallbackAction.confidence || 80),
      }
      : {
        type: 'ASSIGN_TECHNICIAN',
        label: 'Assign Teknisi',
        targetId: rootNodeId,
        targetLabel: rootCandidate.id || '-',
        risk: Number(aiMonitoringResult?.risk || 68),
        confidence: 82,
      };

    const fallbackEvents = [
      {
        id: 'incident_start',
        type: 'incident_start',
        label: 'Incident dimulai',
        atMs: 0,
        severity: 'warning',
        summary: `Gangguan awal terdeteksi pada ${rootCandidate.id || 'cluster utama'}`,
      },
      {
        id: 'anomaly_detected',
        type: 'anomaly_detected',
        label: 'AI deteksi anomaly',
        atMs: 20000,
        severity: 'critical',
        summary: `${Math.max(affectedOnuIds.length, 1)} node ONU terdampak`,
        nodePatches: [
          { id: rootNodeId, status: 'critical', aiHighlight: true, replayRootCause: true },
          ...affectedOnuIds.map((id) => ({ id, status: 'critical', aiHighlight: true })),
        ],
        linkPatches: rootLinkIds.map((id) => ({ id, status: 'critical', aiRootPath: true, aiAffectedPath: true })),
      },
      {
        id: 'root_cause_identified',
        type: 'root_cause_identified',
        label: 'Root cause teridentifikasi',
        atMs: 35000,
        severity: 'critical',
        summary: `Root cause: ${rootCandidate.id || '-'}`,
        nodePatches: [{ id: rootNodeId, status: 'critical', aiHighlight: true, replayRootCause: true }],
        linkPatches: rootLinkIds.map((id) => ({ id, status: 'critical', aiRootPath: true, aiAffectedPath: true })),
      },
      {
        id: 'action_recommended',
        type: 'action_recommended',
        label: 'AI rekomendasikan aksi',
        atMs: 50000,
        severity: 'warning',
        summary: `${suggestedAction.label} untuk target ${suggestedAction.targetLabel}`,
        queueMutation: {
          ...suggestedAction,
          status: AI_ACTION_STATES.RECOMMENDED,
          auto: false,
        },
      },
      {
        id: 'action_executed',
        type: 'action_executed',
        label: 'Aksi dieksekusi',
        atMs: 70000,
        severity: 'warning',
        summary: `${suggestedAction.label} sedang dijalankan`,
        nodePatches: [{ id: String(suggestedAction.targetId || rootNodeId), actionState: 'processing' }],
        linkPatches: rootLinkIds.slice(0, 8).map((id) => ({ id, status: 'warning', processing: true, aiAffectedPath: true })),
        queueMutation: {
          ...suggestedAction,
          status: AI_ACTION_STATES.EXECUTING,
          auto: Boolean(aiMonitoringResult?.auto_mode),
        },
      },
      {
        id: 'recovery_start',
        type: 'recovery_start',
        label: 'Recovery point',
        atMs: 98000,
        severity: 'normal',
        summary: 'Mayoritas node pulih setelah tindakan',
        nodePatches: [
          { id: rootNodeId, status: 'warning', aiHighlight: true, actionState: 'succeeded' },
          ...affectedOnuIds.slice(0, 4).map((id) => ({ id, status: 'online', aiHighlight: false, actionState: 'succeeded' })),
        ],
        linkPatches: rootLinkIds.map((id) => ({ id, status: 'warning', processing: false })),
        queueMutation: {
          ...suggestedAction,
          status: AI_ACTION_STATES.SUCCEEDED,
          auto: Boolean(aiMonitoringResult?.auto_mode),
        },
      },
      {
        id: 'incident_end',
        type: 'incident_end',
        label: 'Incident selesai',
        atMs: 130000,
        severity: 'normal',
        summary: 'Layanan stabil kembali',
        nodePatches: [
          { id: rootNodeId, status: 'online', aiHighlight: false, actionState: '' },
          ...affectedOnuIds.map((id) => ({ id, status: 'online', aiHighlight: false, actionState: '' })),
        ],
        linkPatches: rootLinkIds.map((id) => ({ id, status: 'normal', aiRootPath: false, aiAffectedPath: false, processing: false })),
      },
    ];

    const durationMs = Number(fallbackEvents[fallbackEvents.length - 1]?.atMs || 0) + 20000;
    const lastStamp = parseTimestampMs(lastUpdated) || Date.now();
    const startedAt = lastStamp - durationMs;

    const genericIncident = {
      id: 'incident-latest',
      title: 'Incident Terakhir',
      startedAt,
      durationMs,
      events: fallbackEvents,
      jumpPoints: createReplayJumpPoints(fallbackEvents),
      metadata: {
        totalAffected: Math.max(affectedOnuIds.length, warningOnuNodes.length),
        rootNodeId,
      },
    };

    return [genericIncident, ...ticketIncidents];
  }, [
    aiActionQueue,
    aiMonitoringResult?.auto_mode,
    aiMonitoringResult?.risk,
    incidentTicketsByContext,
    lastUpdated,
    normalizedFieldTickets,
    realtimeOverlayLinks,
    realtimeOverlayNodes,
  ]);

  const activeReplayIncident = useMemo(() => {
    if (isReplayMode && replaySession?.incident && typeof replaySession.incident === 'object') {
      return replaySession.incident;
    }
    const incidents = Array.isArray(replayIncidents) ? replayIncidents : [];
    if (incidents.length === 0) return null;
    return incidents.find((incident) => incident.id === replayIncidentId) || incidents[0];
  }, [isReplayMode, replayIncidentId, replayIncidents, replaySession]);

  const replayRuntime = useMemo(() => {
    if (!isReplayMode || !replaySession || !activeReplayIncident) return null;
    const duration = Number(activeReplayIncident.durationMs || 0);
    const cursor = Math.max(0, Math.min(duration, Number(replayCursorMs || 0)));
    const appliedEvents = (Array.isArray(activeReplayIncident.events) ? activeReplayIncident.events : [])
      .filter((event) => Number(event?.atMs || 0) <= cursor)
      .sort((a, b) => Number(a?.atMs || 0) - Number(b?.atMs || 0));
    const baseNodes = Array.isArray(replaySession.baseNodes) ? replaySession.baseNodes : [];
    const baseLinks = Array.isArray(replaySession.baseLinks) ? replaySession.baseLinks : [];
    const baseQueue = Array.isArray(replaySession.baseQueue) ? replaySession.baseQueue : [];
    const nodeMap = {};
    baseNodes.forEach((node) => { nodeMap[String(node.id || '')] = { ...node }; });
    const linkMap = {};
    baseLinks.forEach((link) => { linkMap[String(link.id || '')] = { ...link }; });
    const queueMap = {};
    baseQueue.forEach((item) => {
      const key = String(item?.key || toActionKey(item));
      queueMap[key] = { ...item, key, id: item?.id || key };
    });
    appliedEvents.forEach((event) => {
      (Array.isArray(event?.nodePatches) ? event.nodePatches : []).forEach((patch) => {
        const id = String(patch?.id || '').trim();
        if (!id || !nodeMap[id]) return;
        nodeMap[id] = { ...nodeMap[id], ...patch };
      });
      (Array.isArray(event?.linkPatches) ? event.linkPatches : []).forEach((patch) => {
        const id = String(patch?.id || '').trim();
        if (!id || !linkMap[id]) return;
        linkMap[id] = { ...linkMap[id], ...patch };
      });
      if (event?.queueMutation && typeof event.queueMutation === 'object') {
        const item = event.queueMutation;
        const key = String(item?.key || toActionKey(item));
        queueMap[key] = {
          key,
          id: key,
          type: String(item?.type || '').toUpperCase(),
          label: String(item?.label || item?.type || 'Action'),
          targetLabel: String(item?.targetLabel || item?.targetId || '-'),
          targetId: String(item?.targetId || ''),
          status: String(item?.status || AI_ACTION_STATES.RECOMMENDED),
          risk: Number(item?.risk || 0),
          confidence: Number(item?.confidence || 0),
          auto: Boolean(item?.auto),
          updatedAt: Date.now(),
        };
      }
    });
    const currentEvent = appliedEvents.length > 0 ? appliedEvents[appliedEvents.length - 1] : null;
    const orderedQueue = Object.values(queueMap).sort((a, b) => Number(b?.updatedAt || 0) - Number(a?.updatedAt || 0));
    return {
      duration,
      cursor,
      appliedEvents,
      currentEvent,
      nodes: Object.values(nodeMap),
      links: Object.values(linkMap),
      queue: orderedQueue,
    };
  }, [activeReplayIncident, isReplayMode, replayCursorMs, replaySession]);

  useEffect(() => {
    const hasNodes = Array.isArray(realtimeOverlayNodes) && realtimeOverlayNodes.length > 0;
    const hasLinks = Array.isArray(realtimeOverlayLinks) && realtimeOverlayLinks.length > 0;
    if (!hasNodes && !hasLinks) return;
    lastStableOverlayRef.current = {
      nodes: hasNodes ? realtimeOverlayNodes.map((node) => ({ ...node })) : lastStableOverlayRef.current.nodes,
      links: hasLinks ? realtimeOverlayLinks.map((link) => ({ ...link })) : lastStableOverlayRef.current.links,
      at: Date.now(),
    };
  }, [realtimeOverlayLinks, realtimeOverlayNodes]);

  const liveOverlayForDisplay = useMemo(() => {
    const now = Date.now();
    const stable = lastStableOverlayRef.current;
    const withinGrace = Number(stable?.at || 0) > 0 && (now - Number(stable.at)) <= 12000;
    const nodesReady = Array.isArray(realtimeOverlayNodes) && realtimeOverlayNodes.length > 0;
    const linksReady = Array.isArray(realtimeOverlayLinks) && realtimeOverlayLinks.length > 0;
    return {
      nodes: nodesReady ? realtimeOverlayNodes : (withinGrace ? (stable?.nodes || []) : []),
      links: linksReady ? realtimeOverlayLinks : (withinGrace ? (stable?.links || []) : []),
    };
  }, [realtimeOverlayLinks, realtimeOverlayNodes]);

  const displayOverlayNodes = useMemo(() => {
    if (isReplayMode && replayRuntime?.nodes) return replayRuntime.nodes;
    return liveOverlayForDisplay.nodes;
  }, [isReplayMode, liveOverlayForDisplay.nodes, replayRuntime?.nodes]);

  const displayOverlayLinks = useMemo(() => {
    if (isReplayMode && replayRuntime?.links) return replayRuntime.links;
    return liveOverlayForDisplay.links;
  }, [isReplayMode, liveOverlayForDisplay.links, replayRuntime?.links]);

  const displayAiQueueSections = useMemo(() => {
    const list = isReplayMode && replayRuntime?.queue
      ? replayRuntime.queue
      : (Array.isArray(aiActionQueue) ? aiActionQueue : []);
    return {
      suggested: list.filter((item) => [AI_ACTION_STATES.DETECTED, AI_ACTION_STATES.RECOMMENDED].includes(item.status)).slice(0, 8),
      executing: list.filter((item) => [AI_ACTION_STATES.APPROVED, AI_ACTION_STATES.EXECUTING].includes(item.status)).slice(0, 8),
      completed: list.filter((item) => AI_ACTION_DONE_STATES.has(item.status)).slice(0, 8),
    };
  }, [aiActionQueue, isReplayMode, replayRuntime?.queue]);

  const replayTimelineMarkers = useMemo(() => {
    if (!activeReplayIncident || !Array.isArray(activeReplayIncident.jumpPoints)) return [];
    const duration = Math.max(1, Number(activeReplayIncident.durationMs || 1));
    return activeReplayIncident.jumpPoints.map((point) => ({
      ...point,
      offsetPct: Math.max(0, Math.min(100, (Number(point.atMs || 0) / duration) * 100)),
    }));
  }, [activeReplayIncident]);

  const replayClockLabel = useMemo(() => {
    if (!isReplayMode || !activeReplayIncident) return '';
    const cursorSec = Math.max(0, Math.floor(Number(replayCursorMs || 0) / 1000));
    const totalSec = Math.max(0, Math.floor(Number(activeReplayIncident.durationMs || 0) / 1000));
    const fmt = (s) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
    return `${fmt(cursorSec)} / ${fmt(totalSec)}`;
  }, [activeReplayIncident, isReplayMode, replayCursorMs]);

  const replayPanelContext = useMemo(() => {
    if (!isReplayMode || !activeReplayIncident) return null;
    const currentEvent = replayRuntime?.currentEvent || null;
    const severity = String(currentEvent?.severity || '').toLowerCase();
    const risk = severity === 'critical' ? 86 : severity === 'warning' ? 63 : 28;
    const rootLabel = String(
      activeReplayIncident?.metadata?.sourceLabel
      || activeReplayIncident?.metadata?.rootNodeId
      || activeReplayIncident?.title
      || '-',
    );
    const impact = Number(activeReplayIncident?.metadata?.totalAffected || 0);
    return {
      risk,
      rootLabel,
      impact,
      summary: String(currentEvent?.summary || 'Replay mode aktif, memilih event timeline.'),
    };
  }, [activeReplayIncident, isReplayMode, replayRuntime?.currentEvent]);

  const startReplayMode = useCallback((incident) => {
    if (!incident) return;
    const incidentSnapshot = {
      ...incident,
      events: Array.isArray(incident?.events) ? incident.events.map((event) => ({
        ...event,
        nodePatches: Array.isArray(event?.nodePatches) ? event.nodePatches.map((patch) => ({ ...patch })) : [],
        linkPatches: Array.isArray(event?.linkPatches) ? event.linkPatches.map((patch) => ({ ...patch })) : [],
        queueMutation: event?.queueMutation && typeof event.queueMutation === 'object' ? { ...event.queueMutation } : undefined,
      })) : [],
      jumpPoints: Array.isArray(incident?.jumpPoints) ? incident.jumpPoints.map((point) => ({ ...point })) : [],
      metadata: incident?.metadata && typeof incident.metadata === 'object' ? { ...incident.metadata } : {},
    };
    setReplayIncidentId(String(incident.id || ''));
    setReplaySession({
      incident: incidentSnapshot,
      baseNodes: Array.isArray(realtimeOverlayNodes) ? realtimeOverlayNodes.map((node) => ({ ...node })) : [],
      baseLinks: Array.isArray(realtimeOverlayLinks) ? realtimeOverlayLinks.map((link) => ({ ...link })) : [],
      baseQueue: Array.isArray(aiActionQueue) ? aiActionQueue.map((item) => ({ ...item })) : [],
      startedAt: Date.now(),
    });
    setReplayCursorMs(0);
    setReplaySpeed(1);
    setReplayPlaying(false);
    setIsReplayMode(true);
    setRightPanelTab('ai');
  }, [aiActionQueue, realtimeOverlayLinks, realtimeOverlayNodes]);

  const stopReplayMode = useCallback(() => {
    setReplayPlaying(false);
    setIsReplayMode(false);
    setReplayCursorMs(0);
    setReplaySession(null);
  }, []);

  const startReplayFromTicket = useCallback((ticketId) => {
    const id = String(ticketId || '').trim();
    if (!id) {
      toast.info('Ticket replay belum tersedia untuk context ini.');
      return;
    }
    const incident = (Array.isArray(replayIncidents) ? replayIncidents : []).find((item) => String(item?.ticketId || '') === id);
    if (!incident) {
      toast.info('Timeline replay untuk ticket ini belum tersedia.');
      return;
    }
    startReplayMode(incident);
  }, [replayIncidents, startReplayMode]);

  useEffect(() => {
    if (isReplayMode) return;
    if (!Array.isArray(replayIncidents) || replayIncidents.length === 0) {
      setReplayIncidentId('');
      return;
    }
    if (!replayIncidentId || !replayIncidents.some((incident) => incident.id === replayIncidentId)) {
      setReplayIncidentId(String(replayIncidents[0].id || ''));
    }
  }, [isReplayMode, replayIncidentId, replayIncidents]);

  useEffect(() => {
    if (!isReplayMode || !activeReplayIncident || !replayPlaying) return undefined;
    const interval = window.setInterval(() => {
      setReplayCursorMs((prev) => {
        const next = prev + 250 * Math.max(1, Number(replaySpeed || 1));
        if (next >= Number(activeReplayIncident.durationMs || 0)) {
          setReplayPlaying(false);
          return Number(activeReplayIncident.durationMs || 0);
        }
        return next;
      });
    }, 250);
    return () => window.clearInterval(interval);
  }, [activeReplayIncident, isReplayMode, replayPlaying, replaySpeed]);

  const incidentSummary = useMemo(() => {
    const context = selectedIncidentContext || null;
    const tickets = Array.isArray(incidentTicketsByContext) ? incidentTicketsByContext : [];
    const primaryTicket = context?.activeTicket || tickets[0] || null;
    const primaryTicketId = String(primaryTicket?.id || '').trim();

    const replayByTicket = primaryTicketId
      ? (Array.isArray(replayIncidents) ? replayIncidents : []).find((incident) => String(incident?.ticketId || '') === primaryTicketId)
      : null;
    const replayRef = replayByTicket || activeReplayIncident || null;
    const replayEvents = Array.isArray(replayRef?.events) ? replayRef.events : [];
    const startedAtMs = Number(replayRef?.startedAt || 0) || null;

    const findEventMs = (types) => {
      const typeSet = new Set(Array.isArray(types) ? types : [types]);
      const hit = replayEvents.find((event) => typeSet.has(String(event?.type || '')));
      if (!hit) return null;
      if (startedAtMs && Number.isFinite(Number(hit?.atMs))) return startedAtMs + Number(hit.atMs);
      return null;
    };

    const anomalyAt = findEventMs(['anomaly_detected']);
    const ticketAt = parseTimestampMs(primaryTicket?.created_at) || findEventMs(['ticket_created']);
    const escalationAt = findEventMs(['escalated']) || (String(primaryTicket?.lifecycle?.key || '') === 'escalated' ? parseTimestampMs(primaryTicket?.updated_at) : null);
    const resolvedAt = parseTimestampMs(primaryTicket?.completed_at) || findEventMs(['resolved', 'incident_end']);
    const inProgressAt = parseTimestampMs(primaryTicket?.approved_at) || findEventMs(['in_progress']);

    const toDuration = (fromMs, toMs) => (fromMs && toMs && toMs > fromMs ? (toMs - fromMs) : null);

    const totalDuration = toDuration(anomalyAt || ticketAt, resolvedAt);
    const timeToTicket = toDuration(anomalyAt, ticketAt);
    const timeToEscalation = toDuration(ticketAt || anomalyAt, escalationAt);
    const recoveryDuration = toDuration(inProgressAt || ticketAt, resolvedAt);

    const contextType = String(context?.sourceType || '').toLowerCase();
    const contextId = String(context?.sourceId || '').trim();
    const ticketType = String(primaryTicket?.sourceType || '').toLowerCase();
    const ticketIdSource = String(primaryTicket?.sourceId || '').trim();
    const replayType = String(replayRef?.sourceType || '').toLowerCase();
    const replayIdSource = String(replayRef?.sourceId || '').trim();
    const contextSourceKey = buildIncidentSourceKey(contextType, contextId);
    const ticketSourceKey = buildIncidentSourceKey(ticketType, ticketIdSource);
    const replaySourceKey = buildIncidentSourceKey(replayType, replayIdSource);
    const sourceCandidates = [
      { origin: 'context', key: contextSourceKey },
      { origin: 'ticket', key: ticketSourceKey },
      { origin: 'replay', key: replaySourceKey },
    ].filter((item) => String(item.key || '').trim());
    const canonicalSourceKey = sourceCandidates[0]?.key || '';
    const sourceMismatch = sourceCandidates.some((item) => item.key !== canonicalSourceKey);
    const [canonicalType = '', ...canonicalParts] = String(canonicalSourceKey || '').split(':');
    const canonicalId = canonicalParts.join(':');

    const lifecycle = primaryTicket?.lifecycle || normalizeTicketLifecycle(primaryTicket?.status);
    const statusLabel = primaryTicket ? (lifecycle?.label || primaryTicket?.status || 'Open') : (replayRef ? 'Replay Ready' : 'Belum Ticket');
    const severity = String(lifecycle?.key || '').toLowerCase() === 'escalated'
      ? 'critical'
      : String(lifecycle?.key || '').toLowerCase() === 'resolved'
        ? 'normal'
        : (Number(context?.impactCount || 0) >= 8 ? 'warning' : 'normal');

    const timelineItems = [
      { key: 'anomaly', label: 'Anomaly', at: anomalyAt },
      { key: 'ticket', label: 'Ticket', at: ticketAt },
      { key: 'escalated', label: 'Escalated', at: escalationAt },
      { key: 'resolved', label: 'Resolved', at: resolvedAt },
    ].filter((item) => Number.isFinite(Number(item.at)));

    const insights = [];
    if (timeToTicket !== null) {
      insights.push(timeToTicket > 15 * 60 * 1000 ? 'Respon ticket lambat, perlu percepatan triase awal.' : 'Respon ticket cukup cepat.');
    }
    if (timeToEscalation !== null) {
      insights.push(timeToEscalation > 25 * 60 * 1000 ? 'Eskalasi terlambat, pertimbangkan trigger otomatis.' : 'Eskalasi dilakukan dalam waktu aman.');
    }
    if (recoveryDuration !== null) {
      insights.push(recoveryDuration > 45 * 60 * 1000 ? 'Recovery memakan waktu lama, perlu SOP mitigasi lebih cepat.' : 'Recovery berada dalam rentang operasional normal.');
    }
    if (insights.length === 0) {
      insights.push('Data timeline belum lengkap, lanjutkan observasi event dan timestamp incident.');
    }

    const recommendations = [];
    const sourceType = canonicalType || String(context?.sourceType || primaryTicket?.sourceType || '').toLowerCase();
    const sourceId = canonicalId || String(context?.sourceId || primaryTicket?.sourceId || '').trim();
    if (sourceType === 'odp' && sourceId) {
      const odpRisk = (capacityPrediction?.odps || []).find((item) => String(item?.id || '').trim() === sourceId);
      if (odpRisk?.risk?.tone === 'danger' || odpRisk?.risk?.tone === 'warn') {
        recommendations.push('Evaluasi penambahan ODP plan di area ini untuk menurunkan tekanan kapasitas.');
      }
    }
    if (sourceType === 'nas' && sourceId) {
      const nasRisk = (capacityPrediction?.nas || []).find((item) => String(item?.id || '').trim() === sourceId);
      if (nasRisk?.risk?.tone === 'danger' || nasRisk?.risk?.tone === 'warn') {
        recommendations.push('Pantau growth NAS dan lakukan redistribusi beban pelanggan bila perlu.');
      }
    }
    if (severity === 'critical') {
      recommendations.push('Prioritaskan eskalasi teknisi dan aktifkan monitoring ketat sampai status stabil.');
    }
    if (recommendations.length === 0) {
      recommendations.push('Lanjutkan monitoring rutin dan validasi kapasitas mingguan.');
    }

    const metricLabel = (value, missingReason, unavailableReason = 'Tidak tersedia') => {
      if (value !== null && Number.isFinite(Number(value))) return formatElapsedShort(value);
      return missingReason || unavailableReason;
    };

    const timeToTicketLabel = metricLabel(
      timeToTicket,
      anomalyAt ? 'Data tidak cukup' : 'Menunggu event anomaly',
      'Tidak tersedia',
    );
    const timeToEscalationLabel = metricLabel(
      timeToEscalation,
      escalationAt ? 'Data tidak cukup' : 'Tidak tersedia',
      'Tidak tersedia',
    );
    const recoveryDurationLabel = metricLabel(
      recoveryDuration,
      resolvedAt ? 'Data tidak cukup' : 'Tidak tersedia',
      'Tidak tersedia',
    );

    return {
      incidentId: primaryTicketId || String(replayRef?.id || '').trim() || '-',
      ticketId: primaryTicketId || '',
      statusLabel,
      severity,
      durationMs: totalDuration,
      impactCount: Number(context?.impactCount || replayRef?.metadata?.totalAffected || 0),
      sourceTypeRaw: sourceType || '',
      sourceType: String(sourceType || '-').toUpperCase(),
      sourceId: String(sourceId || '-'),
      topologyRef: String(context?.topologyNodeId || primaryTicket?.topologyNodeId || replayRef?.metadata?.rootNodeId || '-'),
      customerId: String(context?.customer?.id || primaryTicket?.customerId || '').trim(),
      sourceConsistency: {
        ok: !sourceMismatch,
        detail: sourceMismatch ? 'Source context berbeda antara panel/replay/ticket. Ringkasan memakai context aktif.' : '',
      },
      timelineItems,
      metrics: {
        timeToTicket,
        timeToEscalation,
        recoveryDuration,
        timeToTicketLabel,
        timeToEscalationLabel,
        recoveryDurationLabel,
      },
      insights: insights.slice(0, 3),
      recommendations: recommendations.slice(0, 2),
      hasData: Boolean(context || primaryTicket || replayRef),
    };
  }, [activeReplayIncident, capacityPrediction?.nas, capacityPrediction?.odps, incidentTicketsByContext, replayIncidents, selectedIncidentContext]);

  const openIncidentSummaryFocus = useCallback(() => {
    if (!incidentSummary?.hasData) return;
    const type = String(incidentSummary?.sourceTypeRaw || '').toLowerCase();
    const sourceId = String(incidentSummary?.sourceId || '').trim();
    const topologyRef = String(incidentSummary?.topologyRef || '').trim();

    const focusOverlayNode = (nodeId) => {
      const id = String(nodeId || '').trim();
      if (!id || id === '-') return false;
      const matched = (Array.isArray(displayOverlayNodes) ? displayOverlayNodes : []).find((node) => String(node?.id || '').trim() === id);
      if (!matched) return false;
      if (matched.type === 'olt') {
        handleFocusNode('olt', id);
        return true;
      }
      if (matched.type === 'odp') {
        handleFocusNode('odp', id);
        return true;
      }
      if (matched.type === 'onu' || matched.type === 'customer') {
        setSelectedCustomerId(id);
        handleFocusNode('customer', id);
        return true;
      }
      return false;
    };

    setRightPanelTab('infra');
    if (type === 'customer' && sourceId) {
      const customerExists = filteredCustomersByMode.some((item) => String(item?.id || '').trim() === sourceId);
      if (!customerExists) {
        toast.warning('Source customer ada, tapi node belum ditemukan pada scope filter saat ini.');
        return;
      }
      setSelectedCustomerId(sourceId);
      handleFocusNode('customer', sourceId);
      return;
    }
    if (type === 'odp' && sourceId && sourceId !== '-') {
      const exists = filteredOdps.some((item) => normalizeNodeId(item?.id) === normalizeNodeId(sourceId));
      if (!exists) {
        toast.warning('Source ODP terdeteksi, namun node tidak ditemukan pada scope saat ini.');
        return;
      }
      handleFocusNode('odp', sourceId);
      return;
    }
    if (type === 'olt' && sourceId && sourceId !== '-') {
      const exists = filteredOlts.some((item) => normalizeNodeId(item?.id) === normalizeNodeId(sourceId));
      if (!exists) {
        toast.warning('Source OLT terdeteksi, namun node tidak ditemukan pada scope saat ini.');
        return;
      }
      handleFocusNode('olt', sourceId);
      return;
    }
    if (type === 'nas' && sourceId && sourceId !== '-') {
      const nasExists = Boolean(nasByRouterId[normalizeNodeId(sourceId)]);
      if (!nasExists) {
        toast.warning('Source NAS terdeteksi, namun node tidak ditemukan pada scope saat ini.');
        return;
      }
      setSelectedNasId(sourceId);
      triggerAutoFit();
      toast.success(`Monitoring difokuskan ke NAS ${sourceId}.`);
      return;
    }
    if ((type === 'anomaly_cluster' || type === 'cluster' || type === 'topology_node') && (sourceId || topologyRef)) {
      const focused = focusOverlayNode(topologyRef) || focusOverlayNode(sourceId);
      if (focused) return;
      const clusterHit = (Array.isArray(customerClusters) ? customerClusters : []).find((cluster) => String(cluster?.id || '').trim() === sourceId);
      const fallbackCustomerId = String(clusterHit?.items?.[0]?.id || '').trim();
      if (fallbackCustomerId) {
        setSelectedCustomerId(fallbackCustomerId);
        handleFocusNode('customer', fallbackCustomerId);
        toast.info('Cluster tidak punya node langsung, fokus dialihkan ke customer terdampak.');
        return;
      }
      toast.warning('Source incident tersedia, tapi node/cluster tidak ditemukan pada scope saat ini.');
      return;
    }
    toast.warning('Source incident belum cukup untuk difokuskan ke monitoring.');
  }, [
    customerClusters,
    displayOverlayNodes,
    filteredCustomersByMode,
    filteredOdps,
    filteredOlts,
    handleFocusNode,
    incidentSummary?.hasData,
    incidentSummary?.sourceId,
    incidentSummary?.sourceTypeRaw,
    incidentSummary?.topologyRef,
    nasByRouterId,
    triggerAutoFit,
  ]);

  useEffect(() => {
    setIncidentSummaryExpanded(false);
  }, [incidentSummary?.incidentId]);

  const selectCustomerFromSearch = useCallback((customerId) => {
    setSelectedCustomerId(customerId);
    setAutoFitEnabled(false);
  }, []);

  const renderInfraPopupCard = useCallback((type, node, status, pelangganCount) => {
    const nodeLabel = type === 'olt' ? 'OLT' : 'ODP';
    const statusTone = mapConnectionStatusToTone(status);
    const statusLabel = status === 'online' ? 'Online' : status === 'offline' ? 'Offline' : 'Tidak Diketahui';
    return (
      <div className="noc-popup-card">
        <div className="noc-popup-head">
          <span className={`noc-popup-badge border ${semanticToneToBadgeClass(statusTone)}`}>
            {statusLabel}
          </span>
          <p className="noc-popup-title">{node?.name || `${nodeLabel} -`}</p>
        </div>
        <div className="noc-popup-metrics">
          <div className="noc-popup-metric">
            <p className="label">Perangkat</p>
            <p className="value flex items-center gap-1"><Network className="h-3.5 w-3.5" /> {nodeLabel}</p>
          </div>
          <div className="noc-popup-metric">
            <p className="label">Pelanggan</p>
            <p className="value flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {pelangganCount}</p>
          </div>
          <div className="noc-popup-metric">
            <p className="label">Kode</p>
            <p className="value">{node?.code || '-'}</p>
          </div>
          <div className="noc-popup-metric">
            <p className="label">Kriteria</p>
            <p className="value">{getInfraCategoryLabel(node?.kriteria)}</p>
          </div>
        </div>
        <p className="noc-popup-foot">
          {type === 'olt' ? `Lokasi: ${node?.location || '-'}` : `Alamat: ${node?.address || '-'}`}
        </p>
      </div>
    );
  }, []);

const renderCustomerPopupCard = useCallback((customer, statusColor, aiInsight) => {
    const linkage = resolveCustomerLinkageMeta(customer);
    const billingStatus = String(customer?.status || '').trim().toLowerCase();
    const billingLabel = billingStatus === 'active'
      ? 'Billing Aktif'
      : (billingStatus === 'suspended' ? 'Billing Disuspend' : (billingStatus === 'terminated' ? 'Billing Dihentikan' : 'Billing Tidak Diketahui'));
    const billingTone = semanticToneToBadgeClass(mapBillingStatusToTone(billingStatus));
    const aiLevelTone = mapIncidentSeverityToTone(aiInsight?.level || 'normal');
    const aiRiskTone = mapAiRiskToTone(aiInsight?.risk || 'low');
    const onuStatusLabel = getStatusLabel(customer?.connection_status);
    const pppoeStatusLabel = getPppoeStatusLabel(customer?.pppoe_connection_status);
    const siteLabel = customer?.site_name || customer?.site || linkage?.siteName || '-';
    return (
    <div className="noc-popup-card noc-popup-card-customer">
      <div className="noc-popup-head">
        <p className="noc-popup-title flex items-center gap-2">
          <span className="noc-popup-icon"><User className="w-3.5 h-3.5" /></span>
          <span>{customer?.name || '-'}</span>
        </p>
        <p className="noc-popup-sub">No Internet: {customer?.customer_id || '-'}</p>
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] border ${linkage.mappingTone}`}>{linkage.mappingLabel}</span>
        <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] border ${billingTone}`}>{billingLabel}</span>
        <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] border border-slate-500/40 bg-slate-500/10 text-slate-200">
          ONU: <span className="ml-1" style={{ color: statusColor }}>{onuStatusLabel}</span>
        </span>
      </div>
      <div className="noc-popup-metrics">
        <div className="noc-popup-metric">
          <p className="label">Kontak</p>
          <p className="value flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {customer?.phone || '-'}</p>
        </div>
        <div className="noc-popup-metric">
          <p className="label">Paket</p>
          <p className="value">{customer?.package_name || '-'}</p>
        </div>
        <div className="noc-popup-metric">
          <p className="label">ODP / Site</p>
          <p className="value flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {linkage?.odpName || '-'}</p>
          <p className="text-[10px] text-slate-400 mt-0.5 truncate">{siteLabel}</p>
        </div>
        <div className="noc-popup-metric">
          <p className="label">NAS / OLT</p>
          <p className="value">{linkage?.nasName || '-'} / {linkage?.oltName || '-'}</p>
        </div>
        <div className="noc-popup-metric">
          <p className="label">Status PPPoE</p>
          <p className="value" style={{ color: getPppoeStatusColor(customer?.pppoe_connection_status) }}>{pppoeStatusLabel}</p>
        </div>
        <div className="noc-popup-metric">
          <p className="label">Username PPPoE</p>
          <p className="value font-mono">{customer?.pppoe_username || '-'}</p>
        </div>
        <div className="noc-popup-metric">
          <p className="label">RX/Redaman</p>
          <p className="value">{formatSignal(customer?.status_detail)}</p>
        </div>
        <div className="noc-popup-metric">
          <p className="label">Perangkat Terhubung</p>
          <p className="value">{Number.isFinite(customer?.status_detail?.total_stations) ? customer.status_detail.total_stations : '-'}</p>
        </div>
      </div>
      {aiModeEnabled && aiInsight?.aiSummary && (
        <div className="noc-ai-section mt-2">
          <p className="noc-ai-title">AI Insight</p>
          <p className="text-[11px] text-cyan-200 line-clamp-2">{aiInsight.aiSummary}</p>
          <div className="mt-1 grid grid-cols-3 gap-1 text-[10px]">
            <span className={`rounded border px-1.5 py-0.5 ${semanticToneToBadgeClass(aiLevelTone)}`}>
              Level: {String(aiInsight?.level || 'normal').toUpperCase()}
            </span>
            <span className="rounded border border-cyan-500/30 bg-cyan-500/10 px-1.5 py-0.5 text-cyan-200">
              Score: {Number.isFinite(aiInsight?.score) ? `${aiInsight.score}/100` : '-'}
            </span>
            <span className={`rounded border px-1.5 py-0.5 ${semanticToneToBadgeClass(aiRiskTone)}`}>
              Risk: {String(aiInsight?.risk || 'low').toUpperCase()}
            </span>
          </div>
          <p className="mt-1 text-[10px] text-slate-300">{aiInsight?.prediction || 'Tidak ada prediksi khusus.'}</p>
        </div>
      )}
      <p className="noc-popup-foot">Last Inform: {formatLastInformAge(customer?.status_detail?.last_inform)}</p>
    </div>
  );
  }, [aiModeEnabled, resolveCustomerLinkageMeta]);



  return (

    <div className={`relative min-h-screen bg-[#050505] ${MONITORING_UI_V2_ENABLED ? 'noc-command-shell' : ''}`}>
      {showLoadingOverlay && (
        <StaffPageLoadingOverlay text="Memuat data monitoring jaringan..." />
      )}

      <aside className="hidden">

        <div className="p-6 flex-1 overflow-y-auto">

          <Link to="/" className="flex items-center gap-3 mb-8">

            <img src={ktcLogo} alt="KTC Logo" className="h-10 w-auto" />

            <span className="font-outfit font-bold text-white">Admin</span>

          </Link>



          <nav className="space-y-2">

            <Link to="/dadu" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <Home className="w-5 h-5" />Dashboard

            </Link>

            <Link to="/dadu/pelanggan" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <Users className="w-5 h-5" />Pelanggan

            </Link>

            <Link to="/dadu/ftth" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <Network className="w-5 h-5" />Rancangan FTTH

            </Link>

            <Link to="/dadu/monitoring" className="flex items-center gap-3 px-4 py-3 rounded-lg bg-brand-blue/10 text-brand-blue border border-brand-blue/30">

              <Map className="w-5 h-5" />Monitoring Koneksi

            </Link>

            <Link to="/dadu/penanganan" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <Wrench className="w-5 h-5" />Penanganan

            </Link>

            <Link to="/performa-teknisi" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <Users className="w-5 h-5" />Performa Teknisi

            </Link>

            <Link to="/dadu/billing" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <CreditCard className="w-5 h-5" />Billing

            </Link>

            <Link to="/dadu/voucher" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors"><CreditCard className="w-5 h-5" />Voucher</Link>
            <Link to="/dadu/berita" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <Newspaper className="w-5 h-5" />Berita

            </Link>

            <Link to="/dadu/kontak" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <MessageSquare className="w-5 h-5" />Pesan Masuk

            </Link>

            <Link to="/dadu/users" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">
              <Users className="w-5 h-5" />Manajemen User
            </Link>
            <Link to="/dadu/pengaturan" className="flex items-center gap-3 px-4 py-3 rounded-lg text-gray-400 hover:bg-white/5 hover:text-white transition-colors">

              <Settings className="w-5 h-5" />Pengaturan

            </Link>

          </nav>

        </div>



        <div className="p-6 border-t border-white/10">

          <p className="text-white font-medium text-sm mb-4">{user?.name || 'Admin'}</p>

          <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-400 border-red-400/30 hover:bg-red-500/10 w-full">

            <LogOut className="w-4 h-4 mr-2" />Logout

          </Button>

        </div>

      </aside>



      <header className="hidden">

        <div className="flex items-center justify-between px-4 py-3">

          <div className="flex items-center gap-2">

            <Link to="/dadu"><Button size="sm" variant="ghost" className="text-gray-400"><ArrowLeft className="w-5 h-5" /></Button></Link>

            <span className="font-outfit font-bold text-white">Monitoring Koneksi</span>

          </div>

          <Button size="sm" variant="ghost" onClick={handleLogout} className="text-red-400"><LogOut className="w-5 h-5" /></Button>

        </div>

      </header>



      <header className={`noc-monitoring-topnav bg-[#0A0A0A] border-b border-white/10 relative z-40 ${MONITORING_UI_V2_ENABLED ? 'noc-monitoring-topnav-v2' : ''}`}>
        <div className={`max-w-[1600px] mx-auto px-4 py-3 flex items-center justify-between ${MONITORING_UI_V2_ENABLED ? 'noc-topnav-inner-v2' : ''}`}>
          <Link to="/dadu" className="flex items-center gap-3">
            <img src={ktcLogo} alt="KTC Logo" className="h-9 w-auto" />
            <div>
              <p className="font-outfit font-bold text-white leading-tight">{MONITORING_UI_V2_ENABLED ? 'KTC ISP Platform' : 'Monitoring Koneksi'}</p>
              <p className="text-[11px] text-gray-500 leading-tight">Akses: Admin</p>
            </div>
          </Link>
          {MONITORING_UI_V2_ENABLED ? (
            <div className="noc-topnav-links-v2 hidden lg:flex">
              <button type="button" className="noc-topnav-link-v2">Statistik Jaringan</button>
              <button type="button" className="noc-topnav-link-v2 is-active">Peta Global</button>
            </div>
          ) : null}
          <div className="flex items-center gap-2">
            <span className={`noc-renderer-badge tone-${rendererBadge.tone}`}>
              Renderer: {rendererBadge.label}
            </span>
            <Link to="/dadu"><Button variant="outline" className="btn-secondary"><Home className="w-4 h-4 mr-2" />Kembali</Button></Link>
            <Button
              onClick={() => {
                hasUserAdjustedViewportRef.current = false;
                triggerAutoFit();
                fetchData(true);
              }}
              variant="outline"
              className="btn-secondary"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />Muat Ulang
            </Button>
            <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-400 border-red-400/30 hover:bg-red-500/10"><LogOut className="w-4 h-4" /></Button>
          </div>
        </div>
      </header>

      <AppShell>

        {MONITORING_UI_V2_ENABLED ? (
          <section className="noc-command-hero-v2">
            <div>
            <p className="noc-command-kicker-v2">Peta Global</p>
              <h1 className="noc-command-title-v2">Monitoring Koneksi</h1>
              <p className="noc-command-subtitle-v2">Real-time observability untuk node, router, OLT, ODP, dan pelanggan.</p>
            </div>
            <div className="noc-command-metrics-v2">
              <div className="noc-command-metric-v2">
                <p className="label">Total Links</p>
                <p className="value">{filteredCustomersByMode.length}</p>
              </div>
              <div className="noc-command-metric-v2">
                <p className="label">Online</p>
                <p className="value text-emerald-300">{onlineCount}</p>
              </div>
              <div className="noc-command-metric-v2">
                <p className="label">Offline</p>
                <p className="value text-rose-300">{offlineCount}</p>
              </div>
              <div className="noc-command-metric-v2">
                <p className="label">Belum Terpetakan</p>
                <p className="value text-amber-300">{notMappedCount}</p>
              </div>
            </div>
          </section>
        ) : (
          <StaffPageHeader
            eyebrow="Monitoring"
            title="Monitoring Konektivitas Pelanggan"
          subtitle="Workspace live monitor: observasi peta real-time dan inspeksi entity secara cepat tanpa menutupi canvas utama."
          />
        )}
        <div className={`noc-live-strip ${MONITORING_UI_V2_ENABLED ? 'noc-live-strip-v2' : ''}`}>
          <div className="noc-live-chip">
            <span className={`noc-live-dot ${streamConnected ? 'is-live' : 'is-warn'}`} />
            <span>{streamConnected ? 'LIVE Realtime' : 'Reconnect...'}</span>
          </div>
          <div className="noc-last-update">
            Update terakhir: {lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}{MONITORING_UI_V2_ENABLED ? ` â€¢ Tidak Diketahui ${unknownCount}` : ''}
          </div>
        </div>



        <InfraServiceHealthPanel
          apiBase={API}
          getHeaders={getHeaders}
          onUnauthorized={handleAuthExpired}
            statusOverrides={{
              genieacs: (genieacsHealthAvailable ?? serverAvailable) ? 'ok' : 'down',
              checkedAt: lastUpdated || null,
            }}
        />

        <div
          ref={mapFullscreenRef}
          className={isMapFullscreen ? 'relative h-screen w-screen bg-[#0b1220] p-3 md:p-4' : ''}
        >
          <div className={isMapFullscreen ? 'relative h-full' : 'grid xl:grid-cols-[minmax(0,1fr)_300px] gap-4'}>

            <div className={isMapFullscreen ? 'relative h-full rounded-xl border border-white/15 overflow-hidden bg-[#121212]' : 'bg-[#121212] rounded-xl border border-white/10 overflow-hidden'}>

              <div className={`${isMapFullscreen ? 'relative h-full' : 'relative h-[78vh] min-h-[640px]'} ${focusNode?.id ? 'map-focus-mode' : ''}`}>

              <div className={`noc-map-toolbar ${MONITORING_UI_V2_ENABLED ? 'noc-map-toolbar-v2' : ''}`}>
                <div className="noc-map-toolbar-left">
                  <div className="relative">
                    <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <Input
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' && mapSearchResults.length > 0) {
                          event.preventDefault();
                          selectCustomerFromSearch(mapSearchResults[0].id);
                        }
                      }}
                      placeholder="Cari customer atau distribusi..."
                      className="noc-map-search-input pl-9"
                    />
                    {searchQuery.trim().length > 0 && searchQuery.trim().length < 3 && (
                      <p className="mt-2 text-[11px] text-amber-300">Ketik minimal 3 karakter.</p>
                    )}
                    {searchQuery.trim().length >= 3 && (
                      <div className="noc-map-search-result">
                        {loading ? (
                          <p className="px-3 py-2 text-xs text-amber-300">Menunggu response dari server...</p>
                        ) : mapSearchResults.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-slate-400">Data pelanggan tidak ditemukan.</p>
                        ) : (
                          mapSearchResults.map((customer) => (
                            <button
                              type="button"
                              key={`map-search-${customer.id}`}
                              onClick={() => selectCustomerFromSearch(customer.id)}
                              className="w-full px-3 py-2 text-left hover:bg-slate-800/70 border-b border-slate-700/50 last:border-b-0"
                            >
                              <p className="text-sm text-slate-100 flex items-center gap-2">
                                <User className="w-3.5 h-3.5 text-cyan-300" />
                                <span className="truncate">{customer.name}</span>
                              </p>
                              <p className="text-[11px] text-slate-400 ml-5">{customer.customer_id || '-'} - {customer.odp || '-'}</p>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>
                <div className="noc-map-toolbar-right">
                  <div className="noc-visual-toggle">
                    {VISUAL_MODE_OPTIONS.map((mode) => (
                      <button
                        key={`visual-mode-${mode.value}`}
                        type="button"
                        onClick={() => setVisualMode(mode.value)}
                        className={`noc-visual-toggle-btn ${visualMode === mode.value ? 'is-active' : ''}`}
                      >
                        {mode.label}
                      </button>
                    ))}
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant={isReplayMode ? 'default' : 'outline'}
                    className={isReplayMode ? 'h-8 px-2 bg-cyan-500 hover:bg-cyan-400 text-slate-950' : 'h-8 px-2 btn-secondary noc-map-btn'}
                    onClick={() => {
                      if (isReplayMode) {
                        stopReplayMode();
                      } else {
                        startReplayMode(activeReplayIncident);
                      }
                    }}
                    disabled={!activeReplayIncident}
                  >
                    <History className="w-4 h-4 mr-1.5" />
                    {isReplayMode ? 'Replay ON' : 'Replay'}
                  </Button>
                  {focusNode?.id && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="btn-secondary noc-map-btn"
                      onClick={clearFocusNode}
                    >
                      Reset Focus
                    </Button>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="btn-secondary noc-map-btn"
                    onClick={handleToggleMapFullscreen}
                  >
                    {isMapFullscreen ? <Minimize2 className="w-4 h-4 mr-2" /> : <Maximize2 className="w-4 h-4 mr-2" />}
                    {isMapFullscreen ? 'Keluar Fullscreen' : 'Fullscreen'}
                  </Button>
                  {useGpuMapEngine && isDevMode && !showDebugOverlay ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="btn-secondary noc-map-btn"
                      onClick={() => setShowDebugOverlay(true)}
                    >
                      Debug
                    </Button>
                  ) : null}
                </div>
              </div>

              {isReplayMode && activeReplayIncident ? (
                <div className="noc-replay-toolbar">
                  <div className="noc-replay-toolbar-head">
                    <div>
                      <p className="title">Incident Replay</p>
                      <p className="meta">
                        {activeReplayIncident.title} â€¢ {replayClockLabel} â€¢ {replayPlaying ? 'Playing' : 'Paused'}
                      </p>
                    </div>
                    <div className="controls">
                      {replayIncidents.length > 1 ? (
                        <select
                          className="noc-replay-speed-select"
                          value={String(activeReplayIncident.id)}
                          onChange={(event) => {
                            const target = replayIncidents.find((item) => String(item.id) === String(event.target.value));
                            if (!target) return;
                            startReplayMode(target);
                          }}
                        >
                          {replayIncidents.map((incident) => (
                            <option key={`replay-incident-${incident.id}`} value={incident.id}>{incident.title}</option>
                          ))}
                        </select>
                      ) : null}
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 btn-secondary"
                        onClick={() => setReplayCursorMs(0)}
                      >
                        <SkipBack className="w-3.5 h-3.5 mr-1" />Start
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 btn-secondary"
                        onClick={() => setReplayPlaying((prev) => !prev)}
                      >
                        {replayPlaying ? <Pause className="w-3.5 h-3.5 mr-1" /> : <Play className="w-3.5 h-3.5 mr-1" />}
                        {replayPlaying ? 'Pause' : 'Play'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 btn-secondary"
                        onClick={() => setReplayCursorMs(Number(activeReplayIncident.durationMs || 0))}
                      >
                        <SkipForward className="w-3.5 h-3.5 mr-1" />End
                      </Button>
                      <select
                        className="noc-replay-speed-select"
                        value={String(replaySpeed)}
                        onChange={(event) => setReplaySpeed(Number(event.target.value) || 1)}
                      >
                        {REPLAY_SPEED_OPTIONS.map((speed) => (
                          <option key={`replay-speed-${speed}`} value={speed}>{speed}x</option>
                        ))}
                      </select>
                    </div>
                  </div>
                  <div className="noc-replay-scrubber-wrap">
                    <input
                      type="range"
                      min={0}
                      max={Math.max(1, Number(activeReplayIncident.durationMs || 1))}
                      value={Math.max(0, Math.min(Number(activeReplayIncident.durationMs || 0), Number(replayCursorMs || 0)))}
                      onChange={(event) => {
                        setReplayPlaying(false);
                        setReplayCursorMs(Number(event.target.value) || 0);
                      }}
                      className="noc-replay-scrubber"
                    />
                    <div className="noc-replay-marker-row">
                      {replayTimelineMarkers.map((point) => (
                        <button
                          type="button"
                          key={`replay-marker-${point.key}`}
                          className={`noc-replay-marker ${replayRuntime?.currentEvent?.id === point.key ? 'is-active' : ''}`}
                          style={{ left: `${point.offsetPct}%` }}
                          onClick={() => {
                            setReplayPlaying(false);
                            setReplayCursorMs(Number(point.atMs || 0));
                          }}
                          title={point.label}
                        />
                      ))}
                    </div>
                  </div>
                </div>
              ) : null}

              {useGpuMapEngine ? (
                <Suspense
                  fallback={(
                    <div className="h-full w-full flex items-center justify-center text-sm text-cyan-200 bg-[#08101c]">
                      Memuat GPU map...
                    </div>
                  )}
                >
                  <LazyGpuNetworkMap
                    center={DEFAULT_CENTER}
                    zoom={mapZoom}
                    mapPoints={activeTopologyPoints}
                    nodes={displayOverlayNodes}
                    links={displayOverlayLinks}
                    clusters={realtimeOverlayClusters}
                    focusNodeId={realtimeFocusedNodeId}
                    visualMode={visualMode}
                    selectedCustomerId={selectedCustomerId}
                    searchHighlightSet={searchHighlightSet}
                    onZoomChange={setMapZoom}
                    onNodeClick={handleGpuNodeClick}
                    onClusterClick={handleGpuClusterClick}
                    onMapError={handleGpuMapError}
                    onStatsChange={handleGpuStatsChange}
                    onUserViewportAdjust={handleViewportUserAdjust}
                    autoFitToken={autoFitKey}
                  />
                </Suspense>
              ) : (
              <MapContainer

                center={[DEFAULT_CENTER.latitude, DEFAULT_CENTER.longitude]}

                zoom={13}

                className="h-full w-full staff-monitoring-map"

                scrollWheelZoom

                renderer={MAP_SVG_RENDERER}

              >

                <LeafletTileLayer />



                <FitBoundsController
                  points={activeTopologyPoints}
                  enabled={autoFitEnabled}
                  triggerKey={autoFitKey}
                  onComplete={() => setAutoFitEnabled(false)}
                />
                <AutoFitDisabler
                  onDisable={() => setAutoFitEnabled(false)}
                  onUserAdjust={handleViewportUserAdjust}
                />
                <MapZoomWatcher onChange={setMapZoom} />
                <LeafletRealtimeWebGLLayer
                  enabled={visualMode !== 'normal'}
                  animate={visualMode === 'flow' || visualMode === 'ai'}
                  mode={visualMode}
                  nodes={displayOverlayNodes}
                  links={displayOverlayLinks}
                  focusedNodeId={realtimeFocusedNodeId}
                  onWebglReady={(ok) => {
                    const state = Boolean(ok);
                    setWebglOverlayActive(state);
                    setWebglOverlayFailed(!state);
                    setWebglOverlayProbeDone(true);
                  }}
                />
                <LeafletRealtimeNetworkLayer
                  enabled={visualMode !== 'normal' && webglOverlayProbeDone && !webglOverlayActive}
                  animate={visualMode === 'flow' || visualMode === 'ai'}
                  aiMode={aiModeEnabled}
                  nodes={displayOverlayNodes}
                  links={displayOverlayLinks}
                  clusters={realtimeOverlayClusters}
                  focusedNodeId={realtimeFocusedNodeId}
                />

                <FocusCustomerController customer={selectedCustomer} />



                {odpCoverageZones
                  .filter((zone) => visibleCoverageOdpIds.has(String(zone.id)))
                  .map((zone) => (
                  <Circle
                    key={`odp-coverage-${zone.id}`}
                    center={[zone.latitude, zone.longitude]}
                    radius={aiModeEnabled ? Math.max(36, zone.radius * 0.62) : zone.radius}
                    pathOptions={{
                      color: '#16a34a',
                      fillColor: '#16a34a',
                      fillOpacity: aiModeEnabled ? 0.035 : 0.2,
                      weight: aiModeEnabled ? 0.8 : 1.2,
                      opacity: aiModeEnabled ? 0.36 : 0.85,
                      className: aiModeEnabled ? 'customer-presence ai-soft' : 'customer-presence',
                    }}
                  />
                ))}
                {(viewMode === 'all' || viewMode === 'odp') && filteredOdps.map((odp) => {
                  const odpId = normalizeNodeId(odp?.id);
                  const offlineCount = Number(offlineCountByOdpId[odpId] || 0);
                  if (offlineCount <= 0) return null;
                  const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
                  if (!hasValidCoordinates(odpPosition[0], odpPosition[1])) return null;
                  return (
                    <Circle
                      key={`odp-offline-cluster-${odpId}`}
                      center={odpPosition}
                      radius={Math.max(24, Math.min(120, 20 + offlineCount * 6))}
                      pathOptions={{
                        color: '#ef4444',
                        fillColor: '#ef4444',
                        fillOpacity: 0.09,
                        opacity: 0.65,
                        weight: 1.5,
                        className: 'odp-offline-cluster-ring',
                      }}
                    />
                  );
                })}
                {(viewMode === 'all' || viewMode === 'odp') && filteredOdps.map((odp) => {
                  if (!hasValidCoordinates(odp.latitude, odp.longitude)) return null;
                  const odpId = normalizeNodeId(odp.id);
                  const odpActionFeedbackState = String(actionFeedbackByInfraId[`odp:${odpId}`]?.state || '').toLowerCase();
                  const odpRecommendedState = liveRecommendedTargets.odp.has(odpId) ? AI_ACTION_STATES.RECOMMENDED : '';
                  const odpActionState = odpActionFeedbackState || odpRecommendedState;
                  const aiLevel = String(aiLevelByOdpId[odpId] || 'normal');
                  const hasAiAnomaly = aiModeEnabled && aiLevel === 'critical';
                  const isRootCauseOdp = aiModeEnabled && rootCauseOdpId && rootCauseOdpId === odpId;
                  const odpStatusRaw = String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase();
                  const odpStatus = aiModeEnabled && hasAiAnomaly
                    ? 'offline'
                    : (aiModeEnabled && aiLevel === 'warning' ? 'warning' : odpStatusRaw);
                  const pelangganCount = Number(customerCountByOdpId[odpId] || 0);
                  const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
                  const isRelated = isOdpRelatedToFocus(odp);
                  return (
                    <React.Fragment key={'odp-' + odp.id}>
                    <Marker
                      position={odpPosition}
                      icon={createODPMarker(odpStatus, {
                        rootCause: isRootCauseOdp,
                        focused: focusNode?.type === 'odp' && focusNode?.id === odpId,
                      })}
                      opacity={isRelated ? 1 : 0.28}
                      eventHandlers={{
                        click: () => handleFocusNode('odp', odpId),
                        mouseover: () => setHoveredNode({ type: 'odp', id: odpId }),
                        mouseout: () => setHoveredNode(null),
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -18]} opacity={0.95}>
                        {odp.name} â€¢ {pelangganCount} pelanggan
                      </Tooltip>
                      <Popup className="ktc-map-popup">
                        {renderInfraPopupCard('odp', odp, odpStatus, pelangganCount)}
                      </Popup>
                    </Marker>
                    {(hasAiAnomaly || (aiModeEnabled && aiLevel === 'warning') || isRootCauseOdp) && (
                      <Circle
                        center={odpPosition}
                        radius={isRootCauseOdp ? 72 : (hasAiAnomaly ? 50 : 43)}
                        pathOptions={{
                          className: `ai-node-ring ${isRootCauseOdp ? 'is-root' : ''} ${aiLevel === 'warning' ? 'is-warning' : ''}`.trim(),
                          color: isRootCauseOdp ? '#f43f5e' : (aiLevel === 'warning' ? '#f59e0b' : '#ef4444'),
                          fillColor: isRootCauseOdp ? '#f43f5e' : (aiLevel === 'warning' ? '#f59e0b' : '#ef4444'),
                          fillOpacity: isRootCauseOdp ? 0.018 : 0.012,
                          weight: isRootCauseOdp ? 2.8 : 1.6,
                          opacity: isRootCauseOdp ? 0.88 : 0.64,
                        }}
                      />
                    )}
                    {!!odpActionState && (
                      <Circle
                        center={odpPosition}
                        radius={60}
                        pathOptions={{
                          className: `noc-action-feedback-ring is-${odpActionState}`,
                          color: odpActionState === 'success' || odpActionState === 'succeeded'
                            ? '#22c55e'
                            : (odpActionState === 'failed'
                              ? '#ef4444'
                              : (odpActionState === 'recommended' || odpActionState === 'detected' ? '#22d3ee' : '#facc15')),
                          fillColor: odpActionState === 'success' || odpActionState === 'succeeded'
                            ? '#22c55e'
                            : (odpActionState === 'failed'
                              ? '#ef4444'
                              : (odpActionState === 'recommended' || odpActionState === 'detected' ? '#22d3ee' : '#facc15')),
                          fillOpacity: odpActionState === 'recommended' || odpActionState === 'detected' ? 0.06 : 0.08,
                          weight: odpActionState === 'processing' || odpActionState === 'executing' ? 2.6 : 2.1,
                          dashArray: (odpActionState === 'processing' || odpActionState === 'executing' || odpActionState === 'recommended') ? '6,6' : undefined,
                        }}
                      />
                    )}
                    {isRootCauseOdp && (
                      <Circle
                        center={odpPosition}
                        radius={86}
                        pathOptions={{
                          className: 'ai-node-ring is-root root-shockwave',
                          color: '#fb7185',
                          fillColor: '#fb7185',
                          fillOpacity: 0.01,
                          weight: 1.6,
                          opacity: 0.72,
                        }}
                      />
                    )}
                    </React.Fragment>
                  );
                })}



                {(viewMode === 'all' || viewMode === 'olt') && filteredOlts.map((olt) => {
                  if (!hasValidCoordinates(olt.latitude, olt.longitude)) return null;
                  const oltId = normalizeNodeId(olt.id);
                  const oltActionFeedbackState = String(actionFeedbackByInfraId[`olt:${oltId}`]?.state || '').toLowerCase();
                  const oltRecommendedState = liveRecommendedTargets.olt.has(oltId) ? AI_ACTION_STATES.RECOMMENDED : '';
                  const oltActionState = oltActionFeedbackState || oltRecommendedState;
                  const aiLevel = String(aiLevelByOltId[oltId] || 'normal');
                  const hasAiAnomaly = aiModeEnabled && aiLevel === 'critical';
                  const isRootLinkedOlt = aiModeEnabled && !!rootCauseOdpId && filteredOdps.some((item) => normalizeNodeId(item?.olt_id) === oltId && normalizeNodeId(item?.id) === rootCauseOdpId);
                  const oltStatusRaw = String(oltRuntimeStatusById[oltId] || 'unknown').toLowerCase();
                  const oltStatus = aiModeEnabled && hasAiAnomaly
                    ? 'offline'
                    : (aiModeEnabled && aiLevel === 'warning' ? 'warning' : oltStatusRaw);
                  const pelangganCount = Number(oltCustomerCountById[oltId] || 0);
                  const oltPosition = getInfraDisplayPosition('olt', olt) || [Number(olt.latitude), Number(olt.longitude)];
                  const isRelated = isOltRelatedToFocus(olt);
                  return (
                    <React.Fragment key={'olt-' + olt.id}>
                    <Marker
                      position={oltPosition}
                      icon={createOLTMarker(oltStatus, {
                        rootCause: isRootLinkedOlt,
                        focused: focusNode?.type === 'olt' && focusNode?.id === oltId,
                      })}
                      opacity={isRelated ? 1 : 0.28}
                      eventHandlers={{
                        click: () => handleFocusNode('olt', oltId),
                        mouseover: () => setHoveredNode({ type: 'olt', id: oltId }),
                        mouseout: () => setHoveredNode(null),
                      }}
                    >
                      <Tooltip direction="top" offset={[0, -18]} opacity={0.95}>
                        {olt.name} â€¢ {pelangganCount} pelanggan
                      </Tooltip>
                      <Popup className="ktc-map-popup">
                        {renderInfraPopupCard('olt', olt, oltStatus, pelangganCount)}
                      </Popup>
                    </Marker>
                    {hasAiAnomaly && (
                      <Circle
                        center={oltPosition}
                        radius={52}
                        pathOptions={{
                          className: 'ai-node-ring',
                          color: '#ef4444',
                          fillColor: '#ef4444',
                          fillOpacity: 0.015,
                          weight: 1.6,
                          opacity: 0.64,
                        }}
                      />
                    )}
                    {!!oltActionState && (
                      <Circle
                        center={oltPosition}
                        radius={62}
                        pathOptions={{
                          className: `noc-action-feedback-ring is-${oltActionState}`,
                          color: oltActionState === 'success' || oltActionState === 'succeeded'
                            ? '#22c55e'
                            : (oltActionState === 'failed'
                              ? '#ef4444'
                              : (oltActionState === 'recommended' || oltActionState === 'detected' ? '#22d3ee' : '#facc15')),
                          fillColor: oltActionState === 'success' || oltActionState === 'succeeded'
                            ? '#22c55e'
                            : (oltActionState === 'failed'
                              ? '#ef4444'
                              : (oltActionState === 'recommended' || oltActionState === 'detected' ? '#22d3ee' : '#facc15')),
                          fillOpacity: oltActionState === 'recommended' || oltActionState === 'detected' ? 0.06 : 0.08,
                          weight: oltActionState === 'processing' || oltActionState === 'executing' ? 2.6 : 2.1,
                          dashArray: (oltActionState === 'processing' || oltActionState === 'executing' || oltActionState === 'recommended') ? '6,6' : undefined,
                        }}
                      />
                    )}
                    {isRootLinkedOlt && (
                      <Circle
                        center={oltPosition}
                        radius={68}
                        pathOptions={{
                          className: 'ai-node-ring is-root root-shockwave',
                          color: '#22d3ee',
                          fillColor: '#22d3ee',
                          fillOpacity: 0.01,
                          weight: 1.4,
                          opacity: 0.68,
                        }}
                      />
                    )}
                    </React.Fragment>
                  );
                })}



                
                {(viewMode === 'all' || viewMode === 'odp') && filteredOdps.map((odp) => {

                  const olt = odp?.olt_id ? filteredOltsById[odp.olt_id] : null;

                  if (!olt) return null;

                  if (!hasValidCoordinates(odp.latitude, odp.longitude) || !hasValidCoordinates(olt.latitude, olt.longitude)) return null;



                  const oltPosition = getInfraDisplayPosition('olt', olt) || [Number(olt.latitude), Number(olt.longitude)];
                  const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];
                  const odpId = normalizeNodeId(odp.id);
                  const oltId = normalizeNodeId(olt.id);
                  const oltStatus = String(oltRuntimeStatusById[oltId] || 'unknown').toLowerCase();
                  const odpStatus = String(odpRuntimeStatusById[odpId] || 'unknown').toLowerCase();
                  const infraActionState = String(actionFeedbackByInfraId[`odp:${odpId}`]?.state || actionFeedbackByInfraId[`olt:${oltId}`]?.state || '').toLowerCase();
                  const hasAiAnomaly = aiModeEnabled && (!!anomalyByOdpId[odpId] || !!anomalyByOltId[oltId]);
                  const connection = drawConnection(
                    oltPosition,
                    odpPosition,
                    { error: hasAiAnomaly || oltStatus !== 'online' || odpStatus !== 'online', weight: 2.8 },
                  );
                  const polylineClassName = `${connection.className}${hasAiAnomaly ? ' ai-link-alert' : ''}`;
                  const pelangganCount = Number(customerCountByOdpId[odpId] || 0);
                  const inferredCoreLoad = Math.min(100, pelangganCount * 12 + (hasAiAnomaly ? 12 : 0));
                  const coreBandwidth = getBandwidthStyle(
                    resolveLinkLoad(odp) || inferredCoreLoad,
                    {
                      status: hasAiAnomaly ? 'offline' : (oltStatus === 'online' && odpStatus === 'online' ? 'online' : 'offline'),
                      useGradient: true,
                      minWeight: 2.4,
                      maxWeight: 7.2,
                    },
                  );



                  const corePositions = buildDistributionPath(oltPosition, odpPosition, `core-${olt.id}-${odp.id}`);
                  const coreColor = coreBandwidth.color || connection.pathOptions?.color || '#06b6d4';
                  const coreReversePositions = [...corePositions].reverse();
                  const hasProblem = infraActionState === 'failed' || (aiModeEnabled && hasAiAnomaly) || oltStatus !== 'online' || odpStatus !== 'online';
                  const isHighLoad = !hasProblem && inferredCoreLoad >= 66;
                  const normalWeight = Math.max(1.7, coreBandwidth.weight - 1.3);
                  const warningWeight = Math.max(2.8, coreBandwidth.weight - 0.2);
                  const criticalWeight = Math.max(3.6, coreBandwidth.weight + 0.8);
                  const isRootCausePath = aiModeEnabled && !!rootCauseOdpId && rootCauseOdpId === odpId;
                  const isRelated = isOltRelatedToFocus(olt) || isOdpRelatedToFocus(odp);
                  const focusClass = focusNode?.id ? (isRelated ? 'map-focus-active' : 'map-focus-dim') : '';
                  const aiPathClass = aiModeEnabled ? (hasAiAnomaly || isRootCausePath ? 'ai-path-focus' : 'ai-path-dim') : '';
                  const rootPathClass = isRootCausePath ? 'ai-path-root' : '';
                  const problemClass = `${polylineClassName} ${coreBandwidth.glowClass} link-problem ${focusClass} ${aiPathClass} ${rootPathClass}`.trim();
                  const warningClass = `link-warning ${coreBandwidth.glowClass} ${focusClass} ${aiPathClass}`.trim();
                  const downlinkClass = `link-downlink ${coreBandwidth.glowClass} ${focusClass} ${aiPathClass}`.trim();
                  const uplinkClass = `link-uplink ${focusClass} ${aiPathClass}`.trim();
                  const normalClass = `link-normal ${focusClass} ${aiPathClass}`.trim();
                  const rootWeightBoost = isRootCausePath ? 1.4 : 0;

                  return (
                    <React.Fragment key={`line-olt-odp-${odp.id}`}>
                      {!flowModeEnabled ? (
                        <Polyline
                          className={hasProblem ? problemClass : (isHighLoad ? warningClass : normalClass)}
                          positions={corePositions}
                          pathOptions={{
                            color: hasProblem ? '#ef4444' : (isHighLoad ? '#f59e0b' : '#00d4ff'),
                            weight: (hasProblem ? criticalWeight : (isHighLoad ? warningWeight : normalWeight)) + rootWeightBoost,
                            opacity: hasProblem ? 0.92 : (infraActionState === 'processing' ? 0.96 : (aiModeEnabled && !isRootCausePath ? 0.32 : 0.86)),
                            className: hasProblem ? problemClass : (isHighLoad ? warningClass : normalClass),
                          }}
                        />
                      ) : hasProblem ? (
                        <>
                          <Polyline
                            className={problemClass}
                            positions={corePositions}
                            pathOptions={{
                              ...connection.pathOptions,
                              color: '#ef4444',
                              weight: coreBandwidth.weight + 0.4 + rootWeightBoost,
                              className: problemClass,
                            }}
                          />
                          <LeafletFlowDirection
                            positions={corePositions}
                            color="#ef4444"
                            offset="9%"
                            repeat="72px"
                            pixelSize={8}
                            className={problemClass}
                          />
                        </>
                      ) : (
                        <>
                          <Polyline
                            className={downlinkClass}
                            positions={corePositions}
                            pathOptions={{
                              color: '#00d4ff',
                              weight: (isHighLoad ? warningWeight : coreBandwidth.weight) + rootWeightBoost,
                              opacity: infraActionState === 'processing' ? 0.98 : (aiModeEnabled && !isRootCausePath ? 0.42 : 0.94),
                              className: isHighLoad ? warningClass : downlinkClass,
                            }}
                          />
                          <LeafletFlowDirection
                            positions={corePositions}
                            color={isHighLoad ? '#f59e0b' : '#00d4ff'}
                            offset="9%"
                            repeat="72px"
                            pixelSize={8}
                            className={isHighLoad ? warningClass : downlinkClass}
                          />
                          <Polyline
                            className={uplinkClass}
                            positions={coreReversePositions}
                            pathOptions={{
                              color: '#f59e0b',
                              weight: Math.max(1.6, coreBandwidth.weight - 1.1 + (isRootCausePath ? 0.8 : 0)),
                              opacity: infraActionState === 'processing' ? 0.96 : (aiModeEnabled && !isRootCausePath ? 0.34 : 0.9),
                              dashArray: '10 8',
                              className: uplinkClass,
                            }}
                          />
                          <LeafletFlowDirection
                            positions={coreReversePositions}
                            color="#f59e0b"
                            offset="13%"
                            repeat="84px"
                            pixelSize={7}
                            className={uplinkClass}
                          />
                        </>
                      )}
                    </React.Fragment>
                  );

                })}



                {searchHighlightCustomers.map((customer) => (
                  <Circle
                    key={`search-ring-${customer.id}`}
                    center={[Number(customer.latitude), Number(customer.longitude)]}
                    radius={selectedCustomerId === customer.id ? 28 : 22}
                    pathOptions={{
                      className: selectedCustomerId === customer.id ? 'search-highlight-ring is-selected' : 'search-highlight-ring',
                      color: '#22d3ee',
                      fillColor: '#22d3ee',
                      fillOpacity: selectedCustomerId === customer.id ? 0.16 : 0.1,
                      weight: selectedCustomerId === customer.id ? 2.4 : 1.8,
                      opacity: 0.95,
                    }}
                  />
                ))}

                {(viewMode === 'all' || viewMode === 'onu') && !showCustomerClusters && visibleCustomerPoints.map((customer) => {

                  const odp = resolveFilteredOdp(customer);

                  if (!odp) return null;

                  if (!hasValidCoordinates(customer.latitude, customer.longitude) || !hasValidCoordinates(odp.latitude, odp.longitude)) return null;



                  const insight = aiNodeInsights.byId[String(customer.id || '')];
                  const aiLevel = String(insight?.level || 'normal');
                  const hasAiAnomaly = aiModeEnabled && aiLevel === 'critical';
                  const odpPosition = getInfraDisplayPosition('odp', odp) || [Number(odp.latitude), Number(odp.longitude)];



                  const customerPositions = buildDistributionPath(
                    odpPosition,
                    [Number(customer.latitude), Number(customer.longitude)],
                    `${customer.id}-${odp.id}`,
                  );
                  const customerBandwidth = getBandwidthStyle(
                    estimateCustomerLoad(customer),
                    {
                      status: hasAiAnomaly ? 'offline' : customer.connection_status,
                      useGradient: true,
                      minWeight: 2.2,
                      maxWeight: 6.6,
                    },
                  );
                  const customerColor = hasAiAnomaly ? '#ef4444' : customerBandwidth.color;
                  const isOffline = customer.connection_status === 'offline';
                  const baseLineClass = customer.connection_status === 'online'
                    ? 'customer-link customer-link-online'
                    : (isOffline ? 'customer-link customer-link-offline' : 'customer-link customer-link-unknown');
                  const customerOdpId = normalizeNodeId(odp?.id);
                  const isRootCausePath = aiModeEnabled && !!rootCauseOdpId && rootCauseOdpId === customerOdpId;
                  const isRelated = isCustomerRelatedToFocus(customer);
                  const focusClass = focusNode?.id ? (isRelated ? 'map-focus-active' : 'map-focus-dim') : '';
                  const aiPathClass = aiModeEnabled ? (hasAiAnomaly || isRootCausePath ? 'ai-path-focus' : 'ai-path-dim') : '';
                  const rootPathClass = isRootCausePath ? 'ai-path-root' : '';
                  const lineClass = `${baseLineClass}${hasAiAnomaly ? ' ai-link-alert' : ''} ${customerBandwidth.glowClass} ${focusClass} ${aiPathClass} ${rootPathClass}`.trim();
                  const lineWeight = Math.max(2.2, customerBandwidth.weight + (isRootCausePath ? 0.6 : 0));
                  const lineOpacity = flowModeEnabled
                    ? (aiModeEnabled && !isRootCausePath ? 0.35 : 0.92)
                    : (aiModeEnabled && !isRootCausePath ? 0.22 : 0.86);
                  const lineDashArray = flowModeEnabled ? '10 7' : '8 8';

                  return (
                    <React.Fragment key={`line-odp-customer-${customer.id}`}>
                      <Polyline
                        className={lineClass}
                        positions={customerPositions}
                        pathOptions={{
                          color: isOffline ? '#ef4444' : customerColor,
                          weight: lineWeight,
                          opacity: lineOpacity,
                          dashArray: lineDashArray,
                          className: lineClass,
                        }}
                      />
                      {flowModeEnabled ? (
                        <LeafletFlowDirection
                          positions={customerPositions}
                          color={isOffline ? '#ef4444' : customerColor}
                          offset="12%"
                          repeat="52px"
                          pixelSize={7}
                          className={lineClass}
                        />
                      ) : null}
                    </React.Fragment>
                  );

                })}

                {(viewMode === 'all' || viewMode === 'onu') && showCustomerClusters && customerClusters.map((cluster) => (
                  <Marker
                    key={`customer-cluster-${cluster.id}`}
                    position={[cluster.latitude, cluster.longitude]}
                    icon={createClusterIcon(cluster)}
                    eventHandlers={{
                      click: () => {
                        setAutoFitEnabled(false);
                        setAutoFitKey((value) => value + 1);
                      },
                    }}
                  >
                    <Tooltip direction="top" offset={[0, -14]} opacity={0.95}>
                      Cluster pelanggan ({cluster.count})
                    </Tooltip>
                    <Popup className="ktc-map-popup">
                      <div className="noc-popup-card">
                        <div className="noc-popup-head">
                          <p className="noc-popup-title">Cluster Pelanggan</p>
                          <p className="noc-popup-sub">{cluster.count} pelanggan pada area ini</p>
                        </div>
                        <div className="noc-popup-metrics">
                          <div className="noc-popup-metric"><p className="label">Online</p><p className="value">{cluster.online}</p></div>
                          <div className="noc-popup-metric"><p className="label">Offline</p><p className="value">{cluster.offline}</p></div>
                          <div className="noc-popup-metric"><p className="label">Anomaly</p><p className="value">{cluster.anomaly}</p></div>
                          <div className="noc-popup-metric"><p className="label">Mode</p><p className="value">Clustering</p></div>
                        </div>
                        <p className="noc-popup-foot">Zoom in untuk melihat node detail per pelanggan.</p>
                      </div>
                    </Popup>
                  </Marker>
                ))}



                {(viewMode === 'all' || viewMode === 'onu') && !showCustomerClusters && visibleCustomerPoints.map((customer) => {

                  const statusColor = getStatusColor(customer.connection_status);
                  const insight = aiNodeInsights.byId[String(customer.id || '')];
                  const aiLevel = String(insight?.level || 'normal');
                  const isOptimizing = optimizingCustomerIdSet.has(String(customer.id || ''));
                  const actionFeedbackState = String(actionFeedbackByCustomerId[String(customer.id || '')]?.state || '').toLowerCase();
                  const recommendedFeedbackState = liveRecommendedTargets.customer.has(String(customer.id || '')) ? AI_ACTION_STATES.RECOMMENDED : '';
                  const focusFeedbackState = focusPulseCustomerId === String(customer.id || '') ? 'focus' : '';
                  const mergedFeedbackState = actionFeedbackState || recommendedFeedbackState || focusFeedbackState;
                  const customerActiveTicket = activeTicketByCustomerId[String(customer.id || '').trim()] || null;

                  const isSelected = selectedCustomerId === customer.id;
                  const isRelated = isCustomerRelatedToFocus(customer);



                  return (
                    <React.Fragment key={`customer-wrap-${customer.id}`}>
                    {isOptimizing && (
                      <Circle
                        center={[Number(customer.latitude), Number(customer.longitude)]}
                        radius={Math.max(18, 26 - Math.min(10, mapZoom))}
                        pathOptions={{
                          color: '#22d3ee',
                          fillColor: '#22d3ee',
                          fillOpacity: 0.09,
                          weight: 2,
                          dashArray: '5,6',
                          className: 'ktc-optimization-ring',
                        }}
                        renderer={MAP_SVG_RENDERER}
                      />
                    )}
                    {!!mergedFeedbackState && (
                      <Circle
                        center={[Number(customer.latitude), Number(customer.longitude)]}
                        radius={Math.max(20, 28 - Math.min(10, mapZoom))}
                        pathOptions={{
                          color: mergedFeedbackState === 'success' || mergedFeedbackState === 'succeeded'
                            ? '#22c55e'
                            : (mergedFeedbackState === 'failed'
                              ? '#ef4444'
                              : (mergedFeedbackState === 'focus' || mergedFeedbackState === 'recommended' || mergedFeedbackState === 'detected' ? '#22d3ee' : '#facc15')),
                          fillColor: mergedFeedbackState === 'success' || mergedFeedbackState === 'succeeded'
                            ? '#22c55e'
                            : (mergedFeedbackState === 'failed'
                              ? '#ef4444'
                              : (mergedFeedbackState === 'focus' || mergedFeedbackState === 'recommended' || mergedFeedbackState === 'detected' ? '#22d3ee' : '#facc15')),
                          fillOpacity: mergedFeedbackState === 'focus' ? 0.08 : (mergedFeedbackState === 'recommended' || mergedFeedbackState === 'detected' ? 0.06 : 0.1),
                          weight: mergedFeedbackState === 'processing' || mergedFeedbackState === 'executing'
                            ? 2.4
                            : (mergedFeedbackState === 'focus' ? 2.8 : 2),
                          dashArray: (mergedFeedbackState === 'processing' || mergedFeedbackState === 'executing' || mergedFeedbackState === 'recommended') ? '5,6' : (mergedFeedbackState === 'focus' ? '4,5' : undefined),
                          className: `noc-action-feedback-ring is-${mergedFeedbackState}`,
                        }}
                        renderer={MAP_SVG_RENDERER}
                      />
                    )}
                    <Marker

                      key={`customer-${customer.id}`}

                      position={[Number(customer.latitude), Number(customer.longitude)]}

                      icon={createWifiIcon(
                        customer.connection_status,
                        isSelected,
                        searchHighlightSet.has(String(customer.id || '')),
                        {
                          anomaly: aiModeEnabled && !!insight?.anomaly?.hasAnomaly,
                          predictive: aiModeEnabled && !!insight?.hasPredictive,
                          level: aiLevel,
                          rootCause: aiModeEnabled && !!rootCauseOdpId && normalizeNodeId(resolveFilteredOdp(customer)?.id) === rootCauseOdpId,
                          suspected: aiModeEnabled && (!!insight?.anomaly?.hasAnomaly || !!insight?.hasPredictive),
                          feedback: mergedFeedbackState,
                        },
                        {
                          active: Boolean(customerActiveTicket),
                          state: customerActiveTicket?.lifecycle?.key || '',
                        },
                      )}
                      opacity={isRelated ? 1 : 0.24}

                      eventHandlers={{

                        click: () => {
                          setSelectedCustomerId(customer.id);
                          handleFocusNode('customer', customer.id);
                        },
                        mouseover: () => setHoveredNode({ type: 'customer', id: customer.id }),
                        mouseout: () => setHoveredNode(null),

                      }}

                    >
                      <Tooltip direction="top" offset={[0, -12]} opacity={0.95}>
                        {customer.name} â€¢ {customer.customer_id || '-'}
                      </Tooltip>

                      <Popup className="ktc-map-popup">
                        <div className="space-y-2">
                          {renderCustomerPopupCard(customer, statusColor, insight)}
                          {customerActiveTicket ? (
                            <div className={`rounded border px-2 py-1 text-[11px] ${getTicketToneClass(customerActiveTicket.lifecycle?.tone)}`}>
                              Ticket aktif: {customerActiveTicket.lifecycle?.label || customerActiveTicket.status || 'Open'}
                            </div>
                          ) : null}
                          {optimizingCustomerIdSet.has(String(customer.id || '')) && (
                            <div className="rounded border border-cyan-400/40 bg-cyan-500/10 px-2 py-1 text-[11px] text-cyan-200">
                              Status Optimizing: aktif (auto optimization)
                            </div>
                          )}
                          <div className="grid grid-cols-2 gap-2">
                            <button
                              type="button"
                              onClick={() => openCustomerRecord(customer, 'view')}
                              className="w-full rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 px-3 py-1.5 text-xs font-semibold hover:bg-cyan-500/30"
                            >
                              View Customer
                            </button>
                            <button
                              type="button"
                              onClick={() => {
                                const linkedOdpId = String(customer?.linked_odp_id || customer?.odp_id || '').trim();
                                if (linkedOdpId) {
                                  navigate(`/infrastructure?nodeType=odp&nodeId=${encodeURIComponent(linkedOdpId)}`);
                                  return;
                                }
                                navigate('/infrastructure');
                              }}
                              className="w-full rounded bg-slate-500/20 text-slate-200 border border-slate-400/35 px-3 py-1.5 text-xs font-semibold hover:bg-slate-500/30"
                            >
                              Open in Infrastructure
                            </button>
                          </div>
                        </div>
                      </Popup>

                    </Marker>
                    </React.Fragment>

                  );

                })}

              </MapContainer>
              )}

              {useGpuMapEngine && selectedCustomer ? (
                <div className="absolute left-4 bottom-4 z-[620] w-[min(92vw,320px)]">
                  <div className="rounded-xl border border-cyan-400/30 bg-[#07101d]/92 p-3 shadow-[0_10px_28px_rgba(2,132,199,0.35)] backdrop-blur-sm">
                    {renderCustomerPopupCard(selectedCustomer, getStatusColor(selectedCustomer?.connection_status), aiNodeInsights.byId[String(selectedCustomer?.id || '')])}
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <button
                        type="button"
                        onClick={() => openCustomerRecord(selectedCustomer, 'view')}
                        className="w-full rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 px-3 py-1.5 text-xs font-semibold hover:bg-cyan-500/30"
                      >
                        View Customer
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          const linkedOdpId = String(selectedCustomer?.linked_odp_id || selectedCustomer?.odp_id || '').trim();
                          if (linkedOdpId) {
                            navigate(`/infrastructure?nodeType=odp&nodeId=${encodeURIComponent(linkedOdpId)}`);
                            return;
                          }
                          navigate('/infrastructure');
                        }}
                        className="w-full rounded bg-slate-500/20 text-slate-200 border border-slate-400/35 px-3 py-1.5 text-xs font-semibold hover:bg-slate-500/30"
                      >
                        Open in Infrastructure
                      </button>
                    </div>
                  </div>
                </div>
              ) : null}

              {useGpuMapEngine && !selectedCustomer && focusedInfraEntity ? (
                <div className="absolute left-4 bottom-4 z-[620] w-[min(92vw,320px)]">
                  <div className="rounded-xl border border-cyan-400/30 bg-[#07101d]/92 p-3 shadow-[0_10px_28px_rgba(2,132,199,0.35)] backdrop-blur-sm">
                    {renderInfraPopupCard(
                      focusedInfraEntity.type,
                      focusedInfraEntity.data,
                      focusedInfraEntity.status,
                      focusedInfraEntity.pelangganCount,
                    )}
                  </div>
                </div>
              ) : null}

              {useGpuMapEngine && isDevMode && showDebugOverlay ? (
                <div className="absolute left-4 top-16 z-[625] w-[min(92vw,280px)] rounded-lg border border-cyan-400/35 bg-[#04101b]/92 p-2 text-[11px] text-cyan-100 shadow-[0_10px_28px_rgba(6,182,212,0.25)]">
                  <div className="mb-1 flex items-center justify-between">
                    <strong className="font-semibold text-cyan-200">GPU Debug</strong>
                    <button
                      type="button"
                      className="rounded border border-cyan-400/40 px-1.5 py-0.5 text-[10px] hover:bg-cyan-500/20"
                      onClick={() => setShowDebugOverlay(false)}
                    >
                      hide
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                    <span>FPS</span><span>{Number(gpuStats?.fps || 0)}</span>
                    <span>Nodes</span><span>{Number(gpuStats?.nodeCount || 0)}</span>
                    <span>Lines</span><span>{Number(gpuStats?.lineCount || 0)}</span>
                    <span>Particles</span><span>{Number(gpuStats?.particleCount || 0)}</span>
                    <span>Selected</span><span className="truncate">{String(gpuStats?.selectedNodeId || '-')}</span>
                    <span>WebGL</span><span>{gpuStats?.webgl ? 'OK' : 'FAIL'}</span>
                    <span>Mode</span><span>{gpuStats?.detailMode ? 'detail' : 'cluster'}</span>
                  </div>
                  <p className="mt-1 text-[10px] text-cyan-300/80">
                    Last: {gpuStats?.lastUpdateAt ? new Date(gpuStats.lastUpdateAt).toLocaleTimeString('id-ID') : '-'}
                  </p>
                </div>
              ) : null}

            </div>
            </div>

            <div className={isMapFullscreen ? 'noc-side-panel-shell noc-side-panel-floating absolute right-3 top-14 z-[650] w-[min(92vw,300px)] max-h-[calc(100vh-4.5rem)] overflow-auto rounded-xl bg-[#0f172a]/95 p-2 backdrop-blur-sm space-y-3' : 'noc-side-panel-shell space-y-4'}>

            <div className="noc-panel-tabs">
              {MONITORING_PRIMARY_TABS.map((tabKey) => {
                const label = tabKey === 'infra'
                  ? 'Infrastruktur'
                  : tabKey === 'capacity'
                    ? 'Kapasitas'
                    : tabKey === 'customers'
                      ? 'Pelanggan'
                      : tabKey;
                return (
                  <button
                    key={`monitor-tab-${tabKey}`}
                    type="button"
                    className={`noc-panel-tab ${rightPanelTab === tabKey ? 'is-active' : ''}`}
                    onClick={() => setRightPanelTab(tabKey)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            <div className="noc-side-panel-meta">
              <span>Panel aktif: {rightPanelTabLabel}</span>
              <span>Node: {filteredCustomersByMode.length}</span>
            </div>

            <div className={`bg-[#121212] rounded-xl p-4 border border-white/10 noc-glass-panel noc-side-card ${rightPanelTab !== 'infra' ? 'hidden' : ''}`}>

              <h3 className="text-white font-semibold mb-3">Filter Monitoring</h3>

              <div className="space-y-3">
                <div className="noc-filter-chips">
                  {STATUS_CHIPS.map((chip) => (
                    <button
                      type="button"
                      key={chip.value}
                      onClick={() => setStatusFilter(chip.value)}
                      className={`noc-filter-chip ${statusFilter === chip.value ? 'is-active' : ''}`}
                    >
                      <span className="h-2 w-2 rounded-full" style={{ backgroundColor: semanticToneToColor(chip.tone) }} />
                      <span>{chip.label}</span>
                    </button>
                  ))}
                </div>
                <div className="noc-filter-chips">
                  {VIEW_MODE_OPTIONS.map((mode) => (
                    <button
                      type="button"
                      key={mode.value}
                      onClick={() => setViewMode(mode.value)}
                      className={`noc-filter-chip ${viewMode === mode.value ? 'is-active' : ''}`}
                    >
                      {mode.label}
                    </button>
                  ))}
                </div>

                <Select value={infraFilter} onValueChange={setInfraFilter}>

                  <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">

                    <SelectValue placeholder="Filter infrastruktur" />

                  </SelectTrigger>

                  <SelectContent className="bg-[#121212] border-white/10">

                    <SelectItem value="all" className="text-white">Semua Infrastruktur</SelectItem>

                    <SelectItem value="live" className="text-white">Live</SelectItem>

                    <SelectItem value="plan" className="text-white">Plan</SelectItem>

                  </SelectContent>

                </Select>

                <SearchableComboBox
                  value={selectedNasId === 'all' ? '' : selectedNasId}
                  onChange={(value) => setSelectedNasId(value || 'all')}
                  options={nasFilterOptions}
                  placeholder="Filter NAS"
                  searchPlaceholder="Cari NAS..."
                  emptyLabel="NAS tidak ditemukan"
                  includeEmpty
                  emptyValueLabel="Semua NAS"
                />

                <SearchableComboBox
                  value={selectedOltId === 'all' ? '' : selectedOltId}
                  onChange={(value) => setSelectedOltId(value || 'all')}
                  options={oltFilterOptions}
                  placeholder="Filter OLT"
                  searchPlaceholder="Cari OLT..."
                  emptyLabel="OLT tidak ditemukan"
                  includeEmpty
                  emptyValueLabel="Semua OLT"
                />

              </div>

            </div>

            <div className={`bg-[#121212] rounded-xl p-4 border border-white/10 noc-glass-panel noc-ai-panel noc-side-card ${rightPanelTab !== 'ai' ? 'hidden' : ''}`}>
              <div className="flex items-center justify-between gap-2 mb-3">
              <h3 className="text-white font-semibold">Wawasan AI</h3>
                <span className={`noc-ai-state ${aiModeEnabled ? 'is-on' : 'is-off'}`}>
                  {aiModeEnabled ? 'ON' : 'OFF'}
                </span>
              </div>
              {isReplayMode && activeReplayIncident ? (
                <div className="mb-3 rounded-lg border border-cyan-400/30 bg-cyan-500/10 p-3 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs uppercase tracking-wide text-cyan-100">Linimasa Replay</p>
                    <span className="text-[10px] text-cyan-200">{replayClockLabel}</span>
                  </div>
                  <p className="text-[11px] text-slate-200">{replayRuntime?.currentEvent?.summary || 'Pilih marker timeline untuk melihat detail event.'}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {replayTimelineMarkers.map((point) => (
                      <Button
                        key={`replay-jump-${point.key}`}
                        type="button"
                        size="sm"
                        variant="outline"
                        className={`h-6 px-2 text-[10px] ${replayRuntime?.currentEvent?.id === point.key ? 'bg-cyan-500 text-slate-950 border-cyan-300' : 'btn-secondary'}`}
                        onClick={() => {
                          setReplayPlaying(false);
                          setReplayCursorMs(Number(point.atMs || 0));
                        }}
                      >
                        {point.label}
                      </Button>
                    ))}
                  </div>
                  <div className="max-h-32 overflow-auto space-y-1 pr-1">
                    {(activeReplayIncident.events || []).map((event) => {
                      const isActive = replayRuntime?.currentEvent?.id === event.id;
                      return (
                        <button
                          type="button"
                          key={`replay-event-${event.id}`}
                          className={`w-full text-left rounded border px-2 py-1.5 text-[10px] transition ${isActive ? 'border-cyan-300 bg-cyan-500/20 text-cyan-100' : 'border-white/10 bg-[#0A0A0A] text-slate-300 hover:border-cyan-400/40'}`}
                          onClick={() => {
                            setReplayPlaying(false);
                            setReplayCursorMs(Number(event.atMs || 0));
                          }}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span>{event.label}</span>
                            <span className="text-[9px] opacity-70">{Math.round(Number(event.atMs || 0) / 1000)}s</span>
                          </div>
                          {event.summary ? <p className="mt-0.5 opacity-80">{event.summary}</p> : null}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <div className="mb-3 rounded-lg border border-cyan-400/25 bg-cyan-500/10 p-3 space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs uppercase tracking-wide text-cyan-100">Action Center (Monitoring)</p>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] border ${semanticToneToBadgeClass(mapRiskScoreToTone(isReplayMode ? replayPanelContext?.risk : aiMonitoringResult?.risk))}`}>
                    Risk {Math.max(0, Math.min(100, Number(isReplayMode ? replayPanelContext?.risk : aiMonitoringResult?.risk || 0)))}%
                  </span>
                </div>
                <p className="text-[11px] text-slate-200">
                  {isReplayMode
                    ? `Replay context: ${String(replayPanelContext?.rootLabel || '-')}, impact ${Number(replayPanelContext?.impact || 0)} pelanggan`
                    : `Root cause: ${String(aiMonitoringResult?.root_cause?.odp || '-')}, impact ${Number(aiMonitoringResult?.root_cause?.impact || 0)} pelanggan`}
                </p>
                <div className="flex items-center gap-2">
                  <label className="flex items-center gap-2 text-[11px] text-slate-200">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-cyan-400"
                      checked={Boolean(aiMonitoringResult?.auto_mode)}
                      onChange={(event) => handleToggleAutoAiMode(Boolean(event.target.checked))}
                      disabled={aiAutoModeUpdating || isReplayMode}
                    />
                    Mode AI Otomatis
                  </label>
                  <span className="text-[10px] text-slate-400">{isReplayMode ? 'Replay aktif: eksekusi live dijeda sementara.' : (aiAutoModeUpdating ? 'Menyimpan...' : 'Eksekusi aksi otomatis saat anomali + risiko tinggi')}</span>
                </div>
                <AIInsightPanel
                  actions={Array.isArray(aiActionButtons) && aiActionButtons.length > 0 ? aiActionButtons : (Array.isArray(aiMonitoringResult?.actions) ? aiMonitoringResult.actions.map((type) => ({ type, label: type })) : [])}
                  onExecute={handleExecuteAiAction}
                  busy={aiActionExecuting || isReplayMode}
                />
                {aiActionToast ? (
                  <p className="text-[11px] text-cyan-100">{aiActionToast}</p>
                ) : null}
                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-2 space-y-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Antrean Aksi AI</p>
                  <div>
                    <p className="text-[10px] text-cyan-200/90 mb-1">Disarankan</p>
                    {displayAiQueueSections.suggested.length > 0 ? (
                      <div className="space-y-1">
                        {displayAiQueueSections.suggested.map((item) => (
                          <div key={`queue-suggest-${item.key}`} className="rounded border border-cyan-400/25 bg-cyan-500/5 px-2 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[11px] text-cyan-100 font-medium truncate">{item.label}</p>
                              <span className="text-[10px] text-slate-300">{item.targetLabel}</span>
                            </div>
                            <div className="mt-1 flex items-center justify-between gap-2 text-[10px] text-slate-400">
                              <span>risk {Number(item.risk || 0)}%</span>
                              <span>conf {Number(item.confidence || 0)}%</span>
                              <span>{item.status}</span>
                            </div>
                            {!aiMonitoringResult?.auto_mode ? (
                              <div className="mt-1.5 flex justify-end">
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline"
                                  className="h-6 px-2 text-[10px] btn-secondary"
                                  onClick={() => handleExecuteAiAction(aiActionSourceByKey[item.key] || item)}
                                  disabled={aiActionExecuting || isReplayMode}
                                >
                                  Approve & Execute
                                </Button>
                              </div>
                            ) : null}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500">Tidak ada rekomendasi aktif.</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-amber-200/90 mb-1">Executing</p>
                    {displayAiQueueSections.executing.length > 0 ? (
                      <div className="space-y-1">
                        {displayAiQueueSections.executing.map((item) => (
                          <div key={`queue-run-${item.key}`} className="rounded border border-amber-400/30 bg-amber-500/10 px-2 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[11px] text-amber-100 font-medium truncate">{item.label}</p>
                              <span className="text-[10px] text-slate-300">{formatElapsedShort(item.elapsedMs)}</span>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">{item.targetLabel} â€¢ {item.auto ? 'auto' : 'manual'} â€¢ {item.status}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500">Tidak ada aksi berjalan.</p>
                    )}
                  </div>
                  <div>
                    <p className="text-[10px] text-emerald-200/90 mb-1">Completed</p>
                    {displayAiQueueSections.completed.length > 0 ? (
                      <div className="space-y-1">
                        {displayAiQueueSections.completed.map((item) => (
                          <div key={`queue-done-${item.key}`} className="rounded border border-emerald-400/30 bg-emerald-500/10 px-2 py-1.5">
                            <div className="flex items-center justify-between gap-2">
                              <p className="text-[11px] text-emerald-100 font-medium truncate">{item.label}</p>
                              <span className="text-[10px] text-slate-300">{item.status}</span>
                            </div>
                            <p className="text-[10px] text-slate-400 mt-1">{item.targetLabel}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-[11px] text-slate-500">Belum ada aksi selesai.</p>
                    )}
                  </div>
                  {Array.isArray(aiActionLogs) && aiActionLogs.length > 0 ? (
                    <div className="pt-1 border-t border-white/10">
                      <p className="text-[10px] uppercase tracking-wide text-slate-500">Backend Logs</p>
                      <ul className="mt-1 space-y-1 text-[10px] text-slate-400">
                        {aiActionLogs.slice(0, 3).map((log, idx) => (
                          <li key={`ai-action-log-${idx}`}>
                            risk {Number(log?.risk || 0)}% â€¢ {Array.isArray(log?.actions) ? log.actions.join(', ') || '-' : '-'}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              </div>
              {!aiModeEnabled ? (
                <div className="space-y-3">
                  <div className="rounded-lg border border-cyan-400/20 bg-cyan-500/10 p-3">
                    <p className="text-[10px] uppercase tracking-wide text-cyan-100">Scope Monitoring</p>
                    <p className="mt-1 text-[12px] text-slate-100">{topologyScopeLabel}</p>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="noc-mini-health is-ok">
                      <p className="label">Online</p>
                      <p className="value">{onlineCount}</p>
                    </div>
                    <div className={`noc-mini-health ${offlineCount > 0 ? 'is-warn' : 'is-ok'}`}>
                      <p className="label">Offline</p>
                      <p className="value">{offlineCount}</p>
                    </div>
                    <div className={`noc-mini-health ${warningCount > 0 ? 'is-warn' : 'is-ok'}`}>
                      <p className="label">Warning</p>
                      <p className="value">{warningCount}</p>
                    </div>
                    <div className="noc-mini-health">
                      <p className="label">Tidak Diketahui</p>
                      <p className="value">{unknownCount}</p>
                    </div>
                  </div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
                    <p className="text-[10px] uppercase tracking-wide text-slate-400">Anomali Terakhir</p>
                    <p className="mt-1 text-[12px] text-slate-100">
                      {lastAnomalyTimestamp ? lastAnomalyTimestamp.toLocaleString('id-ID') : 'Belum ada anomali pada scope saat ini'}
                    </p>
                    <p className="mt-2 text-[11px] text-slate-400">
                      AI mode akan menyorot node bermasalah, menambahkan prediksi, dan menampilkan prioritas tindakan operasional.
                    </p>
                    <Button
                      type="button"
                      onClick={() => setVisualMode('ai')}
                      className="mt-3 w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
                    >
                      Aktifkan AI Mode
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-2 text-xs">
                  <div className="noc-ai-item"><span className="dot is-info" /> Rata-rata skor AI: {aiNodeInsights.summary.avgScore}/100</div>
                  <div className="noc-ai-item"><span className="dot is-ok" /> Normal: {aiNodeInsights.summary.normalLevel}</div>
                  <div className="noc-ai-item"><span className="dot is-warning" /> Warning: {aiNodeInsights.summary.warningLevel}</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> Kritis: {aiNodeInsights.summary.criticalLevel}</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.totalAnomaly} node terdeteksi anomaly</div>
                  <div className="noc-ai-item"><span className="dot is-warning" /> {aiNodeInsights.summary.latencyHigh} node latency tinggi</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.signalWeak} node sinyal lemah</div>
                  <div className="noc-ai-item"><span className="dot is-danger" /> {aiNodeInsights.summary.offline} node offline</div>
                  <div className="noc-ai-item"><span className="dot is-warning" /> {aiNodeInsights.summary.packetLossHigh} node packet loss tinggi</div>
                  <div className="noc-ai-item"><span className="dot is-predictive" /> {aiNodeInsights.summary.predictiveCount} node berpotensi gangguan</div>
                  <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-2 space-y-1">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">Trend Jaringan</p>
                    <p className="text-[11px] text-slate-200">Status: {networkTrendState.label}</p>
                    <div className="grid grid-cols-1 gap-2">
                      <div>
                        <p className="text-[10px] text-cyan-300 mb-1">Skor AI (avg)</p>
                        <svg viewBox="0 0 220 56" className="w-full h-12 rounded bg-[#020817] border border-cyan-900/35">
                          <path d={networkScoreSparklinePath} fill="none" stroke="#22d3ee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                      <div>
                        <p className="text-[10px] text-amber-300 mb-1">Latency (avg)</p>
                        <svg viewBox="0 0 220 56" className="w-full h-12 rounded bg-[#020817] border border-amber-900/35">
                          <path d={networkLatencySparklinePath} fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="pt-2 border-t border-white/10 space-y-2">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">Node Bermasalah</p>
                    {aiIssueNodes.length === 0 ? (
                      <p className="text-[11px] text-slate-500">Tidak ada node bermasalah pada filter saat ini.</p>
                    ) : aiIssueNodes.map(({ customer, insight }) => (
                      <div key={`ai-issue-${customer.id}`} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-2 py-2">
                        <p className="text-[12px] text-white font-semibold truncate">{customer.name}</p>
                        <p className="text-[10px] text-slate-400">{customer.customer_id || '-'} â€¢ {customer.odp || '-'}</p>
                        <p className="text-[10px] text-red-300 mt-1 truncate">{insight.anomaly.issues[0]?.label || 'Anomaly terdeteksi'}</p>
                        <p className="text-[10px] text-cyan-300 mt-1">Skor {Number.isFinite(insight.score) ? insight.score : '-'} / 100 ({insight.level || 'normal'})</p>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="mt-2 h-7 w-full btn-secondary text-[11px]"
                          onClick={() => {
                            setSelectedCustomerId(customer.id);
                            handleFocusNode('customer', customer.id);
                            triggerFocusPulse(customer.id);
                          }}
                        >
                          Focus Node
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          className="mt-1 h-7 w-full bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-[11px] font-semibold"
                          onClick={() => {
                            setSelectedCustomerId(customer.id);
                            handleFocusNode('customer', customer.id);
                            setRightPanelTab('tickets');
                            handleCreateIncidentTicket({
                              sourceType: 'customer',
                              sourceId: String(customer.id || '').trim(),
                              sourceLabel: customer.name || customer.customer_id || 'Pelanggan',
                              customer,
                              linkage: resolveCustomerLinkageMeta(customer),
                              activeTicket: activeTicketByCustomerId[String(customer.id || '').trim()] || null,
                              suggestedSeverity: customer.connection_status === 'offline' ? 'high' : 'normal',
                              suggestedSummary: `AI detected anomaly pada pelanggan ${customer.name || customer.customer_id || '-'}.`,
                              impactCount: 1,
                            });
                          }}
                        >
                          Buat Ticket dari AI
                        </Button>
                      </div>
                    ))}
                  </div>
                  <div className="pt-2 border-t border-white/10 space-y-2">
                    <p className="text-[11px] uppercase tracking-wide text-slate-400">Node Detail (Actionable)</p>
                    {selectedCustomer ? (() => {
                      const selectedInsight = aiNodeInsights.byId[String(selectedCustomer?.id || '')];
                      const anomaly = selectedInsight?.anomaly;
                      const score = Number(selectedInsight?.score || 0);
                      const level = String(selectedInsight?.level || 'normal');
                      return (
                        <div className="rounded-lg border border-cyan-300/25 bg-[#0A0A0A] px-2 py-2">
                          <p className="text-[12px] text-white font-semibold truncate">{selectedCustomer.name || '-'}</p>
                          <p className="text-[10px] text-slate-400">{selectedCustomer.customer_id || '-'} Ã¢â‚¬Â¢ {selectedCustomer.odp || '-'}</p>
                          <p className="text-[10px] text-cyan-300 mt-1">Score: {score}/100 ({level})</p>
                          <p className="text-[10px] text-slate-300 mt-1">Offline ODP: {Number(aiMonitoringResult?.root_cause?.impact || 0)} pelanggan</p>
                          {anomaly?.hasAnomaly ? (
                            <ul className="mt-1 space-y-1 text-[10px] text-rose-300">
                              {anomaly.issues.slice(0, 2).map((issue, idx) => (
                                <li key={`selected-issue-${idx}`}>- {issue?.label || 'Anomaly'}</li>
                              ))}
                            </ul>
                          ) : (
                            <p className="mt-1 text-[10px] text-emerald-300">Node dalam kondisi normal.</p>
                          )}
                        </div>
                      );
                    })() : (
                      <p className="text-[11px] text-slate-500">Pilih marker/node di map untuk melihat detail operasional.</p>
                    )}
                  </div>
                  <div className="pt-2 border-t border-white/10 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">Log Self-Healing</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px] btn-secondary"
                        onClick={fetchSelfHealingLogs}
                        disabled={selfHealingLogsLoading}
                      >
                        {selfHealingLogsLoading ? 'Memuat...' : 'Muat Ulang'}
                      </Button>
                    </div>
                    {selfHealingLogs.length === 0 ? (
                      <p className="text-[11px] text-slate-500">Belum ada log self-healing.</p>
                    ) : (
                      <div className="space-y-2 max-h-56 overflow-auto pr-1">
                        {selfHealingLogs.slice(0, 10).map((log) => {
                          const status = String(log?.status || '').toLowerCase();
                          const statusClass = semanticToneToBadgeClass(mapSelfHealingStatusToTone(status));
                          const executedCount = Array.isArray(log?.executed_actions) ? log.executed_actions.length : 0;
                          return (
                            <div key={log?.id || `${log?.customer_id}-${log?.created_at}`} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-2 py-2">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-[12px] text-white font-semibold truncate">{log?.customer_name || log?.customer_number || '-'}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] border ${statusClass}`}>{status || 'tidak diketahui'}</span>
                              </div>
                              <p className="text-[10px] text-slate-400 mt-1">Aksi: {executedCount} â€¢ Score: {log?.analysis?.score ?? '-'}</p>
                              <p className="text-[10px] text-slate-400 truncate">{log?.analysis?.insight || '-'}</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="pt-2 border-t border-white/10 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-[11px] uppercase tracking-wide text-slate-400">Log Auto Optimization</p>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[11px] btn-secondary"
                        onClick={fetchOptimizationLogs}
                        disabled={optimizationLogsLoading}
                      >
                        {optimizationLogsLoading ? 'Memuat...' : 'Muat Ulang'}
                      </Button>
                    </div>
                    {optimizationLogs.length === 0 ? (
                      <p className="text-[11px] text-slate-500">Belum ada log auto optimization.</p>
                    ) : (
                      <div className="space-y-2 max-h-56 overflow-auto pr-1">
                        {optimizationLogs.slice(0, 10).map((log) => {
                          const status = String(log?.status || '').toLowerCase();
                          const statusClass = semanticToneToBadgeClass(mapSelfHealingStatusToTone(status));
                          return (
                            <div key={log?.id || `${log?.customer_id}-${log?.created_at}`} className="rounded-lg border border-white/10 bg-[#0A0A0A] px-2 py-2">
                              <div className="flex items-start justify-between gap-2">
                                <p className="text-[12px] text-white font-semibold truncate">{log?.customer_name || log?.customer_number || '-'}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] border ${statusClass}`}>{status || 'tidak diketahui'}</span>
                              </div>
                              <p className="text-[10px] text-slate-400 mt-1">Decision: {log?.decision?.type || '-'}</p>
                              <p className="text-[10px] text-slate-400 truncate">Metrics: BW {log?.analysis?.metrics?.bandwidth_usage_percent ?? '-'}% â€¢ Lat {log?.analysis?.metrics?.latency_seconds ?? '-'}s â€¢ Load {log?.analysis?.metrics?.node_load_percent ?? '-'}%</p>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className={`bg-[#eaf2ff] rounded-xl p-4 border border-[#93c5fd] space-y-3 text-sm noc-health-panel noc-side-card ${rightPanelTab !== 'infra' ? 'hidden' : ''}`}>
              <div className="grid grid-cols-2 gap-2">
                <div className="noc-mini-health is-ok">
                  <p className="label">GenieACS</p>
                  <p className="value">{(genieacsHealthAvailable ?? serverAvailable) ? 'Up' : 'Down'}</p>
                </div>
                <div className={`noc-mini-health ${streamConnected ? 'is-ok' : 'is-warn'}`}>
                  <p className="label">SSE Realtime</p>
                  <p className="value">{streamConnected ? 'Live' : 'Retry'}</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <MapStatusPill
                  label="Status GenieACS"
                  value={(genieacsHealthAvailable ?? serverAvailable) ? 'Terhubung' : 'Tidak terhubung'}
                  variant={(genieacsHealthAvailable ?? serverAvailable) ? 'success' : 'danger'}
                  tone="light"
                />
                <MapStatusPill
                  label="Status Realtime"
                  value={streamConnected ? 'Aktif (SSE auto update)' : 'Menyambungkan ulang...'}
                  variant={streamConnected ? 'info' : 'warning'}
                  tone="light"
                />
                <MapStatusPill
                  label="Data"
                  value={statusStale ? 'Stale' : 'Live'}
                  variant={statusStale ? 'warning' : 'success'}
                  tone="light"
                />
                <MapStatusPill
                  label="Reconnect SSE"
                  value={streamReconnectCount}
                  variant={streamReconnectCount > 0 ? 'warning' : 'success'}
                  tone="light"
                />
              </div>

              <p className="text-slate-700">Filter Infrastruktur: <span className="text-slate-900 font-medium">{infraFilter === 'all' ? 'Semua' : infraFilter === 'live' ? 'Live' : 'Plan'}</span></p>
              <p className="text-slate-700">Filter NAS: <span className="text-slate-900 font-medium">{selectedNasId === 'all' ? 'Semua' : (nasByRouterId[normalizeNodeId(selectedNasId)]?.name || selectedNasId)}</span></p>
              <p className="text-slate-700">Filter OLT: <span className="text-slate-900 font-medium">{selectedOltId === 'all' ? 'Semua' : (olts.find((olt) => normalizeNodeId(olt.id) === normalizeNodeId(selectedOltId))?.name || selectedOltId)}</span></p>

              <div className="flex flex-wrap gap-2">
                <MapStatusPill label="Online" value={onlineCount} variant="success" tone="light" />
                <MapStatusPill label="Offline/Loss" value={offlineCount} variant="danger" tone="light" />
                <MapStatusPill label="Lainnya" value={unknownCount} variant="warning" tone="light" />
              </div>

              <p className="text-slate-600 text-xs">Pembaruan Terakhir: {lastUpdated ? new Date(lastUpdated).toLocaleString('id-ID') : '-'}</p>
              <div className="rounded-lg border border-[#bfd5ff] bg-white p-3 space-y-1">
                <p className="text-[11px] uppercase tracking-wide text-slate-600">Metrik Sistem</p>
                <p className="text-xs text-slate-700">GenieACS Source: <span className="text-slate-900 font-medium">{metricsLastSource}</span></p>
                <p className="text-xs text-slate-700">Latency avg/p95: <span className="text-slate-900 font-medium">{metricsAvgLatency} / {metricsP95Latency}</span></p>
                <p className="text-xs text-slate-700">Approve transfer: <span className="text-slate-900 font-medium">{metricsTransfer?.success ?? 0}</span> sukses, <span className="text-amber-700">{metricsTransfer?.conflict ?? 0}</span> konflik, <span className="text-red-700">{metricsTransfer?.fail ?? 0}</span> gagal</p>
              </div>
            </div>

            <div className={`bg-[#121212] rounded-xl p-4 border border-white/10 space-y-3 noc-side-card ${rightPanelTab !== 'capacity' ? 'hidden' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-white font-semibold">Risiko Kapasitas</h3>
                <span className="text-[10px] text-slate-400">ODP + NAS Prediction MVP</span>
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400">Strategi Rekomendasi</p>
                  <select
                    value={capacityRecommendationStrategy}
                    onChange={(event) => setCapacityRecommendationStrategy(String(event?.target?.value || 'balanced'))}
                    className="h-8 rounded border border-white/10 bg-[#050505] px-2 text-[11px] text-white"
                  >
                    <option value="cost">Hemat biaya</option>
                    <option value="balanced">Seimbang</option>
                    <option value="growth">Siap pertumbuhan</option>
                  </select>
                </div>
                <p className="text-xs text-slate-400">
                  Kritis {capacityRecommendations.summary.critical} â€¢ Tinggi {capacityRecommendations.summary.high} â€¢ Ekspansi {capacityRecommendations.summary.expansion} â€¢ Penyeimbangan {capacityRecommendations.summary.rebalance}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-2">
                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">ODP Paling Berisiko</p>
                  {topRiskOdp ? (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-white font-semibold">{topRiskOdp.name}</p>
                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getRiskBadgeClass(topRiskOdp.risk.tone)}`}>{topRiskOdp.risk.label}</span>
                      </div>
                      <p className="text-xs text-slate-300 mt-1">{topRiskOdp.used}/{topRiskOdp.capacity} ({topRiskOdp.occupancyPercent}%) â€¢ sisa {topRiskOdp.remaining}</p>
                      <p className="text-xs text-slate-400">{Number.isFinite(topRiskOdp.daysToFull) ? `Estimasi penuh ${topRiskOdp.daysToFull} hari` : 'Estimasi penuh belum cukup data'}</p>
                    </>
                  ) : <p className="text-xs text-slate-500">Belum ada data ODP pada scope ini.</p>}
                </div>
                <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
                  <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">NAS Pertumbuhan Tertinggi</p>
                  {topRiskNas ? (
                    <>
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm text-white font-semibold">{topRiskNas.name}</p>
                        <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getRiskBadgeClass(topRiskNas.risk.tone)}`}>{topRiskNas.risk.label}</span>
                      </div>
                      <p className="text-xs text-slate-300 mt-1">Aktif {topRiskNas.activeCount} â€¢ Growth 30d +{topRiskNas.growth30d}</p>
                      <p className="text-xs text-slate-400">{Number.isFinite(topRiskNas.syntheticDaysToPressure) ? `Estimasi tekanan ${topRiskNas.syntheticDaysToPressure} hari` : 'Estimasi tekanan belum cukup data'}</p>
                    </>
                  ) : <p className="text-xs text-slate-500">Belum ada data NAS pada scope ini.</p>}
                </div>
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Insight Kapasitas</p>
                {capacityPrediction.insights.length === 0 ? (
                  <p className="text-xs text-slate-500">Belum ada insight.</p>
                ) : (
                  <ul className="space-y-1 text-xs text-slate-200">
                    {capacityPrediction.insights.map((insight) => (
                      <li key={`capacity-insight-${insight}`}>â€¢ {insight}</li>
                    ))}
                  </ul>
                )}
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3">
                <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-2">Top ODP Risk</p>
                <div className="space-y-1.5 max-h-36 overflow-auto pr-1">
                  {capacityPrediction.odps.slice(0, 6).map((odp) => (
                    <div key={`capacity-odp-${odp.id}`} className="flex items-center justify-between gap-2 text-xs">
                      <span className="text-slate-200 truncate">{odp.name}</span>
                      <span className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] ${getRiskBadgeClass(odp.risk.tone)}`}>{odp.risk.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <p className="text-[10px] uppercase tracking-wide text-slate-400">Next Action Recommendation</p>
                {capacityRecommendations.recommendations.length === 0 ? (
                  <p className="text-xs text-slate-500">Belum ada rekomendasi kapasitas untuk scope saat ini.</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-auto pr-1">
                    {capacityRecommendations.recommendations.slice(0, 5).map((item) => (
                      <div key={`capacity-rec-${item.id}`} className="rounded-md border border-white/10 bg-[#06080d] p-2.5">
                        <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getUrgencyBadgeClass(item?.urgency?.tone)}`}>{item?.urgency?.label || 'Rendah'}</span>
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getRecommendationTypeBadgeClass(item?.actionType)}`}>{getRecommendationTypeLabel(item?.actionType)}</span>
                          <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] border-cyan-500/45 bg-cyan-500/10 text-cyan-200">Conf {String(item?.confidenceLabel || 'Rendah')}</span>
                          <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] border-slate-500/40 bg-slate-600/10 text-slate-200">{String(item?.sourceType || '-').toUpperCase()}</span>
                          {Number.isFinite(Number(item?.suggestedOdpCapacity)) ? (
                            <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] border-cyan-500/45 bg-cyan-500/10 text-cyan-200">ODP {Number(item.suggestedOdpCapacity)}</span>
                          ) : null}
                        </div>
                        <p className="text-xs text-white font-medium">{item.title}</p>
                        <p className="text-[11px] text-cyan-200 mt-0.5">Aksi disarankan: <span className="text-slate-200">{item.suggestedAction || item.summary}</span></p>
                        <p className="text-[11px] text-slate-400 mt-1">Alasan utama: {item.reasonLines?.[0] || '-'}</p>
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[10px] btn-secondary"
                            onClick={() => {
                              if (item.sourceType === 'nas' && item.sourceId) {
                                setSelectedNasId(String(item.sourceId));
                                setRightPanelTab('nodes');
                                toast.success(`Monitoring difokuskan ke NAS ${item.sourceLabel}.`);
                                return;
                              }
                              if (item.sourceType === 'odp' && item.sourceId) {
                                const linkedOdp = odps.find((odp) => String(odp?.id || '') === String(item.sourceId));
                                if (linkedOdp?.router_id) setSelectedNasId(String(linkedOdp.router_id));
                                if (linkedOdp?.olt_id) setSelectedOltId(String(linkedOdp.olt_id));
                                setRightPanelTab('nodes');
                                toast.success(`Monitoring difokuskan ke ${item.sourceLabel}.`);
                              }
                            }}
                          >
                            Lihat Monitoring
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 px-2 text-[10px] btn-secondary"
                      onClick={() => navigate(item?.links?.planning || '/dadu/ftth')}
                          >
                            Buka FTTH Planning
                          </Button>
                          {item.sourceType === 'nas' ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-[10px] btn-secondary"
                              onClick={() => navigate(item?.links?.nas || '/dadu/voucher?tab=nas')}
                            >
                              Buka NAS
                            </Button>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className={`bg-[#121212] rounded-xl p-4 border border-white/10 space-y-3 noc-side-card ${rightPanelTab !== 'queue' ? 'hidden' : ''}`}>
              <div className="flex items-center justify-between gap-2">
              <h3 className="text-white font-semibold">Antrean Aksi</h3>
                <span className="text-[10px] text-slate-400">Aksi Operasional Berikutnya</span>
              </div>
              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <div className="flex items-center justify-between gap-2 text-xs">
                  <p className="text-slate-300">Menunggu <span className="text-white font-semibold">{actionQueueSummary.pending}</span> | Diterima <span className="text-white font-semibold">{actionQueueSummary.accepted}</span></p>
                  <p className="text-slate-400">Kritis {actionQueueSummary.critical} | Tinggi {actionQueueSummary.high} | Sedang {actionQueueSummary.medium}</p>
                </div>
                <div className="flex flex-wrap items-center gap-1.5 text-[10px]">
                  <span className="inline-flex px-2 py-0.5 rounded-full border border-rose-500/45 bg-rose-500/10 text-rose-200">Kritis: {actionQueueSummary.critical}</span>
                  <span className="inline-flex px-2 py-0.5 rounded-full border border-orange-500/45 bg-orange-500/10 text-orange-200">Tinggi: {actionQueueSummary.high}</span>
                  <span className="inline-flex px-2 py-0.5 rounded-full border border-amber-500/45 bg-amber-500/10 text-amber-200">Sedang: {actionQueueSummary.medium}</span>
                  <span className="inline-flex px-2 py-0.5 rounded-full border border-sky-500/45 bg-sky-500/10 text-sky-200">Rendah: {actionQueueSummary.low}</span>
                  <span className="inline-flex px-2 py-0.5 rounded-full border border-cyan-500/45 bg-cyan-500/10 text-cyan-200">Total: {actionQueueSummary.total}</span>
                </div>
                <p className="text-[10px] text-slate-500">
                  Guard: {recommendationGuardSummary.suppressedLowConfidence} low-confidence disembunyikan | {recommendationGuardSummary.coolingDown} item cooldown aktif
                </p>
                <p className="text-[10px] text-slate-500">
                  Adaptive patterns: {adaptivePatternStatsSummary.count} | Top+ {Number(adaptivePatternStatsSummary?.positive?.adaptive_modifier || 0).toFixed(1)} | Top- {Number(adaptivePatternStatsSummary?.negative?.adaptive_modifier || 0).toFixed(1)}
                </p>
                <div className="flex flex-wrap items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-[11px] text-slate-300">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-cyan-400"
                      checked={actionQueueShowLowUrgency}
                      onChange={(event) => setActionQueueShowLowUrgency(Boolean(event.target.checked))}
                    />
                    Tampilkan urgency rendah
                  </label>
                  <label className="inline-flex items-center gap-2 text-[11px] text-slate-300">
                    <input
                      type="checkbox"
                      className="h-4 w-4 accent-cyan-400"
                      checked={actionQueueShowCompleted}
                      onChange={(event) => setActionQueueShowCompleted(Boolean(event.target.checked))}
                    />
                    Tampilkan completed
                  </label>
                </div>
              </div>

              {actionQueueItems.length === 0 ? (
                <p className="text-xs text-slate-500">Tidak ada item queue aktif pada scope saat ini.</p>
              ) : (
                <div className="space-y-2 max-h-[32rem] overflow-auto pr-1">
                  {ACTION_QUEUE_GROUP_ORDER.map((urgencyKey) => {
                    const groupItems = actionQueueGrouped[urgencyKey] || [];
                    const count = groupItems.length;
                    if (count === 0 && urgencyKey === 'rendah' && !actionQueueShowLowUrgency) return null;
                    if (count === 0) return null;
                    const defaultOpen = urgencyKey === 'kritis' || urgencyKey === 'tinggi';
                    const isOpen = urgencyKey === 'kritis' ? true : Boolean(actionQueueGroupOpen[urgencyKey] ?? defaultOpen);
                    return (
                      <div key={`queue-group-${urgencyKey}`} className="rounded-lg border border-white/10 bg-[#0A0A0A]">
                        <div className="flex items-center justify-between gap-2 px-3 py-2 border-b border-white/10">
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getUrgencyBadgeClass(getActionQueueUrgencyTone(urgencyKey))}`}>{urgencyKey}</span>
                            <span className="text-[11px] text-slate-300">{count} item</span>
                          </div>
                          {urgencyKey !== 'kritis' ? (
                            <Button
                              type="button"
                              size="sm"
                              variant="ghost"
                              className="h-6 px-2 text-[10px] text-slate-300"
                              onClick={() => setActionQueueGroupOpen((prev) => ({ ...prev, [urgencyKey]: !isOpen }))}
                            >
                              {isOpen ? 'Sembunyikan' : 'Tampilkan'}
                            </Button>
                          ) : (
                            <span className="text-[10px] text-rose-300">Selalu tampil</span>
                          )}
                        </div>
                        {isOpen ? (
                          <div className="space-y-2 p-2">
                            {groupItems.map((item) => {
                              const itemToken = toActionQueueTestIdToken(item?.id || `${item?.source_type || 'src'}-${item?.source_id || 'id'}`);
                              const approveTarget = item?.isAutoAction ? 'approved' : 'accepted';
                              const completeTarget = item?.isAutoAction ? 'executed' : 'completed';
                              const ignoreTarget = item?.isAutoAction ? 'skipped' : 'ignored';
                              const approveLabel = item?.isAutoAction ? 'Approve' : 'Accept';
                              const completeLabel = item?.isAutoAction ? 'Execute' : 'Complete';
                              const ignoreLabel = item?.isAutoAction ? 'Skip' : 'Ignore';
                              const holdActive = Boolean(item?.hold_flag);
                              return (
                                <div
                                  key={`queue-item-${item.id}`}
                                  data-testid={`action-queue-item-${itemToken}`}
                                  className={`rounded-lg border border-white/10 bg-[#090909] p-3 ${getAgeHighlightClass(item.ageWeight)}`}
                                >
                                <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
                                  <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] border-white/20 bg-white/[0.04] text-slate-200">#{Number(item?.decision_rank || 0)}</span>
                                  <span data-testid={`action-queue-urgency-${itemToken}`} className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getUrgencyBadgeClass(getActionQueueUrgencyTone(item.urgency))}`}>{item.urgency}</span>
                                  <span data-testid={`action-queue-type-${itemToken}`} className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getRecommendationTypeBadgeClass(item.action_type === 'expansion' ? 'capacity_expansion' : item.action_type)}`}>{getRecommendationTypeLabel(item.action_type === 'expansion' ? 'capacity_expansion' : item.action_type)}</span>
                                  <span data-testid={`action-queue-status-${itemToken}`} className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getActionQueueStatusClass(item.status)}`}>{item.status}</span>
                                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getActionClassBadgeClass(item?.action_class)}`}>{String(item?.action_class || 'operational')}</span>
                                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getDecisionLabelClass(item?.decision_label)}`}>{String(item?.decision_label || 'Conditional')}</span>
                                  <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] border-violet-500/45 bg-violet-500/10 text-violet-200">Score {Number(item?.decision_score || 0).toFixed(1)}</span>
                                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${Number(item?.adaptive_modifier || 0) > 0 ? 'border-emerald-500/45 bg-emerald-500/10 text-emerald-200' : Number(item?.adaptive_modifier || 0) < 0 ? 'border-rose-500/45 bg-rose-500/10 text-rose-200' : 'border-slate-500/45 bg-slate-500/10 text-slate-300'}`}>
                                    Adapt {Number(item?.adaptive_modifier || 0) >= 0 ? '+' : ''}{Number(item?.adaptive_modifier || 0).toFixed(1)}
                                  </span>
                                  <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] border-cyan-500/45 bg-cyan-500/10 text-cyan-200">Conf {item.confidenceLabel || 'Rendah'}</span>
                                  {item?.isAutoAction ? (
                                    <span data-testid={`action-queue-mode-${itemToken}`} className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getAutoActionModeClass(item?.action_mode)}`}>{getAutoActionModeLabel(item?.action_mode)}</span>
                                  ) : null}
                                </div>
                                <p className="text-[10px] text-slate-400 mt-0.5">
                                  {String(item.source_type || '-').toUpperCase()} | Usia {item.ageLabel}
                                  {Number(item.mergedCount || 1) > 1 ? ` | Gabung ${item.mergedCount}` : ''}
                                  {item.isNew ? ' | Baru' : ''}
                                  {item.isUpdated ? ' | Diperbarui' : ''}
                                  {item?.rule_key ? ` | Rule ${item.rule_key}` : ''}
                                  {item?.hold_flag ? ' | HOLD' : ''}
                                </p>
                                <p className="text-[11px] text-cyan-200 mt-0.5">Aksi: <span className="text-slate-200">{item.recommended_action}</span></p>
                                <p className="text-[11px] text-slate-400 mt-1">Alasan: {Array.isArray(item.reason_all) ? String(item.reason_all[0] || item.reason || '-') : item.reason}</p>
                                <p className="text-[10px] text-slate-500 mt-1">
                                  Base {Number(item?.base_score || 0).toFixed(1)} | Adapt {Number(item?.adaptive_modifier || 0) >= 0 ? '+' : ''}{Number(item?.adaptive_modifier || 0).toFixed(1)} | Final {Number(item?.final_score || item?.decision_score || 0).toFixed(1)}
                                </p>
                                {item?.decision_reason ? (
                                  <p className="text-[11px] text-violet-200/90 mt-1">Decision: {item.decision_reason}</p>
                                ) : null}
                                {item?.adaptive_reason ? (
                                  <p className="text-[10px] text-slate-400 mt-1">{item.adaptive_reason}</p>
                                ) : null}
                                {Array.isArray(item.reason_all) && item.reason_all.length > 1 ? (
                                  <details className="mt-1">
                                    <summary className="cursor-pointer text-[10px] text-slate-500 hover:text-slate-300">Lihat detail</summary>
                                    <div className="mt-1 space-y-1">
                                      {item.reason_all.slice(1, 4).map((line) => (
                                        <p key={`reason-detail-${item.id}-${line}`} className="text-[10px] text-slate-500">- {line}</p>
                                      ))}
                                    </div>
                                  </details>
                                ) : null}
                                <div className="mt-2 flex items-center justify-between gap-2">
                                  <p className="text-[10px] text-slate-500">Dibuat: {item.created_at ? new Date(item.created_at).toLocaleString('id-ID') : '-'}</p>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    className="h-7 px-2 text-[10px] btn-secondary"
                                    onClick={() => handleOpenActionQueueWorkflow(item)}
                                  >
                                    {getActionQueuePrimaryCtaLabel(item)}
                                  </Button>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1.5">
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    data-testid={`action-queue-approve-${itemToken}`}
                                    className="h-7 px-2 text-[10px] btn-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:border-slate-700 disabled:text-slate-500"
                                    disabled={!canTransitionActionQueueStatus(item.status, approveTarget)}
                                    onClick={() => handleActionQueueStatusChange(item, approveTarget)}
                                  >
                                    {approveLabel}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    data-testid={`action-queue-execute-${itemToken}`}
                                    className="h-7 px-2 text-[10px] btn-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:border-slate-700 disabled:text-slate-500"
                                    disabled={!canTransitionActionQueueStatus(item.status, completeTarget)}
                                    onClick={() => handleActionQueueStatusChange(item, completeTarget)}
                                  >
                                    {completeLabel}
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    data-testid={`action-queue-skip-${itemToken}`}
                                    className="h-7 px-2 text-[10px] btn-secondary disabled:opacity-40 disabled:cursor-not-allowed disabled:border-slate-700 disabled:text-slate-500"
                                    disabled={!canTransitionActionQueueStatus(item.status, ignoreTarget)}
                                    onClick={() => handleActionQueueStatusChange(item, ignoreTarget)}
                                  >
                                    {ignoreLabel}
                                  </Button>
                                  {item?.isAutoAction ? (
                                    <Button
                                      type="button"
                                      size="sm"
                                      variant="outline"
                                      data-testid={`action-queue-hold-${itemToken}`}
                                      className={`h-7 px-2 text-[10px] btn-secondary ${holdActive ? 'border-amber-500/45 text-amber-200' : ''}`}
                                      onClick={() => handleActionQueueHoldToggle(item)}
                                    >
                                      {holdActive ? 'Lepas Hold' : 'Hold'}
                                    </Button>
                                  ) : null}
                                </div>
                                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                                  <button
                                    type="button"
                                    className={`h-7 rounded border px-2 text-[10px] transition ${getActionQueueFeedbackClass(item.feedbackType === 'positive' ? 'positive' : '')}`}
                                    onClick={() => handleActionQueueFeedback(item, 'positive')}
                                    title="Tandai rekomendasi ini membantu"
                                  >
                                    Membantu
                                  </button>
                                  <button
                                    type="button"
                                    className={`h-7 rounded border px-2 text-[10px] transition ${getActionQueueFeedbackClass(item.feedbackType === 'negative' ? 'negative' : '')}`}
                                    onClick={() => handleActionQueueFeedback(item, 'negative')}
                                    title="Tandai rekomendasi ini tidak relevan"
                                  >
                                    Tidak relevan
                                  </button>
                                  {item.feedbackType ? (
                                    <p className="mt-1 text-[10px] text-slate-500">
                                      {item.feedbackType === 'positive' ? 'Ditandai membantu' : 'Ditandai tidak relevan'}
                                    </p>
                                  ) : null}
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="px-3 py-2 text-[11px] text-slate-500">Group disembunyikan.</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className={`bg-[#121212] rounded-xl p-4 border border-white/10 space-y-3 noc-side-card noc-ticket-panel ${rightPanelTab !== 'tickets' ? 'hidden' : ''}`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-white font-semibold">Tiket Insiden</h3>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  className="h-7 px-2 text-[11px] btn-secondary"
                  onClick={fetchFieldTickets}
                  disabled={loadingFieldTickets}
                >
                  {loadingFieldTickets ? 'Memuat...' : 'Muat Ulang'}
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="noc-mini-health is-warn">
                  <p className="label">Terbuka</p>
                  <p className="value">{incidentTicketSummary.open}</p>
                </div>
                <div className="noc-mini-health is-ok">
                  <p className="label">Diproses</p>
                  <p className="value">{incidentTicketSummary.inProgress}</p>
                </div>
                <div className="noc-mini-health">
                  <p className="label">Ditunda</p>
                  <p className="value">{incidentTicketSummary.onHold}</p>
                </div>
                <div className="noc-mini-health is-danger">
                  <p className="label">Dieskalasi</p>
                  <p className="value">{incidentTicketSummary.escalated}</p>
                </div>
              </div>

              <div className="rounded-lg border border-violet-400/25 bg-violet-500/5 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-violet-200">Monitoring Incidents</p>
                    <p className="text-[11px] text-slate-400">Incident Intelligence dari korelasi monitoring.</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[10px] btn-secondary"
                    onClick={fetchMonitoringIncidents}
                    disabled={loadingMonitoringIncidents}
                  >
                    {loadingMonitoringIncidents ? 'Memuat...' : 'Muat Ulang'}
                  </Button>
                </div>

                <div className="grid grid-cols-3 gap-2 text-[11px]">
                  <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                    <p className="text-slate-400">Terbuka/Monitoring</p>
                    <p className="text-slate-100 font-medium">{monitoringIncidentSummary.open + monitoringIncidentSummary.monitoring}</p>
                  </div>
                  <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                    <p className="text-slate-400">Kritis/Tinggi</p>
                    <p className="text-slate-100 font-medium">{monitoringIncidentSummary.critical + monitoringIncidentSummary.high}</p>
                  </div>
                  <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                    <p className="text-slate-400">Context Match</p>
                    <p className="text-slate-100 font-medium">{monitoringIncidentsByContext.length}</p>
                  </div>
                </div>

                {monitoringIncidents.length === 0 ? (
                  <p className="text-xs text-slate-500">Belum ada incident monitoring aktif.</p>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-auto pr-1">
                    {monitoringIncidents.slice(0, 8).map((incident) => {
                      const incidentId = String(incident?.id || '').trim();
                      const severity = String(incident?.severity || 'low').toLowerCase();
                      const status = String(incident?.status || 'monitoring').toLowerCase();
                      const recommendedActions = Array.isArray(incident?.recommended_actions) ? incident.recommended_actions : [];
                      const prioritizedActions = prioritizeIncidentActions(recommendedActions);
                      const isResolvedState = status === 'resolved' || status === 'false_positive';
                      const canResolve = !isResolvedState && !resolvingMonitoringIncidentId;
                      const statusClass = semanticToneToBadgeClass(mapIncidentStatusToTone(status));
                      const severityClass = semanticToneToBadgeClass(mapIncidentSeverityToTone(severity));
                      return (
                        <div key={`monitoring-incident-${incidentId}`} className="rounded border border-white/10 bg-[#060b14] px-2 py-2 noc-ticket-row noc-ticket-row-incident">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-[12px] text-white font-semibold truncate">
                              {String(incident?.incident_type || 'incident').replace(/_/g, ' ')}
                            </p>
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${severityClass}`}>
                              {getIncidentSeverityLabel(severity)}
                            </span>
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-1.5">
                            <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${statusClass}`}>
                              {getIncidentStatusLabel(status)}
                            </span>
                            <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] text-slate-200 border-slate-500/40 bg-slate-600/10">
                              Dampak {Number(incident?.impact_count || 0)}
                            </span>
                            <span className="inline-flex px-2 py-0.5 rounded-full border text-[10px] text-slate-300 border-slate-500/30 bg-slate-700/20">
                              Confidence {Math.round(Number(incident?.confidence || 0) * 100)}%
                            </span>
                          </div>
                          <p className="text-[10px] text-slate-400 mt-1">
                            Root: {String(incident?.root_source_type || '-').toUpperCase()} â€¢ {incident?.root_source_id || '-'}
                          </p>
                          <p className="text-[10px] text-slate-500 mt-1">
                            Update: {incident?.updated_at ? new Date(incident.updated_at).toLocaleString('id-ID') : '-'}
                          </p>
                          {prioritizedActions.length > 0 ? (
                            <div className="mt-2 space-y-1">
                              <p className="text-[10px] uppercase tracking-wide text-violet-200">Rekomendasi</p>
                              <div className="flex flex-wrap gap-1.5">
                                {prioritizedActions.map((action, idx) => (
                                  <button
                                    key={`admin-monitoring-incident-action-${incidentId}-${idx}`}
                                    type="button"
                                    className="inline-flex items-center rounded-full border border-cyan-400/30 bg-cyan-500/10 px-2 py-0.5 text-[10px] text-cyan-100 hover:bg-cyan-500/20"
                                    onClick={() => handleIncidentRecommendedAction(action, incident)}
                                  >
                                    {getIncidentRecommendedActionLabel(action)}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : null}
                          <div className="mt-2 grid grid-cols-3 gap-1.5">
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 text-[10px] btn-secondary"
                              onClick={() => {
                                const sourceType = normalizeIncidentSourceType(incident?.root_source_type);
                                const sourceId = String(incident?.root_source_id || '').trim();
                                if (!sourceType || !sourceId) return;
                                setIncidentSourceOverride({
                                  sourceType,
                                  sourceId,
                                  sourceKey: buildIncidentSourceKey(sourceType, sourceId),
                                });
                                setRightPanelTab('tickets');
                              }}
                            >
                              Fokus Source
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              className="h-7 text-[10px] bg-emerald-500/85 hover:bg-emerald-400 text-slate-950"
                              onClick={() => handleResolveMonitoringIncident(incident, false)}
                              disabled={!canResolve || resolvingMonitoringIncidentId === incidentId}
                            >
                              {resolvingMonitoringIncidentId === incidentId ? '...' : 'Resolve'}
                            </Button>
                            <Button
                              type="button"
                              size="sm"
                              variant="outline"
                              className="h-7 text-[10px] border-rose-400/35 text-rose-200 bg-rose-500/10 hover:bg-rose-500/20"
                              onClick={() => handleResolveMonitoringIncident(incident, true)}
                              disabled={!canResolve || resolvingMonitoringIncidentId === incidentId}
                            >
                              False Positive
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-[11px] uppercase tracking-wide text-slate-400">Sumber Incident Terbaru</p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 px-2 text-[10px] btn-secondary"
                    onClick={() => setIncidentSourceOverride(null)}
                  >
                    Ikuti Selection
                  </Button>
                </div>
                {recentIncidentSources.length === 0 ? (
                  <p className="text-xs text-slate-500">Belum ada source ticket terbaru.</p>
                ) : (
                  <div className="flex flex-wrap gap-1.5">
                    {recentIncidentSources.map((item) => {
                      const isActive = String(incidentSourceOverride?.sourceType || '').toLowerCase() === String(item.sourceType || '').toLowerCase()
                        && String(incidentSourceOverride?.sourceId || '').trim() === String(item.sourceId || '').trim();
                      return (
                        <button
                          key={`ticket-source-${item.sourceKey}`}
                          type="button"
                          onClick={() => setIncidentSourceOverride({ sourceType: item.sourceType, sourceId: item.sourceId, sourceKey: item.sourceKey })}
                          className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] transition ${
                            isActive
                              ? 'border-cyan-400/45 bg-cyan-500/20 text-cyan-100'
                              : 'border-white/15 bg-white/[0.03] text-slate-300 hover:border-white/30 hover:text-white'
                          }`}
                          title={`${String(item.sourceType || '').toUpperCase()} â€¢ ${item.sourceId}`}
                        >
                          <span className="uppercase">{String(item.sourceType || '').replace(/_/g, ' ')}</span>
                          <span className="text-slate-400">â€¢</span>
                          <span className="truncate max-w-[8rem]">{item.sourceId}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-[10px] text-slate-500">
                  Gunakan ini untuk fokus cepat ke context ODP/OLT/NAS/cluster meski tidak sedang terpilih di map.
                </p>
              </div>

              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Context Terpilih</p>
                {selectedIncidentContext ? (
                  <>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <p className="text-sm text-white font-semibold">{selectedIncidentContext.sourceLabel}</p>
                        <p className="text-[11px] text-slate-400">
                          Source: {String(selectedIncidentContext.sourceType || '-').toUpperCase()} â€¢ Dampak {Number(selectedIncidentContext.impactCount || 0)} pelanggan
                        </p>
                      </div>
                      <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${selectedIncidentContext.activeTicket ? getTicketToneClass(selectedIncidentContext.activeTicket.lifecycle?.tone) : 'text-slate-300 border-slate-500/40 bg-slate-600/10'}`}>
                        {selectedIncidentContext.activeTicket ? `Ticket ${selectedIncidentContext.activeTicket.lifecycle?.label || 'Open'}` : 'Belum Ticket'}
                      </span>
                    </div>
                    <p className="text-xs text-slate-300">{selectedIncidentContext.suggestedSummary}</p>
                    {incidentSourceOverride?.sourceType && incidentSourceOverride?.sourceId ? (
                      <p className="text-[11px] text-cyan-200">
                        Context dikunci dari daftar source ticket: {String(incidentSourceOverride.sourceType || '').toUpperCase()} â€¢ {incidentSourceOverride.sourceId}
                      </p>
                    ) : null}
                    {selectedIncidentContext.activeTicket ? (
                      <div className="rounded border border-amber-400/35 bg-amber-500/10 px-2 py-1 text-[11px] text-amber-100">
                        Issue ini sudah memiliki ticket aktif ({selectedIncidentContext.activeTicket.lifecycle?.label || selectedIncidentContext.activeTicket.status || 'Open'}).
                      </div>
                    ) : null}
                    <div className="grid grid-cols-3 gap-2">
                      <Button
                        type="button"
                        size="sm"
                        className="h-8 bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"
                        onClick={() => handleCreateIncidentTicket(selectedIncidentContext)}
                        disabled={creatingIncidentTicket || !selectedIncidentContext?.sourceKey}
                      >
                        {creatingIncidentTicket ? 'Membuat...' : 'Buat Ticket'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 btn-secondary"
                        onClick={() => selectedIncidentContext?.activeTicket?.id ? navigate(`/dadu/penanganan?ticket=${selectedIncidentContext.activeTicket.id}`) : setRightPanelTab('customers')}
                      >
                        {selectedIncidentContext?.activeTicket?.id ? 'Lihat Ticket' : 'Lihat Source'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-8 btn-secondary"
                        onClick={() => startReplayFromTicket(selectedIncidentContext?.activeTicket?.id)}
                        disabled={!selectedIncidentContext?.activeTicket?.id}
                      >
                        Replay Incident
                      </Button>
                    </div>
                    {!selectedIncidentContext?.customer?.id ? (
                      <p className="text-[11px] text-amber-200">Context ini tidak punya customer langsung. Ticket tetap bisa dibuat berbasis source incident.</p>
                    ) : null}
                  </>
                ) : (
                  <p className="text-xs text-slate-500">Pilih node pelanggan/ODP/OLT untuk melihat konteks ticket incident.</p>
                )}
              </div>

              <div className="rounded-lg border border-cyan-400/30 bg-cyan-500/5 p-3 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-cyan-200">Incident Summary</p>
                    <p className="text-[12px] text-slate-200">Ringkasan cepat insiden untuk RCA operasional.</p>
                  </div>
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${
                    semanticToneToBadgeClass(mapIncidentSeverityToTone(incidentSummary?.severity))
                  }`}>
                    {incidentSummary?.statusLabel || 'N/A'}
                  </span>
                </div>

                {incidentSummary?.hasData ? (
                  <>
                    <div className="grid grid-cols-2 gap-2 text-[11px]">
                      <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                        <p className="text-slate-400">Incident ID</p>
                        <p className="text-slate-100 font-medium truncate">{incidentSummary.incidentId || '-'}</p>
                      </div>
                      <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                        <p className="text-slate-400">Durasi</p>
                        <p className="text-slate-100 font-medium">{incidentSummary.durationMs ? formatElapsedShort(incidentSummary.durationMs) : '-'}</p>
                      </div>
                      <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                        <p className="text-slate-400">Dampak</p>
                        <p className="text-slate-100 font-medium">{incidentSummary.impactCount} pelanggan</p>
                      </div>
                      <div className="rounded border border-white/10 bg-[#060b14] px-2 py-1.5">
                        <p className="text-slate-400">Source</p>
                        <p className="text-slate-100 font-medium truncate">{incidentSummary.sourceType} â€¢ {incidentSummary.sourceId}</p>
                      </div>
                    </div>
                    {!incidentSummary?.sourceConsistency?.ok ? (
                      <p className="text-[11px] text-amber-200">
                        {incidentSummary.sourceConsistency.detail}
                      </p>
                    ) : null}

                    <div className="rounded border border-white/10 bg-[#060b14] px-2 py-2 space-y-1">
                      <p className="text-[10px] uppercase tracking-wide text-slate-400">Ringkasan Linimasa</p>
                      <div className="flex flex-wrap gap-1.5">
                        {incidentSummary.timelineItems.length > 0 ? (
                          incidentSummary.timelineItems.map((item) => (
                            <span
                              key={`incident-summary-timeline-${item.key}`}
                              className="inline-flex items-center gap-1 rounded-full border border-cyan-400/25 bg-cyan-500/10 px-2 py-0.5 text-[10px] text-cyan-100"
                            >
                              <span>{item.label}</span>
                              <span className="text-cyan-200/80">{formatCompactDateTime(item.at)}</span>
                            </span>
                          ))
                        ) : (
                          <span className="text-[11px] text-slate-500">Timeline belum lengkap.</span>
                        )}
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-1.5">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[10px] btn-secondary"
                        onClick={() => incidentSummary.ticketId ? startReplayFromTicket(incidentSummary.ticketId) : startReplayMode(activeReplayIncident)}
                        disabled={!activeReplayIncident && !incidentSummary.ticketId}
                      >
                        Lihat Replay
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[10px] btn-secondary"
                        onClick={openIncidentSummaryFocus}
                      >
                        Lihat Monitoring
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 px-2 text-[10px] btn-secondary"
                        onClick={() => incidentSummary.customerId ? navigate('/dadu/pelanggan', { state: { viewCustomerId: incidentSummary.customerId, source: 'monitoring-summary' } }) : setRightPanelTab('customers')}
                      >
                        Lihat Pelanggan
                      </Button>
                    </div>

                    <button
                      type="button"
                      className="text-[10px] text-cyan-200 hover:text-cyan-100 underline underline-offset-2"
                      onClick={() => setIncidentSummaryExpanded((prev) => !prev)}
                    >
                      {incidentSummaryExpanded ? 'Sembunyikan insight detail' : 'Tampilkan insight detail'}
                    </button>

                    {incidentSummaryExpanded ? (
                      <div className="rounded border border-white/10 bg-[#060b14] px-2 py-2 text-[11px] space-y-2">
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Insight</p>
                          <ul className="list-disc pl-4 space-y-1 text-slate-200">
                            {incidentSummary.insights.map((item) => (
                              <li key={`incident-summary-insight-${item}`}>{item}</li>
                            ))}
                          </ul>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Metric</p>
                          <div className="grid grid-cols-1 gap-1 text-slate-200">
                            <p>Time to ticket: {incidentSummary.metrics.timeToTicketLabel || 'Tidak tersedia'}</p>
                            <p>Time to escalation: {incidentSummary.metrics.timeToEscalationLabel || 'Tidak tersedia'}</p>
                            <p>Recovery duration: {incidentSummary.metrics.recoveryDurationLabel || 'Tidak tersedia'}</p>
                          </div>
                        </div>
                        <div>
                          <p className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Rekomendasi</p>
                          <ul className="list-disc pl-4 space-y-1 text-slate-200">
                            {incidentSummary.recommendations.map((item) => (
                              <li key={`incident-summary-reco-${item}`}>{item}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    ) : null}
                  </>
                ) : (
                  <p className="text-xs text-slate-500">Belum ada incident context yang cukup untuk dirangkum.</p>
                )}
              </div>

              <div className="rounded-lg border border-white/10 bg-[#0A0A0A] p-3 space-y-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-400">Ticket pada Context Ini</p>
                {incidentTicketsByContext.length === 0 ? (
                  <p className="text-xs text-slate-500">Belum ada ticket pada context terpilih.</p>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-auto pr-1">
                    {incidentTicketsByContext.slice(0, 10).map((ticket) => (
                      <div key={`incident-ticket-${ticket.id}`} className="rounded border border-white/10 bg-[#060b14] px-2 py-2 noc-ticket-row">
                        <div className="flex items-center justify-between gap-2">
                          <p className="text-[12px] text-white font-semibold truncate">
                            {ticket.customer_name || ticket.customer_code || ticket.customerId || ticket.sourceId || '-'}
                          </p>
                          <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] ${getTicketToneClass(ticket.lifecycle?.tone)}`}>
                            {ticket.lifecycle?.label || ticket.status || 'Open'}
                          </span>
                        </div>
                        <p className="text-[10px] text-slate-400 mt-1">{ticket.issue_notes || '-'}</p>
                        <p className="text-[10px] text-slate-500 mt-1">Update: {ticket.updated_at ? new Date(ticket.updated_at).toLocaleString('id-ID') : '-'}</p>
                        <div className="mt-2 grid grid-cols-3 gap-1.5">
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px] btn-secondary"
                            onClick={() => navigate(`/dadu/penanganan?ticket=${ticket.id}`)}
                          >
                            Lihat
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            className="h-7 text-[10px] btn-secondary"
                            onClick={() => handleJoinIncidentTicket(ticket)}
                            disabled={updatingTicketId === ticket.id || isTicketLifecycleLocked(ticket)}
                          >
                            {updatingTicketId === ticket.id ? '...' : 'Progress'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            className="h-7 text-[10px] bg-emerald-500/85 hover:bg-emerald-400 text-slate-950"
                            onClick={() => handleCompleteIncidentTicket(ticket, false)}
                            disabled={updatingTicketId === ticket.id || isTicketLifecycleLocked(ticket)}
                          >
                            Resolve
                          </Button>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="mt-1.5 h-7 w-full text-[10px] btn-secondary"
                          onClick={() => startReplayFromTicket(ticket.id)}
                        >
                          Replay Incident
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="mt-1.5 h-7 w-full text-[10px] border-rose-400/35 text-rose-200 bg-rose-500/10 hover:bg-rose-500/20"
                          onClick={() => handleCompleteIncidentTicket(ticket, true)}
                          disabled={updatingTicketId === ticket.id || isTicketLifecycleLocked(ticket)}
                        >
                          Eskalasi
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>



            <div className={`bg-[#121212] rounded-xl p-4 border border-white/10 noc-side-card noc-customer-panel ${rightPanelTab !== 'customers' ? 'hidden' : ''}`}>

              <h3 className="text-white font-semibold mb-3">Pelanggan ({filteredCustomersByMode.length})</h3>

              <div className="noc-customer-summary mb-3">
                <span className="noc-customer-summary-chip is-online">Online {onlineCount}</span>
                <span className="noc-customer-summary-chip is-offline">Offline {offlineCount}</span>
                <span className="noc-customer-summary-chip is-unknown">Tidak Diketahui {unknownCount}</span>
              </div>

              <div className="noc-filter-chips mb-3">
                {[
                  { value: 'all', label: 'Semua' },
                  { value: 'online', label: 'Online' },
                  { value: 'offline', label: 'Offline' },
                  { value: 'anomaly', label: 'Anomali' },
                ].map((mode) => (
                  <button
                    type="button"
                    key={`customer-mode-${mode.value}`}
                    onClick={() => setCustomerFilterMode(mode.value)}
                    className={`noc-filter-chip ${customerFilterMode === mode.value ? 'is-active' : ''}`}
                  >
                    {mode.label}
                  </button>
                ))}
              </div>

              <div className="max-h-[42vh] overflow-auto space-y-2 pr-1">

                {filteredCustomersByMode.length === 0 && (

                  <p className="text-gray-500 text-sm">Tidak ada pelanggan sesuai filter.</p>

                )}



                {filteredCustomersByMode.map((customer) => {

                  const color = getStatusColor(customer.connection_status);
                  const linkage = resolveCustomerLinkageMeta(customer);
                  const activeTicket = activeTicketByCustomerId[String(customer.id || '').trim()] || null;

                  const isSelected = selectedCustomerId === customer.id;



                  return (

                    <button

                      type="button"

                      key={customer.id}

                      onClick={() => {
                        setSelectedCustomerId(customer.id);
                        handleFocusNode('customer', customer.id);
                      }}

                      className={`w-full text-left rounded-lg border p-3 transition noc-customer-row ${isSelected ? 'is-selected border-brand-blue bg-brand-blue/10' : 'border-white/10 bg-[#0A0A0A] hover:border-white/25'}`}

                    >

                      <div className="flex items-center justify-between gap-2">

                        <p className="text-white text-sm font-semibold truncate">{customer.name}</p>

                        <span className="text-[10px] px-2 py-0.5 rounded-full border" style={{ color, borderColor: color, backgroundColor: `${color}22` }}>

                          {getStatusLabel(customer.connection_status)}

                        </span>

                      </div>

                      <p className="text-gray-400 text-xs mt-1">{customer.customer_id} - {customer.odp || '-'}</p>

                      <p className="text-gray-500 text-xs">RX/Redaman: {formatSignal(customer.status_detail)}</p>
                      <p className="text-gray-500 text-xs">PPPoE: {getPppoeStatusLabel(customer.pppoe_connection_status)}</p>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                        <span className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] ${linkage.mappingTone}`}>
                          {linkage.mappingLabel}
                        </span>
                        <span className="inline-flex px-1.5 py-0.5 rounded-full border text-[10px] text-slate-200 border-slate-500/40 bg-slate-600/10">
                          NAS: {linkage.nasName}
                        </span>
                        {activeTicket ? (
                          <span className={`inline-flex px-1.5 py-0.5 rounded-full border text-[10px] ${getTicketToneClass(activeTicket.lifecycle?.tone)}`}>
                            Ticket: {activeTicket.lifecycle?.label || activeTicket.status || 'Open'}
                          </span>
                        ) : null}
                      </div>

                    </button>

                  );

                })}

              </div>

            </div>



            <div className={`bg-[#121212] rounded-xl p-4 border border-white/10 text-xs text-gray-300 space-y-2 noc-side-card noc-legend-panel ${rightPanelTab !== 'infra' ? 'hidden' : ''}`}>

              <div className="flex items-center justify-between gap-2">
                <p className="font-semibold text-white">Legenda</p>
                <span className="text-[10px] text-cyan-200/85 uppercase tracking-wide">Map Guide</span>
              </div>

              <p className="noc-legend-title">Infrastruktur</p>
              <div className="flex items-center gap-2"><span className={getInfraNodePreviewClassName('olt', 'live')} /> OLT live</div>
              <div className="flex items-center gap-2"><span className={getInfraNodePreviewClassName('olt', 'plan')} /> OLT plan</div>
              <div className="flex items-center gap-2"><span className={getInfraNodePreviewClassName('odp', 'live')} /> ODP live</div>
              <div className="flex items-center gap-2"><span className={getInfraNodePreviewClassName('odp', 'plan')} /> ODP plan</div>

              <p className="noc-legend-title">Status Pelanggan</p>
              <div className="flex items-center gap-2"><span className="map-legend-dot healthy pulse" /> Online</div>
              <div className="flex items-center gap-2"><span className="map-legend-dot down pulse" /> Offline / Loss</div>
              <div className="flex items-center gap-2"><span className="map-legend-dot unknown" /> Tidak Diketahui</div>

              <p className="noc-legend-title">Jalur</p>
              <div className="flex items-center gap-2"><span className="map-legend-line ftth" /> Distribusi FTTH</div>
              <div className="flex items-center gap-2"><span className="map-legend-line htb" /> Distribusi HTB</div>
              <div className="flex items-center gap-2"><span className="w-6 h-[2px] bg-red-500 inline-block" /> Problem (merah glow)</div>

            </div>

          </div>

        </div>
        </div>

      </AppShell>


    </div>

  );

};



export default AdminConnectivityMap;



















